import React, { useEffect, useState, useCallback, createContext, useContext, Component, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ChemicalsTab, ChemicalsSettingsBlock, InvoiceApplicationRecord, LogApplicationModal } from "@/components/Chemicals";
import { BillingProgress, UpcomingChargesCard } from "@/components/BillingProgress";
import { ServicePicker } from "@/components/ServicePicker";
import { ServiceCombobox } from "@/components/ServiceCombobox";
import { CustomerAutocompleteInput } from "@/components/CustomerAutocompleteInput";
import { TextQuoteButton } from "@/components/TextQuoteButton";
import { LayoutDashboard, Users, Calendar as CalendarIcon, MapPin, CreditCard, Settings as SettingsIcon, FlaskConical, HelpCircle, GraduationCap, LogOut, Link2, Smartphone, Zap, X, Copy, MessageSquare, Lock } from "lucide-react";

// SEO Component for dynamic page metadata
const SEO = ({ title, description, path = "", noIndex = false }) => {
  const baseUrl = "https://app.zipleadpro.com";
  const fullTitle = title ? `${title} | ZipLeadPro` : "ZipLeadPro - Intake Forms, Quotes & Job Scheduling";
  const defaultDescription = "Capture leads with custom intake forms, collect photos, send professional quotes with deposits, accept payments, and schedule jobs for your service business.";
  const ogImage = `${baseUrl}/og-image.png`;
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <link rel="canonical" href={`${baseUrl}${path}`} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:url" content={`${baseUrl}${path}`} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="800" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SHOW_WATCH_IT_WORK = false;
const SHOW_SETUP_TOUR = false;
const SHOW_LINK_LIVE_POPUP = false;
const API = `${BACKEND_URL}/api`;
// Debug: Log API URL on startup
// Base URL without /api for serving static files
const STATIC_URL = BACKEND_URL?.replace(/\/api$/, '') || '';

// ============== TOUR STATE MANAGEMENT ==============
// Clear all tour-related localStorage keys for a given slug
// This ensures the tour ALWAYS launches for new accounts, even if the browser
// has cached data from a previously deleted account
const clearTourStateForSlug = (slug) => {
  if (!slug) return;
  const keysToRemove = [
    `zipleadpro_tour_${slug}`,           // Tour completed flag
    `zipleadpro_tour_skipped_${slug}`,   // Tour skipped flag
    `zipleadpro_tour_step_${slug}`,      // Current tour step
    `checklist_step2_completed_${slug}`, // Checklist state
    `zlp_setup_celebrated_${slug}`,      // Celebration popup
    `zlp_notification_popup_${slug}`,    // Notification popup
    `zlp_install_dismissed_${slug}`,     // Install prompt dismissed
    `zlp_login_count_${slug}`,           // Login count
  ];
  
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('[TourState] Failed to remove key:', key, e);
    }
  });
};

// ============== INTERACTIVE PRODUCT TOUR ==============
const ProductTour = ({ 
  slug, 
  isNewUser, 
  onComplete, 
  setActiveSection, 
  businessCreatedAt,
  activeSection,
  services = [],
  isPreviewOpen = false,
  formMode = 'buttons',
  formUrl = '',
  openMobileMenu,
  onClosePreview,
  // New callbacks for unified tour
  isEditorExpanded = false,
  onFormTapped,
  onFieldToggled,
  onChangesSaved,
  onLinkCopied
}) => {
  // Keys for localStorage
  const tourKey = `zipleadpro_tour_${slug}`;
  const tourSkippedKey = `zipleadpro_tour_skipped_${slug}`;
  const stepKey = `zipleadpro_tour_step_${slug}`;
  
  // Unified Interactive Tour - ALL steps require user action (no "Next" buttons)
  // 10 steps guiding user through complete setup
  // IMPORTANT: This must be defined before any useEffect that references it
  const steps = useMemo(() => [
    {
      id: "add-service",
      title: "Welcome! Add Your First Service 👋",
      description: "Let's bring your business to life! Click the + Add button to create your first service offering.",
      buttonText: null,
      navigateTo: "setup",
      highlightSelector: "[data-tour='add-service-btn']",
      scrollTo: true,
      interactive: true,
      waitFor: "service-created"
    },
    {
      id: "open-preview",
      title: "See Your Form Come Alive 👁️",
      description: "Now let's see what your customers will experience. Open the preview to watch your form take shape!",
      buttonText: null,
      navigateTo: "setup",
      highlightSelector: "[data-tour='preview-button']",
      scrollTo: true,
      interactive: true,
      waitFor: "preview-opened"
    },
    {
      id: "choose-form-style",
      title: "Try both styles!",
      description: "Toggle between Buttons and Form to see the difference",
      buttonText: "Looks Good",
      navigateTo: "setup",
      highlightSelector: "[data-tour='form-style-select']",
      scrollTo: true,
      interactive: true,
      waitFor: null,
      minimalCard: true
    },
    {
      id: "expand-editor",
      title: "Open Settings ⚙️",
      description: "Click to reveal your customization options!",
      buttonText: null,
      navigateTo: "setup",
      highlightSelector: "[data-tour='customize-expand']",
      scrollTo: true,
      interactive: true,
      waitFor: "editor-expanded"
    },
    {
      id: "field-controls",
      title: "Choose What Matters 📋",
      description: "You're in control! Toggle fields on or off to collect exactly what you need.\n\nMark fields as required to ensure you get the important stuff.\n\nClick any toggle to continue.",
      buttonText: null,
      navigateTo: "setup",
      highlightSelector: "[data-tour='field-settings']",
      scrollTo: true,
      interactive: true,
      waitFor: "field-toggled"
    },
    {
      id: "done",
      title: "You Did It! 🎉",
      description: "Your professional lead form is ready to start bringing in customers. Share your link and watch the leads roll in!",
      buttonText: "Finish Tour",
      navigateTo: "setup",
      highlightSelector: null,
      interactive: false,
      waitFor: null
    }
  ], []);
  
  // Initialize state from localStorage - but with better logic
  const [currentStep, setCurrentStep] = useState(() => {
    try {
      const saved = localStorage.getItem(stepKey);
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });
  
  // Visibility: Tour is visible if:
  // 1. User is new (hasn't completed/skipped tour) AND
  // 2. Tour hasn't been explicitly hidden this session
  const [explicitlyHidden, setExplicitlyHidden] = useState(() => {
    // Initialize from localStorage to persist skip across navigation
    try {
      return localStorage.getItem(tourSkippedKey) === 'true';
    } catch {
      return false;
    }
  });
  const [tourStarted, setTourStarted] = useState(false);
  
  // Compute visibility: show tour if new user and not explicitly hidden
  const isVisible = isNewUser && !explicitlyHidden;
  
  const [highlightRect, setHighlightRect] = useState(null);
  const [tourSkipped, setTourSkipped] = useState(false);
  const [initialFormMode, setInitialFormMode] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [cardOpacity, setCardOpacity] = useState(1); // For smooth fade transitions
  const [isMobile, setIsMobile] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  
  // Save current step whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(stepKey, String(currentStep));
    } catch (e) {
      console.error('[ProductTour] Failed to save step:', e);
    }
  }, [currentStep, stepKey]);
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Reposition highlight on resize/scroll (fixes Android alignment when URL bar hides/shows)
  useEffect(() => {
    if (!isVisible || !highlightRect) return;
    
    const step = steps[currentStep];
    if (!step?.highlightSelector) return;
    
    const repositionHighlight = () => {
      const el = document.querySelector(step.highlightSelector);
      if (el) {
        const rect = el.getBoundingClientRect();
        setHighlightRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        });
      }
    };
    
    window.addEventListener('resize', repositionHighlight);
    window.addEventListener('scroll', repositionHighlight, true);
    
    return () => {
      window.removeEventListener('resize', repositionHighlight);
      window.removeEventListener('scroll', repositionHighlight, true);
    };
  }, [isVisible, currentStep, highlightRect, steps]);
  
  // Watch for dialog/modal elements appearing (Radix Dialog uses [data-state="open"])
  useEffect(() => {
    const checkDialogOpen = () => {
      const openDialog = document.querySelector('[role="dialog"][data-state="open"]');
      setIsDialogOpen(!!openDialog);
    };
    
    // Check initially and set up observer
    checkDialogOpen();
    const observer = new MutationObserver(checkDialogOpen);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    
    return () => observer.disconnect();
  }, []);
  
  // Check if tour was skipped previously
  useEffect(() => {
    const hasSkippedTour = localStorage.getItem(tourSkippedKey) === 'true';
    setTourSkipped(hasSkippedTour);
  }, [tourSkippedKey, isNewUser]);
  
  // Track initial form mode to detect changes (step 2 is choose-form-style)
  useEffect(() => {
    if (currentStep === 2 && initialFormMode === null) {
      setInitialFormMode(formMode);
    }
  }, [currentStep, formMode, initialFormMode]);
  
  // Listen for user actions to advance interactive steps
  useEffect(() => {
    if (!isVisible) return;
    
    const step = steps[currentStep];
    if (!step.waitFor) return;
    
    // Service created
    if (step.waitFor === "service-created" && services.length > 0) {
      setTimeout(() => advanceStep(), 500);
    }
    
    // Preview opened
    if (step.waitFor === "preview-opened" && isPreviewOpen) {
      setTimeout(() => advanceStep(), 300);
    }
    
    // Form style changed
    if (step.waitFor === "form-style-changed" && initialFormMode !== null && formMode !== initialFormMode) {
      setTimeout(() => advanceStep(), 300);
    }
    
    // Editor expanded
    if (step.waitFor === "editor-expanded" && isEditorExpanded) {
      setTimeout(() => advanceStep(), 300);
    }
    
    // Field toggled - listen for clicks on field settings
    if (step.waitFor === "field-toggled") {
      const handler = (e) => {
        const fieldSettings = document.querySelector("[data-tour='field-settings']");
        const requiredSettings = document.querySelector("[data-tour='required-settings']");
        if ((fieldSettings && fieldSettings.contains(e.target)) || 
            (requiredSettings && requiredSettings.contains(e.target))) {
          setTimeout(() => advanceStep(), 300);
        }
      };
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [currentStep, isVisible, services.length, isPreviewOpen, formMode, initialFormMode, isEditorExpanded]);
  
  // Navigate and highlight when step changes
  useEffect(() => {
    if (!isVisible) return;
    
    const step = steps[currentStep];
    
    // Always navigate to setup for the tour (all steps are on setup page)
    if (step.navigateTo && setActiveSection) {
      setActiveSection(step.navigateTo);
    }
    
    // Fade out card, then update highlight, then fade back in
    setCardOpacity(0);
    
    const timer = setTimeout(() => {
      if (step.highlightSelector) {
        const el = document.querySelector(step.highlightSelector);
        if (el) {
          if (step.scrollTo) {
            // Use instant scroll to exact position - no animation
            const r = el.getBoundingClientRect();
            const y = window.pageYOffset + r.top - 100;
            window.scrollTo(0, y);
          }
          
          // Use requestAnimationFrame and add delay for Android rendering
          requestAnimationFrame(() => {
            setTimeout(() => {
              const rect = el.getBoundingClientRect();
              // Account for any scroll offset (fixes Android alignment issues)
              setHighlightRect({
                top: rect.top - 8,
                left: rect.left - 8,
                width: rect.width + 16,
                height: rect.height + 16
              });
              // Fade back in after position is set
              setTimeout(() => setCardOpacity(1), 50);
            }, 50); // Small delay helps Android render correctly
          });
        } else {
          setHighlightRect(null);
          setTimeout(() => setCardOpacity(1), 50);
        }
      } else {
        setHighlightRect(null);
        setTimeout(() => setCardOpacity(1), 50);
      }
    }, 400);
    
    return () => clearTimeout(timer);
  }, [currentStep, isVisible, setActiveSection]);
  
  const advanceStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };
  
  const completeTour = async () => {
    try {
      localStorage.setItem(tourKey, 'true');
      localStorage.removeItem(tourSkippedKey);
      localStorage.removeItem(stepKey); // Clean up step tracking
      // Log tour completion activity
      if (slug) {
        try {
          await api.logActivity(slug, 'tour_completed', 'onboarding', true, { steps_completed: currentStep + 1 });
        } catch (e) {
          console.error('[ProductTour] Failed to log tour completion:', e);
        }
      }
    } catch (err) {
      console.error('[ProductTour] Error saving to localStorage:', err);
    }
    setExplicitlyHidden(true);
    setHighlightRect(null);
    // Close preview panel so celebration popup is visible
    if (onClosePreview) onClosePreview();
    if (onComplete) onComplete();
  };
  
  const skipTour = async () => {
    try {
      localStorage.setItem(tourSkippedKey, 'true');
      localStorage.removeItem(stepKey);
      // Log tour skip activity
      if (slug) {
        try {
          await api.logActivity(slug, 'tour_skipped', 'onboarding', true, { skipped_at_step: currentStep });
        } catch (e) {
          console.error('[ProductTour] Failed to log tour skip:', e);
        }
      }
    } catch (e) {
      console.error('[ProductTour] Failed to save skip state:', e);
    }
    setTourSkipped(true);
    setExplicitlyHidden(true);
    setHighlightRect(null);
  };
  
  if (!isVisible) {
    return null;
  }
  
  
  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;
  const isInteractiveStep = step.interactive && step.waitFor;
  
  // Create segmented overlay for interactive steps (allows clicking through highlighted area)
  const renderOverlay = () => {
    if (isInteractiveStep && highlightRect) {
      // Create a frame around the highlighted area with 4 corner pieces + 4 edge pieces
      // This ensures the highlighted area is completely clear
      return (
        <>
          {/* Top-left corner */}
          <div 
            className="absolute bg-black/50 left-0 top-0"
            style={{ 
              width: Math.max(0, highlightRect.left),
              height: Math.max(0, highlightRect.top)
            }}
          />
          {/* Top-center (above highlight) */}
          <div 
            className="absolute bg-black/50 top-0"
            style={{ 
              left: highlightRect.left,
              width: highlightRect.width,
              height: Math.max(0, highlightRect.top)
            }}
          />
          {/* Top-right corner */}
          <div 
            className="absolute bg-black/50 right-0 top-0"
            style={{ 
              left: highlightRect.left + highlightRect.width,
              height: Math.max(0, highlightRect.top)
            }}
          />
          {/* Middle-left (left of highlight) */}
          <div 
            className="absolute bg-black/50 left-0"
            style={{ 
              top: highlightRect.top,
              width: Math.max(0, highlightRect.left),
              height: highlightRect.height
            }}
          />
          {/* Middle-right (right of highlight) */}
          <div 
            className="absolute bg-black/50 right-0"
            style={{ 
              top: highlightRect.top,
              left: highlightRect.left + highlightRect.width,
              height: highlightRect.height
            }}
          />
          {/* Bottom-left corner */}
          <div 
            className="absolute bg-black/50 left-0 bottom-0"
            style={{ 
              width: Math.max(0, highlightRect.left),
              top: highlightRect.top + highlightRect.height
            }}
          />
          {/* Bottom-center (below highlight) */}
          <div 
            className="absolute bg-black/50 bottom-0"
            style={{ 
              left: highlightRect.left,
              width: highlightRect.width,
              top: highlightRect.top + highlightRect.height
            }}
          />
          {/* Bottom-right corner */}
          <div 
            className="absolute bg-black/50 right-0 bottom-0"
            style={{ 
              left: highlightRect.left + highlightRect.width,
              top: highlightRect.top + highlightRect.height
            }}
          />
        </>
      );
    }
    // Full overlay for non-interactive steps
    return <div className="absolute inset-0 bg-black/50" />;
  };
  
  // Hide overlay when a dialog is open (let user interact with dialog)
  const shouldHideOverlay = isDialogOpen;
  
  return createPortal(
    <div className={`fixed inset-0 z-[10001] ${isInteractiveStep || shouldHideOverlay || step.minimalCard ? 'pointer-events-none' : ''}`}>
      {/* Segmented or full overlay - hide when dialog is open or minimalCard step */}
      {!shouldHideOverlay && !step.minimalCard && (
        <div className="pointer-events-auto">
          {renderOverlay()}
        </div>
      )}
      
      {/* Highlight border (visual only, doesn't block clicks) - hide when dialog open */}
      {highlightRect && !shouldHideOverlay && (
        <div
          className="absolute border-4 border-amber-400 rounded-xl pointer-events-none"
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            boxShadow: '0 0 40px 10px rgba(255,200,0,0.4)',
            opacity: cardOpacity,
            transition: 'opacity 0.2s ease-out, top 0.3s ease-out, left 0.3s ease-out, width 0.3s ease-out, height 0.3s ease-out',
            animation: 'tourPulse 2s ease-in-out infinite'
          }}
        />
      )}
      
      {/* Minimal floating bar for form-style step */}
      {step.minimalCard ? (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-full shadow-lg px-4 py-2 pointer-events-auto flex items-center gap-3 z-[10001]"
          style={{
            opacity: cardOpacity,
            transition: 'opacity 0.2s ease-out'
          }}
        >
          <span className="text-sm font-medium text-gray-700">{step.title}</span>
          <button
            onClick={advanceStep}
            className="px-4 py-1.5 bg-amber-500 text-white rounded-full text-sm font-semibold"
          >
            {step.buttonText}
          </button>
          <button
            onClick={skipTour}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            Skip
          </button>
        </div>
      ) : (
      /* Standard tour card - hide completely when dialog is open */
      !isDialogOpen ? (
      <div
        className="fixed bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-[90%] mx-auto pointer-events-auto"
        style={{
          top: highlightRect 
            ? (highlightRect.top > window.innerHeight / 2 ? '8%' : 'auto')
            : '50%',
          bottom: highlightRect 
            ? (highlightRect.top <= window.innerHeight / 2 ? '8%' : 'auto')
            : 'auto',
          left: '50%',
          transform: highlightRect ? 'translateX(-50%)' : 'translate(-50%, -50%)',
          opacity: cardOpacity,
          transition: 'opacity 0.2s ease-out, top 0.3s ease-out, bottom 0.3s ease-out'
        }}
      >
        {/* Progress bar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-amber-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 font-medium">{currentStep + 1}/{steps.length}</span>
        </div>
        
        {/* Content */}
        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
        <p className="text-gray-600 mb-4 leading-relaxed whitespace-pre-line">{step.description}</p>
        
        {/* Interactive step hint */}
        {!isDialogOpen && step.highlightSelector && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-amber-800 text-sm flex items-center gap-2">
              <span className="text-lg">👆</span>
              <span>Click the highlighted area to continue</span>
            </p>
          </div>
        )}
        
        {/* Dialog open hint - guide user through the form */}
        {isDialogOpen && step.waitFor === "service-created" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-slate-900 text-sm flex items-center gap-2">
              <span className="text-lg">✍️</span>
              <span>Fill in your service details and click Save!</span>
            </p>
          </div>
        )}
        
        {/* Buttons */}
        <div className="flex flex-col gap-2">
          <div className="flex gap-3">
            <button
              onClick={() => setShowSkipConfirm(true)}
              className="flex-1 py-3 text-gray-500 hover:text-gray-700 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Skip Tour
            </button>
            {step.buttonText && (
              <button
                onClick={advanceStep}
                className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold transition-colors"
              >
                {step.buttonText}
              </button>
            )}
          </div>
          {showSkipConfirm && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => { setShowSkipConfirm(false); setExplicitlyHidden(true); }}
                className="flex-1 py-2 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Skip for now
              </button>
              <button
                onClick={skipTour}
                className="flex-1 py-2 text-xs text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Don't show again
              </button>
            </div>
          )}
        </div>
      </div>
      ) : null
      )}
      
      {/* Pulse animation style */}
      <style>{`
        @keyframes tourPulse {
          0%, 100% { box-shadow: 0 0 40px 10px rgba(255,200,0,0.4); }
          50% { box-shadow: 0 0 60px 15px rgba(255,200,0,0.6); }
        }
      `}</style>
    </div>,
    document.body
  );
};

// ============== ERROR BOUNDARY ==============
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600">Something went wrong</CardTitle>
              <CardDescription>We encountered an unexpected error.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                {this.state.error?.message || "An error occurred while loading this page."}
              </p>
              <Button onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============== AUTH CONTEXT ==============
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Verify token and get user info
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          setUser({ ...res.data, token });
        })
        .catch(() => {
          // Token invalid - clear all auth data
          localStorage.removeItem("token");
          localStorage.removeItem("businessSlug");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      // No token - ensure businessSlug is also cleared
      localStorage.removeItem("businessSlug");
      // Use setTimeout to avoid cascading renders
      setTimeout(() => setLoading(false), 0);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    setUser({ ...userData, token });
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("businessSlug");
    setUser(null);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============== PROTECTED ROUTE ==============
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper to safely extract error message (prevents React error #31)
const getErrorMessage = (error, fallback = "An error occurred") => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || d.message || String(d)).join(', ');
  if (typeof detail === 'object') return detail.msg || detail.message || fallback;
  return fallback;
};

// ============== API SERVICE ==============

// Resilient retry helper: Safari mobile in particular surfaces transient backend
// hiccups (cold-start, edge dropouts) as a hard network error before any HTTP
// response is returned. We retry once after a brief delay to absorb 1-second blips,
// but bail out fast if the backend is genuinely unreachable — staring at a spinner
// for 30+ seconds is worse UX than seeing a clear error and being able to retry.
const retryNetwork = async (fn, attempts = 2) => {
  let lastErr = null;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      // Only retry on true network-level failures (no response object). If the
      // server responded with a 4xx/5xx, that's a real answer — surface it.
      const isNetwork = !err?.response;
      lastErr = err;
      if (!isNetwork || i === attempts - 1) throw err;
      await new Promise(r => setTimeout(r, 500));
    }
  }
  throw lastErr;
};

const api = {
  // Auth
  adminLogin: async (email, password) => {
    const response = await retryNetwork(() => axios.post(`${API}/auth/admin-login`, { email, password }, { withCredentials: false, timeout: 8000 }));
    return response.data;
  },
  businessLogin: async (email, password) => {
    const response = await retryNetwork(() => axios.post(`${API}/auth/business-login`, { email, password }, { withCredentials: false, timeout: 8000 }));
    return response.data;
  },
  businessForgotPassword: async (email) => {
    const response = await axios.post(`${API}/auth/business-forgot-password`, { email });
    return response.data;
  },
  businessResetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API}/auth/business-reset-password`, { token, new_password: newPassword });
    return response.data;
  },
  createFreeTrialAccount: async (data) => {
    const response = await axios.post(`${API}/auth/create-free-trial`, data);
    return response.data;
  },
  verifyMagicLink: async (token) => {
    const response = await axios.post(`${API}/auth/verify-magic-link`, { token });
    return response.data;
  },
  getMe: async () => {
    const response = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // WebAuthn / Face ID
  webauthnRegisterStart: async (email, userType = "business") => {
    const response = await axios.post(`${API}/auth/webauthn/register/start`, { email, user_type: userType });
    return response.data;
  },
  webauthnRegisterFinish: async (email, userType, credential) => {
    const response = await axios.post(`${API}/auth/webauthn/register/finish`, { email, user_type: userType, credential });
    return response.data;
  },
  webauthnAuthStart: async (email, userType = "business") => {
    const response = await axios.post(`${API}/auth/webauthn/auth/start`, { email, user_type: userType });
    return response.data;
  },
  webauthnAuthFinish: async (email, userType, credential) => {
    const response = await axios.post(`${API}/auth/webauthn/auth/finish`, { email, user_type: userType, credential });
    return response.data;
  },
  webauthnCheckPasskey: async (email, userType = "business") => {
    const response = await axios.get(`${API}/auth/webauthn/check/${encodeURIComponent(email)}?user_type=${userType}`);
    return response.data;
  },
  
  // Admin Password Reset
  adminRequestPasswordReset: async (email) => {
    const response = await axios.post(`${API}/auth/admin/request-password-reset`, { email });
    return response.data;
  },
  adminResetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API}/auth/admin/reset-password`, { token, new_password: newPassword });
    return response.data;
  },
  
  // Subscription
  createSubscriptionCheckout: async (slug, billingInterval = "monthly") => {
    const response = await axios.post(`${API}/tenants/${slug}/create-subscription-checkout`, { billing_interval: billingInterval }, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Leads
  createLead: async (data, businessSlug = null, skipNotification = false) => {
    let url = businessSlug ? `${API}/leads/t/${businessSlug}` : `${API}/leads`;
    if (skipNotification) {
      url += '?skip_notification=true';
    }
    const response = await axios.post(url, data);
    return response.data;
  },
  getLeads: async (businessSlug = null, status = null) => {
    let url = `${API}/leads`;
    const params = new URLSearchParams();
    if (businessSlug && businessSlug !== "all") params.append("tenant_slug", businessSlug);
    if (status && status !== "all") params.append("status", status);
    if (params.toString()) url += `?${params.toString()}`;
    const response = await axios.get(url, { headers: getAuthHeaders() });
    return response.data;
  },
  updateLead: async (leadId, data) => {
    const response = await axios.patch(`${API}/leads/${leadId}`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteLead: async (leadId) => {
    await axios.delete(`${API}/leads/${leadId}`, { headers: getAuthHeaders() });
  },
  bulkDeleteLeads: async (slug, ids) => {
    const response = await axios.post(`${API}/tenants/${slug}/leads/bulk-delete`, { ids }, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Customer Management
  getCustomers: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/customers`, { headers: getAuthHeaders() });
    return response.data;
  },
  createCustomer: async (slug, data) => {
    const response = await axios.post(`${API}/tenants/${slug}/customers`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  updateCustomer: async (slug, email, data) => {
    const response = await axios.patch(`${API}/tenants/${slug}/customers/${encodeURIComponent(email)}`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteCustomer: async (slug, email) => {
    const response = await axios.delete(`${API}/tenants/${slug}/customers/${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
    return response.data;
  },
  bulkDeleteCustomers: async (slug, emails) => {
    const response = await axios.post(`${API}/tenants/${slug}/customers/bulk-delete`, { ids: emails }, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Job Scheduling
  getUnscheduledJobs: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/unscheduled-jobs`, { headers: getAuthHeaders() });
    return response.data;
  },
  getScheduledJobs: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/scheduled-jobs`, { headers: getAuthHeaders() });
    return response.data;
  },
  scheduleJob: async (leadId, scheduledDate, scheduledTime) => {
    const response = await axios.post(`${API}/leads/${leadId}/schedule`, { 
      scheduled_date: scheduledDate, 
      scheduled_time: scheduledTime || null 
    }, { headers: getAuthHeaders() });
    return response.data;
  },
  rescheduleJob: async (leadId, scheduledDate, scheduledTime) => {
    const response = await axios.patch(`${API}/leads/${leadId}/reschedule`, { 
      scheduled_date: scheduledDate, 
      scheduled_time: scheduledTime || null 
    }, { headers: getAuthHeaders() });
    return response.data;
  },
  removeFromQueue: async (leadId) => {
    const response = await axios.post(`${API}/leads/${leadId}/remove-from-queue`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  cancelSchedule: async (leadId) => {
    const response = await axios.post(`${API}/leads/${leadId}/cancel-schedule`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Businesses
  getBusinesses: async () => {
    const response = await axios.get(`${API}/tenants`, { headers: getAuthHeaders() });
    return response.data;
  },
  getBusiness: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}`, { headers: getAuthHeaders() });
    return response.data;
  },
  createBusiness: async (data) => {
    const response = await axios.post(`${API}/tenants`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  updateBusiness: async (slug, data) => {
    const response = await axios.patch(`${API}/tenants/${slug}`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  publishDomain: async (slug, publishMode, customDomain = null) => {
    const response = await axios.post(`${API}/tenants/${slug}/publish`, { 
      publish_mode: publishMode, 
      custom_domain: customDomain 
    }, { headers: getAuthHeaders() });
    return response.data;
  },
  verifyDomain: async (slug) => {
    const response = await axios.post(`${API}/tenants/${slug}/verify-domain`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getDomainStatus: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/domain-status`, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteBusiness: async (slug) => {
    await axios.delete(`${API}/tenants/${slug}`, { headers: getAuthHeaders() });
  },
  markSetupCelebrated: async (slug) => {
    const response = await axios.post(`${API}/tenants/${slug}/mark-celebrated`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Admin Subscription Management (via Stripe API)
  adminManageSubscription: async (slug, action, reason = null, trialDays = null) => {
    const response = await axios.post(`${API}/tenants/${slug}/admin-subscription`, { action, reason, trial_days: trialDays }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Business self-service subscription management (pause/resume/cancel)
  businessManageSubscription: async (slug, action) => {
    const response = await axios.post(`${API}/tenants/${slug}/subscription-action`, { action }, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Admin: Send magic link to business
  adminSendMagicLink: async (slug) => {
    const response = await axios.post(`${API}/admin/tenants/${slug}/send-magic-link`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Accept terms for existing tenant
  acceptTerms: async (slug, data) => {
    const response = await axios.post(`${API}/tenants/${slug}/accept-terms`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Product Catalog
  getProducts: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/products`);
    return response.data;
  },
  
  createProductCheckout: async (slug, items, customerEmail, customerName, shippingRequired = false) => {
    const response = await axios.post(`${API}/tenants/${slug}/product-checkout`, {
      items,
      customer_email: customerEmail,
      customer_name: customerName,
      shipping_required: shippingRequired
    });
    return response.data;
  },
  
  // Debug endpoints for quick subscription fixes
  debugGetTenantStatus: async (slug) => {
    const response = await axios.get(`${API}/debug/tenant/${slug}`);
    return response.data;
  },
  
  debugFixSubscription: async (slug, newStatus) => {
    const response = await axios.post(`${API}/debug/tenant/${slug}/fix-subscription?new_status=${newStatus}`);
    return response.data;
  },
  
  // Logo
  uploadLogo: async (slug, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API}/tenants/${slug}/logo`, formData, {
      headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  
  deleteLogo: async (slug) => {
  const response = await axios.delete(`${API}/tenants/${slug}/logo`, {
    headers: getAuthHeaders(),
  });
  return response.data;
},

  // Intake Config
  getIntakeConfig: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/intake-config`);
    return response.data;
  },
  
  // Services
  getServices: async (slug, includeInactive = false) => {
    const response = await axios.get(`${API}/tenants/${slug}/services?include_inactive=${includeInactive}`);
    return response.data;
  },
  createService: async (slug, data) => {
    const response = await axios.post(`${API}/tenants/${slug}/services`, data);
    return response.data;
  },
  updateService: async (slug, serviceId, data) => {
    const response = await axios.patch(`${API}/tenants/${slug}/services/${serviceId}`, data);
    return response.data;
  },
  deleteService: async (slug, serviceId) => {
    await axios.delete(`${API}/tenants/${slug}/services/${serviceId}`);
  },
  reorderServices: async (slug, serviceIds) => {
    const response = await axios.post(`${API}/tenants/${slug}/services/reorder`, serviceIds);
    return response.data;
  },
  
  // Test Email & Logs
  sendTestEmail: async (slug, testEmail) => {
    const response = await axios.post(`${API}/tenants/${slug}/test-email`, { test_email: testEmail });
    return response.data;
  },
  getEmailLogs: async (slug = null) => {
    const url = slug ? `${API}/tenants/${slug}/email-logs` : `${API}/email-logs`;
    const response = await axios.get(url);
    return response.data;
  },
  getEmailContent: async (logId) => {
    const response = await axios.get(`${API}/email-logs/${logId}/content`);
    return response.data;
  },
  // Activity Tracking
  getActivityLogs: async (slug) => {
    const response = await axios.get(`${API}/admin/tenants/${slug}/activity`, { headers: getAuthHeaders() });
    return response.data;
  },
  logActivity: async (slug, eventType, category, success = true, metadata = null) => {
    const response = await axios.post(`${API}/tenants/${slug}/activity`, {
      event_type: eventType,
      category,
      success,
      metadata
    }, { headers: getAuthHeaders() });
    return response.data;
  },
  sendBroadcastEmail: async (subject, htmlContent, sendTo = "all") => {
    const response = await axios.post(`${API}/admin/broadcast-email`, {
      subject,
      html_content: htmlContent,
      send_to: sendTo
    }, { headers: getAuthHeaders() });
    return response.data;
  },
  downloadTermsPdf: async (slug) => {
    const response = await axios.get(`${API}/admin/tenants/${slug}/terms-pdf`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `terms_agreement_${slug}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  getEmbedCode: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/embed-code`);
    return response.data;
  },
  
  // Packages & Checkout
  getPackages: async () => {
    const response = await axios.get(`${API}/packages`);
    return response.data;
  },
  // New: Free trial signup (no card required)
  createTrialAccount: async (data) => {
    const response = await axios.post(`${API}/signup/trial`, data);
    return response.data;
  },
  createCheckoutSession: async (data) => {
    const response = await axios.post(`${API}/checkout/create-session`, data);
    return response.data;
  },
  getCheckoutSession: async (sessionId) => {
    const response = await axios.get(`${API}/checkout/session/${sessionId}`);
    return response.data;
  },
  checkEmailExists: async (email) => {
    const response = await axios.post(`${API}/checkout/check-email`, { email });
    return response.data;
  },
  sendExistingAccountMagicLink: async (email) => {
    const response = await axios.post(`${API}/checkout/send-magic-link`, { email });
    return response.data;
  },
  
  // Onboarding
  validateOnboardingToken: async (token) => {
    const response = await axios.get(`${API}/onboarding/${token}`);
    return response.data;
  },
  updateOnboarding: async (token, data) => {
    const response = await axios.patch(`${API}/onboarding/${token}`, data);
    return response.data;
  },
  completeOnboarding: async (token, password = null) => {
    const response = await axios.post(`${API}/onboarding/${token}/complete`, { password });
    return response.data;
  },

  // Stripe Connect
  getStripeConnectStatus: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/stripe-connect/status`, { headers: getAuthHeaders() });
    return response.data;
  },
  createStripeConnectAccount: async (slug) => {
    const response = await axios.post(`${API}/tenants/${slug}/stripe-connect/create-account`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getStripeConnectDashboardLink: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/stripe-connect/dashboard-link`, { headers: getAuthHeaders() });
    return response.data;
  },
  getStripeConnectDashboardData: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/stripe-connect/dashboard-data`, { headers: getAuthHeaders() });
    return response.data;
  },
  createStripePayout: async (slug) => {
    const response = await axios.post(`${API}/tenants/${slug}/stripe-connect/payout`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getStripePayoutSettingsLink: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/stripe-connect/payout-settings-link`, { headers: getAuthHeaders() });
    return response.data;
  },
  getStripeAccountDetails: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/stripe-connect/account-details`, { headers: getAuthHeaders() });
    return response.data;
  },
  listMyStripeAccounts: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/stripe-connect/my-accounts`, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteOrphanStripeAccount: async (slug, accountId) => {
    const response = await axios.delete(`${API}/tenants/${slug}/stripe-connect/delete-orphan/${accountId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  getAdminStripeConnectAccounts: async () => {
    const response = await axios.get(`${API}/admin/stripe-connect/all-accounts`, { headers: getAuthHeaders() });
    return response.data;
  },
  listAllPlatformStripeAccounts: async () => {
    const response = await axios.get(`${API}/admin/stripe-connect/list-all-platform-accounts`, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteStripeAccount: async (accountId) => {
    const response = await axios.delete(`${API}/admin/stripe-connect/delete-account/${accountId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  disconnectStripeAccount: async (slug) => {
    const response = await axios.post(`${API}/tenants/${slug}/stripe-connect/disconnect`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  refreshStripeConnectLink: async (slug) => {
    const response = await axios.post(`${API}/tenants/${slug}/stripe-connect/refresh-link`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  createBillingPortal: async (slug) => {
    const response = await axios.post(`${API}/tenants/${slug}/billing-portal`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Create product in connected Stripe account
  createStripeProduct: async (slug, name, priceCents, description = null, imageUrl = null, quantityLimit = null) => {
    const response = await axios.post(`${API}/tenants/${slug}/products`, {
      name,
      price_cents: priceCents,
      description,
      image_url: imageUrl,
      quantity_limit: quantityLimit
    }, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Upload product image
  uploadProductImage: async (slug, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/tenants/${slug}/product-image`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  // Update product
  updateStripeProduct: async (slug, productId, data) => {
    const response = await axios.put(`${API}/tenants/${slug}/products/${productId}`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Delete product
  deleteStripeProduct: async (slug, productId) => {
    const response = await axios.delete(`${API}/tenants/${slug}/products/${productId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  
  // Add additional product image
  addProductAdditionalImage: async (slug, productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/tenants/${slug}/products/${productId}/additional-images`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  // Remove additional product image
  removeProductAdditionalImage: async (slug, productId, imageUrl) => {
    const response = await axios.delete(`${API}/tenants/${slug}/products/${productId}/additional-images`, {
      headers: getAuthHeaders(),
      params: { image_url: imageUrl }
    });
    return response.data;
  },

  // Quotes
  getQuotes: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/quotes`, { headers: getAuthHeaders() });
    return response.data;
  },
  getQuote: async (slug, quoteId) => {
    const response = await axios.get(`${API}/tenants/${slug}/quotes/${quoteId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  createQuote: async (slug, data) => {
    const response = await axios.post(`${API}/tenants/${slug}/quotes`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  updateQuote: async (slug, quoteId, data) => {
    const response = await axios.patch(`${API}/tenants/${slug}/quotes/${quoteId}`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  sendQuote: async (slug, quoteId) => {
    const response = await axios.post(`${API}/tenants/${slug}/quotes/${quoteId}/send`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  sendQuoteWithoutPayment: async (slug, quoteId) => {
    const response = await axios.post(`${API}/tenants/${slug}/quotes/${quoteId}/send-without-payment`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  collectBalance: async (slug, quoteId, data) => {
    const response = await axios.post(`${API}/tenants/${slug}/quotes/${quoteId}/collect-balance`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteQuote: async (slug, quoteId) => {
    await axios.delete(`${API}/tenants/${slug}/quotes/${quoteId}`, { headers: getAuthHeaders() });
  },
  bulkDeleteQuotes: async (slug, ids) => {
    const response = await axios.post(`${API}/tenants/${slug}/quotes/bulk-delete`, { ids }, { headers: getAuthHeaders() });
    return response.data;
  },
  recordOfflinePayment: async (slug, quoteId, method, amount) => {
    const response = await axios.post(`${API}/tenants/${slug}/quotes/${quoteId}/record-payment`, { method, amount }, { headers: getAuthHeaders() });
    return response.data;
  },
  manualApproveQuote: async (slug, quoteId) => {
    const response = await axios.post(`${API}/tenants/${slug}/quotes/${quoteId}/manual-approve`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  unapproveQuote: async (slug, quoteId) => {
    const response = await axios.post(`${API}/tenants/${slug}/quotes/${quoteId}/unapprove`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteOfflinePayment: async (slug, quoteId, paymentId) => {
    const response = await axios.delete(`${API}/tenants/${slug}/quotes/${quoteId}/offline-payments/${paymentId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Quote Email Template
  getQuoteEmailTemplate: async (slug) => {
    const response = await axios.get(`${API}/tenants/${slug}/quote-email-template`, { headers: getAuthHeaders() });
    return response.data;
  },
  updateQuoteEmailTemplate: async (slug, data) => {
    const response = await axios.patch(`${API}/tenants/${slug}/quote-email-template`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  // Lead Details
  getLead: async (leadId) => {
    const response = await axios.get(`${API}/leads/${leadId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Admin Quotes (all quotes across all tenants)
  getAllQuotes: async (tenantSlug = null, status = null) => {
    let url = `${API}/quotes`;
    const params = new URLSearchParams();
    if (tenantSlug && tenantSlug !== "all") params.append("tenant_slug", tenantSlug);
    if (status && status !== "all") params.append("status", status);
    if (params.toString()) url += `?${params.toString()}`;
    const response = await axios.get(url, { headers: getAuthHeaders() });
    return response.data;
  },

  // Photo Upload
  uploadPhoto: async (slug, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API}/tenants/${slug}/upload-photo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  deletePhoto: async (photoId) => {
    await axios.delete(`${API}/photos/${photoId}`);
  },
  uploadLeadFile: async (slug, formData) => {
    const response = await axios.post(`${API}/tenants/${slug}/upload-file`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
};

// ============== PRICING HELPERS ==============
const formatPrice = (service) => {
    if (service.is_modifier) {
    if (service.modifier_type === "percentage" && service.modifier_percent != null) {
      return `+${service.modifier_percent}%`;
    }
    if (service.modifier_type === "fixed" && service.modifier_fixed != null) {
      return `+$${Number(service.modifier_fixed).toFixed(2)}`;
    }
    return null;
  }

  if (!service.price_value && service.pricing_type === "none") return null;
  
  const price = service.price_value ? `$${service.price_value.toFixed(2)}` : "";
  
  switch (service.pricing_type) {
    case "fixed":
      return price;
    case "starting_at":
      return `Starting at ${price}`;
    case "per_unit":
      return `${price} ${service.unit_label || "per unit"}`;
    default:
      return null;
  }
};

// ============== GET STARTED PAGE ==============
const GetStarted = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);
  const [onboardingIncomplete, setOnboardingIncomplete] = useState(false);
  const [needsReactivation, setNeedsReactivation] = useState(false);
  const [existingSlug, setExistingSlug] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    business_name: "",
    password: "",
    confirmPassword: "",
    phone: "",
    wants_setup_call: false,
    terms_accepted: false,
    disclaimer_accepted: false,
    signatory_name: "",
  });
  const [showTerms, setShowTerms] = useState(false);

  // Clear any stale auth data when visiting signup page
  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('businessSlug');
  }, []);

  // Track if user has manually interacted with the form
  const [userInteracted, setUserInteracted] = useState(false);
  
  const handleSubmit = async (e, isManualClick = false) => {
    if (e) e.preventDefault();
    
    // Guard against browser autofill auto-submission
    if (!userInteracted && !isManualClick) {
      return;
    }
    
    if (!formData.email || !formData.business_name) {
      toast.error("Please fill in all fields");
      return;
    }
    
    // Validate password
    if (!formData.password || formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!formData.phone || formData.phone.trim().length < 7) {
      toast.error("Please enter a valid business phone number");
      return;
    }

    if (!formData.terms_accepted) {
      toast.error("You must agree to the Terms of Service and Privacy Policy");
      return;
    }

    if (!formData.disclaimer_accepted) {
      toast.error("You must acknowledge that ZipLeadPro is a software tool");
      return;
    }

    if (!formData.signatory_name || formData.signatory_name.trim().length < 2) {
      toast.error("Please enter your full legal name");
      return;
    }

    setSubmitting(true);
    try {
      // First check if email already has an account
      const checkResult = await api.checkEmailExists(formData.email);
      
      if (checkResult.status === "existing_account") {
        setExistingAccount(true);
        setOnboardingIncomplete(!checkResult.onboarding_completed);
        setNeedsReactivation(checkResult.needs_reactivation || false);
        setExistingSlug(checkResult.tenant_slug || null);
        setSubmitting(false);
        return;
      }
      
      // If pending signup exists, redirect to continue onboarding
      if (checkResult.status === "pending_signup") {
        toast.success("Continuing your signup...");
        window.location.href = `/onboarding/${checkResult.onboarding_token}`;
        return;
      }
      
      // Email is new - create FREE trial account with password
      const result = await api.createTrialAccount({
        email: formData.email,
        business_name: formData.business_name,
        password: formData.password,
        phone: formData.phone,
        wants_setup_call: formData.wants_setup_call,
        terms_accepted: formData.terms_accepted,
        disclaimer_accepted: formData.disclaimer_accepted,
        terms_version: "2026-03-12",
        signatory_name: formData.signatory_name.trim()
      });
      
      if (result.success && result.token) {
        // Clear any stale tour state from previous accounts before starting fresh
        clearTourStateForSlug(result.slug);
        
        // Auto-login: Store token and redirect to dashboard
        localStorage.setItem("token", result.token);
        localStorage.setItem("businessSlug", result.slug);
        toast.success("Account created! Taking you to your dashboard...");
        window.location.href = `/dashboard/${result.slug}`;
      } else {
        toast.error("Failed to create account. Please try again.");
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Account creation error:", error);
      if (error.response?.status === 409 && error.response?.data?.detail === "existing_account") {
        setExistingAccount(true);
        setSubmitting(false);
        return;
      }
      toast.error(getErrorMessage(error, "Failed to create account"));
      setSubmitting(false);
    }
  };

  const features = [
    "Stop answering the same questions over and over",
    "Send customers one link to request work",
    "Customers upload job photos before you quote",
    "New job request delivered instantly",
    "Send clean professional quotes in seconds",
    "Collect deposits online before the job",
    "Deposit-Paid jobs move straight into your shceduling queue",
    "Manage quotes and scheduled jobs from your phone",
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <SEO 
        title="Start Free Trial"
        description="Build your lead intake form in minutes. Collect photos, send quotes with deposits, and schedule jobs. 10-day free trial, no credit card required."
        path="/get-started"
      />
      <nav className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <img src="/zlp-logo.png" alt="ZipLeadPro" className="h-10 sm:h-20 w-auto object-contain shrink-0" />
          <span className="text-base sm:text-2xl font-bold whitespace-nowrap">ZipLeadPro</span>
        </Link>
        <Link to="/login" className="shrink-0">
          <Button variant="ghost" className="text-white hover:bg-white/10 text-sm sm:text-base">Log In</Button>
        </Link>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <span>✨</span> No Credit Card Required
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Start Your Free Trial</h1>
          <p className="text-xl text-gray-300">Build your lead intake form in minutes. Start collecting leads today.</p>
        </div>

        {/* Pricing Highlight */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8 text-center">
          <div className="text-5xl font-bold text-white mb-2">$0</div>
          <div className="text-blue-500 font-medium mb-3">for 10 days • Full access • No card needed</div>
          <div className="text-gray-400 text-sm">
            Then <span className="text-white font-semibold">$29/month</span> or <span className="text-white font-semibold">$290/year</span> <span className="text-blue-500">(save $58)</span> if you love it. Cancel anytime.
          </div>
        </div>

        {/* What You Get Card */}
        <Card className="bg-white/10 border-white/20 mb-8">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-white text-2xl">How ZLP Makes Your Job Easier</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid grid-cols-2 gap-3 mb-4">
              {features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-gray-200 text-sm">
                  <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Signup Form or Existing Account */}
        <Card className="bg-white/10 border-white/20">
          {existingAccount ? (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-white text-2xl">
                  Welcome Back!
                </CardTitle>
                <CardDescription className="text-gray-300 text-base mt-2">
                  An account with this email already exists.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={() => navigate("/login")}
                  size="lg"
                  className="w-full bg-slate-900 hover:bg-slate-900 text-white py-6 text-lg font-semibold"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
                <p className="text-center text-gray-400 text-sm">
                  Forgot your password? You can reset it from the login page.
                </p>
                <Button
                  onClick={() => {
                    setExistingAccount(false);
                    setOnboardingIncomplete(false);
                    setNeedsReactivation(false);
                    setExistingSlug(null);
                    setFormData({ ...formData, email: "", password: "", confirmPassword: "" });
                  }}
                  variant="ghost"
                  className="w-full text-gray-500 hover:text-white hover:bg-white/10 text-sm"
                >
                  Use a different email
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-white">Create Your Account</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Business Name</Label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      placeholder="Your Business Name"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                      autoComplete="organization"
                      data-testid="input-business-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@business.com"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                      autoComplete="email"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Password</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                      autoComplete="new-password"
                      data-testid="input-password"
                    />
                    <p className="text-xs text-gray-400">At least 6 characters</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Confirm Password</Label>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                      autoComplete="new-password"
                      data-testid="input-confirm-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Business Phone Number *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Phone number"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                      autoComplete="tel"
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Your Full Legal Name *</Label>
                    <Input
                      value={formData.signatory_name}
                      onChange={(e) => setFormData({ ...formData, signatory_name: e.target.value })}
                      placeholder="John Smith"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                      autoComplete="name"
                      data-testid="input-signatory-name"
                    />
                    <p className="text-xs text-gray-400">Person authorized to accept terms on behalf of the business</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <Label className="text-white text-sm">Would you like a free phone call to help you set up your new system?</Label>
                    <div className="flex gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, wants_setup_call: true })}
                        className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${formData.wants_setup_call ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
                        data-testid="setup-call-yes"
                      >
                        Yes, please!
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, wants_setup_call: false })}
                        className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${!formData.wants_setup_call ? 'bg-white/20 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
                        data-testid="setup-call-no"
                      >
                        No thanks
                      </button>
                    </div>
                  </div>

                  {/* Terms and Conditions */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="terms_accepted"
                        checked={formData.terms_accepted}
                        onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                        className="mt-1 w-4 h-4 rounded border-white/30 bg-white/10 text-blue-600 focus:ring-blue-600"
                        data-testid="checkbox-terms"
                      />
                      <label htmlFor="terms_accepted" className="text-sm text-gray-300">
                        I agree to the{" "}
                        <button 
                          type="button" 
                          onClick={() => setShowTerms(true)} 
                          className="text-blue-300 hover:text-blue-200 underline"
                        >
                          Terms of Service and Privacy Policy
                        </button>
                      </label>
                    </div>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="disclaimer_accepted"
                        checked={formData.disclaimer_accepted}
                        onChange={(e) => setFormData({ ...formData, disclaimer_accepted: e.target.checked })}
                        className="mt-1 w-4 h-4 rounded border-white/30 bg-white/10 text-blue-600 focus:ring-blue-600"
                        data-testid="checkbox-disclaimer"
                      />
                      <label htmlFor="disclaimer_accepted" className="text-sm text-gray-300">
                        I understand ZipLeadPro is a software tool and does not guarantee business results
                      </label>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="lg"
                    className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold text-lg py-6"
                    disabled={submitting || !formData.email || !formData.business_name || !formData.password || formData.password.length < 6 || formData.password !== formData.confirmPassword || !formData.phone || formData.phone.trim().length < 7 || !formData.terms_accepted || !formData.disclaimer_accepted || !formData.signatory_name || formData.signatory_name.trim().length < 2}
                    onClick={() => {
                      setUserInteracted(true);
                      handleSubmit(null, true);
                    }}
                    data-testid="create-account-btn"
                  >
                    {submitting ? "Creating your account..." : "Start My Free Trial →"}
                  </Button>

                  <p className="text-center text-sm text-gray-400">
                    No credit card required. Start building immediately.
                  </p>
                  
                  <p className="text-center text-sm text-gray-400">
                    Already have an account?{" "}
                    <Link to="/login" className="text-blue-300 hover:text-blue-200 underline">
                      Log in
                    </Link>
                  </p>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Terms Modal */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Terms of Service and Privacy Policy</DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm max-w-none text-gray-700 space-y-4">
            <p className="text-sm text-gray-500">Effective Date: March 12, 2026 | Platform: ZipLeadPro | Company: Baseline Holdings LLC</p>
            
            <p>These Terms of Service and Privacy Policy ("Terms") govern your access to and use of the ZipLeadPro platform ("Platform", "Service"), operated by Baseline Holdings LLC ("Company", "we", "our", or "us").</p>
            
            <p><strong>By creating an account, accessing the platform, or using the Service, you ("User", "Business", or "you") agree to be legally bound by these Terms. If you do not agree to these Terms, you must not use the platform.</strong></p>

            <p><strong>If you create an account on behalf of a business, organization, or trade name, you represent that you are authorized to accept these Terms on its behalf. If no legal entity exists, these Terms apply to you individually.</strong></p>

            <h3 className="font-semibold mt-4">1. Description of Service</h3>
            <p>ZipLeadPro is a software platform that provides tools designed to help businesses streamline customer intake, quoting workflows, and optional payment collection. The platform may allow businesses to receive customer inquiries, collect project details and photos, generate quotes, communicate with customers, optionally collect deposits through third-party payment processors, and manage leads and service requests.</p>
            <p>ZipLeadPro provides software tools only. Baseline Holdings LLC does not act as a contractor, broker, agent, payment processor, or service provider between businesses and their customers.</p>

            <h3 className="font-semibold mt-4">2. Business Responsibility</h3>
            <p>Businesses using the platform are solely responsible for their operations including: services offered to customers, quotes, pricing, and agreements, work performed for customers, compliance with all applicable laws, tax reporting and tax collection, and customer communications and obligations.</p>
            <p><strong>ZipLeadPro does not guarantee leads, customers, bookings, or revenue. The platform is a productivity tool and not a lead generation guarantee.</strong></p>

            <h3 className="font-semibold mt-4">3. Payments and Financial Transactions</h3>
            <p>ZipLeadPro may integrate with third-party payment processors such as Stripe. All payments and deposits are processed directly between the business and the payment processor. Baseline Holdings LLC does not hold customer funds, does not process payments directly, does not control payment processor policies, and is not responsible for payment failures, chargebacks, or financial disputes.</p>

            <h3 className="font-semibold mt-4">4. Platform Availability</h3>
            <p>ZipLeadPro is a cloud-based platform and may experience interruptions. Baseline Holdings LLC does not guarantee uninterrupted service availability. Users acknowledge that use of the platform occurs at their own risk.</p>

            <h3 className="font-semibold mt-4">5. Data Collection</h3>
            <p>The platform collects and stores information necessary to operate the service including business account information, customer contact information, project descriptions, uploaded photos or documents, usage data and analytics, and payment-related metadata.</p>

            <h3 className="font-semibold mt-4">6. Data Use and Commercial Rights</h3>
            <p>By using the platform, you grant Baseline Holdings LLC a perpetual, irrevocable, worldwide, royalty-free license to collect, store, process, analyze, distribute, license, commercialize, share, or sell data collected through the platform. Use of the platform constitutes consent to this data use.</p>

            <h3 className="font-semibold mt-4">7-9. Platform Usage Data, Security & Retention</h3>
            <p>The platform may automatically collect technical data including IP addresses, browser type, device type, and activity logs. Baseline Holdings LLC takes reasonable measures to protect stored information but no online system can be guaranteed to be completely secure. Data may be retained even after accounts are closed.</p>

            <h3 className="font-semibold mt-4">10-11. Acceptable Use & Account Termination</h3>
            <p>Users may not use the platform to conduct illegal activities, misrepresent services, collect data for fraudulent purposes, or interfere with platform functionality. Accounts violating these rules may be suspended or terminated.</p>

            <h3 className="font-semibold mt-4">12. Intellectual Property</h3>
            <p>All software, systems, branding, and technology related to ZipLeadPro are the exclusive property of Baseline Holdings LLC.</p>

            <h3 className="font-semibold mt-4">13. Limitation of Liability</h3>
            <p>To the maximum extent permitted by law, Baseline Holdings LLC shall not be liable for any indirect, incidental, special, or consequential damages. If liability is found, the maximum total liability shall not exceed the amount paid by the user for the service during the 30 days preceding the claim.</p>

            <h3 className="font-semibold mt-4">14-16. Indemnification, Arbitration & Class Action Waiver</h3>
            <p>Users agree to defend and indemnify Baseline Holdings LLC from any claims arising from their use of the platform. Any disputes shall be resolved through binding arbitration. Users waive the right to participate in any class action against Baseline Holdings LLC.</p>

            <h3 className="font-semibold mt-4">17-18. Governing Law & Changes</h3>
            <p>These Terms shall be governed by the laws of the State of Iowa. Baseline Holdings LLC may update these Terms at any time. Continued use constitutes acceptance.</p>

            <h3 className="font-semibold mt-4">Contact</h3>
            <p>Questions regarding these Terms may be directed to: <strong>help@zipleadpro.com</strong></p>
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={() => setShowTerms(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== CHECKOUT SUCCESS PAGE ==============
const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  // Log session ID for debugging
  useEffect(() => {
  }, [sessionId]);

  useEffect(() => {
    // Always show success after a brief delay - webhook handles the actual work
    const timer = setTimeout(() => {
      setStatus("success");
    }, 2000);

    // Optionally try to verify session, but don't crash if it fails
    if (sessionId) {
      api.getCheckoutSession(sessionId)
        .then((data) => {
          if (data?.status === "complete" || data?.status === "paid") {
            setStatus("success");
          }
        })
        .catch((err) => {
          console.error("[CheckoutSuccess] API error (non-fatal):", err);
          // Don't set error - just let the timer show success
        });
    }

    return () => clearTimeout(timer);
  }, [sessionId]);

  // Always render something - never white screen
  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardContent className="pt-8 pb-8 text-center">
          {status === "loading" ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h1>
              <p className="text-gray-600">Please wait while we set up your account.</p>
            </>
          ) : status === "success" ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful! 🎉</h1>
              <p className="text-gray-600 mb-6">
                Your ZipLeadPro account has been created.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-slate-900 font-medium mb-1">Check Your Email</p>
                <p className="text-slate-900 text-sm">
                  We have sent you an email with a link to complete your setup and access your dashboard.
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Did not receive the email? Check your spam folder or contact support.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Almost There!</h1>
              <p className="text-gray-600 mb-4">
                Your payment was received. Check your email for the setup link.
              </p>
              {error && (
                <p className="text-sm text-red-600 mb-4">{error}</p>
              )}
              <Button onClick={() => window.location.href = "/get-started"}>
                Return to Get Started
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== INVOICE PAGE ==============

// Short-link redirect: GET /i/:code → fetch target, then window.location.replace
const ShortLinkRedirect = () => {
  const { code } = useParams();
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/short-links/${code}`);
        if (!res.ok) {
          if (!cancelled) setError(true);
          return;
        }
        const data = await res.json();
        if (data.target_url && !cancelled) {
          window.location.replace(data.target_url);
        } else if (!cancelled) {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center">
      {error ? (
        <>
          <h1 className="text-xl font-heading font-semibold text-slate-900 mb-2">Link not found</h1>
          <p className="text-sm text-slate-500">This estimate link is invalid or has been removed.</p>
        </>
      ) : (
        <>
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm text-slate-500">Opening your estimate…</p>
        </>
      )}
    </div>
  );
};

const InvoicePage = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const fromPayment = searchParams.get("from") === "payment";
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        // If coming from payment success page, get token from the quote's public data
        let invoiceToken = token;
        if (!invoiceToken && fromPayment) {
          try {
            const pubRes = await axios.get(`${API}/quotes/${quoteId}/public`);
            // The public endpoint doesn't expose the token, so we need the invoice endpoint with a lookup
            const tokenRes = await axios.get(`${API}/invoices/${quoteId}/token-lookup`);
            invoiceToken = tokenRes.data.token;
          } catch { /* fallback below */ }
        }
        if (!invoiceToken) {
          setError("Invoice link is invalid or expired.");
          return;
        }
        const res = await axios.get(`${API}/invoices/${quoteId}?token=${invoiceToken}`);
        setInvoice({ ...res.data, token: invoiceToken });
      } catch (err) {
        setError("Invoice not found.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [quoteId, token, fromPayment]);

  if (loading) return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
    </div>
  );

  if (error || !invoice) return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md"><CardContent className="pt-8 pb-8 text-center">
        <p className="text-red-600 font-medium">{error || "Invoice not found"}</p>
      </CardContent></Card>
    </div>
  );

  const statusStyles = {
    draft: "bg-gray-100 text-gray-700", sent: "bg-amber-100 text-amber-800", viewed: "bg-amber-100 text-amber-800",
    deposit_paid: "bg-amber-100 text-amber-800", paid: "bg-blue-100 text-slate-900", expired: "bg-red-100 text-red-800"
  };
  const statusLabels = {
    draft: "Draft", sent: "Unpaid", viewed: "Unpaid", deposit_paid: "Deposit Paid", paid: "Paid in Full", expired: "Expired"
  };

  const pdfUrl = `${API}/invoices/${quoteId}/pdf?token=${invoice.token}`;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 print:bg-white print:py-0">
      <SEO title={`Invoice ${invoice.invoice_number}`} description="View and download your invoice" noIndex={true} />
      
      {/* Action bar - hidden in print */}
      <div className="max-w-3xl mx-auto mb-4 flex justify-between items-center print:hidden">
        <h1 className="text-lg font-semibold text-gray-700">Invoice</h1>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-testid="print-invoice-btn">
            Print
          </button>
          <a href={pdfUrl} download className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-900 transition-colors" data-testid="download-pdf-btn">
            Download PDF
          </a>
        </div>
      </div>
      
      {/* Invoice card */}
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden print:shadow-none print:rounded-none">
        {/* Header */}
        <div className="px-8 pt-8 pb-6">
          <div className="flex justify-between items-start">
            <div>
              {invoice.business.logo_url && (
                <img src={invoice.business.logo_url} alt={invoice.business.name} className="h-12 mb-3 object-contain" />
              )}
              <h2 className="text-2xl font-bold text-gray-900">{invoice.business.name}</h2>
              <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                {invoice.business.email && <p>{invoice.business.email}</p>}
                {invoice.business.phone && <p>{invoice.business.phone}</p>}
                {invoice.business.address && <p>{invoice.business.address}</p>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-slate-900 mb-1">INVOICE</p>
              <p className="text-sm text-gray-500">{invoice.invoice_number}</p>
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${statusStyles[invoice.status] || "bg-gray-100 text-gray-700"}`}>
                {statusLabels[invoice.status] || invoice.status}
              </span>
            </div>
          </div>
        </div>
        
        {/* Meta info */}
        <div className="px-8 py-4 bg-gray-50 grid grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Bill To</p>
            <p className="font-semibold text-gray-900">{invoice.customer.name}</p>
            {invoice.customer.email && <p className="text-sm text-gray-600">{invoice.customer.email}</p>}
            {invoice.customer.phone && <p className="text-sm text-gray-600">{invoice.customer.phone}</p>}
            {invoice.customer.address && <p className="text-sm text-gray-600">{invoice.customer.address}</p>}
          </div>
          <div className="text-right">
            <div className="space-y-1.5">
              <div><span className="text-xs text-gray-400 uppercase">Date: </span><span className="text-sm font-medium text-gray-700">{invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : "N/A"}</span></div>
              {invoice.quote_sent_at && <div><span className="text-xs text-gray-400 uppercase">Sent: </span><span className="text-sm font-medium text-gray-700">{new Date(invoice.quote_sent_at).toLocaleDateString()}</span></div>}
              {invoice.paid_at && <div><span className="text-xs text-gray-400 uppercase">Paid: </span><span className="text-sm font-medium text-blue-800">{new Date(invoice.paid_at).toLocaleDateString()}</span></div>}
            </div>
          </div>
        </div>
        
        {/* Line items table */}
        <div className="px-8 py-6">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left py-3 px-4 rounded-tl-lg text-sm font-semibold">Description</th>
                <th className="text-center py-3 px-4 text-sm font-semibold">Qty</th>
                <th className="text-right py-3 px-4 text-sm font-semibold">Unit Price</th>
                <th className="text-right py-3 px-4 rounded-tr-lg text-sm font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.line_items || []).map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="py-3 px-4 text-sm text-gray-900">{item.description}</td>
                  <td className="py-3 px-4 text-sm text-gray-600 text-center">{item.quantity || 1}</td>
                  <td className="py-3 px-4 text-sm text-gray-600 text-right">${(item.unit_price || 0).toFixed(2)}</td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-900 text-right">${(item.total || (item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Configurable Items - Customer can select */}
        {(invoice.configurable_items || []).length > 0 && invoice.status !== "paid" && (
          <div className="px-8 pb-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-800 mb-3">
                Select the services you'd like to include:
              </p>
              <div className="space-y-2">
                {(invoice.configurable_items || []).map((ci) => {
                  const isSelected = (invoice.selected_configurable_ids || []).includes(ci.id);
                  const ciTotal = ci.total || (ci.quantity || 1) * (ci.unit_price || 0);
                  return (
                    <label key={ci.id} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-amber-500 bg-amber-100' : 'border-gray-200 bg-white hover:border-amber-300'}`}>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={async () => {
                            const currentIds = invoice.selected_configurable_ids || [];
                            const newIds = isSelected ? currentIds.filter(id => id !== ci.id) : [...currentIds, ci.id];
                            try {
                              const res = await axios.post(`${API}/quotes/${invoice.id}/select-options`, { selected_ids: newIds, selected_choice_ids: invoice.selected_choice_option_ids || [] });
                              if (res.data.quote) {
                                setInvoice(prev => ({
                                  ...prev,
                                  ...res.data.quote,
                                  selected_configurable_ids: newIds,
                                  token: prev.token
                                }));
                              }
                            } catch { }
                          }}
                          className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">{ci.description}</span>
                          {ci.quantity > 1 && <span className="text-xs text-gray-500 ml-1">(x{ci.quantity})</span>}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">+${ciTotal.toFixed(2)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Show selected configurable items when paid */}
        {(invoice.configurable_items || []).length > 0 && invoice.status === "paid" && (invoice.selected_configurable_ids || []).length > 0 && (
          <div className="px-8 pb-2">
            <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Selected Services</p>
            <table className="w-full">
              <tbody>
                {(invoice.configurable_items || []).filter(ci => (invoice.selected_configurable_ids || []).includes(ci.id)).map((ci, idx) => (
                  <tr key={idx} className="bg-amber-50">
                    <td className="py-2 px-4 text-sm text-gray-900">{ci.description}</td>
                    <td className="py-2 px-4 text-sm text-gray-600 text-center">{ci.quantity || 1}</td>
                    <td className="py-2 px-4 text-sm text-gray-600 text-right">${(ci.unit_price || 0).toFixed(2)}</td>
                    <td className="py-2 px-4 text-sm font-medium text-gray-900 text-right">${(ci.total || (ci.quantity || 1) * (ci.unit_price || 0)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Totals */}
        <div className="px-8 pb-6">
          <div className="flex justify-end">
            <div className="w-72 space-y-2">
              {(() => {
                const baseSubtotal = invoice.subtotal || 0;
                const selectedCiTotal = (invoice.configurable_items || [])
                  .filter(ci => (invoice.selected_configurable_ids || []).includes(ci.id))
                  .reduce((sum, ci) => sum + (ci.total || (ci.quantity || 1) * (ci.unit_price || 0)), 0);
                const grossSubtotal = baseSubtotal + selectedCiTotal;
                // Apply per-quote discount BEFORE tax. Identical math to the
                // Quote Builder so customer sees exactly what the business
                // priced.
                const discountPct = Math.max(0, Math.min(100, parseFloat(invoice.discount_percent) || 0));
                const discountAmount = grossSubtotal * (discountPct / 100);
                const liveSubtotal = grossSubtotal - discountAmount;
                const discountLabel = (invoice.discount_label || "").trim() || "Discount";
                const taxRate = invoice.tax_rate || 0;
                const liveTax = taxRate > 0 ? Math.round(liveSubtotal * taxRate) / 100 : 0;
                const liveTotal = liveSubtotal + liveTax;
                const depositPaid = invoice.deposit_paid_amount || 0;
                const liveBalance = Math.max(0, liveTotal - depositPaid);
                return (
                  <>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Base Services</span>
                      <span>${baseSubtotal.toFixed(2)}</span>
                    </div>
                    {selectedCiTotal > 0 && (
                      <div className="flex justify-between text-sm text-amber-700">
                        <span>Selected Services</span>
                        <span>+${selectedCiTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {discountPct > 0 && (
                      <div className="flex justify-between text-sm text-blue-700" data-testid="invoice-discount-row">
                        <span>{discountLabel} ({discountPct}%)</span>
                        <span>−${discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal</span>
                      <span>${liveSubtotal.toFixed(2)}</span>
                    </div>
                    {taxRate > 0 && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{invoice.tax_state ? `${invoice.tax_state} Tax` : 'Tax'} ({taxRate}%)</span>
                        <span>${liveTax.toFixed(2)}</span>
                      </div>
                    )}
                    {invoice.deposit_amount > 0 && invoice.deposit_amount < liveTotal && (
                      <>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Deposit Amount</span>
                          <span>${(invoice.deposit_amount || 0).toFixed(2)}</span>
                        </div>
                        {depositPaid > 0 && (
                          <div className="flex justify-between text-sm text-blue-700">
                            <span>Deposit Paid</span>
                            <span>-${depositPaid.toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}
                    {invoice.status === "paid" && (invoice.total_paid || 0) > 0 && (
                      <div className="flex justify-between text-sm text-blue-700">
                        <span>Total Paid</span>
                        <span>-${(invoice.total_paid || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t-2 border-gray-900 text-lg font-bold">
                      {invoice.status === "paid" ? (
                        <><span className="text-blue-800">PAID IN FULL</span><span className="text-blue-800">${liveTotal.toFixed(2)}</span></>
                      ) : (
                        <><span>Balance Due</span><span>${liveBalance.toFixed(2)}</span></>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        
        {/* Notes */}
        {invoice.notes && (
          <div className="px-8 pb-6">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-blue-400 uppercase mb-1">Notes</p>
              <p className="text-sm text-blue-900 whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          </div>
        )}
        
        {/* Approve / Decline buttons for all quotes */}
        {invoice.status !== "paid" && invoice.status !== "approved" && invoice.status !== "declined" && invoice.status !== "deposit_paid" && (
          <div className="px-8 pb-6">
            <div className="space-y-3">
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/quotes/${invoice.id}/approve`);
                    setInvoice(prev => ({ ...prev, status: "approved" }));
                  } catch { }
                }}
                className="w-full py-4 rounded-lg text-lg font-bold text-white transition-colors"
                style={{ background: '#059669' }}
                data-testid="approve-estimate-btn"
              >
                Yes! Approve
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/quotes/${invoice.id}/decline`);
                    setInvoice(prev => ({ ...prev, status: "declined" }));
                  } catch { }
                }}
                className="w-full py-3 rounded-lg text-sm font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 border border-gray-200 transition-colors"
                data-testid="decline-estimate-btn"
              >
                Politely Decline
              </button>
            </div>
          </div>
        )}
        
        {/* Payment options after approval */}
        {invoice.status === "approved" && (() => {
          const baseSubtotal = invoice.subtotal || 0;
          const ciTotal = (invoice.configurable_items || [])
            .filter(ci => (invoice.selected_configurable_ids || []).includes(ci.id))
            .reduce((sum, ci) => sum + (ci.total || (ci.quantity || 1) * (ci.unit_price || 0)), 0);
          const grossSubtotal = baseSubtotal + ciTotal;
          // Apply per-quote discount BEFORE tax (identical math to invoice
          // totals box + backend pay-off-now endpoint, so display and charge
          // match exactly).
          const discountPct = Math.max(0, Math.min(100, parseFloat(invoice.discount_percent) || 0));
          const servicesSubtotal = grossSubtotal * (1 - discountPct / 100);
          const taxRate = invoice.tax_rate || 0;
          const taxAmount = taxRate > 0 ? Math.round(servicesSubtotal * taxRate) / 100 : 0;
          const fullTotal = servicesSubtotal + taxAmount;
          const rawDeposit = servicesSubtotal * 0.25;
          const roundedDeposit = Math.ceil(rawDeposit / 10) * 10;
          // Rounding to $10 must never push the deposit to half the job or more (tiny/heavily discounted quotes)
          const depositAmount = roundedDeposit >= servicesSubtotal * 0.5 ? Math.ceil(rawDeposit * 100) / 100 : roundedDeposit;
          const depositAllowed = depositAmount >= 0.5 && depositAmount < fullTotal;
          const fullAllowed = fullTotal >= 0.5;
          const remainingAfterDeposit = servicesSubtotal - depositAmount + taxAmount;

          return (
            <div className="px-8 pb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-4 text-center">
                <p className="text-lg font-bold text-slate-900 mb-1">Estimate Approved!</p>
                <p className="text-sm text-blue-800">
                  {invoice.business?.payments_enabled === false || !fullAllowed
                    ? "Thank you for approving your estimate."
                    : "Choose how you'd like to pay:"}
                </p>
              </div>
              {invoice.business?.payments_enabled === false ? (
                // Stripe isn't connected for this business — instead of showing
                // pay buttons that fail with a toast, guide the customer to
                // coordinate payment directly with the business owner.
                <div
                  className="rounded-xl border-2 border-blue-200 bg-blue-50 p-5 text-center"
                  data-testid="payment-offline-panel"
                >
                  <p className="font-semibold text-slate-900 text-base mb-1">
                    Next step: connect with {invoice.business?.name || "the business"}
                  </p>
                  <p className="text-sm text-slate-700">
                    Online payment isn't set up on this estimate. Please reach out
                    {invoice.business?.phone ? (
                      <> at <a className="font-semibold text-blue-800 underline" href={`tel:${invoice.business.phone}`}>{invoice.business.phone}</a></>
                    ) : null}
                    {invoice.business?.email ? (
                      <>{invoice.business?.phone ? " or " : " at "}
                        <a className="font-semibold text-blue-800 underline" href={`mailto:${invoice.business.email}`}>{invoice.business.email}</a>
                      </>
                    ) : null}
                    {" "}to confirm payment details and schedule your service.
                  </p>
                  <p className="text-xs text-slate-500 mt-3">Estimated total: <span className="font-semibold text-slate-900">${fullTotal.toFixed(2)}</span></p>
                </div>
              ) : (
              <div className="space-y-3">
                {/* Deposit option */}
                {depositAllowed && (
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/quotes/${invoice.id}/estimate-checkout`, { payment_type: "deposit" });
                      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
                    } catch (e) { toast.error(e.response?.data?.detail || "Payment unavailable"); }
                  }}
                  className="w-full rounded-xl border-2 border-blue-600 bg-white p-4 text-left hover:bg-blue-50 transition-colors"
                  data-testid="pay-deposit-btn"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-slate-900 text-lg">Pay Deposit</p>
                      <p className="text-xs text-gray-500 mt-0.5">25% deposit, tax-free. Remaining balance + tax collected at service completion.</p>
                    </div>
                    <span className="text-2xl font-bold text-blue-800">${Number.isInteger(depositAmount) ? depositAmount.toFixed(0) : depositAmount.toFixed(2)}</span>
                  </div>
                  {remainingAfterDeposit > 0 && (
                    <p className="text-xs text-gray-400 mt-2">Remaining at completion: ${remainingAfterDeposit.toFixed(2)}{taxAmount > 0 ? ` (incl. ${invoice.tax_state || ''} tax)` : ''}</p>
                  )}
                </button>
                )}
                
                {/* Full payment option */}
                {fullAllowed ? (
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/quotes/${invoice.id}/estimate-checkout`, { payment_type: "full" });
                      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
                    } catch (e) { toast.error(e.response?.data?.detail || "Payment unavailable"); }
                  }}
                  className="w-full rounded-xl border-2 border-blue-800 bg-white p-4 text-left hover:bg-blue-50 transition-colors"
                  data-testid="pay-full-btn"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-slate-900 text-lg">Pay in Full</p>
                      <p className="text-xs text-gray-500 mt-0.5">Complete payment now{taxAmount > 0 ? ` including ${invoice.tax_state || 'state'} tax` : ''}.</p>
                    </div>
                    <span className="text-2xl font-bold text-slate-900">${fullTotal.toFixed(2)}</span>
                  </div>
                </button>
                ) : (
                  <p className="text-sm text-slate-600 text-center" data-testid="no-payment-due">
                    No online payment is due for this estimate.
                  </p>
                )}
              </div>
              )}
            </div>
          );
        })()}
        
        {/* Declined confirmation */}
        {invoice.status === "declined" && (
          <div className="px-8 pb-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="text-lg font-medium text-gray-700">Estimate Declined</p>
              <p className="text-sm text-gray-500 mt-1">No worries — the business has been notified. Feel free to reach out if anything changes.</p>
            </div>
          </div>
        )}

        {/* Chemical application record (visible if this lead has applications logged) */}
        <InvoiceApplicationRecord quoteId={quoteId} token={invoice.token} />

        {/* Billing progress (visible for split-visits / installments schedules) */}
        <BillingProgress quoteId={quoteId} token={invoice.token} />
        
        {/* Footer */}
        <div className="px-8 py-4 bg-gray-50 border-t text-center">
          <p className="text-xs text-gray-400">Invoice {invoice.invoice_number} | Generated by ZipLeadPro</p>
        </div>
      </div>
    </div>
  );
};


// ============== QUOTE PAYMENT SUCCESS PAGE ==============
const QuotePaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const paymentType = searchParams.get("type"); // 'deposit', 'full', or 'balance'
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try to fetch quote details for a richer confirmation
    const fetchQuoteDetails = async () => {
      try {
        if (quoteId) {
          const response = await axios.get(`${API}/api/quotes/${quoteId}/public`);
          setQuoteDetails(response.data);
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    
    // Small delay to ensure payment processed
    setTimeout(fetchQuoteDetails, 1500);
  }, [quoteId]);

  const getPaymentTypeText = () => {
    if (paymentType === 'deposit') return 'Deposit';
    if (paymentType === 'balance') return 'Remaining Balance';
    return 'Payment';
  };

  const getNextStepsText = () => {
    if (paymentType === 'deposit') {
      return "Your spot is reserved! The business will reach out to confirm your appointment and discuss the remaining balance.";
    }
    if (paymentType === 'balance') {
      return "Your balance is paid in full. Thank you for your business!";
    }
    return "Your payment is complete. The business will be in touch soon to get started.";
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardContent className="pt-8 pb-8 text-center">
          {loading ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-200 border-t-blue-700 rounded-full animate-spin"></div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h1>
              <p className="text-gray-600">Please wait while we confirm your payment.</p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{getPaymentTypeText()} Successful! 🎉</h1>
              <p className="text-gray-600 mb-6">{getNextStepsText()}</p>
              
              {quoteDetails && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-500 mb-2">Payment Details</p>
                  {quoteDetails.business_name && (
                    <p className="font-medium text-gray-900 mb-1">{quoteDetails.business_name}</p>
                  )}
                  {quoteDetails.quote_number && (
                    <p className="text-sm text-gray-600">Quote #{quoteDetails.quote_number}</p>
                  )}
                  {paymentType === 'deposit' && quoteDetails.deposit_amount && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm text-gray-600">Deposit Paid: <span className="font-semibold text-blue-700">${quoteDetails.deposit_amount?.toFixed(2)}</span></p>
                      {quoteDetails.quoted_total && (
                        <p className="text-sm text-gray-600">Remaining Balance: <span className="font-medium">${(quoteDetails.quoted_total - quoteDetails.deposit_amount)?.toFixed(2)}</span></p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-slate-900 font-medium mb-1">Confirmation Sent</p>
                <p className="text-blue-800 text-sm">
                  A receipt has been sent to your email address.
                </p>
              </div>
              
              {quoteId && (
                <a 
                  href={`/invoice/${quoteId}?from=payment`} 
                  className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-900 transition-colors mb-4"
                  data-testid="view-invoice-btn"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  View &amp; Download Invoice
                </a>
              )}
              
              <p className="text-sm text-gray-500">
                Questions? Contact the business directly by replying to your quote email.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== QUOTE PAYMENT CANCEL PAGE ==============
const QuotePaymentCancel = () => {
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Cancelled</h1>
          <p className="text-gray-600 mb-6">
            No worries! Your payment was not processed. You can return to the quote anytime to complete your payment.
          </p>
          <p className="text-sm text-gray-500">
            If you have questions about the quote, feel free to reply to the email you received.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== SHARED ROUTE PAGE ==============
const SharedRoutePage = () => {
  const { routeId } = useParams();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        const res = await axios.get(`${API}/shared/route/${routeId}`);
        setRoute(res.data);
      } catch {
        setRoute(null);
      }
      setLoading(false);
    };
    fetchRoute();
  }, [routeId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p>Loading route...</p></div>;
  if (!route) return <div className="min-h-screen flex items-center justify-center"><p>Route not found</p></div>;

  const stops = route.stops || [];
  const stopsForUrl = stops.map(s => s.address.replace(/ /g, '+'));
  const appleMapsUrl = `https://maps.apple.com/?saddr=Current+Location&daddr=${stopsForUrl.join('+to:')}&dirflg=d`;
  const googleMapsUrl = `https://www.google.com/maps/dir/My+Location/${stopsForUrl.join('/')}`;

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardContent className="pt-8 pb-8">
          <h1 className="text-xl font-bold text-gray-900 mb-1 text-center">{route.name}</h1>
          {route.business_name && <p className="text-sm text-gray-500 text-center mb-4">{route.business_name}</p>}
          {route.date && <p className="text-xs text-gray-400 text-center mb-4">{route.date}</p>}
          
          <div className="space-y-2 mb-6">
            {stops.map((stop, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: idx === stops.length - 1 ? '#EF4444' : idx === 0 ? '#10B981' : '#3B82F6' }}>
                  {idx + 1}
                </span>
                <span className="flex-1">{stop.address}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            <a href={appleMapsUrl} className="flex items-center justify-center gap-2 w-full py-3 bg-black text-white rounded-lg font-medium text-sm hover:bg-gray-800 transition-colors">
              🍎 Open in Apple Maps
            </a>
            <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors">
              🗺️ Open in Google Maps
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// ============== ONBOARDING SUCCESS PAGE ==============
const OnboardingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(sessionId ? "loading" : "error");
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    if (!sessionId) return;

    const checkSession = async () => {
      try {
        const data = await api.getCheckoutSession(sessionId);
        setSessionData(data);
        setStatus(data.status === "completed" ? "success" : "pending");
      } catch (error) {
        console.error("Failed to verify session:", error);
        setStatus("error");
      }
    };

    checkSession();
  }, [sessionId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-gray-600">Verifying your payment...</div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <CardTitle className="text-red-700">Something went wrong</CardTitle>
            <CardDescription>We could not verify your payment. Please contact support.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/get-started")} variant="outline">Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <CardTitle className="text-amber-700">Processing Payment</CardTitle>
            <CardDescription>Your payment is being processed. This may take a moment.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">You will receive an email with your setup link shortly.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="max-w-lg text-center" data-testid="success-card">
        <CardHeader>
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <CardTitle className="text-2xl text-blue-800">Payment Successful!</CardTitle>
          <CardDescription className="text-base">
            Welcome to ZipLeadPro! Your account has been created.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <p className="font-medium text-slate-900">What happens next:</p>
            <ol className="mt-2 space-y-2 text-sm text-blue-800">
              <li>1. Check your email for your setup link</li>
              <li>2. Set up your business info</li>
              <li>3. Add your services and prices</li>
              <li>4. Share your form with customers</li>
            </ol>
          </div>
          
          {sessionData?.onboarding_token && (
            <Button 
              onClick={() => navigate(`/onboarding/${sessionData.onboarding_token}`)}
              className="w-full"
              data-testid="start-onboarding-btn"
            >
              Start Onboarding Now
            </Button>
          )}
          
          <p className="text-sm text-gray-600">
            A receipt has been sent to {sessionData?.customer_email}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== ONBOARDING PAGE ==============
const OnboardingPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    notify_email: "",
    reply_to_email: "",
    intake_mode: "simple_form",
    field_config: {
      name_required: true,
      email_required: true,
      phone_enabled: true,
      phone_required: false,
      address_enabled: false,
      address_required: false,
      file_upload_enabled: false,
      message_enabled: true,
      photo_mode: "off",
      form_background_color: "#eff6ff",
      form_text_color: "#111827",
      logo_size: 96,
      tagline: "",
      submit_button_text: "Get My Quote",
      custom_questions: [],
    },
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      const data = await api.validateOnboardingToken(token);
      setOnboardingData(data);
      setFormData((prev) => ({
        ...prev,
        name: data.business_name || "",
        slug: data.tenant_slug || "",
        notify_email: data.business_email || "",
      }));
      
      if (data.onboarding_completed) {
        navigate(`/admin`);
      }
    } catch (error) {
      console.error("Invalid token:", error);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateOnboarding(token, formData);
      
      if (logoFile && onboardingData?.tenant_slug) {
        await api.uploadLogo(onboardingData.tenant_slug, logoFile);
      }
      
      return true;
    } catch (error) {
      toast.error("Failed to save");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    // Validate password
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    const saved = await handleSave();
    if (!saved) return;

    try {
      const result = await api.completeOnboarding(token, password);
      
      if (result.token) {
        // Clear any stale tour state from browser cache before starting fresh
        clearTourStateForSlug(result.tenant_slug);
        
        localStorage.setItem('token', result.token);
        localStorage.setItem('businessSlug', result.tenant_slug);
      }
      
      toast.success("🎉 Setup complete! Your form is ready!");
      navigate(`/dashboard/${result.tenant_slug}`);
    } catch (error) {
      toast.error("Failed to complete onboarding");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  const formLink = onboardingData?.tenant_slug ? `${window.location.origin}/api/share/${onboardingData.tenant_slug}` : null;

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Set Up Your Lead Form</h1>
          <p className="text-gray-600 mt-1 text-sm">Customize your form, then start capturing leads</p>
        </div>

        {/* Progress Steps - Now 2 steps */}
        <div className="flex items-center justify-center mb-6">
          {[1, 2].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm ${
                  step >= s ? "bg-slate-900 text-white" : "bg-gray-200 text-gray-600"
                }`}
              >
                {step > s ? "✓" : s}
              </div>
              {s < 2 && <div className={`w-16 h-1 ${step > s ? "bg-slate-900" : "bg-gray-200"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Notification Email + Form Customization (All-in-One) */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Notification Setup Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <span>📬</span> Notification Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-sm">Where should we send new leads?</Label>
                  <Input
                    type="email"
                    value={formData.notify_email}
                    onChange={(e) => setFormData({ ...formData, notify_email: e.target.value })}
                    placeholder="your@email.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Business Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your Business Name"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Form Builder - Using MobilePreviewSheet inline */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <span>🎨</span> Customize Your Form
                </CardTitle>
                <CardDescription>See changes in real-time below</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <MobilePreviewSheet
                  intakeMode={formData.intake_mode}
                  fieldConfig={formData.field_config}
                  services={null}
                  logoUrl={logoPreview}
                  businessName={formData.name}
                  slug={onboardingData?.tenant_slug}
                  showOnAllScreens={true}
                  onIntakeModeChange={(mode) => setFormData(prev => ({ ...prev, intake_mode: mode }))}
                  onFieldConfigChange={(key, value) => setFormData(prev => ({ 
                    ...prev, 
                    field_config: { ...prev.field_config, [key]: value } 
                  }))}
                  onLogoChange={(file) => {
                    if (file) {
                      setLogoFile(file);
                      setLogoPreview(URL.createObjectURL(file));
                    } else {
                      setLogoFile(null);
                      setLogoPreview(null);
                    }
                  }}
                />
              </CardContent>
            </Card>

            {/* Next Button */}
            <Button 
              onClick={() => setStep(2)} 
              className="w-full py-6 text-lg bg-slate-900 hover:bg-slate-900 font-semibold"
              disabled={!formData.notify_email || !formData.name}
            >
              Continue to Review →
            </Button>
          </div>
        )}

        {/* Step 2: Review & Complete */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span>🚀</span> Ready to Launch!
              </CardTitle>
              <CardDescription>Create your password and activate your form</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Password Setup */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="onboard-password">Create Password *</Label>
                  <Input
                    id="onboard-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">At least 6 characters</p>
                </div>
                <div>
                  <Label htmlFor="onboard-confirm-password">Confirm Password *</Label>
                  <Input
                    id="onboard-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1"
                    required
                  />
                </div>
              </div>
              
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-slate-900 font-medium">
                  <span>✅</span> Lead alerts will go to: <strong>{formData.notify_email}</strong>
                </div>
                <div className="flex items-center gap-2 text-slate-900 font-medium">
                  <span>✅</span> Business: <strong>{formData.name}</strong>
                </div>
                <div className="flex items-center gap-2 text-slate-900 font-medium">
                  <span>✅</span> Form style: <strong>{formData.intake_mode === "service_buttons" ? "Service Buttons" : "Simple Form"}</strong>
                </div>
              </div>

              {/* Your Link Preview */}
              {formLink && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium mb-2">📋 Your Form Link (ready after setup):</p>
                  <code className="text-xs bg-blue-100 px-2 py-1 rounded block overflow-x-auto">{formLink}</code>
                </div>
              )}

              <p className="text-sm text-gray-500 text-center">
                You can always customize more in your dashboard after setup.
              </p>

              {/* Navigation buttons */}
              <div className="flex gap-4 pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 py-6 text-base">
                  ← Back
                </Button>
                <Button 
                  onClick={handleComplete} 
                  disabled={saving || !password || password.length < 6 || password !== confirmPassword} 
                  className="flex-1 py-6 text-base bg-blue-700 hover:bg-blue-800 font-semibold"
                >
                  {saving ? "Setting up..." : "✓ Activate My Form"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============== INTAKE PREVIEW COMPONENT ==============
const IntakePreview = ({ intakeMode, fieldConfig, services, logoUrl, businessName, backgroundColor, cardBackgroundColor, textColor, fontFamily, logoSize, onSave, saving, hideLabel = false, onIntakeModeChange, slug, shopHeadline, shopHeadlineSize, shopHeadlineColor, shopFooter, shopFooterSize, shopFooterColor }) => {
  const [logoError, setLogoError] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [viewMode, setViewMode] = useState('customer'); // 'cheatsheet' or 'customer' - defaulting to customer view
  const [demoPhotos, setDemoPhotos] = useState([]); // For interactive photo upload demo
  const [showInteractiveHint, setShowInteractiveHint] = useState(true); // Hint that preview is interactive
  const [hasInteracted, setHasInteracted] = useState(false); // Track if user has clicked anything
  
  // Auto-dismiss interactive hint after 20 seconds
  useEffect(() => {
    if (showInteractiveHint && !hasInteracted) {
      const timer = setTimeout(() => setShowInteractiveHint(false), 20000);
      return () => clearTimeout(timer);
    }
  }, [showInteractiveHint, hasInteracted]);
  
  // Handle any interaction with the preview
  const handlePreviewInteraction = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
      setShowInteractiveHint(false);
    }
  };
  
  // Real products from Stripe (when connected)
  const [realProducts, setRealProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsFetched, setProductsFetched] = useState(false);
  
  // Demo cart state for Product Catalog preview
  const [demoCart, setDemoCart] = useState([]);
  const [showDemoCartDrawer, setShowDemoCartDrawer] = useState(false);
  
  // Demo products for Product Catalog preview (fallback when no Stripe products)
  const demoProducts = [
    { id: 'demo-prod-1', name: "Basic Package", price: 1999, image: "📦", isDemo: true },
    { id: 'demo-prod-2', name: "Pro Package", price: 4999, image: "⭐", isDemo: true },
    { id: 'demo-prod-3', name: "Custom Service", price: 2499, image: "🔧", isDemo: true },
    { id: 'demo-prod-4', name: "Add-on Item", price: 999, image: "➕", isDemo: true }
  ];
  
  // Fetch real products when in product_catalog mode - always try, handle errors gracefully
  useEffect(() => {
    if (intakeMode === 'product_catalog' && slug && !productsFetched) {
      setLoadingProducts(true);
      api.getProducts(slug)
        .then(products => {
          // Transform Stripe products to our format
          const formatted = products.map(p => ({
            id: p.id,
            name: p.name,
            price: p.prices?.[0]?.unit_amount || 0,
            priceId: p.prices?.[0]?.id,
            image: p.images?.[0] || null,
            description: p.description,
            isDemo: false
          }));
          setRealProducts(formatted);
        })
        .catch(err => {
          setRealProducts([]);
        })
        .finally(() => {
          setLoadingProducts(false);
          setProductsFetched(true);
        });
    }
  }, [intakeMode, slug, productsFetched]);
  
  // Reset productsFetched when slug changes
  useEffect(() => {
    setProductsFetched(false);
    setRealProducts([]);
  }, [slug]);
  
  // Use real products if available, otherwise demo
  const displayProducts = realProducts.length > 0 ? realProducts : demoProducts;
  const isUsingDemoProducts = realProducts.length === 0;
  
  const addToDemoCart = (product) => {
    setDemoCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, qty: 1 }];
    });
  };
  
  const updateDemoCartQty = (productId, qty) => {
    if (qty < 1) {
      setDemoCart(prev => prev.filter(item => item.id !== productId));
    } else {
      setDemoCart(prev => prev.map(item => item.id === productId ? { ...item, qty } : item));
    }
  };
  
  const removeDemoCartItem = (productId) => {
    setDemoCart(prev => prev.filter(item => item.id !== productId));
  };
  
  const demoCartTotal = demoCart.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const demoCartCount = demoCart.reduce((sum, item) => sum + item.qty, 0);
  
  // Demo services with modifiers for education - shown when no real services exist
  // Using generic names so users know ZipLeadPro works for ANY service business
  const demoServices = [
    { id: 'demo-1', label: 'Basic Service', description: 'Standard package', price_value: 99, pricing_type: 'starting_at', active: true, is_modifier: false },
    { id: 'demo-2', label: 'Premium Service', description: 'Full-service option', price_value: 199, pricing_type: 'fixed', active: true, is_modifier: false },
    { id: 'demo-3', label: 'Rush Service', description: 'Priority scheduling', price_value: null, pricing_type: 'none', active: true, is_modifier: true, modifier_type: 'percentage', modifier_percent: 20 },
    { id: 'demo-4', label: 'After Hours', description: 'Evening/weekend', price_value: null, pricing_type: 'none', active: true, is_modifier: true, modifier_type: 'fixed', modifier_fixed: 50 },
  ];
  
  const displayServices = services && services.length > 0 ? services.filter(s => s.active) : demoServices;
  const isUsingDemoServices = !services || services.length === 0;
  const fc = fieldConfig || {};
  const isServiceButtons = intakeMode === "service_buttons";
  const photoMode = fc.photo_mode || "off";
  const isCheatSheet = viewMode === 'cheatsheet';
  
  // Separate base services and modifiers
  const baseServices = displayServices.filter(s => !s.is_modifier);
  const modifierServices = displayServices.filter(s => s.is_modifier);
  
  // Check if any base service is selected (required for modifiers)
  const hasBaseServiceSelected = selectedServices.some(id => {
    const service = displayServices.find(s => s.id === id);
    return service && !service.is_modifier;
  });
  
const calculateTotal = () => {
  let baseTotal = 0;
  let modifierTotal = 0;

  const serviceById = new Map(
    displayServices.map(s => [String(s.id), s])
  );

  for (const id of selectedServices) {
    const service = serviceById.get(String(id));
    if (!service) continue;

    if (!service.is_modifier && service.price_value) {
      baseTotal += Number(service.price_value) || 0;
    }
  }

  for (const id of selectedServices) {
    const service = serviceById.get(String(id));
    if (!service) continue;

    if (service.is_modifier) {
      if (service.modifier_type === "percentage" && service.modifier_percent) {
        modifierTotal += baseTotal * (Number(service.modifier_percent) / 100);
      } else if (service.modifier_type === "fixed" && service.modifier_fixed) {
        modifierTotal += Number(service.modifier_fixed) || 0;
      }
    }
  }

  return { baseTotal, modifierTotal, total: baseTotal + modifierTotal };
};
  
  const totals = calculateTotal();
  
  // Toggle service selection
  const toggleService = (serviceId) => {
    handlePreviewInteraction(); // Track interaction
    const service = displayServices.find(s => s.id === serviceId);
    if (!service) return;
    
    // If it's a modifier and no base service selected, don't allow selection
    if (service.is_modifier && !hasBaseServiceSelected && !selectedServices.includes(serviceId)) {
      return;
    }
    
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };
  
  // Generate background style
  const bgStyle = backgroundColor 
    ? { backgroundColor } 
    : { background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)' };
  
  // Text styling
  const textStyle = textColor ? { color: textColor } : {};
  const fontStyle = fontFamily ? { fontFamily } : {};
  
  // Detect dark background for auto text contrast (use card bg since that's where text appears)
  const isDarkBg = (cardBackgroundColor || backgroundColor) && (
    (cardBackgroundColor || backgroundColor).toLowerCase().startsWith('#0') ||
    (cardBackgroundColor || backgroundColor).toLowerCase().startsWith('#1') ||
    (cardBackgroundColor || backgroundColor).toLowerCase().startsWith('#2') ||
    (cardBackgroundColor || backgroundColor).toLowerCase().startsWith('#3') ||
    (cardBackgroundColor || backgroundColor) === '#000' ||
    (cardBackgroundColor || backgroundColor) === '#000000'
  );
  const autoTextColor = isDarkBg ? '#ffffff' : (textColor || '#111827');
  
  // Logo size
  const previewLogoSize = logoSize || 96;
  
  // Build logo URL properly
  const getLogoSrc = () => {
    if (!logoUrl) return null;
    if (logoUrl.startsWith('blob:')) return logoUrl;
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) return logoUrl;
    if (logoUrl.startsWith('/api/')) {
      return `${BACKEND_URL}${logoUrl}`;
    }
    return `${STATIC_URL}${logoUrl}`;
  };
  
  const logoSrc = getLogoSrc();
  
  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);
  
  const formatPreviewPrice = (service) => {
    // Hide all pricing if hide_pricing is enabled
    if (fc.hide_pricing) return null;
    
    if (service.is_modifier) {
      if (service.modifier_type === 'percentage') return `+${service.modifier_percent}%`;
      if (service.modifier_type === 'fixed') return `+$${service.modifier_fixed}`;
      return null;
    }
    if (!service.price_value || service.pricing_type === "none") return null;
    if (service.pricing_type === "starting_at") return `From $${service.price_value}`;
    if (service.pricing_type === "per_unit") return `$${service.price_value}/unit`;
    return `$${service.price_value}`;
  };

  // Render a service item - adapts based on viewMode
  const renderServiceItem = (service, isModifier = false) => {
    const isSelected = selectedServices.includes(service.id);
    const isDisabled = isModifier && !hasBaseServiceSelected && !isSelected;
    
    // In customer view, modifiers look the same as regular services
    const useModifierStyling = isCheatSheet && isModifier;
    
    return (
      <div 
        key={service.id} 
        onClick={() => !isDisabled && toggleService(service.id)}
        className={`border rounded p-2 text-xs transition-all ${
          isDisabled 
            ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
            : isSelected 
              ? useModifierStyling
                ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600 cursor-pointer'
                : 'border-blue-800 bg-blue-50 ring-1 ring-blue-800 cursor-pointer'
              : useModifierStyling
                ? 'border-blue-200 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 cursor-pointer'
        }`}
      >
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-2">
            <div className={`h-3.5 w-3.5 mt-0.5 rounded-sm border flex items-center justify-center ${
              isSelected 
                ? useModifierStyling ? 'bg-blue-700 border-blue-700' : 'bg-slate-900 border-slate-900'
                : useModifierStyling ? 'border-blue-300 bg-white' : 'border-gray-300 bg-white'
            }`}>
              {isSelected && (
                <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <div>
              <div className="font-medium flex items-center gap-1" style={{ color: autoTextColor }}>
                {service.label}
                {isCheatSheet && isModifier && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">modifier</span>
                )}
              </div>
              {service.description && (
                <div className="text-xs" style={{ color: autoTextColor, opacity: 0.7 }}>{service.description}</div>
              )}
            </div>
          </div>
          <span className={`font-medium whitespace-nowrap ${useModifierStyling ? 'text-blue-700' : 'text-slate-900'}`}>
            {formatPreviewPrice(service)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4" style={{ backgroundColor: backgroundColor || '#f9fafb' }} onClick={handlePreviewInteraction}>
      {/* 1. Header - "This is a preview" */}
      <div className="mb-3">
        {!hideLabel && (
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide text-center mb-2">
            Live Preview {isUsingDemoServices && <span className="text-blue-800">(Demo)</span>}
          </div>
        )}
        
        {/* 2. Mode selector toggle buttons */}
        {onIntakeModeChange && (
          <div className="flex bg-blue-100 rounded-lg p-0.5 mb-2" data-tour="form-style-select">
            <button
              onClick={() => onIntakeModeChange('simple_form')}
              className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-all ${
                intakeMode === 'simple_form'
                  ? 'bg-white text-slate-900 shadow-sm font-medium' 
                  : 'text-slate-900 hover:text-slate-900'
              }`}
            >
              Simple Form Mode
            </button>
            <button
              onClick={() => onIntakeModeChange('service_buttons')}
              className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-all ${
                intakeMode === 'service_buttons'
                  ? 'bg-white text-slate-900 shadow-sm font-medium' 
                  : 'text-slate-900 hover:text-slate-900'
              }`}
            >
              Button Select Mode
            </button>
          </div>
        )}
        
        {/* View Mode Toggle - Hidden for now, defaulting to Customer View */}
        {/* 
        <div className="flex bg-gray-200 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('cheatsheet')}
            className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-all flex items-center justify-center gap-1 ${
              isCheatSheet 
                ? 'bg-white text-slate-900 shadow-sm font-medium' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <span>📚</span> Cheat Sheet
          </button>
          <button
            onClick={() => setViewMode('customer')}
            className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-all flex items-center justify-center gap-1 ${
              !isCheatSheet 
                ? 'bg-white text-slate-900 shadow-sm font-medium' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <span>👁️</span> Customer View
          </button>
        </div>
        */}
      </div>
      
      {/* 3. Interactive hint - shows until user interacts or 20 seconds pass */}
      {isUsingDemoServices && showInteractiveHint && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3 text-center animate-pulse">
          <p className="text-xs text-amber-700">
            👆 <span className="font-medium">Try tapping around your preview form!</span> Get a feel for what your customers will see & experience
          </p>
        </div>
      )}
      
      {/* 4. Sample services info badge */}
      {isUsingDemoServices && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3 text-center">
          <p className="text-xs text-slate-900">
            💡 <span className="font-medium">Sample services shown.</span> ZipLeadPro works for any service business!
          </p>
        </div>
      )}
      
      {/* Cheat Sheet Educational Callout - Show when there are modifiers or using demo */}
      {isCheatSheet && (isUsingDemoServices || modifierServices.length > 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
          <p className="text-xs text-slate-900 mb-2">
            <span className="font-semibold">💡 Pro Tip:</span> {modifierServices.length > 0 ? (
              <>Your add-ons (<span className="font-medium">{modifierServices.map(m => m.label).join(', ')}</span>) let you quickly adjust quotes. Select a base service first, then add extras!</>
            ) : (
              <>Add <span className="font-medium">extras</span> like "Rush Service +20%" or "After Hours +$50" to quickly adjust quotes for customers.</>
            )}
          </p>
          <p className="text-xs text-blue-700 italic">
            ⓘ Labels shown here are for your reference only — customers won't see these badges.
          </p>
        </div>
      )}
      
      {/* Customer View Info - Only in customer mode */}
      {!isCheatSheet && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
          <p className="text-xs text-blue-700 text-center">
            👁️ This is a preview. You can open this at any time and see what your customers will see. 
          </p>
        </div>
      )}
      
      {/* Mini preview of the customer form */}
      <div className="rounded-lg shadow-sm border overflow-hidden" style={{ ...fontStyle, backgroundColor: backgroundColor || '#ffffff' }}>
        {/* Header - Same centered logo for ALL modes */}
        <div className="p-4 text-center" style={{ backgroundColor: cardBackgroundColor || undefined }}>
          {logoSrc && !logoError && (
            <img 
              src={logoSrc} 
              alt="Business logo" 
              className="mx-auto mb-2 object-contain" 
              style={{ height: `${previewLogoSize}px` }}
              onError={() => setLogoError(true)}
            />
          )}
          <h3 className="text-lg font-bold" style={{ color: autoTextColor }}>{businessName || "Your Business"}</h3>
          {intakeMode !== 'product_catalog' && (
            <p className="text-xs" style={{ color: autoTextColor, opacity: 0.8 }}>{fieldConfig?.tagline || "Request a quote today"}</p>
          )}
        </div>
        
        <div className="p-4 space-y-4" style={{ ...fontStyle, backgroundColor: cardBackgroundColor || undefined }}>
          {/* Product Catalog Preview */}
          {intakeMode === 'product_catalog' ? (
            <div className="space-y-3">
              {/* Custom Headline Preview - Always show area, use actual size */}
              <div className="text-center pb-2">
                <h2 
                  style={{ 
                    fontSize: `${parseInt(shopHeadlineSize) || 24}px`, 
                    color: shopHeadlineColor || '#111827'
                  }}
                  className="font-semibold"
                  data-testid="preview-shop-headline"
                >
                  {shopHeadline || <span className="text-gray-300 italic">Shop headline...</span>}
                </h2>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Products</span>
                <button 
                  onClick={() => setShowDemoCartDrawer(true)}
                  className="text-xs bg-blue-100 text-slate-900 px-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-blue-200 transition-colors"
                  data-testid="preview-cart-button"
                >
                  🛒 Cart {demoCartCount > 0 && <span className="bg-slate-900 text-white rounded-full px-1.5 text-[10px]">{demoCartCount}</span>}
                </button>
              </div>
              
              {/* Loading state */}
              {loadingProducts && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto"></div>
                  <p className="text-xs text-gray-500 mt-2">Loading products...</p>
                </div>
              )}
              
              {/* Products Grid - Real or Demo */}
              {!loadingProducts && (
                <div className="grid grid-cols-2 gap-2">
                  {displayProducts.map((product) => {
                    const inCart = demoCart.find(item => item.id === product.id);
                    return (
                      <div key={product.id} className="bg-gray-50 rounded-lg p-2 border hover:border-blue-300 transition-colors">
                        {/* Product Image - Real image or emoji for demo */}
                        {product.isDemo ? (
                          <div className="text-2xl text-center mb-1">{product.image}</div>
                        ) : product.image ? (
                          <img src={product.image} alt={product.name} className="w-full h-16 object-cover rounded mb-1" />
                        ) : (
                          <div className="w-full h-16 bg-gray-200 rounded mb-1 flex items-center justify-center">
                            <span className="text-gray-400 text-xs">No image</span>
                          </div>
                        )}
                        <div className="text-xs font-medium truncate text-center">{product.name}</div>
                        <div className="text-xs text-slate-900 font-bold text-center">${(product.price / 100).toFixed(2)}</div>
                        {inCart ? (
                          <div className="flex items-center justify-center gap-1 mt-1">
                            <button 
                              onClick={() => updateDemoCartQty(product.id, inCart.qty - 1)}
                              className="w-6 h-6 text-xs bg-gray-200 rounded hover:bg-gray-300"
                            >-</button>
                            <span className="text-xs font-medium w-6 text-center">{inCart.qty}</span>
                            <button 
                              onClick={() => updateDemoCartQty(product.id, inCart.qty + 1)}
                              className="w-6 h-6 text-xs bg-gray-200 rounded hover:bg-gray-300"
                            >+</button>
                          </div>
                        ) : (
                          <button 
                            onClick={() => addToDemoCart(product)}
                            className="w-full mt-1 text-xs bg-slate-900 text-white py-1 rounded hover:bg-slate-900"
                            data-testid={`add-product-${product.id}`}
                          >
                            Add
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Status message */}
              <p className="text-xs text-gray-500 text-center italic">
                {isUsingDemoProducts 
                  ? "✨ Demo products shown - Connect Stripe & add products to see your real catalog"
                  : `✅ Showing ${displayProducts.length} product${displayProducts.length !== 1 ? 's' : ''} from your Stripe account`
                }
              </p>
              
              {/* Demo Cart Drawer */}
              {showDemoCartDrawer && (
                <div className="fixed inset-0 z-[100]" onClick={() => setShowDemoCartDrawer(false)}>
                  <div className="absolute inset-0 bg-black/30" />
                  <div 
                    className="absolute right-0 top-0 h-full w-full max-w-xs bg-white shadow-xl flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-3 border-b flex items-center justify-between">
                      <h3 className="text-sm font-semibold">Your Cart ({demoCartCount})</h3>
                      <button onClick={() => setShowDemoCartDrawer(false)} className="p-1 hover:bg-gray-100 rounded text-gray-500">✕</button>
                    </div>
                    <div className="flex-1 overflow-auto p-3">
                      {demoCart.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 text-sm">Your cart is empty</div>
                      ) : (
                        <div className="space-y-2">
                          {demoCart.map(item => (
                            <div key={item.id} className="flex gap-2 p-2 bg-gray-50 rounded-lg">
                              {/* Cart item image */}
                              {item.isDemo ? (
                                <div className="text-2xl">{item.image}</div>
                              ) : item.image ? (
                                <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                              ) : (
                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">No img</div>
                              )}
                              <div className="flex-1">
                                <div className="text-xs font-medium">{item.name}</div>
                                <div className="text-xs text-gray-600">${(item.price / 100).toFixed(2)}</div>
                                <div className="flex items-center gap-1 mt-1">
                                  <button onClick={() => updateDemoCartQty(item.id, item.qty - 1)} className="w-5 h-5 text-xs bg-gray-200 rounded">-</button>
                                  <span className="text-xs w-5 text-center">{item.qty}</span>
                                  <button onClick={() => updateDemoCartQty(item.id, item.qty + 1)} className="w-5 h-5 text-xs bg-gray-200 rounded">+</button>
                                  <button onClick={() => removeDemoCartItem(item.id)} className="ml-auto text-red-500 text-xs">✕</button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {demoCart.length > 0 && (
                      <div className="p-3 border-t space-y-2">
                        <div className="flex justify-between text-sm font-semibold">
                          <span>Total</span>
                          <span>${(demoCartTotal / 100).toFixed(2)}</span>
                        </div>
                        <button 
                          onClick={() => { setShowDemoCartDrawer(false); setDemoCart([]); }}
                          className="w-full text-xs bg-slate-900 text-white py-2.5 rounded-lg hover:bg-slate-900 font-medium"
                        >
                          {isUsingDemoProducts ? "Demo Checkout →" : "Proceed to Checkout →"}
                        </button>
                        <p className="text-[10px] text-gray-400 text-center">
                          {isUsingDemoProducts ? "Preview only" : "You'll enter your info on the next screen"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : isServiceButtons ? (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-700">Select Services</label>
              
              {isCheatSheet ? (
                /* Cheat Sheet Mode - Separate sections with labels */
                <>
                  {/* Base Services */}
                  <div className="space-y-2">
                    {baseServices.slice(0, 3).map((service) => renderServiceItem(service, false))}
                  </div>
                  
                  {/* Modifiers Section with Label */}
                  {modifierServices.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-dashed">
                      <label className="text-xs font-semibold text-blue-800 flex items-center gap-1 mb-2">
                        <span>⚡</span> Add-ons & Modifiers
                        {!hasBaseServiceSelected && (
                          <span className="text-gray-400 font-normal ml-1">(select a service first)</span>
                        )}
                      </label>
                      <div className="space-y-2">
                        {modifierServices.slice(0, 2).map((service) => renderServiceItem(service, true))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Customer View Mode - All services look the same */
                <div className="space-y-2">
                  {displayServices.slice(0, 5).map((service) => renderServiceItem(service, service.is_modifier))}
                </div>
              )}
            </div>
          ) : (
            /* Simple Form Mode - Interactive Multi-Select Dropdown */
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-700">Service Type</label>
              <div className="relative">
                <div 
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="border rounded p-2 text-xs bg-white flex justify-between items-center cursor-pointer hover:border-blue-300"
                >
                  <span className={selectedServices.length > 0 ? 'text-gray-900' : 'text-gray-500'}>
                    {selectedServices.filter(id => {
                      const s = displayServices.find(svc => String(svc.id) === String(id));
                      return s && !s.is_modifier;
                    }).length > 0 
                      ? `${selectedServices.filter(id => {
                          const s = displayServices.find(svc => String(svc.id) === String(id));
                          return s && !s.is_modifier;
                        }).length} service${selectedServices.filter(id => {
                          const s = displayServices.find(svc => String(svc.id) === String(id));
                          return s && !s.is_modifier;
                        }).length > 1 ? 's' : ''} selected`
                      : 'Select services...'}
                  </span>
                  <svg className={`h-3 w-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {dropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-40 overflow-y-auto">
                    {displayServices.filter(s => !s.is_modifier).map((service) => {
                      const isSelected = selectedServices.includes(service.id);
                      return (
                        <div 
                          key={service.id}
                          onClick={() => toggleService(service.id)}
                          className={`p-2 text-xs cursor-pointer flex justify-between items-center ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <span className="flex items-center gap-2">
                            <div className={`h-3 w-3 rounded border flex items-center justify-center ${isSelected ? 'bg-slate-900 border-slate-900' : 'border-gray-300'}`}>
                              {isSelected && (
                                <svg className="h-2 w-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            {service.label}
                          </span>
                          {formatPreviewPrice(service) && (
                            <span className="text-slate-900">{formatPreviewPrice(service)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Modifiers for dropdown mode - shown after selecting a service */}
              {modifierServices.length > 0 && hasBaseServiceSelected && (
                <div className="mt-2 pt-2 border-t border-dashed">
                  {isCheatSheet && (
                    <label className="text-xs font-semibold text-blue-800 mb-1 block">⚡ Add-ons</label>
                  )}
                  {modifierServices.slice(0, 2).map((service) => {
                    const isSelected = selectedServices.includes(service.id);
                    return (
                      <div 
                        key={service.id}
                        onClick={() => toggleService(service.id)}
                        className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs ${
                          isSelected 
                            ? isCheatSheet ? 'bg-blue-50' : 'bg-blue-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className={`h-3 w-3 rounded border flex items-center justify-center ${
                          isSelected 
                            ? isCheatSheet ? 'bg-blue-700 border-blue-700' : 'bg-slate-900 border-slate-900'
                            : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="h-2 w-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="flex-1">{service.label}</span>
                        <span className={isCheatSheet ? 'text-blue-700' : 'text-slate-900'}>{formatPreviewPrice(service)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Running Total - Not shown for product catalog (cart handles this) */}
          {intakeMode !== 'product_catalog' && selectedServices.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="text-xs space-y-1">
                {selectedServices.map(id => {
                  const service = displayServices.find(s => String(s.id) === String(id));
                  if (!service) return null;
                  
                  let priceDisplay = '';
                  if (Boolean(service.is_modifier)) {
                    if (service.modifier_type === 'percentage') {
                      const amount = totals.baseTotal * (Number(service.modifier_percent) / 100);
                      priceDisplay = `+$${amount.toFixed(2)}`;
                    } else {
                      priceDisplay = `+$${service.modifier_fixed}`;
                    }
                  } else {
                    priceDisplay = service.price_value ? `$${service.price_value}` : '$0';
                  }
                  
                  return (
                    <div key={id} className="flex justify-between text-gray-600">
                      <span className="flex items-center gap-1">
                        {service.label}
                        {isCheatSheet && service.is_modifier && (
                          <span className="text-blue-600 text-xs">
                            ({service.modifier_type === 'percentage' ? `${service.modifier_percent}%` : 'flat'})
                          </span>
                        )}
                      </span>
                      <span>{priceDisplay}</span>
                    </div>
                  );
                })}
                {/* Only show totals if pricing is not hidden */}
                {!fc.hide_pricing && (
                  <div className="flex justify-between font-semibold text-slate-900 pt-1 border-t border-blue-200 mt-1">
                    <span>Current Selection Value</span>
                    <span>${totals.total.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Pricing disclaimer - matches live form */}
          {!fc.hide_pricing && selectedServices.length > 0 && (
            <p className="text-[10px] text-gray-400 italic">
              * Pricing shown is for reference only and is not final.
            </p>
          )}
          
          {/* Contact Information - Not shown for product catalog (Stripe Checkout handles this) */}
          {intakeMode !== 'product_catalog' && (
          <div className="border-t pt-3">
            <label className="text-xs font-semibold text-gray-700 block mb-2">Contact Information</label>
            <div className="space-y-2">
              {/* Name field */}
              <div className="space-y-1">
                <label className="text-xs" style={{ color: autoTextColor }}>
                  Name {fc.name_required && <span className="text-red-500">*</span>}
                </label>
                <div className="border rounded p-1.5 text-xs text-gray-400 bg-gray-50">Your name</div>
              </div>
              
              {/* Email field */}
              <div className="space-y-1">
                <label className="text-xs" style={{ color: autoTextColor }}>
                  Email {fc.email_required && <span className="text-red-500">*</span>}
                </label>
                <div className="border rounded p-1.5 text-xs text-gray-400 bg-gray-50">your@email.com</div>
              </div>
              
              {/* Phone field - conditional */}
              {fc.phone_enabled && (
                <div className="space-y-1">
                  <label className="text-xs" style={{ color: autoTextColor }}>
                    Phone {fc.phone_required && <span className="text-red-500">*</span>}
                  </label>
                  <div className="border rounded p-1.5 text-xs text-gray-400 bg-gray-50">Phone number</div>
                </div>
              )}
              
              {/* Address field - conditional */}
              {fc.address_enabled && (
                <div className="space-y-1">
                  <label className="text-xs" style={{ color: autoTextColor }}>Address {fc.address_required && <span className="text-red-500">*</span>}</label>
                  <div className="border rounded p-1.5 text-xs text-gray-400 bg-gray-50">123 Main St</div>
                </div>
              )}
              
              {/* Message field - conditional */}
              {fc.message_enabled && (
                <div className="space-y-1">
                  <label className="text-xs" style={{ color: autoTextColor }}>Message / Details</label>
                  <div className="border rounded p-1.5 text-xs text-gray-400 bg-gray-50 h-12">Tell us about your needs...</div>
                </div>
              )}
              
              {/* Custom Questions - render in preview */}
              {fc.custom_questions && fc.custom_questions.length > 0 && (
                <div className="space-y-3 pt-2 border-t">
                  {fc.custom_questions.map((q) => (
                    <div key={q.id} className="space-y-1">
                      <label className="text-xs" style={{ color: autoTextColor }}>
                        {q.label || 'Question'} {q.required && <span className="text-red-500">*</span>}
                      </label>
                      {q.type === 'text' ? (
                        <input
                          type="text"
                          disabled
                          placeholder="Text answer..."
                          className="w-full text-xs border rounded px-2 py-1 bg-gray-50"
                        />
                      ) : (
                      <div className="space-y-1">
                        {(q.options || []).filter(opt => opt).map((option, optIdx) => (
                          <label key={optIdx} className="flex items-center gap-2 text-xs text-gray-500">
                            <input
                              type={q.type === 'radio' ? 'radio' : 'checkbox'}
                              disabled
                              className="w-3 h-3"
                            />
                            <span>{option || `Option ${optIdx + 1}`}</span>
                          </label>
                        ))}
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Photo upload - conditional */}
              {photoMode !== "off" && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-600">
                    Photos {photoMode === "required" && <span className="text-red-500">*</span>}
                    {photoMode === "optional" && <span className="text-gray-400"> (optional)</span>}
                  </label>
                  {fc.photo_instructions && (
                    <p className="text-xs text-gray-500 whitespace-pre-wrap">{fc.photo_instructions}</p>
                  )}
                  <div className="border-2 border-dashed rounded p-3 text-center bg-gray-50">
                    <svg className="h-6 w-6 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs text-gray-500">Click to upload photos</span>
                  </div>
                  <p className="text-[10px] text-gray-400">Max 5MB per photo. JPG, PNG supported.</p>
                </div>
              )}
            </div>
          </div>
          )}
          
          {/* File Upload Section - conditional */}
          {intakeMode !== 'product_catalog' && (fc.file_upload_mode === "optional" || fc.file_upload_mode === "required") && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-600">
                Project Files {fc.file_upload_mode === "required" && <span className="text-red-500">*</span>}
                {fc.file_upload_mode === "optional" && <span className="text-gray-400"> (optional)</span>}
              </label>
              {fc.file_upload_instructions && (
                <p className="text-xs text-gray-500 whitespace-pre-wrap">{fc.file_upload_instructions}</p>
              )}
              <div className="border-2 border-dashed rounded p-3 text-center bg-gray-50">
                <svg className="h-6 w-6 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-xs text-gray-500">Click to upload project files</span>
              </div>
              <p className="text-[10px] text-gray-400">Max {fc.file_upload_max_size_mb || 500}MB per file. All file types supported.</p>
            </div>
          )}

          {/* Submit button - Not shown for product catalog */}
          {intakeMode !== 'product_catalog' && (
          <button 
            disabled 
            className={`w-full text-xs font-medium py-2 rounded opacity-75 cursor-not-allowed ${isDarkBg ? 'bg-white text-black' : 'bg-slate-900 text-white'}`}
          >
            {fieldConfig?.submit_button_text || "Submit Request"}
          </button>
          )}
        </div>
        
        {/* Custom Footer Preview - Full width like live, only in product_catalog mode */}
        {intakeMode === 'product_catalog' && (
          <div className="bg-white border-t py-4">
            <div className="text-center">
              <p 
                style={{ 
                  fontSize: `${parseInt(shopFooterSize) || 14}px`, 
                  color: shopFooterColor || '#6b7280'
                }}
                data-testid="preview-shop-footer"
              >
                {shopFooter || <span className="text-gray-300 italic">Shop footer...</span>}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Powered by footer - matches live form */}
      {intakeMode !== 'product_catalog' && (
        <p className="text-center text-[10px] text-gray-400 mt-2">
          Powered by <span className="font-semibold">ZipLeadPro</span>
        </p>
      )}
      
      {/* Save Button - appears when onSave is provided */}
      {onSave && (
        <button
          onClick={onSave}
          disabled={saving}
          className="w-full mt-3 bg-slate-900 hover:bg-slate-900 text-white text-sm font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? "Saving..." : "💾 Save Form Settings"}
        </button>
      )}
    </div>
  );
};

// ============== PRODUCT IMAGE CAROUSEL ==============
const ProductImageCarousel = ({ images, productName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mainAspectRatio, setMainAspectRatio] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Load main image to get its natural aspect ratio
  useEffect(() => {
    if (images && images.length > 0) {
      const img = new Image();
      img.onload = () => {
        const ratio = img.naturalHeight / img.naturalWidth;
        setMainAspectRatio(ratio);
        setImageLoaded(true);
      };
      img.onerror = () => {
        setMainAspectRatio(0.75); // Default to 4:3 if image fails
        setImageLoaded(true);
      };
      img.src = images[0];
    }
  }, [images]);
  
  if (!images || images.length === 0) {
    return (
      <div className="w-full bg-gray-100 flex items-center justify-center" style={{ aspectRatio: '4/3' }}>
        <span className="text-gray-400">No image</span>
      </div>
    );
  }
  
  // Show placeholder while loading main image dimensions
  if (!imageLoaded) {
    return (
      <div className="w-full bg-gray-100 animate-pulse" style={{ aspectRatio: '4/3' }} />
    );
  }
  
  const goTo = (index) => {
    if (index < 0) setCurrentIndex(images.length - 1);
    else if (index >= images.length) setCurrentIndex(0);
    else setCurrentIndex(index);
  };
  
  // Calculate container style based on main image aspect ratio
  // Clamp between reasonable bounds (0.5 = wide landscape, 1.5 = tall portrait)
  const clampedRatio = Math.min(Math.max(mainAspectRatio, 0.5), 1.5);
  
  return (
    <div 
      className="relative w-full group bg-gray-100"
      style={{ aspectRatio: `1/${clampedRatio}` }}
    >
      {/* Current image - object-contain to show full image, letterboxed if needed */}
      <img 
        src={images[currentIndex]} 
        alt={productName} 
        className="w-full h-full object-contain"
      />
      
      {/* Navigation arrows - only show if multiple images */}
      {images.length > 1 && (
        <>
          <button 
            onClick={(e) => { e.stopPropagation(); goTo(currentIndex - 1); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
          >
            ‹
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); goTo(currentIndex + 1); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
          >
            ›
          </button>
          
          {/* Dots indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, idx) => (
              <button 
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                className={`w-2 h-2 rounded-full transition-colors ${idx === currentIndex ? 'bg-white shadow' : 'bg-white/50'}`}
              />
            ))}
          </div>
          
          {/* Image counter */}
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
            {currentIndex + 1}/{images.length}
          </div>
        </>
      )}
    </div>
  );
};

// ============== PRODUCT CATALOG COMPONENT ==============
const ProductCatalog = ({ 
  slug, 
  businessName, 
  logoUrl,
  logoSize,
  shopHeadline,
  shopHeadlineSize,
  shopHeadlineColor,
  shopFooter,
  shopFooterSize,
  shopFooterColor,
  backgroundColor
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stripeNotConnected, setStripeNotConnected] = useState(false);
  const [cart, setCart] = useState([]);
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadProducts = async () => {
    try {
      const data = await api.getProducts(slug);
      setProducts(data);
      setStripeNotConnected(false);
    } catch (error) {
      console.error("[ProductCatalog] Failed to load products:", error);
      console.error("[ProductCatalog] Error response:", error.response?.data);
      // Check if it's a Stripe not connected error
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("Stripe not connected")) {
        setStripeNotConnected(true);
      } else {
        toast.error("Failed to load products");
      }
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product, priceId) => {
    const quantityLimit = product.quantity_limit;
    const existing = cart.find(item => item.priceId === priceId);
    
    if (existing) {
      if (quantityLimit && existing.quantity >= quantityLimit) {
        toast.error(`Maximum quantity (${quantityLimit}) reached for ${product.name}`);
        return;
      }
      setCart(cart.map(item => 
        item.priceId === priceId 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      const price = product.prices.find(p => p.id === priceId);
      setCart([...cart, {
        productId: product.id,
        priceId,
        name: product.name,
        image: product.images?.[0],
        unitAmount: price.unit_amount,
        currency: price.currency,
        quantity: 1,
        quantityLimit
      }]);
    }
    toast.success(`Added ${product.name} to cart`);
  };

  const updateCartQuantity = (priceId, quantity) => {
    const item = cart.find(i => i.priceId === priceId);
    if (quantity < 1) {
      setCart(cart.filter(item => item.priceId !== priceId));
    } else if (item?.quantityLimit && quantity > item.quantityLimit) {
      toast.error(`Maximum quantity (${item.quantityLimit}) reached for ${item.name}`);
    } else {
      setCart(cart.map(item => 
        item.priceId === priceId ? { ...item, quantity } : item
      ));
    }
  };

  const removeFromCart = (priceId) => {
    setCart(cart.filter(item => item.priceId !== priceId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.unitAmount * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setCheckingOut(true);
    try {
      const items = cart.map(item => ({ price_id: item.priceId, quantity: item.quantity }));
      // No email/name needed - Stripe Checkout will collect customer info
      const result = await api.createProductCheckout(slug, items, null, null, true);
      window.location.href = result.checkout_url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
      setCheckingOut(false);
    }
  };

  const formatPrice = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Show message if Stripe is not connected
  if (stripeNotConnected) {
    const isDarkBg = backgroundColor && (
      backgroundColor.toLowerCase().startsWith('#0') ||
      backgroundColor.toLowerCase().startsWith('#1') ||
      backgroundColor.toLowerCase().startsWith('#2') ||
      backgroundColor.toLowerCase().startsWith('#3') ||
      backgroundColor === '#000' ||
      backgroundColor === '#000000'
    );
    const textColorClass = isDarkBg ? 'text-white' : 'text-gray-900';
    
    return (
      <div className="min-h-screen" style={{ backgroundColor: backgroundColor || '#f9fafb' }}>
        <div className="p-4 text-center" style={{ backgroundColor: backgroundColor || '#f9fafb' }}>
          {logoUrl && <img src={logoUrl} alt={businessName} className="mx-auto mb-2 object-contain" style={{ height: `${logoSize || 96}px` }} />}
          <h1 className={`text-2xl font-bold ${textColorClass}`}>{businessName || "Shop"}</h1>
        </div>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="text-6xl mb-4">🛒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Coming Soon!</h2>
            <p className="text-gray-600 mb-4">
              Our online store is being set up. Please check back soon to browse our products!
            </p>
            <p className="text-sm text-gray-400">
              Powered by ZipLeadPro
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Determine text color based on background (dark bg needs light text)
  const isDarkBg = backgroundColor && (
    backgroundColor.toLowerCase().startsWith('#0') ||
    backgroundColor.toLowerCase().startsWith('#1') ||
    backgroundColor.toLowerCase().startsWith('#2') ||
    backgroundColor.toLowerCase().startsWith('#3') ||
    backgroundColor === '#000' ||
    backgroundColor === '#000000'
  );
  const textColorClass = isDarkBg ? 'text-white' : 'text-gray-900';
  const cartTextClass = isDarkBg ? 'text-gray-200 hover:text-white' : 'text-gray-600 hover:text-gray-900';

  return (
    <div className="min-h-[100dvh] flex flex-col" style={{ backgroundColor: backgroundColor || '#f9fafb' }}>
      {/* Header with centered Logo - same as other form modes */}
      <div className="p-4 text-center" style={{ backgroundColor: backgroundColor || '#f9fafb' }}>
        {logoUrl && <img src={logoUrl} alt={businessName} className="mx-auto mb-2 object-contain" style={{ height: `${logoSize || 96}px` }} />}
        <h1 className={`text-2xl font-bold ${textColorClass}`}>{businessName || "Shop"}</h1>
      </div>
      
      {/* Sticky Cart Bar */}
      <div className="sticky top-0 z-10" style={{ backgroundColor: backgroundColor || '#f9fafb' }}>
        <div className="max-w-6xl mx-auto px-4 py-2 flex justify-end">
          <button
            onClick={() => setShowCart(true)}
            className={`relative p-2 flex items-center gap-2 ${cartTextClass}`}
            data-testid="cart-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="text-sm">Cart</span>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-slate-900 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Custom Headline */}
      {shopHeadline && (
        <div className="max-w-6xl mx-auto px-4 pt-8 text-center">
          <h2 
            style={{ 
              fontSize: `${shopHeadlineSize}px`, 
              color: shopHeadlineColor 
            }}
            className="font-semibold"
            data-testid="shop-headline"
          >
            {shopHeadline}
          </h2>
        </div>
      )}

      {/* Products Grid - Masonry-style layout for varying card heights */}
      <div className="max-w-6xl mx-auto px-4 py-8 flex-1">
        {products.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No products available</div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {products.filter(p => p.prices && p.prices.length > 0).map(product => (
              <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden break-inside-avoid" data-testid={`product-${product.id}`}>
                <ProductImageCarousel 
                  images={[...(product.images || []), ...(product.additional_images || [])]} 
                  productName={product.name}
                />
                <div className="p-4">
                  <h3 className="font-semibold text-lg">{product.name}</h3>
                  {product.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-lg font-bold text-slate-900">
                      {formatPrice(product.prices[0].unit_amount, product.prices[0].currency)}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => addToCart(product, product.prices[0].id)}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      Add to Cart
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custom Footer */}
      {shopFooter && (
        <div className="bg-white border-t py-6 mt-auto">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p 
              style={{ 
                fontSize: `${shopFooterSize}px`, 
                color: shopFooterColor 
              }}
              data-testid="shop-footer"
            >
              {shopFooter}
            </p>
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      {showCart && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCart(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Your Cart ({cartCount})</h2>
              <button onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 rounded">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {cart.length === 0 ? (
                <div className="text-center py-8 text-gray-500">Your cart is empty</div>
              ) : (
                <div className="space-y-4">
                  {cart.map(item => {
                    const atLimit = item.quantityLimit && item.quantity >= item.quantityLimit;
                    return (
                    <div key={item.priceId} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No img</span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-gray-600">{formatPrice(item.unitAmount, item.currency)}</div>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateCartQuantity(item.priceId, item.quantity - 1)}
                            className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                          >-</button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.priceId, item.quantity + 1)}
                            className={`w-6 h-6 rounded flex items-center justify-center ${atLimit ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'}`}
                            disabled={atLimit}
                          >+</button>
                          <button
                            onClick={() => removeFromCart(item.priceId)}
                            className="ml-auto text-red-500 text-sm"
                          >Remove</button>
                        </div>
                        {atLimit && (
                          <p className="text-xs text-amber-600 mt-1">Max quantity ({item.quantityLimit}) reached</p>
                        )}
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-4 border-t space-y-4">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
                <Button
                  className="w-full"
                  onClick={handleCheckout}
                  disabled={checkingOut}
                  data-testid="checkout-button"
                >
                  {checkingOut ? "Processing..." : "Proceed to Checkout →"}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  You'll enter your info on the next screen
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== CUSTOMER INTAKE FORM ==============
const CustomerIntakeForm = () => {
  const { businessSlug } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    message: "",
    custom_answers: {},
  });

  useEffect(() => {
    if (businessSlug) {
      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessSlug]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const loadConfig = async () => {
    try {
      const data = await api.getIntakeConfig(businessSlug);
      setConfig(data);
    } catch (error) {
      console.error("Failed to load intake config:", error);
      setConfig({
        tenant_name: "Request a Quote",
        intake_mode: "simple_form",
        field_config: {
          name_required: true,
          email_required: true,
          phone_enabled: true,
          phone_required: false,
          address_enabled: false,
          file_upload_enabled: false,
          message_enabled: true,
          photo_mode: "off",
          photo_instructions: ""
        },
        services: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

 const updateQuantity = (serviceId, qty) => {
  // Keep qty as a STRING while user types (allows backspace)
  setQuantities((prev) => ({ ...prev, [serviceId]: qty }));
};
const normalizeQty = useCallback((serviceId) => {
  const raw = quantities[serviceId];
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}, [quantities]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingPhoto(true);
    try {
      for (const file of files) {
        // Check file size client-side (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Photo "${file.name}" is too large. Maximum size is 10MB.`);
          continue;
        }
        const result = await api.uploadPhoto(businessSlug, file);
        setPhotos(prev => [...prev, { 
          id: result.photo_id, 
          url: result.photo_url, 
          filename: result.filename,
          preview: URL.createObjectURL(file)
        }]);
      }
    } catch (error) {
      console.error("Photo upload error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to upload photo: ${errorMsg}`);
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await api.deletePhoto(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      console.error("Failed to remove photo:", error);
    }
  };

  // File upload handler with compression for large files
  const handleFileUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files || []);
    if (uploadedFiles.length === 0) return;

    setUploadingFile(true);
    try {
      for (const file of uploadedFiles) {
        // Check file size (max 500MB per file)
        const maxSize = (config?.field_config?.file_upload_max_size_mb || 500) * 1024 * 1024;
        if (file.size > maxSize) {
          toast.error(`File "${file.name}" is too large. Maximum size is ${config?.field_config?.file_upload_max_size_mb || 500}MB.`);
          continue;
        }

        // Upload file
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const result = await api.uploadLeadFile(businessSlug, formDataUpload);
        
        setFiles(prev => [...prev, { 
          id: result.file_url,
          name: file.name,
          size: file.size,
          url: result.file_url 
        }]);
      }
      toast.success(`${uploadedFiles.length} file(s) uploaded`);
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Failed to upload file");
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check address requirement
    const fc = config?.field_config || {};
    if (fc.address_required && fc.address_enabled && !formData.customer_address?.trim()) {
      toast.error("Please enter your address.");
      return;
    }

    // Check photo requirement
    const photoMode = config?.field_config?.photo_mode || "off";
    if (photoMode === "required" && photos.length === 0) {
      toast.error("Please upload at least one photo before submitting.");
      return;
    }

    // Check file upload requirement
    const fileMode = config?.field_config?.file_upload_mode || "off";
    if (fileMode === "required" && files.length === 0) {
      toast.error("Please upload at least one file before submitting.");
      return;
    }

    // Check required custom questions
    const customQuestions = config?.field_config?.custom_questions || [];
    for (const q of customQuestions) {
      if (q.required) {
        const answer = formData.custom_answers[q.id];
        if (!answer || (Array.isArray(answer) && answer.length === 0)) {
          toast.error(`Please answer the required question: "${q.label}"`);
          return;
        }
      }
    }

    setSubmitting(true);

    try {
      const pricingSnapshot = selectedServices.map((serviceId) => {
        const service = config.services.find((s) => s.id === serviceId);
        const qty = quantities[serviceId] || 1;
        
        // Calculate actual price for modifiers
        let calculatedPrice = service?.price_value || 0;
        if (service?.is_modifier) {
          if (service.modifier_type === "percentage" && service.modifier_percent) {
            // Calculate percentage based on base services total
            const baseTotal = selectedServices.reduce((sum, sid) => {
              const s = config.services.find(sv => sv.id === sid);
              if (s && !s.is_modifier) {
                return sum + ((s.price_value || 0) * (quantities[sid] || 1));
              }
              return sum;
            }, 0);
            calculatedPrice = Math.round(baseTotal * (service.modifier_percent / 100) * 100) / 100;
          } else if (service.modifier_type === "fixed" && service.modifier_fixed) {
            calculatedPrice = service.modifier_fixed;
          }
        }
        
        return {
          service_id: serviceId,
          label: service?.label || "",
          pricing_type: service?.pricing_type || "none",
          price_value: calculatedPrice,
          unit_label: service?.unit_label || null,
          quantity: qty,
          is_modifier: service?.is_modifier || false,
          modifier_type: service?.modifier_type || null,
          modifier_percent: service?.modifier_percent || null,
          modifier_fixed: service?.modifier_fixed || null,
        };
      });

      const leadData = {
        ...formData,
        source_url: window.location.href,
        selected_service_ids: selectedServices,
        quantities,
        pricing_snapshot: pricingSnapshot,
        photo_urls: photos.map(p => p.url),
        file_urls: files.map(f => ({ url: f.url, name: f.name, size: f.size })),
      };

      await api.createLead(leadData, businessSlug);
      setSubmitted(true);
      toast.success("Thank you! Your request has been submitted.");
    } catch (error) {
      console.error("Error submitting lead:", error);
      if (error.response) {
        console.error("Server response:", error.response.status, error.response.data);
        toast.error(`Failed to submit: ${error.response.data?.detail || 'Server error'}`);
      } else if (error.request) {
        console.error("No response received:", error.request);
        toast.error("Failed to submit: No response from server. Please check your connection.");
      } else {
        toast.error(`Failed to submit: ${error.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center" data-testid="success-card">
          <CardHeader>
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <CardTitle className="text-2xl text-blue-800">Thank You!</CardTitle>
            <CardDescription className="text-base mt-2">
              Your request has been submitted to <strong>{config?.tenant_name || 'us'}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
              <p className="font-medium mb-1">Keep an eye on your inbox</p>
              <p className="text-blue-600">Your quote is on the way. Don't see it? Check your spam or promotions folder, and make sure email notifications are turned on.</p>
            </div>
            <Button onClick={() => { setSubmitted(false); setSelectedServices([]); setQuantities({}); }} variant="outline">
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show "Coming Soon" page when business subscription is inactive (trial/unpaid)
  if (config && config.subscription_active === false) {
    // Build logo URL for the Coming Soon page
    const logoUrl = config.logo_url 
      ? (config.logo_url.startsWith('/api/') 
          ? `${BACKEND_URL}${config.logo_url}` 
          : config.logo_url)
      : null;
    
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center bg-white/95 backdrop-blur" data-testid="subscription-inactive-card">
          <CardHeader className="pb-2">
            {logoUrl && (
              <div className="mx-auto mb-4">
                <img 
                  src={logoUrl} 
                  alt={config.tenant_name || 'Business'} 
                  className="h-20 w-20 object-contain rounded-lg mx-auto"
                />
              </div>
            )}
            <CardTitle className="text-2xl text-gray-900">
              {config.tenant_name || 'This Business'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
              <div className="text-4xl mb-3">🚀</div>
              <p className="text-lg font-semibold text-slate-900 mb-2">Coming Soon!</p>
              <p className="text-sm text-slate-900">
                We're putting the finishing touches on our online request system. 
                Check back soon to submit your request!
              </p>
            </div>
            
            <div className="text-sm text-gray-500">
              <p>In the meantime, feel free to contact us directly.</p>
            </div>
            
            <div className="pt-2">
              <p className="text-xs text-gray-400">
                Powered by <span className="font-semibold text-slate-900">ZipLeadPro</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Product Catalog Mode - render ProductCatalog component
  if (config && config.intake_mode === "product_catalog") {
    const logoUrl = config.logo_url 
      ? (config.logo_url.startsWith('/api/') 
          ? `${BACKEND_URL}${config.logo_url}` 
          : config.logo_url.startsWith('http') 
            ? config.logo_url 
            : `${STATIC_URL}${config.logo_url}`)
      : null;
    
    return (
      <ProductCatalog 
        slug={businessSlug} 
        businessName={config.tenant_name}
        logoUrl={logoUrl}
        logoSize={config.field_config?.logo_size}
        shopHeadline={config.shop_headline}
        shopHeadlineSize={config.shop_headline_size}
        shopHeadlineColor={config.shop_headline_color}
        shopFooter={config.shop_footer}
        shopFooterSize={config.shop_footer_size}
        shopFooterColor={config.shop_footer_color}
        backgroundColor={config.field_config?.form_background_color}
      />
    );
  }

  const fc = config.field_config;
  const hasServices = config.services && config.services.length > 0;
  const hasPricing = hasServices && !fc.hide_pricing && config.services.some((s) => s.pricing_type !== "none" && s.price_value);
  
  // Local formatPrice that respects hide_pricing
  const formatPriceLocal = (service) => {
    if (fc.hide_pricing) return null;
    return formatPrice(service);
  };
  
  // Form styling
  const formBgColor = fc.form_background_color;
  const formCardBgColor = fc.form_card_background_color;
  const formTextColor = fc.form_text_color;
  const formFontFamily = fc.form_font_family;
  const logoSize = fc.logo_size || 96;
  
  const bgStyle = formBgColor 
    ? { backgroundColor: formBgColor } 
    : {};
  const bgClass = formBgColor 
    ? "min-h-screen py-8 px-4" 
    : "min-h-screen bg-stone-50 py-8 px-4";
  
  const textStyle = formTextColor ? { color: formTextColor } : {};
  const fontStyle = formFontFamily ? { fontFamily: formFontFamily } : {};

  // Auto text color based on CARD background (where text appears)
  const isDarkBg = formCardBgColor && (
    formCardBgColor.toLowerCase().startsWith('#0') ||
    formCardBgColor.toLowerCase().startsWith('#1') ||
    formCardBgColor.toLowerCase().startsWith('#2') ||
    formCardBgColor.toLowerCase().startsWith('#3') ||
    formCardBgColor === '#000' ||
    formCardBgColor === '#000000'
  );
  const autoLabelColor = isDarkBg ? '#ffffff' : undefined;

  // Build logo URL - handle both old (/api/uploads/) and new (/api/tenants/{slug}/logo-image) formats
  const getLogoUrl = () => {
    if (!config.logo_url) return null;
    if (config.logo_url.startsWith('http://') || config.logo_url.startsWith('https://')) return config.logo_url;
    // For API URLs starting with /api, prepend BACKEND_URL
    if (config.logo_url.startsWith('/api/')) {
      return `${BACKEND_URL}${config.logo_url}`;
    }
    // For old format relative URLs (e.g., /uploads/...), use STATIC_URL
    return `${STATIC_URL}${config.logo_url}`;
  };

  const logoUrl = getLogoUrl();

  return (
    <div className={bgClass} style={{ ...bgStyle, ...fontStyle }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt={config.tenant_name} 
              className="mx-auto mb-4 object-contain" 
              style={{ height: `${logoSize}px` }}
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <h1 className="text-3xl font-bold" style={formTextColor ? { color: formTextColor } : { color: '#111827' }} data-testid="form-title">
            {config.tenant_name || "Request a Quote"}
          </h1>
          <p className="mt-2" style={formTextColor ? { color: formTextColor, opacity: 0.8 } : { color: '#4b5563' }}>{fc.tagline || "Request a quote today"}</p>
        </div>

        <Card data-testid="intake-form-card" style={{ ...fontStyle, backgroundColor: formCardBgColor || undefined }}>
          <CardHeader style={{ backgroundColor: formCardBgColor || undefined }}>
            <CardTitle style={{ color: autoLabelColor }}>Request a Quote</CardTitle>
            <CardDescription style={{ color: autoLabelColor, opacity: 0.7 }}>Fill out the form below and we will get back to you ASAP.</CardDescription>
          </CardHeader>
          <CardContent style={{ backgroundColor: formCardBgColor || undefined }}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {hasServices && config.intake_mode === "service_buttons" && (
                <div className="space-y-4">
                  <Label className="text-base font-semibold" style={{ color: autoLabelColor }}>Select Services</Label>
                  <div className="grid gap-3">
                    {config.services.filter(s => s.active).map((service) => {
                      const isSelected = selectedServices.includes(service.id);
                      return (
                        <div
                          key={service.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            isSelected
                              ? isDarkBg ? "border-white bg-white/10" : "border-blue-800 bg-blue-50"
                              : isDarkBg ? "border-gray-600 hover:border-gray-400 hover:bg-white/5" : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleService(service.id);
                          }}
                          data-testid={`service-${service.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div 
                                className={`h-4 w-4 mt-1 shrink-0 rounded-sm border shadow ${
                                  isSelected 
                                    ? "bg-slate-900 border-slate-900" 
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {isSelected && (
                                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <div className="font-medium" style={{ color: autoLabelColor }}>{service.label}</div>
                                {service.description && (
                                  <div className="text-sm mt-1" style={{ color: autoLabelColor, opacity: 0.7 }}>{service.description}</div>
                                )}
                              </div>
                            </div>
                            {formatPriceLocal(service) && (
                              <div className="text-sm font-medium text-slate-900">
                                {formatPriceLocal(service)}
                              </div>
                            )}
                          </div>
                          {isSelected && service.pricing_type === "per_unit" && (
                            <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <Label className="text-sm">Quantity:</Label>
                              <Input
                                type="number"
                                min="1"
                                value={quantities?.[service.id] ?? ""}
                                onChange={(e) => updateQuantity(service.id, e.target.value)}
                                className="w-20"
                                data-testid={`quantity-${service.id}`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Live Running Total */}
                  {hasPricing && selectedServices.length > 0 && (() => {
                    const services = config.services || [];

// 1) base total
const baseTotal = selectedServices.reduce((total, serviceId) => {
  const service = services.find(s => String(s.id) === String(serviceId));
  if (!service) return total;

  if (Boolean(service.is_modifier)) return total;
  if (!service.price_value || service.pricing_type === "none") return total;

  if (service.pricing_type === "per_unit") {
    const qty = quantities?.[serviceId] || 1;
    return total + (Number(service.price_value) * Number(qty));
  }

  return total + Number(service.price_value);
}, 0);

// 2) modifiers applied to baseTotal
const modifierTotal = selectedServices.reduce((total, serviceId) => {
  const service = services.find(s => String(s.id) === String(serviceId));
  if (!service) return total;

  if (!Boolean(service.is_modifier)) return total;

  if (service.modifier_type === "percentage" && service.modifier_percent != null) {
    return total + (baseTotal * (Number(service.modifier_percent) / 100));
  }

  if (service.modifier_type === "fixed" && service.modifier_fixed != null) {
    return total + Number(service.modifier_fixed);
  }

  return total;
}, 0);

const runningTotal = baseTotal + modifierTotal;
                    
                    return runningTotal > 0 ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-sm text-gray-600">Current Selection Value</span>
                            <p className="text-xs text-gray-500 mt-0.5">{selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} selected</p>
                          </div>
                          <div className="text-2xl font-bold text-slate-900">
                            ${runningTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  {hasPricing && (
                    <p className="text-sm text-gray-500 italic">
                      * Pricing shown is for reference only and is not final.
                    </p>
                  )}
                </div>
              )}

              {config.intake_mode === "simple_form" && hasServices && (() => {
                const activeServices = config.services.filter(s => s.active);
                const baseServices = activeServices.filter(s => !s.is_modifier);
                const modifierServices = activeServices.filter(s => s.is_modifier);
                const hasBaseServiceSelected = selectedServices.some(id => {
                  const s = activeServices.find(svc => String(svc.id) === String(id));
                  return s && !s.is_modifier;
                });
                
                return (
                <div className="space-y-3">
                  <Label>Service Type</Label>
                  {/* Multi-select dropdown */}
                  <div className="relative" ref={dropdownRef}>
                    <div 
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="border rounded-md p-2.5 bg-white flex justify-between items-center cursor-pointer hover:border-blue-300 transition-colors"
                      data-testid="service-select"
                    >
                      <span className={selectedServices.filter(id => {
                        const s = activeServices.find(svc => String(svc.id) === String(id));
                        return s && !s.is_modifier;
                      }).length > 0 ? 'text-gray-900 text-sm' : 'text-gray-500 text-sm'}>
                        {(() => {
                          const baseCount = selectedServices.filter(id => {
                            const s = activeServices.find(svc => String(svc.id) === String(id));
                            return s && !s.is_modifier;
                          }).length;
                          return baseCount > 0 
                            ? `${baseCount} service${baseCount > 1 ? 's' : ''} selected`
                            : 'Select services...';
                        })()}
                      </span>
                      <svg className={`h-4 w-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {dropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {baseServices.map((service) => {
                          const isSelected = selectedServices.includes(service.id);
                          return (
                            <div 
                              key={service.id}
                              onClick={() => toggleService(service.id)}
                              className={`p-2.5 text-sm cursor-pointer flex justify-between items-center border-b last:border-b-0 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                              data-testid={`service-option-${service.id}`}
                            >
                              <span className="flex items-center gap-2">
                                <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-slate-900 border-slate-900' : 'border-gray-300'}`}>
                                  {isSelected && (
                                    <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                {service.label}
                              </span>
                              {formatPriceLocal(service) && (
                                <span className="text-slate-900 font-medium">{formatPriceLocal(service)}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Modifiers - shown after selecting a base service */}
                  {modifierServices.length > 0 && hasBaseServiceSelected && (
                    <div className="pt-2 border-t border-dashed">
                      <Label className="text-sm text-gray-600 mb-2">Add-ons</Label>
                      <div className="space-y-1 mt-1">
                        {modifierServices.map((service) => {
                          const isSelected = selectedServices.includes(service.id);
                          return (
                            <div 
                              key={service.id}
                              onClick={() => toggleService(service.id)}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                              data-testid={`modifier-option-${service.id}`}
                            >
                              <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-slate-900 border-slate-900' : 'border-gray-300'}`}>
                                {isSelected && (
                                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className="flex-1">{service.label}</span>
                              {formatPriceLocal(service) && (
                                <span className="text-slate-900 font-medium">{formatPriceLocal(service)}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Per-unit quantity inputs */}
                  {selectedServices.map(serviceId => {
                    const service = activeServices.find(s => String(s.id) === String(serviceId));
                    if (!service || service.pricing_type !== "per_unit") return null;
                    return (
                      <div key={serviceId} className="flex items-center gap-2 pl-1">
                        <Label className="text-sm">{service.label} — Quantity:</Label>
                        <Input
                          type="number"
                          min="1"
                          value={quantities?.[serviceId] ?? ""}
                          onChange={(e) => updateQuantity(serviceId, e.target.value)}
                          className="w-20"
                          data-testid={`quantity-${serviceId}`}
                        />
                      </div>
                    );
                  })}

                  {/* Running Total */}
                  {hasPricing && selectedServices.length > 0 && (() => {
                    const services = config.services || [];
                    const baseTotal = selectedServices.reduce((total, serviceId) => {
                      const service = services.find(s => String(s.id) === String(serviceId));
                      if (!service) return total;
                      if (Boolean(service.is_modifier)) return total;
                      if (!service.price_value || service.pricing_type === "none") return total;
                      if (service.pricing_type === "per_unit") {
                        const qty = quantities?.[serviceId] || 1;
                        return total + (Number(service.price_value) * Number(qty));
                      }
                      return total + Number(service.price_value);
                    }, 0);
                    const modifierTotal = selectedServices.reduce((total, serviceId) => {
                      const service = services.find(s => String(s.id) === String(serviceId));
                      if (!service) return total;
                      if (!Boolean(service.is_modifier)) return total;
                      if (service.modifier_type === "percentage" && service.modifier_percent != null) {
                        return total + (baseTotal * (Number(service.modifier_percent) / 100));
                      }
                      if (service.modifier_type === "fixed" && service.modifier_fixed != null) {
                        return total + Number(service.modifier_fixed);
                      }
                      return total;
                    }, 0);
                    const runningTotal = baseTotal + modifierTotal;
                    return runningTotal > 0 ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-sm text-gray-600">Current Selection Value</span>
                            <p className="text-xs text-gray-500 mt-0.5">{selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} selected</p>
                          </div>
                          <div className="text-2xl font-bold text-slate-900">
                            ${runningTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {hasPricing && selectedServices.length > 0 && (
                    <p className="text-sm text-gray-500 italic">
                      * Pricing shown is for reference only and is not final.
                    </p>
                  )}
                </div>
                );
              })()}

              <Separator />

              <div className="space-y-4">
                <Label className="text-base font-semibold" style={{ color: autoLabelColor }}>Contact Information</Label>
                
                <div className="space-y-2">
                  <Label htmlFor="customer_name" style={{ color: autoLabelColor }}>
                    Name {fc.name_required && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="Your name"
                    required={fc.name_required}
                    data-testid="input-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer_email" style={{ color: autoLabelColor }}>
                    Email {fc.email_required && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    placeholder="your@email.com"
                    required={fc.email_required}
                    data-testid="input-email"
                  />
                </div>

                {fc.phone_enabled && (
                  <div className="space-y-2">
                    <Label htmlFor="customer_phone" style={{ color: autoLabelColor }}>
                      Phone {fc.phone_required && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="customer_phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      placeholder="Phone number"
                      required={fc.phone_required}
                      data-testid="input-phone"
                    />
                  </div>
                )}

                {fc.address_enabled && (
                  <div className="space-y-2">
                    <Label htmlFor="customer_address" style={{ color: autoLabelColor }}>Address {fc.address_required && <span className="text-red-500">*</span>}</Label>
                    <Textarea
                      id="customer_address"
                      value={formData.customer_address}
                      onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                      placeholder="Your address"
                      rows={2}
                      data-testid="input-address"
                      required={fc.address_required}
                    />
                  </div>
                )}

                {/* Custom Questions */}
                {fc.custom_questions && fc.custom_questions.length > 0 && (
                  <div className="space-y-4 pt-2">
                    {fc.custom_questions.map((q) => (
                      <div key={q.id} className="space-y-2">
                        <Label style={{ color: autoLabelColor }}>
                          {q.label} {q.required && <span className="text-red-500">*</span>}
                        </Label>
                        {q.type === 'radio' ? (
                          <div className="space-y-2">
                            {(q.options || []).map((option, optIdx) => (
                              <label key={optIdx} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={q.id}
                                  value={option}
                                  checked={formData.custom_answers[q.id] === option}
                                  onChange={() => setFormData(prev => ({
                                    ...prev,
                                    custom_answers: { ...prev.custom_answers, [q.id]: option }
                                  }))}
                                  className="w-4 h-4"
                                />
                                <span style={{ color: autoLabelColor }}>{option}</span>
                              </label>
                            ))}
                          </div>
                        ) : q.type === 'text' ? (
                          <Input
                            type="text"
                            value={formData.custom_answers[q.id] || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              custom_answers: { ...prev.custom_answers, [q.id]: e.target.value }
                            }))}
                            placeholder="Type your answer..."
                            className="w-full"
                          />
                        ) : (
                          <div className="space-y-2">
                            {(q.options || []).map((option, optIdx) => (
                              <label key={optIdx} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  value={option}
                                  checked={(formData.custom_answers[q.id] || []).includes(option)}
                                  onChange={(e) => {
                                    const current = formData.custom_answers[q.id] || [];
                                    const updated = e.target.checked
                                      ? [...current, option]
                                      : current.filter(o => o !== option);
                                    setFormData(prev => ({
                                      ...prev,
                                      custom_answers: { ...prev.custom_answers, [q.id]: updated }
                                    }));
                                  }}
                                  className="w-4 h-4"
                                />
                                <span style={{ color: autoLabelColor }}>{option}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {fc.message_enabled && (
                  <div className="space-y-2">
                    <Label htmlFor="message" style={{ color: autoLabelColor }}>Message / Details</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your needs..."
                      rows={4}
                      data-testid="input-message"
                    />
                  </div>
                )}

                {/* Photo Upload Section */}
                {fc.photo_mode && fc.photo_mode !== "off" && (
                  <div className="space-y-3 pt-2">
                    <div>
                      <Label className="text-base font-semibold">
                        Photos {fc.photo_mode === "required" && <span className="text-red-500">*</span>}
                      </Label>
                      {fc.photo_instructions && (
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{fc.photo_instructions}</p>
                      )}
                    </div>
                    
                    {/* Photo Previews */}
                    {photos.length > 0 && (
                      <div className="flex flex-wrap gap-3">
                        {photos.map((photo) => (
                          <div key={photo.id} className="relative group">
                            <img 
                              src={photo.preview || `${BACKEND_URL}${photo.url}`} 
                              alt={photo.filename}
                              className="w-24 h-24 object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(photo.id)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    <div>
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                          {uploadingPhoto ? (
                            <span className="text-gray-500">Uploading...</span>
                          ) : (
                            <>
                              <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-sm text-gray-600">Click to upload photos</span>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoUpload}
                          className="hidden"
                          disabled={uploadingPhoto}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">Max 5MB per photo. JPG, PNG supported.</p>
                    </div>
                  </div>
                )}

                {/* File Upload Section */}
                {(fc.file_upload_mode === "optional" || fc.file_upload_mode === "required") && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium">
                        📁 Project Files {fc.file_upload_mode === "required" && <span className="text-red-500">*</span>}
                      </Label>
                      {fc.file_upload_instructions && (
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{fc.file_upload_instructions}</p>
                      )}
                    </div>
                    
                    {/* File List */}
                    {files.length > 0 && (
                      <div className="space-y-2">
                        {files.map((file) => (
                          <div key={file.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border">
                            <div className="flex items-center gap-3 min-w-0">
                              <svg className="w-8 h-8 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(file.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    <div>
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                          {uploadingFile ? (
                            <span className="text-gray-500">Uploading...</span>
                          ) : (
                            <>
                              <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <span className="text-sm text-gray-600">Click to upload project files</span>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                          disabled={uploadingFile}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">Max {fc.file_upload_max_size_mb || 500}MB per file. All file types supported.</p>
                    </div>
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className={`w-full ${isDarkBg ? 'bg-white text-black hover:bg-gray-200' : ''}`}
                disabled={submitting} 
                data-testid="submit-btn"
              >
                {submitting ? "Submitting..." : (fc.submit_button_text || "Submit Request")}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Powered by <span className="font-semibold">ZipLeadPro</span>
        </p>
      </div>
    </div>
  );
};

// ============== ADMIN DASHBOARD ==============
const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const businessesData = await api.getBusinesses();
      setBusinesses(businessesData);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        logout();
        navigate("/login");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate" data-testid="admin-title">ZipLeadPro Admin</h1>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge variant="outline" className="text-slate-900 hidden sm:inline-flex">Admin</Badge>
              <Button variant="outline" size="sm" onClick={handleLogout}>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="businesses">
          <TabsList className="mb-6 flex flex-wrap h-auto gap-1 bg-transparent p-0">
            <TabsTrigger value="businesses" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white rounded-full px-4 py-2 text-sm" data-testid="tab-businesses">Businesses</TabsTrigger>
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white rounded-full px-4 py-2 text-sm" data-testid="tab-subscriptions">Billing</TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white rounded-full px-4 py-2 text-sm" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white rounded-full px-4 py-2 text-sm" data-testid="tab-emails">Emails</TabsTrigger>
          </TabsList>

          <TabsContent value="businesses">
            <BusinessManager businesses={businesses} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="subscriptions">
            <SubscriptionManager businesses={businesses} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="payments">
            <AdminStripeConnectDashboard />
          </TabsContent>

          <TabsContent value="emails">
            <EmailLogsView />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ============== EDIT LEAD FORM ==============
const EditLeadForm = ({ lead, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    customer_name: lead.customer_name || "",
    customer_email: lead.customer_email || "",
    customer_phone: lead.customer_phone || "",
    customer_address: lead.customer_address || "",
    message: lead.message || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="edit-name" className="text-xs">Name</Label>
          <Input
            id="edit-name"
            value={formData.customer_name}
            onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="edit-email" className="text-xs">Email</Label>
          <Input
            id="edit-email"
            type="email"
            value={formData.customer_email}
            onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
          />
        </div>
      </div>
      <div>
        <Label htmlFor="edit-phone" className="text-xs">Phone</Label>
        <Input
          id="edit-phone"
          value={formData.customer_phone}
          onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="edit-address" className="text-xs">Address</Label>
        <Input
          id="edit-address"
          value={formData.customer_address}
          onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="edit-message" className="text-xs">Message</Label>
        <textarea
          id="edit-message"
          className="w-full p-2 border rounded text-sm"
          rows={3}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
        />
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={saving} size="sm">
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// ============== BUSINESS MANAGER (Single-focus UI: List OR Detail, never both) ==============
const BusinessManager = ({ businesses, onRefresh }) => {
  const [uiState, setUiState] = useState("list"); // "list" | "detail" | "create" | "edit"
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const handleSelectBusiness = (business) => {
    setSelectedBusiness(business);
    setUiState("detail");
  };

  const handleBack = () => {
    setSelectedBusiness(null);
    setUiState("list");
  };

  const handleEdit = (business) => {
    setSelectedBusiness(business);
    setUiState("edit");
  };

  const handleCreate = () => {
    setSelectedBusiness(null);
    setUiState("create");
  };

  const handleFormSuccess = () => {
    setSelectedBusiness(null);
    setUiState("list");
    onRefresh();
  };

  // CREATE STATE
  if (uiState === "create") {
    return (
      <div className="space-y-4">
        <button 
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-2"
          data-testid="back-to-businesses"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back to Businesses</span>
        </button>
        <BusinessForm onClose={handleBack} onSuccess={handleFormSuccess} />
      </div>
    );
  }

  // EDIT STATE
  if (uiState === "edit" && selectedBusiness) {
    return (
      <div className="space-y-4">
        <button 
          onClick={() => { setUiState("detail"); }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-2"
          data-testid="back-to-detail"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back to {selectedBusiness.name || "Business"}</span>
        </button>
        <BusinessForm 
          business={selectedBusiness} 
          onClose={() => setUiState("detail")} 
          onSuccess={() => { onRefresh(); setUiState("detail"); }} 
        />
      </div>
    );
  }

  // DETAIL STATE (full-screen business management)
  if (uiState === "detail" && selectedBusiness) {
    return (
      <BusinessDetails
        business={selectedBusiness}
        onBack={handleBack}
        onEdit={() => handleEdit(selectedBusiness)}
        onRefresh={onRefresh}
        onDeleted={handleBack}
      />
    );
  }

  // LIST STATE (default)
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl font-semibold">Businesses</h2>
        <Button onClick={handleCreate} data-testid="add-business-btn">+ Add Business</Button>
      </div>

      <div className="space-y-3">
        {businesses.map((business) => (
          <div 
            key={business.tenant_id} 
            onClick={() => handleSelectBusiness(business)}
            className="bg-white border rounded-lg p-4 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer active:bg-gray-50"
            data-testid={`business-row-${business.slug}`}
          >
            <div className="flex items-center gap-3">
              {business.logo_url ? (
                <img src={`${STATIC_URL}${business.logo_url}`} alt={business.name} className="w-10 h-10 object-contain rounded flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-400 text-lg font-semibold">{(business.name || "?")[0].toUpperCase()}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base truncate">{business.name || "Unnamed Business"}</h3>
                <p className="text-sm text-gray-500 truncate">/{business.slug}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge className={`text-xs ${business.status === "active" ? "bg-blue-100 text-slate-900" : "bg-gray-100 text-gray-800"}`}>
                  {business.status}
                </Badge>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        ))}
        {businesses.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p>No businesses yet</p>
            <Button onClick={handleCreate} variant="outline" className="mt-4">Create your first business</Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== ADMIN STRIPE CONNECT DASHBOARD ==============
const AdminStripeConnectDashboard = () => {
  const [data, setData] = useState(null);
  const [allAccounts, setAllAccounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showAccountManager, setShowAccountManager] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(null);

  const fetchData = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const result = await api.getAdminStripeConnectAccounts();
      setData(result);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch Stripe Connect accounts:", error);
      if (showLoading) toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllAccounts = async () => {
    try {
      const result = await api.listAllPlatformStripeAccounts();
      setAllAccounts(result);
    } catch (error) {
      toast.error("Failed to load platform accounts");
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm(`Are you sure you want to DELETE Stripe account ${accountId}? This cannot be undone!`)) {
      return;
    }
    
    setDeletingAccount(accountId);
    try {
      await api.deleteStripeAccount(accountId);
      toast.success("Stripe account deleted successfully");
      fetchAllAccounts(); // Refresh list
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete account"));
    } finally {
      setDeletingAccount(null);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading && !data) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            <span className="ml-3 text-gray-600">Loading payment accounts...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Stripe Connect Overview
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-600 animate-pulse" title="Live updating"></span>
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">Payment accounts for all businesses</p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setShowAccountManager(!showAccountManager);
                  if (!allAccounts) fetchAllAccounts();
                }}
              >
                {showAccountManager ? "Hide" : "Manage"} Accounts
              </Button>
              <div className="text-xs text-gray-500">
                {lastUpdated && <span>Updated {lastUpdated.toLocaleTimeString()}</span>}
              </div>
              <button 
                onClick={() => fetchData(true)} 
                className="text-xs text-slate-900 hover:text-slate-900 underline"
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-xs text-slate-900 uppercase tracking-wide">Total Businesses</p>
              <p className="text-2xl font-bold text-slate-900">{data?.total_businesses || 0}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-xs text-blue-700 uppercase tracking-wide">Connected</p>
              <p className="text-2xl font-bold text-slate-900">{data?.total_connected || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Not Connected</p>
              <p className="text-2xl font-bold text-gray-900">{(data?.total_businesses || 0) - (data?.total_connected || 0)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-xs text-blue-700 uppercase tracking-wide">Platform Volume</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(data?.platform_total_volume)}</p>
            </div>
          </div>

          {/* Account Manager Section */}
          {showAccountManager && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="font-bold text-red-900 mb-2">Stripe Account Manager</h3>
              <p className="text-sm text-red-700 mb-4">
                View all Stripe accounts on your platform. Orphaned accounts (not linked to any business) can be safely deleted.
              </p>
              
              {allAccounts ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-4 mb-2 text-sm">
                    <span className="text-gray-600">Total: {allAccounts.total}</span>
                    <span className="text-red-600">Orphaned: {allAccounts.orphaned_count}</span>
                    <button onClick={fetchAllAccounts} className="text-slate-900 hover:underline">Refresh</button>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto border rounded bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-2">Account ID</th>
                          <th className="text-left py-2 px-2">Name</th>
                          <th className="text-left py-2 px-2">Linked To</th>
                          <th className="text-center py-2 px-2">Status</th>
                          <th className="text-right py-2 px-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allAccounts.accounts.map((acc) => (
                          <tr key={acc.account_id} className={`border-b ${acc.is_orphaned ? 'bg-red-50' : ''}`}>
                            <td className="py-2 px-2 font-mono text-gray-600">{acc.account_id.slice(-8)}</td>
                            <td className="py-2 px-2">{acc.business_name || acc.email || '-'}</td>
                            <td className="py-2 px-2">
                              {acc.linked_to_tenant ? (
                                <span className="text-blue-800">{acc.linked_to_tenant}</span>
                              ) : (
                                <span className="text-red-600 font-medium">Orphaned</span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {acc.charges_enabled ? '✓' : '✗'}
                            </td>
                            <td className="py-2 px-2 text-right">
                              {acc.is_orphaned && (
                                <button
                                  onClick={() => handleDeleteAccount(acc.account_id)}
                                  disabled={deletingAccount === acc.account_id}
                                  className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                                >
                                  {deletingAccount === acc.account_id ? 'Deleting...' : 'Delete'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Loading accounts...</p>
                </div>
              )}
            </div>
          )}

          {/* Accounts Table */}
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Business</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">Available</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">Pending</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">Total Volume</th>
                </tr>
              </thead>
              <tbody>
                {data?.accounts?.map((account) => (
                  <tr key={account.tenant_id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{account.name}</div>
                      <div className="text-xs text-gray-500">/{account.slug}</div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{account.email || '-'}</td>
                    <td className="py-3 px-4 text-center">
                      {account.connected ? (
                        <span className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          Connected
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                          Not Connected
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-blue-700">
                      {account.connected ? formatCurrency(account.balance?.available) : '-'}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-amber-600">
                      {account.connected ? formatCurrency(account.balance?.pending) : '-'}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-gray-900">
                      {account.connected ? formatCurrency(account.total_volume) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(!data?.accounts || data.accounts.length === 0) && (
            <div className="text-center py-8 text-gray-500">
              No businesses found
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== SUBSCRIPTION MANAGER ==============
const SubscriptionManager = ({ businesses, onRefresh }) => {
  const [updatingId, setUpdatingId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebugDialog, setShowDebugDialog] = useState(null);
  const [quickFixLoading, setQuickFixLoading] = useState(null);

  // Quick debug check for a business
  const handleDebugCheck = async (business) => {
    try {
      const result = await api.debugGetTenantStatus(business.slug);
      setDebugInfo(result);
      setShowDebugDialog(business);
    } catch (error) {
      toast.error("Failed to get debug info: " + (error.response?.data?.detail || error.message));
    }
  };

  // Quick fix to disable a business form link immediately
  const handleQuickDisable = async (slug) => {
    setQuickFixLoading(slug);
    try {
      const result = await api.debugFixSubscription(slug, "canceled");
      toast.success(`Form link DISABLED for ${slug}. ${result.message}`);
      onRefresh();
      setShowDebugDialog(null);
      setDebugInfo(null);
    } catch (error) {
      toast.error("Failed to disable: " + (error.response?.data?.detail || error.message));
    } finally {
      setQuickFixLoading(null);
    }
  };

  // Quick fix to enable a business form link immediately
  const handleQuickEnable = async (slug) => {
    setQuickFixLoading(slug);
    try {
      const result = await api.debugFixSubscription(slug, "active");
      toast.success(`Form link ENABLED for ${slug}. ${result.message}`);
      onRefresh();
      setShowDebugDialog(null);
      setDebugInfo(null);
    } catch (error) {
      toast.error("Failed to enable: " + (error.response?.data?.detail || error.message));
    } finally {
      setQuickFixLoading(null);
    }
  };

  const subscriptionStatuses = [
    { value: 'active', label: 'Active', color: 'bg-blue-100 text-slate-900', description: 'Full access to all features' },
    { value: 'trialing', label: 'Trial', color: 'bg-blue-100 text-blue-800', description: 'Free trial period' },
    { value: 'past_due', label: 'Past Due', color: 'bg-amber-100 text-amber-800', description: 'Payment failed, grace period' },
    { value: 'paused', label: 'Paused', color: 'bg-amber-100 text-amber-800', description: 'Temporarily paused - no charges' },
    { value: 'canceled', label: 'Canceled', color: 'bg-red-100 text-red-800', description: 'Subscription ended' },
    { value: 'unpaid', label: 'Unpaid', color: 'bg-gray-100 text-gray-800', description: 'No active subscription' },
  ];

  // Actions available based on current status
  const getAvailableActions = (status) => {
    const actions = [];
    if (status === 'active' || status === 'trialing') {
      actions.push({ value: 'pause', label: 'Pause', icon: '⏸️', color: 'text-amber-600 hover:bg-amber-50' });
      actions.push({ value: 'cancel', label: 'Cancel', icon: '❌', color: 'text-red-600 hover:bg-red-50' });
    } else if (status === 'paused') {
      actions.push({ value: 'resume', label: 'Resume', icon: '▶️', color: 'text-blue-700 hover:bg-blue-50' });
      actions.push({ value: 'cancel', label: 'Cancel', icon: '❌', color: 'text-red-600 hover:bg-red-50' });
    } else if (status === 'canceled' || status === 'unpaid' || status === 'past_due' || !status) {
      actions.push({ value: 'activate', label: 'Activate', icon: '✅', color: 'text-blue-700 hover:bg-blue-50' });
    }
    return actions;
  };

  const getStatusBadge = (status) => {
    const statusInfo = subscriptionStatuses.find(s => s.value === status) || { color: 'bg-gray-100 text-gray-800', label: status || 'Unknown' };
    return <Badge className={statusInfo.color}>{statusInfo.label}</Badge>;
  };

  const handleAction = async (business, action) => {
    setUpdatingId(business.tenant_id);
    try {
      const result = await api.adminManageSubscription(business.slug, action, actionReason || null);
      
      if (result.stripe_updated) {
        toast.success(`Subscription ${action}d via Stripe for ${business.name || business.slug}`);
      } else if (result.stripe_error) {
        toast.success(`Subscription ${action}d locally. Note: ${result.stripe_error}`);
      } else {
        toast.success(`Subscription ${action}d for ${business.name || business.slug}`);
      }
      
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to ${action} subscription`));
    } finally {
      setUpdatingId(null);
      setShowConfirmDialog(null);
      setActionReason("");
    }
  };

  const activeCount = businesses.filter(b => b.subscription_status === 'active').length;
  const trialCount = businesses.filter(b => b.subscription_status === 'trialing').length;
  const pausedCount = businesses.filter(b => b.subscription_status === 'paused').length;
  const canceledCount = businesses.filter(b => b.subscription_status === 'canceled').length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Billing Overview</h2>
          <p className="text-sm text-gray-500">Manage plans and payments for all businesses</p>
        </div>
        <Button variant="outline" onClick={onRefresh} data-testid="refresh-subscriptions-btn">Refresh</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold">{businesses.length}</div>
            <p className="text-xs text-gray-500">Total Businesses</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-blue-700">{activeCount}</div>
            <p className="text-xs text-gray-500">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-blue-600">{trialCount}</div>
            <p className="text-xs text-gray-500">Trial</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-amber-600">{pausedCount}</div>
            <p className="text-xs text-gray-500">Paused</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-red-600">{canceledCount}</div>
            <p className="text-xs text-gray-500">Canceled</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">🔗 Stripe Integration:</span> Actions sync directly with Stripe. Pause/Resume controls payment collection. 
            Cancel ends the subscription. Changes persist through webhooks.
          </p>
        </CardContent>
      </Card>

      {/* All Plans */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Business Plans</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          {/* Mobile: Card layout */}
          <div className="sm:hidden divide-y">
            {businesses.map((business) => {
              const actions = getAvailableActions(business.subscription_status);
              return (
                <div key={business.tenant_id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{business.name || 'Unnamed'}</div>
                      <div className="text-xs text-gray-500">{business.slug}</div>
                    </div>
                    {getStatusBadge(business.subscription_status)}
                  </div>
                  <div className="text-sm text-gray-600 truncate">{business.notify_email || '—'}</div>
                  <div className="text-xs">
                    {business.stripe_subscription_id ? (
                      <span className="text-blue-700">✓ Stripe Sub Active</span>
                    ) : business.stripe_customer_id ? (
                      <span className="text-amber-600">Customer Only</span>
                    ) : (
                      <span className="text-gray-400">Not connected</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-xs border-amber-300 text-amber-600"
                      onClick={() => handleDebugCheck(business)}
                    >
                      🔧 Debug
                    </Button>
                    {actions.map((action) => (
                      <Button
                        key={action.value}
                        variant="outline"
                        size="sm"
                        className={`h-8 px-3 text-xs ${action.color}`}
                        onClick={() => setShowConfirmDialog({ business, action: action.value, actionLabel: action.label })}
                        disabled={updatingId === business.tenant_id}
                      >
                        {action.icon} {action.label}
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Table layout */}
          <table className="hidden sm:table w-full">
            <thead>
              <tr className="border-b text-left">
                <th className="pb-3 font-medium text-gray-600">Business</th>
                <th className="pb-3 font-medium text-gray-600">Email</th>
                <th className="pb-3 font-medium text-gray-600">Status</th>
                <th className="pb-3 font-medium text-gray-600">Stripe</th>
                <th className="pb-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {businesses.map((business) => {
                const actions = getAvailableActions(business.subscription_status);
                return (
                  <tr key={business.tenant_id} className="border-b hover:bg-gray-50">
                    <td className="py-3">
                      <div>
                        <div className="font-medium">{business.name || 'Unnamed'}</div>
                        <div className="text-xs text-gray-500">{business.slug}</div>
                      </div>
                    </td>
                    <td className="py-3 text-sm">{business.notify_email || '-'}</td>
                    <td className="py-3">{getStatusBadge(business.subscription_status)}</td>
                    <td className="py-3 text-sm">
                      {business.stripe_subscription_id ? (
                        <span className="text-blue-700 text-xs" title={business.stripe_subscription_id}>✓ Sub Active</span>
                      ) : business.stripe_customer_id ? (
                        <span className="text-amber-600 text-xs">Customer Only</span>
                      ) : (
                        <span className="text-gray-400 text-xs">Not connected</span>
                      )}
                    </td>
                    <td className="py-3">
                      <div className="flex gap-1 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs border-amber-300 text-amber-600 hover:bg-amber-50"
                          onClick={() => handleDebugCheck(business)}
                          data-testid={`debug-${business.slug}-btn`}
                        >
                          🔧 Debug
                        </Button>
                        {actions.map((action) => (
                          <Button
                            key={action.value}
                            variant="ghost"
                            size="sm"
                            className={`h-7 px-2 text-xs ${action.color}`}
                            onClick={() => setShowConfirmDialog({ business, action: action.value, actionLabel: action.label })}
                            disabled={updatingId === business.tenant_id}
                            data-testid={`${action.value}-${business.slug}-btn`}
                          >
                            {action.icon} {action.label}
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <Dialog open={true} onOpenChange={() => { setShowConfirmDialog(null); setActionReason(""); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm: {showConfirmDialog.actionLabel} Subscription</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p>
                {showConfirmDialog.action === 'pause' && "This will pause payment collection. The customer keeps access but won't be charged."}
                {showConfirmDialog.action === 'resume' && "This will resume payment collection. Next invoice will be generated normally."}
                {showConfirmDialog.action === 'cancel' && "This will permanently cancel the subscription. The customer will lose access."}
                {showConfirmDialog.action === 'activate' && "This will grant active subscription status to this business."}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-gray-500">Business:</span>
                <strong>{showConfirmDialog.business.name || showConfirmDialog.business.slug}</strong>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-500">Current Status:</span>
                {getStatusBadge(showConfirmDialog.business.subscription_status)}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reason" className="text-sm">Reason (optional, for records)</Label>
                <Input 
                  id="reason"
                  placeholder="e.g., Customer requested temporary hold"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                />
              </div>
              
              {showConfirmDialog.action === 'cancel' && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ Warning: This action cannot be easily undone. The customer will need to re-subscribe.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowConfirmDialog(null); setActionReason(""); }}>Cancel</Button>
              <Button 
                onClick={() => handleAction(showConfirmDialog.business, showConfirmDialog.action)}
                disabled={updatingId === showConfirmDialog.business.tenant_id}
                className={showConfirmDialog.action === 'cancel' ? 'bg-red-600 hover:bg-red-700' : ''}
                data-testid="confirm-action-btn"
              >
                {updatingId ? 'Processing...' : `${showConfirmDialog.actionLabel} Subscription`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Debug Dialog - Quick Fix for Form Link */}
      {showDebugDialog && debugInfo && (
        <Dialog open={true} onOpenChange={() => { setShowDebugDialog(null); setDebugInfo(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>🔧 Debug: {showDebugDialog.name || showDebugDialog.slug}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Status Summary */}
              <div className={`p-4 rounded-lg ${debugInfo.subscription_active_calculated ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{debugInfo.subscription_active_calculated ? '✅' : '🚫'}</span>
                  <span className="font-bold text-lg">
                    Form Link: {debugInfo.subscription_active_calculated ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{debugInfo.status_reason}</p>
              </div>

              {/* Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Slug:</span>
                  <code className="bg-gray-200 px-2 py-0.5 rounded">{debugInfo.slug}</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status in DB:</span>
                  <Badge className={debugInfo.subscription_status === 'active' ? 'bg-blue-100 text-slate-900' : debugInfo.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                    {debugInfo.subscription_status || 'null'}
                  </Badge>
                </div>
                {debugInfo.trial_ends_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Trial Ends:</span>
                    <span>{new Date(debugInfo.trial_ends_at).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Stripe Customer:</span>
                  <span className={debugInfo.stripe_customer_id ? 'text-blue-700' : 'text-gray-400'}>
                    {debugInfo.stripe_customer_id ? '✓ Connected' : 'Not connected'}
                  </span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Quick Fix Actions:</p>
                <div className="flex gap-3">
                  <Button 
                    variant="destructive"
                    className="flex-1"
                    onClick={() => handleQuickDisable(debugInfo.slug)}
                    disabled={quickFixLoading === debugInfo.slug || !debugInfo.subscription_active_calculated}
                    data-testid="quick-disable-btn"
                  >
                    {quickFixLoading === debugInfo.slug ? 'Processing...' : '🚫 DISABLE Form Link'}
                  </Button>
                  <Button 
                    variant="default"
                    className="flex-1 bg-blue-700 hover:bg-blue-800"
                    onClick={() => handleQuickEnable(debugInfo.slug)}
                    disabled={quickFixLoading === debugInfo.slug || debugInfo.subscription_active_calculated}
                    data-testid="quick-enable-btn"
                  >
                    {quickFixLoading === debugInfo.slug ? 'Processing...' : '✅ ENABLE Form Link'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  These buttons immediately update the database. No Stripe sync.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// ============== BUSINESS FORM ==============
const BusinessForm = ({ business, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: business?.name || "",
    slug: business?.slug || "",
    notify_email: business?.notify_email || "",
    reply_to_email: business?.reply_to_email || "",
    status: business?.status || "draft",
    intake_mode: business?.intake_mode || "simple_form",
    field_config: business?.field_config || {
      name_required: true,
      email_required: true,
      phone_enabled: true,
      phone_required: false,
      address_enabled: false,
      file_upload_enabled: false,
      message_enabled: true,
    },
  });
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/[\s_]+/g, "-").slice(0, 50);

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData((prev) => ({
      ...prev,
      name,
      slug: !business ? generateSlug(name) : prev.slug,
    }));
  };

  const updateFieldConfig = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      field_config: { ...prev.field_config, [key]: value },
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let savedBusiness;
      if (business) {
        savedBusiness = await api.updateBusiness(business.slug, formData);
      } else {
        savedBusiness = await api.createBusiness(formData);
      }
      if (logoFile) {
        await api.uploadLogo(savedBusiness.slug, logoFile);
      }
      toast.success(business ? "Business updated" : "Business created");
      onSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save business"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{business ? "Edit Business" : "Add New Business"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business Name</Label>
              <Input value={formData.name} onChange={handleNameChange} placeholder="Acme Services" />
            </div>
            <div className="space-y-2">
              <Label>Your Page Link (short name)</Label>
              <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="acme-services" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Notification Email</Label>
              <Input type="email" value={formData.notify_email} onChange={(e) => setFormData({ ...formData, notify_email: e.target.value })} placeholder="leads@business.com" />
            </div>
            <div className="space-y-2">
              <Label>Reply-To Email</Label>
              <Input type="email" value={formData.reply_to_email} onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })} placeholder="contact@business.com" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Logo</Label>
              <Input type="file" accept="image/*" onChange={handleLogoChange} />
              <Button
  type="button"
  variant="outline"
  onClick={async () => {
    await api.deleteLogo(business.slug);
    toast.success("Logo removed");
    onSuccess?.();
  }}
>
  Remove logo
</Button>

            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <Label className="text-base font-semibold">Form Style</Label>
            <Select value={formData.intake_mode} onValueChange={(val) => setFormData({ ...formData, intake_mode: val })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="simple_form">Simple Contact Form</SelectItem>
                <SelectItem value="service_buttons">Pick Services + Quote</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="space-y-4">
            <Label className="text-base font-semibold">Customer Info to Collect</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 border rounded">
                <span>Name Required</span>
                <Switch checked={formData.field_config.name_required} onCheckedChange={(v) => updateFieldConfig("name_required", v)} />
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <span>Email Required</span>
                <Switch checked={formData.field_config.email_required} onCheckedChange={(v) => updateFieldConfig("email_required", v)} />
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <span>Show Phone</span>
                <Switch checked={formData.field_config.phone_enabled} onCheckedChange={(v) => updateFieldConfig("phone_enabled", v)} />
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <span>Phone Required</span>
                <Switch checked={formData.field_config.phone_required} onCheckedChange={(v) => updateFieldConfig("phone_required", v)} disabled={!formData.field_config.phone_enabled} />
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <span>Show Address</span>
                <Switch checked={formData.field_config.address_enabled} onCheckedChange={(v) => updateFieldConfig("address_enabled", v)} />
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <span>Show Message</span>
                <Switch checked={formData.field_config.message_enabled} onCheckedChange={(v) => updateFieldConfig("message_enabled", v)} />
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : (business ? "Update" : "Create")}</Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ============== BUSINESS DETAILS ==============
const BusinessDetails = ({ business, onBack, onEdit, onRefresh, onDeleted }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [services, setServices] = useState([]);
  const [embedCode, setEmbedCode] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [copied, setCopied] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [fullBusiness, setFullBusiness] = useState(business);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  const [subAction, setSubAction] = useState(null);
  const [trialDaysInput, setTrialDaysInput] = useState("14");
  const [businessLeads, setBusinessLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [viewingLead, setViewingLead] = useState(null);
  const [editingLead, setEditingLead] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business.slug]);

  const loadData = async () => {
    try {
      const [servicesData, codeData, logsData, businessData, activityData, leadsData] = await Promise.all([
        api.getServices(business.slug, true),
        api.getEmbedCode(business.slug),
        api.getEmailLogs(business.slug),
        api.getBusiness(business.slug),
        api.getActivityLogs(business.slug),
        api.getLeads(business.slug),
      ]);
      setServices(servicesData);
      setEmbedCode(codeData);
      setEmailLogs(logsData.slice(0, 10));
      setFullBusiness(businessData);
      setActivityLogs(activityData.activities || []);
      setActivityStats(activityData.stats || null);
      setBusinessLeads(leadsData || []);
      setLeadsLoading(false);
    } catch (error) {
      console.error("Failed to load data:", error);
      setLeadsLoading(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.deleteLead(leadId);
      toast.success("Lead deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${business.name}"? This action cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deleteBusiness(business.slug);
      toast.success("Business deleted");
      onDeleted();
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete business"));
    } finally {
      setDeleting(false);
    }
  };

  const handleSendMagicLink = async () => {
    setSendingMagicLink(true);
    try {
      const result = await api.adminSendMagicLink(business.slug);
      toast.success(result.message || "Magic link sent!");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to send magic link"));
    } finally {
      setSendingMagicLink(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(""), 2000);
  };

  const publicUrl = embedCode?.form_url || `${window.location.origin}/api/share/${business.slug}`;

  // Tab items for mobile-friendly navigation
  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "setup", label: "Setup" },
    { id: "email-logs", label: "Activity & Logs" },
  ];

  return (
    <div className="space-y-4">
      {/* Back button - always visible at top */}
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        data-testid="back-to-businesses"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="font-medium">Back to Businesses</span>
      </button>

      {/* Business header */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {business.logo_url ? (
              <img src={`${STATIC_URL}${business.logo_url}`} alt={business.name} className="w-14 h-14 object-contain rounded flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                <span className="text-gray-400 text-xl font-semibold">{(business.name || "?")[0].toUpperCase()}</span>
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-xl font-bold truncate">{business.name || "Unnamed Business"}</h1>
              <p className="text-sm text-gray-500">/{business.slug}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onEdit}>Edit</Button>
            <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={handleDelete} disabled={deleting}>
              {deleting ? "..." : "Delete"}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile-friendly tabs - horizontal scroll replaced with wrapping grid */}
      <div className="bg-white border rounded-lg">
        <div className="grid grid-cols-3 sm:grid-cols-6 border-b">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-2 text-sm font-medium text-center transition-colors ${
                activeTab === tab.id 
                  ? "text-slate-900 border-b-2 border-slate-900 bg-blue-50/50" 
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-4">
          {/* OVERVIEW TAB */}
          {activeTab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span className="text-gray-500">Status</span>
                  <Badge className={business.status === "active" ? "bg-blue-100 text-slate-900" : "bg-gray-100 text-gray-800"}>{business.status}</Badge>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span className="text-gray-500">Subscription</span>
                  <span className="font-medium">{business.subscription_status || "—"}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span className="text-gray-500">Package</span>
                  <span className="font-medium capitalize">{business.package || "—"}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span className="text-gray-500">Form Style</span>
                  <span className="font-medium">{business.intake_mode === "service_buttons" ? "Services + Quote" : "Simple Form"}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span className="text-gray-500">Online Payments</span>
                  <span>{business.stripe_connect_enabled ? "✅ Connected" : "❌ Not Set Up"}</span>
                </div>
                <div className="flex flex-col p-2 bg-gray-50 rounded">
                  <span className="text-gray-500 text-xs">Notify Email</span>
                  <span className="font-medium text-sm truncate">{business.notify_email || "—"}</span>
                </div>
                <div className="flex flex-col p-2 bg-gray-50 rounded">
                  <span className="text-gray-500 text-xs">Phone</span>
                  <span className="font-medium text-sm">{fullBusiness?.phone || business?.phone || "—"}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span className="text-gray-500">Wants Setup Call</span>
                  <span className={`font-medium ${(fullBusiness?.wants_setup_call || business?.wants_setup_call) ? 'text-blue-700' : 'text-gray-400'}`}>
                    {(fullBusiness?.wants_setup_call || business?.wants_setup_call) ? '✅ Yes' : 'No'}
                  </span>
                </div>
              </div>

              {/* Terms Acceptance */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Terms Agreement</h3>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs"
                    onClick={async () => {
                      try {
                        await api.downloadTermsPdf(business.slug);
                        toast.success("PDF downloaded");
                      } catch (error) {
                        toast.error("Failed to download PDF");
                      }
                    }}
                    data-testid="download-terms-pdf"
                  >
                    Download PDF
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span className="text-gray-500 text-xs">Signatory Name</span>
                    <span className="font-medium text-xs">{fullBusiness?.signatory_name || '—'}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span className="text-gray-500 text-xs">Terms Accepted</span>
                    <span className={`font-medium text-xs ${fullBusiness?.terms_accepted ? 'text-blue-700' : 'text-red-500'}`}>
                      {fullBusiness?.terms_accepted ? '✅ Yes' : '❌ No'}
                    </span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span className="text-gray-500 text-xs">Disclaimer Acknowledged</span>
                    <span className={`font-medium text-xs ${fullBusiness?.disclaimer_accepted ? 'text-blue-700' : 'text-red-500'}`}>
                      {fullBusiness?.disclaimer_accepted ? '✅ Yes' : '❌ No'}
                    </span>
                  </div>
                  {fullBusiness?.terms_accepted_at && (
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span className="text-gray-500 text-xs">Accepted On</span>
                      <span className="font-medium text-xs">{new Date(fullBusiness.terms_accepted_at).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span className="text-gray-500 text-xs">Terms Version</span>
                    <span className="font-medium text-xs">{fullBusiness?.terms_version || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => window.open(`/t/${business.slug}`, "_blank")}>
                  Preview Form
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleSendMagicLink} 
                  disabled={sendingMagicLink || !business.notify_email}
                  data-testid="send-magic-link-btn"
                >
                  {sendingMagicLink ? "Sending..." : "Send Login Link"}
                </Button>
              </div>
              {!business.notify_email && (
                <p className="text-sm text-amber-600">⚠️ Add a notification email to send login links</p>
              )}

              {/* Billing & Plan */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3">Billing & Plan</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge className={
                    fullBusiness?.subscription_status === "active" ? "bg-blue-100 text-slate-900" :
                    fullBusiness?.subscription_status === "trialing" ? "bg-blue-100 text-blue-800" :
                    fullBusiness?.subscription_status === "paused" ? "bg-amber-100 text-amber-800" :
                    fullBusiness?.subscription_status === "canceled" ? "bg-red-100 text-red-800" :
                    "bg-gray-100 text-gray-800"
                  }>
                    {fullBusiness?.subscription_status || business?.subscription_status || "unknown"}
                  </Badge>
                  {(fullBusiness?.stripe_subscription_id || business?.stripe_subscription_id) && (
                    <Badge variant="outline" className="text-xs">Has Stripe Sub</Badge>
                  )}
                  {(fullBusiness?.trial_ends_at || business?.trial_ends_at) && (
                    <span className="text-xs text-gray-500">Trial ends: {new Date(fullBusiness?.trial_ends_at || business?.trial_ends_at).toLocaleDateString()}</span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-blue-700 border-blue-300"
                    onClick={async () => {
                      setSubAction("activate");
                      try {
                        await api.adminManageSubscription(business.slug, "activate");
                        toast.success("Subscription activated");
                        loadData();
                        onRefresh();
                      } catch (e) { toast.error("Failed"); }
                      setSubAction(null);
                    }}
                    disabled={subAction}
                  >
                    {subAction === "activate" ? "..." : "Activate"}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-amber-600 border-amber-300"
                    onClick={async () => {
                      setSubAction("pause");
                      try {
                        await api.adminManageSubscription(business.slug, "pause");
                        toast.success("Subscription paused");
                        loadData();
                        onRefresh();
                      } catch (e) { toast.error("Failed"); }
                      setSubAction(null);
                    }}
                    disabled={subAction}
                  >
                    {subAction === "pause" ? "..." : "Pause"}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-blue-600 border-blue-300"
                    onClick={async () => {
                      setSubAction("resume");
                      try {
                        await api.adminManageSubscription(business.slug, "resume");
                        toast.success("Subscription resumed");
                        loadData();
                        onRefresh();
                      } catch (e) { toast.error("Failed"); }
                      setSubAction(null);
                    }}
                    disabled={subAction}
                  >
                    {subAction === "resume" ? "..." : "Resume"}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 border-red-300"
                    onClick={async () => {
                      if (!window.confirm("Cancel this subscription?")) return;
                      setSubAction("cancel");
                      try {
                        await api.adminManageSubscription(business.slug, "cancel");
                        toast.success("Subscription canceled");
                        loadData();
                        onRefresh();
                      } catch (e) { toast.error("Failed"); }
                      setSubAction(null);
                    }}
                    disabled={subAction}
                  >
                    {subAction === "cancel" ? "..." : "Cancel"}
                  </Button>
                </div>

                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium">Start Trial:</span>
                  <Input 
                    type="text" 
                    value={trialDaysInput} 
                    onChange={(e) => setTrialDaysInput(e.target.value)}
                    className="w-20 h-8"
                  />
                  <span className="text-sm text-gray-600">days</span>
                  <Button 
                    size="sm"
                    onClick={async () => {
                      const days = parseInt(trialDaysInput) || 14;
                      setSubAction("start_trial");
                      try {
                        const result = await api.adminManageSubscription(business.slug, "start_trial", null, days);
                        if (result.stripe_error) {
                          toast.error(`Trial set locally but Stripe failed: ${result.stripe_error}`);
                        } else if (result.stripe_updated) {
                          toast.success(`${days}-day trial started (Stripe updated)`);
                        } else {
                          toast.success(`${days}-day trial started (local only - no Stripe sub)`);
                        }
                        loadData();
                        onRefresh();
                      } catch (e) { toast.error("Failed to start trial"); }
                      setSubAction(null);
                    }}
                    disabled={subAction}
                  >
                    {subAction === "start_trial" ? "..." : "Start Trial"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* SETUP TAB */}
          {activeTab === "setup" && (
            <SetupSection slug={business.slug} business={fullBusiness} services={services} onRefresh={loadData} />
          )}

          {/* EMAIL LOGS TAB */}
          {activeTab === "email-logs" && (
            <div className="space-y-6">
              {/* Quick Stats */}
              {activityStats && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold mb-3 text-sm">Quick Stats</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-gray-500 text-xs">Last Login</div>
                      <div className="font-medium">{activityStats.last_login ? new Date(activityStats.last_login).toLocaleDateString() : 'Never'}</div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-gray-500 text-xs">Tour</div>
                      <div className="font-medium">
                        {activityStats.tour_status === 'completed' ? '✅ Done' : 
                         activityStats.tour_status === 'skipped' ? '⏭️ Skipped' : '⏳ Pending'}
                      </div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-gray-500 text-xs">Demos Run</div>
                      <div className="font-medium">{activityStats.demos_run}</div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-gray-500 text-xs">Real Quotes</div>
                      <div className="font-medium text-blue-700">{activityStats.real_quotes_sent || 0}</div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-gray-500 text-xs">Demo Quotes</div>
                      <div className="font-medium text-blue-600">{activityStats.demo_quotes_sent || 0}</div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-gray-500 text-xs">Errors (7d)</div>
                      <div className={`font-medium ${activityStats.errors_7d > 0 ? 'text-red-600' : ''}`}>{activityStats.errors_7d}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Activity Feed */}
              <div>
                <h3 className="font-semibold mb-2 text-sm">Activity (Last 3 weeks)</h3>
                {activityLogs.length === 0 ? (
                  <p className="text-sm text-gray-500">No activity recorded yet</p>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {activityLogs.map((activity) => {
                      const icons = {
                        login: '🔐',
                        tour_completed: '✅',
                        tour_skipped: '⏭️',
                        demo_run: '🎬',
                        quote_created: '📄',
                        quote_sent: '📤',
                        demo_quote_sent: '🎭',
                        quote_failed: '❌',
                        error: '⚠️',
                        stripe_connected: '💳'
                      };
                      const labels = {
                        login: 'Logged in',
                        tour_completed: 'Completed setup tour',
                        tour_skipped: 'Skipped setup tour',
                        demo_run: 'Ran demo',
                        quote_created: 'Created quote',
                        quote_sent: 'Sent quote',
                        demo_quote_sent: 'Sent demo quote',
                        quote_failed: 'Quote send failed',
                        error: 'Error',
                        stripe_connected: 'Connected Stripe'
                      };
                      // Check if it's a demo quote from metadata
                      const isDemo = activity.metadata?.is_demo;
                      const eventType = activity.event_type === 'quote_sent' && isDemo ? 'demo_quote_sent' : activity.event_type;
                      
                      const date = new Date(activity.created_at);
                      const isToday = new Date().toDateString() === date.toDateString();
                      const timeStr = isToday 
                        ? `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        : date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      
                      return (
                        <div key={activity.id} className={`flex items-center justify-between p-2 rounded text-sm ${!activity.success ? 'bg-red-50' : isDemo ? 'bg-blue-50' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <span>{icons[eventType] || '📌'}</span>
                            <span className={`${!activity.success ? 'text-red-700' : isDemo ? 'text-blue-700' : ''}`}>
                              {labels[eventType] || activity.event_type}
                            </span>
                            {activity.metadata?.quote_number && <span className="text-gray-400 text-xs">{activity.metadata.quote_number}</span>}
                            {activity.metadata?.error && <span className="text-red-500 text-xs truncate max-w-32">({activity.metadata.error})</span>}
                          </div>
                          <span className="text-gray-400 text-xs whitespace-nowrap">{timeStr}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Separator />

              {/* Email Logs */}
              <div>
                <h3 className="font-semibold mb-2 text-sm">Email Logs</h3>
                {emailLogs.length === 0 ? (
                  <p className="text-sm text-gray-500">No emails sent yet</p>
                ) : (
                  <div className="space-y-2">
                    {emailLogs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className="flex-shrink-0">{log.email_type}</Badge>
                          <span className="truncate text-gray-600">{log.to_email}</span>
                        </div>
                        <Badge className={`flex-shrink-0 ${log.status === "sent" ? "bg-blue-100 text-slate-900" : "bg-red-100 text-red-800"}`}>
                          {log.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Leads Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Leads ({businessLeads.length})</h3>
                </div>

                {leadsLoading ? (
                  <p className="text-sm text-gray-500">Loading leads...</p>
                ) : businessLeads.length === 0 ? (
                  <p className="text-sm text-gray-500">No leads yet</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {businessLeads.slice(0, 10).map((lead) => (
                      <div key={lead.id} className={`p-2 rounded text-sm border ${lead.is_demo ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate flex items-center gap-1">
                              {lead.customer_name || "—"}
                              {lead.is_demo && <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">Demo</Badge>}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{lead.customer_email || "—"}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                          <span>{new Date(lead.created_at).toLocaleDateString()}</span>
                          {lead.pricing_snapshot?.length > 0 && (
                            <span>• {lead.pricing_snapshot.length} service(s)</span>
                          )}
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => setViewingLead(lead)}>
                            View
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 text-xs px-2 text-red-600 hover:text-red-700" onClick={() => handleDeleteLead(lead.id)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                    {businessLeads.length > 10 && (
                      <p className="text-xs text-gray-400 text-center pt-2">Showing 10 of {businessLeads.length} leads</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lead Details Dialog */}
          <Dialog open={!!viewingLead} onOpenChange={() => { setViewingLead(null); setEditingLead(false); }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingLead ? "Edit Customer" : "Lead Details"}</DialogTitle>
              </DialogHeader>
              {viewingLead && !editingLead && (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-500 text-xs">Name</Label>
                      <p className="font-medium">{viewingLead.customer_name || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">Email</Label>
                      <p className="font-medium">{viewingLead.customer_email || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">Phone</Label>
                      <p className="font-medium">{viewingLead.customer_phone || "—"}</p>
                    </div>
                  </div>
                  {viewingLead.customer_address && (
                    <div>
                      <Label className="text-gray-500 text-xs">Address</Label>
                      <p>{viewingLead.customer_address}</p>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => { navigator.clipboard.writeText(viewingLead.customer_address); toast.success("Address copied!"); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-lg text-gray-600 hover:bg-gray-50"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                          Copy Address
                        </button>
                        <button
                          onClick={() => { setViewingLead(null); navigate(`/dashboard/${slug}?section=routes&tab=measure&t=${Date.now()}`); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-lg text-gray-600 hover:bg-gray-50"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                          Measure Property
                        </button>
                      </div>
                    </div>
                  )}
                  {viewingLead.pricing_snapshot?.length > 0 && (
                    <div>
                      <Label className="text-gray-500 text-xs">Selected Services</Label>
                      <ul className="mt-1 space-y-1">
                        {viewingLead.pricing_snapshot.map((ps, i) => (
                          <li key={i} className="flex justify-between">
                            <span>{ps.label} {ps.quantity > 1 && `x ${ps.quantity}`}</span>
                            {ps.price_value && <span className="text-gray-500">${ps.price_value.toFixed(2)}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {viewingLead.message && (
                    <div>
                      <Label className="text-gray-500 text-xs">Message</Label>
                      <p className="whitespace-pre-wrap">{viewingLead.message}</p>
                    </div>
                  )}
                  {viewingLead.is_demo && (
                    <div className="bg-blue-50 text-blue-700 p-2 rounded text-xs">
                      This is a demo lead
                    </div>
                  )}
                  <div className="text-xs text-gray-400">
                    Submitted: {new Date(viewingLead.created_at).toLocaleString()}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setEditingLead(true)}>
                    Edit
                  </Button>
                </div>
              )}
              {viewingLead && editingLead && (
                <EditLeadForm 
                  lead={viewingLead} 
                  onSave={async (updatedData) => {
                    try {
                      await api.updateLead(viewingLead.id, updatedData);
                      toast.success("Customer updated");
                      setViewingLead(null);
                      setEditingLead(false);
                      fetchBusinessLeads(expandedBusinessSlug);
                    } catch (error) {
                      toast.error(getErrorMessage(error, "Failed to update customer"));
                    }
                  }}
                  onCancel={() => setEditingLead(false)}
                />
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

// ============== SERVICES MANAGER ==============
const ServicesManager = ({ slug, services, onRefresh }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.deleteService(slug, serviceId);
      toast.success("Service deleted");
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete"));
    }
  };

  const moveService = async (index, direction) => {
    const newOrder = [...services];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    try {
      await api.reorderServices(slug, newOrder.map(s => s.id));
      onRefresh();
    } catch (error) {
      toast.error("Failed to reorder");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Services</h3>
        <Button size="sm" onClick={() => setShowCreate(true)}>+ Add Service</Button>
      </div>

      {showCreate && (
        <ServiceForm slug={slug} onClose={() => setShowCreate(false)} onSuccess={() => { setShowCreate(false); onRefresh(); }} />
      )}

      {editingService && (
        <ServiceForm slug={slug} service={editingService} onClose={() => setEditingService(null)} onSuccess={() => { setEditingService(null); onRefresh(); }} />
      )}

      {services.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No services yet.</p>
      ) : (
        <div className="space-y-2">
          {services.map((service, idx) => (
            <div key={service.id} className={`border rounded p-3 flex items-center justify-between ${!service.active ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => moveService(idx, -1)} disabled={idx === 0}>↑</Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => moveService(idx, 1)} disabled={idx === services.length - 1}>↓</Button>
                </div>
                <div>
                  <div className="font-medium flex items-center gap-1.5">{service.label}
                    {service.public === false && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">Internal</span>}
                  </div>
                  <div className="text-sm text-gray-500">{formatPrice(service) || "No pricing"}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditingService(service)}>Edit</Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(service.id)}>Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== SERVICE FORM ==============
const ServiceForm = ({ slug, service, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    label: service?.label || "",
    description: service?.description || "",
    pricing_type: service?.pricing_type || "none",
    price_value: service?.price_value || "",
    unit_label: service?.unit_label || "",
    active: service?.active ?? true,
    public: service?.public ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.label.trim()) { toast.error("Label is required"); return; }
    setSaving(true);
    try {
      const data = { ...formData, price_value: formData.price_value ? parseFloat(formData.price_value) : null };
      if (service) {
        await api.updateService(slug, service.id, data);
      } else {
        await api.createService(slug, data);
      }
      toast.success(service ? "Service updated" : "Service created");
      onSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save service"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>{service ? "Edit Service" : "Add Service"}</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Label *</Label>
            <Input value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} placeholder="e.g., Basic Package" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pricing Type</Label>
              <Select value={formData.pricing_type} onValueChange={(v) => setFormData({ ...formData, pricing_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Pricing</SelectItem>
                  <SelectItem value="fixed">Fixed Price</SelectItem>
                  <SelectItem value="starting_at">Starting At</SelectItem>
                  <SelectItem value="per_unit">Per Unit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.pricing_type !== "none" && (
              <div className="space-y-2">
                <Label>Price ($)</Label>
                <Input type="number" step="0.01" value={formData.price_value} onChange={(e) => setFormData({ ...formData, price_value: e.target.value })} />
              </div>
            )}
          </div>
          {formData.pricing_type === "per_unit" && (
            <div className="space-y-2">
              <Label>Unit Label</Label>
              <Input value={formData.unit_label} onChange={(e) => setFormData({ ...formData, unit_label: e.target.value })} placeholder="per hour" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Switch checked={formData.active} onCheckedChange={(v) => setFormData({ ...formData, active: v })} />
            <Label>Active</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={formData.public} onCheckedChange={(v) => setFormData({ ...formData, public: v })} />
            <Label>{formData.public ? "Public — visible on intake form" : "Internal — quote builder only"}</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ============== EMAIL LOGS VIEW ==============
const EmailLogsView = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [sendTo, setSendTo] = useState("all");
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [emailContent, setEmailContent] = useState(null);

  const emailTypeBadge = (type) => {
    const styles = {
      quote:                 "bg-blue-100 text-slate-900",
      lead:                  "bg-blue-100 text-blue-800",
      onboarding:            "bg-blue-100 text-slate-900",
      login:                 "bg-gray-100 text-gray-700",
      broadcast:             "bg-blue-100 text-slate-900",
      admin_notification:    "bg-amber-100 text-amber-800",
      subscription:          "bg-blue-100 text-slate-900",
      winback:               "bg-amber-100 text-amber-800",
      abandoned_onboarding:  "bg-red-100 text-red-800",
      test:                  "bg-blue-100 text-slate-900",
    };
    const labels = {
      quote: "Quote",
      lead: "New Lead",
      onboarding: "Onboarding",
      login: "Login Link",
      broadcast: "Broadcast",
      admin_notification: "Admin Alert",
      subscription: "Subscription",
      winback: "Win-Back",
      abandoned_onboarding: "Abandoned",
      test: "Test",
    };
    return (
      <Badge variant="outline" className={`text-xs ${styles[type] || "bg-gray-100 text-gray-600"}`}>
        {labels[type] || type}
      </Badge>
    );
  };

  const statusBadge = (status) => {
    const styles = {
      sent:      "bg-blue-100 text-slate-900",
      failed:    "bg-red-100 text-red-800",
      scheduled: "bg-amber-100 text-amber-800",
    };
    return <Badge className={styles[status] || "bg-gray-100 text-gray-600"}>{status}</Badge>;
  };

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    setLoading(true);
    try { setLogs(await api.getEmailLogs()); }
    catch (error) { toast.error("Failed to load logs"); }
    finally { setLoading(false); }
  };

  const handleOpenEmail = async (log) => {
    setSelectedEmail(log);
    setShowEmailDialog(true);
    setEmailContent(null);
    
    // If email already has content, use it directly
    if (log.html_content) {
      setEmailContent(log.html_content);
      return;
    }
    
    // Otherwise, fetch/regenerate content from backend
    setLoadingContent(true);
    try {
      const result = await api.getEmailContent(log.id);
      setEmailContent(result.html_content);
    } catch (error) {
      console.error("Failed to load email content:", error);
      setEmailContent(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!subject.trim() || !htmlContent.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to send this email to ${sendTo === "all" ? "ALL" : sendTo.toUpperCase()} businesses?`)) {
      return;
    }
    
    // Wrap plain text in basic HTML formatting
    const formattedHtml = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #4F46E5;">ZipLeadPro</h2>
  ${htmlContent.split('\n').map(line => `<p style="margin: 10px 0;">${line}</p>`).join('\n')}
  <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;" />
  <p style="color: #666; font-size: 12px;">You're receiving this because you have a ZipLeadPro account.</p>
</div>`;
    
    setSending(true);
    try {
      const result = await api.sendBroadcastEmail(subject, formattedHtml, sendTo);
      toast.success(result.message);
      setShowComposer(false);
      setSubject("");
      setHtmlContent("");
      loadLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Email Management</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadLogs}>Refresh</Button>
          <Button onClick={() => { setShowComposer(true); setHtmlContent(""); }} className="bg-slate-900 hover:bg-slate-900">
            <span className="mr-2">📧</span>
            Send Announcement
          </Button>
        </div>
      </div>

      {/* Broadcast Email Composer */}
      {showComposer && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <span>📧</span>
              Send Announcement to All Businesses
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Send To</label>
                <select 
                  value={sendTo} 
                  onChange={(e) => setSendTo(e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="all">All Businesses</option>
                  <option value="active">Active Subscriptions Only</option>
                  <option value="trialing">Trial Users Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Subject</label>
                <Input 
                  value={subject} 
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject line"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Message</label>
              <textarea 
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                rows={8}
                className="w-full border rounded-md p-3 text-sm"
                placeholder="Type your announcement message here..."
              />
            </div>

            {showPreview && (
              <div className="border rounded-md p-4 bg-white">
                <p className="text-sm font-medium mb-2 text-gray-500">Preview:</p>
                <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
              </div>
            )}

            <div className="flex gap-2 justify-end flex-wrap">
              <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
                {showPreview ? "Hide Preview" : "Show Preview"}
              </Button>
              <Button variant="outline" onClick={() => setShowComposer(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendBroadcast} 
                disabled={sending || !subject.trim() || !htmlContent.trim()}
                className="bg-slate-900 hover:bg-slate-900"
              >
                {sending ? "Sending..." : "Send to All"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Logs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Email Logs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-gray-500">Loading...</div> :
          logs.length === 0 ? <div className="p-8 text-center text-gray-500">No logs</div> :
          <>
            {/* Mobile: Card layout */}
            <div className="sm:hidden divide-y">
              {logs.map((log) => (
                <div 
                  key={log.id} 
                  className={`p-4 space-y-2 cursor-pointer hover:bg-gray-50 transition-colors ${log.is_demo ? 'bg-blue-50 hover:bg-blue-100' : ''}`}
                  onClick={() => handleOpenEmail(log)}
                  data-testid={`email-log-mobile-${log.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{log.to_email}</div>
                      <div className="text-xs text-gray-500">{new Date(log.created_at).toLocaleString()}</div>
                    </div>
                    {statusBadge(log.status)}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">{log.tenant_slug}</Badge>
                    {emailTypeBadge(log.email_type)}
                    {log.is_demo ? <Badge className="text-xs bg-blue-100 text-blue-700">Demo</Badge> : <Badge className="text-xs bg-gray-100 text-gray-600">Real</Badge>}
                  </div>
                  {log.subject && <div className="text-xs text-gray-500 truncate">{log.subject}</div>}
                  <div className="text-xs text-slate-900">📧 Click to view content</div>
                </div>
              ))}
            </div>

            {/* Desktop: Table layout */}
            <Table className="hidden sm:table">
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Business</TableHead><TableHead>Type</TableHead><TableHead>Source</TableHead><TableHead>To</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Content</TableHead></TableRow></TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow 
                    key={log.id} 
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${log.is_demo ? 'bg-blue-50 hover:bg-blue-100' : ''}`}
                    onClick={() => handleOpenEmail(log)}
                    data-testid={`email-log-row-${log.id}`}
                  >
                    <TableCell className="text-sm text-gray-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</TableCell>
                    <TableCell><Badge variant="outline">{log.tenant_slug}</Badge></TableCell>
                    <TableCell>{emailTypeBadge(log.email_type)}</TableCell>
                    <TableCell>{log.is_demo ? <Badge className="text-xs bg-blue-100 text-blue-700">Demo</Badge> : <Badge className="text-xs bg-gray-100 text-gray-600">Real</Badge>}</TableCell>
                    <TableCell className="text-sm">{log.to_email}</TableCell>
                    <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{log.subject || "—"}</TableCell>
                    <TableCell>{statusBadge(log.status)}</TableCell>
                    <TableCell>
                      <Badge className="text-xs bg-blue-100 text-slate-900">View</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>}
        </CardContent>
      </Card>

      {/* Email Content Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="email-content-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>📧</span>
              Email Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="space-y-4 overflow-y-auto flex-1">
              {/* Email metadata */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg text-sm">
                <div>
                  <span className="text-gray-500">To:</span>
                  <span className="ml-2 font-medium">{selectedEmail.to_email}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <span className="ml-2">{statusBadge(selectedEmail.status)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Type:</span>
                  <span className="ml-2">{emailTypeBadge(selectedEmail.email_type)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Date:</span>
                  <span className="ml-2">{new Date(selectedEmail.created_at).toLocaleString()}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Subject:</span>
                  <span className="ml-2 font-medium">{selectedEmail.subject || "—"}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Business:</span>
                  <span className="ml-2"><Badge variant="outline">{selectedEmail.tenant_slug}</Badge></span>
                </div>
                {selectedEmail.error_message && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Error:</span>
                    <span className="ml-2 text-red-600">{selectedEmail.error_message}</span>
                  </div>
                )}
              </div>

              {/* Email content preview */}
              <div>
                <h4 className="text-sm font-medium mb-2 text-gray-700">Email Content:</h4>
                {loadingContent ? (
                  <div className="p-8 text-center text-gray-500 border rounded-lg bg-gray-50">
                    <div className="animate-spin inline-block w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full mb-2"></div>
                    <p>Loading email content...</p>
                  </div>
                ) : emailContent ? (
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <iframe
                      srcDoc={emailContent}
                      title="Email Content Preview"
                      className="w-full h-[400px] border-0"
                      sandbox="allow-same-origin"
                      data-testid="email-content-iframe"
                    />
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-400 border rounded-lg bg-gray-50">
                    <p>Email content not available</p>
                    <p className="text-xs mt-1">Could not load email content</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== LOGIN PAGE ==============
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loginType, setLoginType] = useState("business");
  // Separate state for admin and business to avoid conflicts
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [businessPassword, setBusinessPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLinkSent, setResetLinkSent] = useState(false);
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [hasPasskey, setHasPasskey] = useState(false);
  const [checkingPasskey, setCheckingPasskey] = useState(false);
  const [faceIdSupported, setFaceIdSupported] = useState(false);
  
  // Get current email based on active tab (for passkey check)
  const currentEmail = loginType === "admin" ? adminEmail : businessEmail;

  // Check WebAuthn support
  useEffect(() => {
    const checkSupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setFaceIdSupported(available);
        } catch {
          setFaceIdSupported(false);
        }
      }
    };
    checkSupport();
  }, []);

  // Check if email has a passkey
  useEffect(() => {
    const checkPasskey = async () => {
      if (!currentEmail || currentEmail.length < 5 || !currentEmail.includes('@')) return;
      setCheckingPasskey(true);
      try {
        const result = await api.webauthnCheckPasskey(currentEmail, loginType === "admin" ? "admin" : "business");
        setHasPasskey(result.has_passkey);
      } catch {
        setHasPasskey(false);
      } finally {
        setCheckingPasskey(false);
      }
    };
    const timeout = setTimeout(checkPasskey, 500);
    return () => clearTimeout(timeout);
  }, [currentEmail, loginType]);

  // Clear any stale auth data when visiting login page
  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('businessSlug');
  }, []);

  // WebAuthn helpers
  const base64urlToBuffer = (base64url) => {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
    const binary = atob(base64 + pad);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  };

  const bufferToBase64url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const handleFaceIdLogin = async () => {
    const emailForAuth = loginType === "admin" ? adminEmail : businessEmail;
    if (!emailForAuth) {
      toast.error("Please enter your email first");
      return;
    }
    
    setLoading(true);
    setLoginError("");
    const userType = loginType === "admin" ? "admin" : "business";
    
    try {
      // Get authentication options from server
      const options = await api.webauthnAuthStart(emailForAuth, userType);
      
      // Convert challenge and allowCredentials
      const publicKeyOptions = {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        allowCredentials: options.allowCredentials?.map(c => ({
          ...c,
          id: base64urlToBuffer(c.id)
        }))
      };
      
      // Trigger Face ID / Touch ID
      const assertion = await navigator.credentials.get({ publicKey: publicKeyOptions });
      
      // Send response to server
      const credentialResponse = {
        id: assertion.id,
        rawId: bufferToBase64url(assertion.rawId),
        type: assertion.type,
        response: {
          clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
          authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
          signature: bufferToBase64url(assertion.response.signature),
          userHandle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null
        }
      };
      
      const result = await api.webauthnAuthFinish(emailForAuth, userType, credentialResponse);
      
      if (result.status === "success") {
        login(result.access_token, {
          role: result.role || (userType === "admin" ? "admin" : "business"),
          tenant_slug: result.tenant_slug,
          tenant_name: result.tenant_name
        });
        toast.success("Welcome back!", { id: "login-success" });
        navigate(userType === "admin" ? "/admin" : `/dashboard/${result.tenant_slug}`);
      }
    } catch (error) {
      console.error("Face ID login error:", error);
      if (error.name === 'NotAllowedError') {
        toast.error("Face ID cancelled or not available");
      } else {
        toast.error(getErrorMessage(error, "Face ID login failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // IMPORTANT: iOS Face ID/Autofill Fix - DO NOT REMOVE
  // iOS autofill doesn't trigger React's onChange, so we must read DOM values directly on submit.
  // This has been a recurring issue - reading from DOM is the only reliable solution.
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Read values directly from DOM to handle iOS autofill
    const emailInput = document.getElementById('admin-email');
    const passwordInput = document.getElementById('admin-password');
    const emailValue = emailInput?.value || adminEmail;
    const passwordValue = passwordInput?.value || adminPassword;
    
    try {
      const data = await api.adminLogin(emailValue, passwordValue);
      login(data.access_token, { role: data.role, email: emailValue });
      toast.success("Login successful!", { id: "login-success" });
      navigate("/admin");
    } catch (error) {
      console.error("Admin login error:", error);
      console.error("Error response:", error.response);
      const errorDetail = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Login failed: ${errorDetail}`);
    } finally {
      setLoading(false);
    }
  };

  // IMPORTANT: iOS Face ID/Autofill Fix - DO NOT REMOVE
  // iOS autofill doesn't trigger React's onChange, so we must read DOM values directly on submit.
  // This has been a recurring issue - reading from DOM is the only reliable solution.
  const handleBusinessLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");
    
    const emailValue = businessEmail;
    const passwordValue = businessPassword;
    
    if (!emailValue || !emailValue.trim()) {
      setLoginError("Please enter your email address");
      setLoading(false);
      return;
    }
    if (!passwordValue) {
      setLoginError("Please enter your password");
      setLoading(false);
      return;
    }
    
    try {
      // Resilient fetch: Safari mobile is notorious for surfacing transient backend
      // hiccups as a hard "Load failed" with no HTTP response. We try twice with a
      // tight 8s timeout each — long enough to absorb a quick blip, short enough
      // that the user never sits on the spinner for more than ~17 seconds.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/business-login`;
      const payload = JSON.stringify({ email: emailValue.trim(), password: passwordValue });
      let response = null;
      let lastNetErr = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 8000);
          response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            signal: controller.signal,
          });
          clearTimeout(timer);
          break;
        } catch (netErr) {
          lastNetErr = netErr;
          if (attempt < 1) {
            await new Promise(r => setTimeout(r, 500));
            continue;
          }
        }
      }
      if (!response) {
        console.error("Business login network error:", lastNetErr);
        setLoginError("Server isn't responding right now. Please try again in a minute.");
        setLoading(false);
        return;
      }
      const result = await response.json();
      
      if (result.status === "success") {
        login(result.access_token, { 
          role: result.role, 
          tenant_slug: result.tenant_slug,
          tenant_name: result.tenant_name 
        });
        toast.success("Welcome back!", { id: "login-success" });
        navigate(`/dashboard/${result.tenant_slug}`);
      } else if (result.status === "not_found") {
        setAccountNotFound(true);
      } else if (result.status === "wrong_password") {
        setLoginError("Incorrect password. Please try again.");
      } else if (result.status === "no_password") {
        setLoginError("No password set. Click 'Forgot Password' to create one.");
      } else if (result.status === "password_required") {
        setLoginError("Please enter your password.");
      } else {
        setLoginError(result.message || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("Business login error:", error);
      setLoginError(`Login failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const emailToReset = loginType === "admin" ? adminEmail : businessEmail;
    if (!emailToReset) {
      toast.error("Please enter your email first");
      return;
    }
    setLoading(true);
    setLoginError("");
    try {
      const result = await api.businessForgotPassword(emailToReset);
      if (result.status === "sent") {
        setResetLinkSent(true);
      } else {
        toast.error(result.message || "Failed to send reset link");
      }
    } catch (error) {
      console.error("[FORGOT PASSWORD] Error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to send reset link";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Account not found screen
  if (accountNotFound) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="text-4xl mb-2">🔍</div>
            <CardTitle className="text-2xl">Account Not Found</CardTitle>
            <CardDescription>
              We couldn't find an account with that email address.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm">
                <strong>{businessEmail}</strong> is not registered with ZipLeadPro yet.
              </p>
            </div>
            <p className="text-gray-600 text-center">
              Ready to get started? Create your account and start receiving customer requests today.
            </p>
            <Button 
              onClick={() => navigate("/get-started")} 
              className="w-full bg-slate-900 hover:bg-slate-900"
            >
              Create Your Account
            </Button>
            <Button
              onClick={() => {
                setAccountNotFound(false);
                setBusinessEmail("");
                setBusinessPassword("");
              }}
              variant="outline"
              className="w-full"
            >
              Try a Different Email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset link sent screen
  if (resetLinkSent) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Check Your Email</CardTitle>
            <CardDescription>
              We've sent a password reset link to <strong>{email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">
              Click the link in your email to reset your password.
            </p>
            <p className="text-sm text-gray-500">
              Didn't receive it? Check your spam folder or{" "}
              <button 
                className="text-slate-900 hover:underline"
                onClick={() => setResetLinkSent(false)}
              >
                try again
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <SEO 
        title="Login"
        description="Sign in to your ZipLeadPro dashboard to manage leads, send quotes, and schedule jobs."
        path="/login"
      />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="text-2xl font-bold text-slate-900 mb-2">ZipLeadPro</div>
          <CardTitle className="text-2xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to access your dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={loginType} onValueChange={setLoginType} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="admin">Platform Admin</TabsTrigger>
              <TabsTrigger value="business">Business</TabsTrigger>
            </TabsList>

            <TabsContent value="admin">
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600">Log in as ZipLeadPro Developer</p>
                </div>
                <div>
                  <Label htmlFor="admin-email">Email</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    onInput={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@example.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="admin-password">Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    onInput={(e) => setAdminPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In as Admin"}
                </Button>
                
                {/* Face ID for Admin */}
                {faceIdSupported && adminEmail && hasPasskey && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2"
                    onClick={handleFaceIdLogin}
                    disabled={loading}
                  >
                    <span>🔐</span>
                    Sign in with Face ID
                  </Button>
                )}

                <div className="text-center">
                  <Link to="/admin/forgot-password" className="text-sm text-slate-900 hover:text-slate-900 hover:underline">
                    Forgot Password?
                  </Link>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="business">
              <form onSubmit={handleBusinessLogin} className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600">Log in to your Business Dashboard</p>
                </div>
                <div>
                  <Label htmlFor="business-email">Business Email</Label>
                  <Input
                    id="business-email"
                    type="email"
                    value={businessEmail}
                    onChange={(e) => { setBusinessEmail(e.target.value); setLoginError(""); }}
                    onInput={(e) => { setBusinessEmail(e.target.value); setLoginError(""); }}
                    placeholder="you@business.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="business-password">Password</Label>
                  <Input
                    id="business-password"
                    type="password"
                    value={businessPassword}
                    onChange={(e) => { setBusinessPassword(e.target.value); setLoginError(""); }}
                    onInput={(e) => { setBusinessPassword(e.target.value); setLoginError(""); }}
                    placeholder="••••••••"
                    required
                  />
                </div>
                
                {loginError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-700 text-sm">{loginError}</p>
                  </div>
                )}
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
                
                {/* Face ID for Business */}
                {faceIdSupported && businessEmail && hasPasskey && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2"
                    onClick={handleFaceIdLogin}
                    disabled={loading}
                  >
                    <span>🔐</span>
                    Sign in with Face ID
                  </Button>
                )}
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-slate-900 hover:text-slate-900 hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
                
                <div className="text-center text-sm mt-4">
                  Don't have an account yet?{" "}
                  <a href="/get-started" className="text-slate-900 underline">
                    Get started
                  </a>
                </div>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== ADMIN FORGOT PASSWORD ==============
const AdminForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await api.adminRequestPasswordReset(email);
      setEmailSent(true);
    } catch (error) {
      // Always show success message for security (don't reveal if email exists)
      setEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <CardTitle className="text-2xl">Check Your Email</CardTitle>
            <CardDescription>
              If an admin account exists for <strong>{email}</strong>, we've sent a password reset link.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                The reset link will expire in 1 hour. Check your spam folder if you don't see the email.
              </p>
            </div>
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="w-full"
            >
              ← Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="text-2xl font-bold text-slate-900 mb-2">ZipLeadPro</div>
          <CardTitle className="text-2xl">Reset Admin Password</CardTitle>
          <CardDescription>
            Enter your admin email and we'll send you a reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="reset-email">Admin Email</Label>
              <Input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                data-testid="input-reset-email"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700">
              ← Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== ADMIN RESET PASSWORD ==============
const AdminResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await api.adminResetPassword(token, password);
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      const errorMessage = getErrorMessage(err, "Failed to reset password");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <CardTitle className="text-2xl text-red-700">Invalid Reset Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/admin/forgot-password")}
              className="w-full"
            >
              Request New Reset Link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <CardTitle className="text-2xl text-blue-800">Password Reset!</CardTitle>
            <CardDescription>
              Your admin password has been reset successfully.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/login")}
              className="w-full"
            >
              Log In with New Password
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="text-2xl font-bold text-slate-900 mb-2">ZipLeadPro</div>
          <CardTitle className="text-2xl">Set New Password</CardTitle>
          <CardDescription>
            Enter your new admin password below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                data-testid="input-new-password"
              />
              <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="input-confirm-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700">
              ← Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== BUSINESS PASSWORD RESET ==============
const BusinessResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await api.businessResetPassword(token, password);
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      const errorMessage = getErrorMessage(err, "Failed to reset password");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <CardTitle className="text-2xl text-red-700">Invalid Reset Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/login")}
              className="w-full"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <CardTitle className="text-2xl text-blue-800">Password Reset!</CardTitle>
            <CardDescription>
              Your password has been successfully reset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/login")}
              className="w-full"
            >
              Sign In Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Reset Your Password</CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700">
              ← Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== MAGIC LINK VERIFICATION ==============
const MagicLinkVerify = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const token = searchParams.get("token");
    const redirect = searchParams.get("redirect");
    
    if (!token) {
      // Use setTimeout to avoid cascading renders
      setTimeout(() => {
        setError("Invalid login link");
        setVerifying(false);
      }, 0);
      return;
    }

    api.verifyMagicLink(token)
      .then((data) => {
        // Check if onboarding is incomplete - redirect to onboarding first
        if (data.onboarding_completed === false && data.onboarding_token) {
          toast.success("Let's finish setting up your account!");
          navigate(`/onboarding/${data.onboarding_token}`);
          return;
        }
        
        login(data.access_token, {
          role: data.role,
          tenant_slug: data.tenant_slug,
          tenant_name: data.tenant_name
        });
        toast.success("Login successful!", { id: "login-success" });
        
        // Use redirect param if provided, otherwise default to dashboard
        const targetPath = redirect || `/dashboard/${data.tenant_slug}`;
        navigate(targetPath);
      })
      .catch((err) => {
        const errorMessage = err.response?.data?.detail || "Login link is invalid or expired";
        setError(errorMessage);
        // Check if it's an expired or invalid link - show special UI for both
        const isLinkIssue = errorMessage.toLowerCase().includes("expired") || 
                           errorMessage.toLowerCase().includes("no longer valid") ||
                           errorMessage.toLowerCase().includes("invalid");
        if (isLinkIssue) {
          setIsExpired(true);
        }
        setVerifying(false);
      });
  }, [searchParams, login, navigate]);

  const handleResendLink = async () => {
    if (!resendEmail || !resendEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setResending(true);
    try {
      await api.businessLogin(resendEmail);
      setResendSuccess(true);
      toast.success("New login link sent! Check your email.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send login link. Please try again.");
    } finally {
      setResending(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your login...</p>
        </div>
      </div>
    );
  }

  // Special UI for expired links
  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <span className="text-3xl">⏰</span>
            </div>
            <CardTitle className="text-xl text-gray-900">Login Link Expired</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600 text-center text-sm">
              Your login link has expired. Login links are valid for <strong>24 hours</strong> for security reasons.
            </p>
            
            {!resendSuccess ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium mb-3">
                    Get a new login link instantly:
                  </p>
                  <div className="space-y-3">
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="w-full"
                      data-testid="resend-email-input"
                    />
                    <Button 
                      onClick={handleResendLink} 
                      disabled={resending}
                      className="w-full bg-slate-900 hover:bg-slate-900"
                      data-testid="resend-link-btn"
                    >
                      {resending ? "Sending..." : "Send New Login Link"}
                    </Button>
                  </div>
                </div>
                
                <div className="text-center">
                  <button 
                    onClick={() => navigate("/login")}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Back to login page
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl mb-2">✅</div>
                  <p className="text-slate-900 font-medium">New link sent!</p>
                  <p className="text-blue-800 text-sm mt-1">
                    Check your inbox at <strong>{resendEmail}</strong>
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800">
                    <strong>💡 Tip:</strong> Bookmark the email! You can reuse the same link for 24 hours without requesting a new one.
                  </p>
                </div>
                
                <Button 
                  variant="outline"
                  onClick={() => {
                    setResendSuccess(false);
                    setResendEmail("");
                  }}
                  className="w-full"
                >
                  Send to different email
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Generic error UI
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-3xl">❌</span>
          </div>
          <CardTitle className="text-red-600">Login Failed</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-600">{error}</p>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-3">Need a new login link?</p>
            <div className="space-y-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="w-full"
              />
              <Button 
                onClick={handleResendLink} 
                disabled={resending}
                className="w-full"
                variant="outline"
              >
                {resending ? "Sending..." : "Send Login Link"}
              </Button>
            </div>
          </div>
          
          <Button onClick={() => navigate("/login")} variant="ghost">
            Back to Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== BUSINESS DASHBOARD ==============
// ============== BUSINESS DASHBOARD WITH SIDEBAR ==============
// Returns true when the tenant's subscription is paused, canceled, or the free
// trial has expired. Mirrors the server-side gate (`is_subscription_active` in
// services/subscription.py) so the UI shows a "locked" state for the exact
// same conditions the backend now refuses. Used to visibly disable the
// outbound-action buttons (Send Quote / Build Quote / Collect Balance /
// Manual Approve) so users see a lock icon + tooltip rather than getting a
// 402 toast after clicking.
const isSubInactive = (business) => {
  if (!business) return false; // unknown — don't block until we know
  const status = business.subscription_status;
  if (status === "active") return false;
  if (status === "trialing") {
    if (!business.trial_ends_at) return true;
    return new Date(business.trial_ends_at) < new Date();
  }
  // paused, canceled, anything else
  return true;
};

const SUB_LOCK_TOOLTIP = "Reactivate your subscription to use this";

// Global event-based trigger for the subscription-required modal. We dispatch
// this from (a) clicking any locked action button and (b) any axios response
// that returns HTTP 402. A single modal mounted at the BusinessDashboard root
// listens for the event and shows a persistent screen with a real Subscribe
// button — replacing the disappearing toast that was easy to miss.
const showSubRequired = () => {
  try {
    window.dispatchEvent(new CustomEvent("zlp-sub-required"));
  } catch (e) {
    // SSR / non-browser env — no-op
  }
};

// Wire an axios interceptor exactly once at module load. Any 402 response from
// the backend (the subscription gate raises HTTPException(402, ...)) opens the
// modal AND swallows the default error toast so users only see the persistent
// screen, never a transient toast they might miss.
if (typeof axios !== "undefined" && !axios.__zlpSubInterceptor) {
  axios.interceptors.response.use(
    (resp) => resp,
    (err) => {
      if (err?.response?.status === 402) {
        showSubRequired();
      }
      return Promise.reject(err);
    }
  );
  axios.__zlpSubInterceptor = true;
}

const BusinessDashboard = () => {
  const { slug } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Initialize section from URL so a reload (Stripe redirect, browser refresh,
  // window.location.reload(), deploys, etc.) lands the user back on the same
  // section instead of dumping them onto "overview".
  const [activeSection, _setActiveSectionRaw] = useState(() => searchParams.get("section") || "overview");

  // Wrapper that keeps URL ↔ state in sync. Every nav click in the sidebar (and
  // every programmatic setActiveSection call from a child) writes ?section=...
  // into the URL via replaceState so the reload preserves the location without
  // polluting browser history.
  const setActiveSection = useCallback((next) => {
    _setActiveSectionRaw((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get("section") !== value) {
          params.set("section", value);
          // `tab` is contextual to the current section — clear it so a stale
          // value from the previous section doesn't get reinterpreted by the
          // new one (e.g. tab=quotes carrying over into Routes section).
          params.delete("tab");
          // Transient deep-link params — consume once, don't re-fire on reload.
          params.delete("openQuote");
          const qs = params.toString();
          const url = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
          window.history.replaceState(window.history.state, "", url);
        }
      } catch (_) { /* ignore — SSR/no-window */ }
      return value;
    });
  }, []);

  // Handle section navigation via query param (back/forward, deep links)
  useEffect(() => {
    const section = searchParams.get("section");
    if (section) { _setActiveSectionRaw(section); }
    // Email "Build Quote" buttons land here with ?openQuote=<leadId> to launch
    // the FULL quote builder (same one in the Quotes tab) pre-populated for that lead.
    // The Quote Builder lives INSIDE LeadsSection (people icon) → quotes sub-tab,
    // so we must mount LeadsSection by setting activeSection="leads". The internal
    // useEffect in LeadsSection then auto-switches to the quotes sub-tab and opens
    // the dialog (driven by openQuoteDialog=true).
    const openQuoteLeadId = searchParams.get("openQuote");
    if (openQuoteLeadId) {
      setActiveSection("leads");
      setQuoteForLeadId(openQuoteLeadId);
      setOpenQuoteDialog(true);
    }
  }, [searchParams, setActiveSection]);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [annualPricingEnabled, setAnnualPricingEnabled] = useState(false);
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showPostCelebration, setShowPostCelebration] = useState(false);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [formUrl, setFormUrl] = useState("");
  const [showTour, setShowTour] = useState(false);
  
  // Terms acceptance state for existing users
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);
  const [termsForm, setTermsForm] = useState({ signatory_name: "", terms_accepted: false, disclaimer_accepted: false });
  const [termsSubmitting, setTermsSubmitting] = useState(false);
  
  // PWA Install state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSInstallModal, setShowIOSInstallModal] = useState(false);
  const [showInstallReminder, setShowInstallReminder] = useState(false);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  
  // Demo submission state
  const [sendingDemo, setSendingDemo] = useState(false);
  const [showDemoSuccess, setShowDemoSuccess] = useState(false);
  
  // Job scheduling state
  const [unscheduledJobs, setUnscheduledJobs] = useState([]);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingJob, setSchedulingJob] = useState(null);
  
  // Plan picker dialog state
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [planPickerInterval, setPlanPickerInterval] = useState("monthly");
  const [planPickerLoading, setPlanPickerLoading] = useState(false);

  // ==== Subscription-required modal ====
  // Listens for the global `zlp-sub-required` event (fired by locked button
  // clicks AND by any 402 response from the server-side subscription gate).
  // Shows a persistent screen with a real "Reactivate Subscription" CTA so
  // users can never miss the message the way a disappearing toast could.
  const [showSubRequiredModal, setShowSubRequiredModal] = useState(false);
  useEffect(() => {
    const open = () => setShowSubRequiredModal(true);
    window.addEventListener("zlp-sub-required", open);
    return () => window.removeEventListener("zlp-sub-required", open);
  }, []);

  // Quick action state - for opening quote dialog from dashboard
  const [openQuoteDialog, setOpenQuoteDialog] = useState(false);
  const [quoteForLeadId, setQuoteForLeadId] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [schedulingLoading, setSchedulingLoading] = useState(false);
  
  // Track when tour completes to trigger celebration
  const [tourJustCompleted, setTourJustCompleted] = useState(false);
  
  // Interactive tour state - tracks user actions for guided tour
  const [tourPreviewOpen, setTourPreviewOpen] = useState(false);
  const [tourFormMode, setTourFormMode] = useState(business?.intake_mode || 'simple_form');
  const [tourEditorExpanded, setTourEditorExpanded] = useState(false);
  
  // External control to close preview (used when tour completes)
  const [closePreviewTrigger, setClosePreviewTrigger] = useState(0);
  
  // Swipe navigation removed - was causing accidental page switches
  
  // Setup completion checks (for celebration popup)
  const hasBusinessName = business?.name && business.name.trim() !== "";
  const hasContactEmail = business?.notify_email && business.notify_email.trim() !== "";
  const hasServices = services.filter(s => s.active).length > 0;
  
  // Step 2 completion: Track if user has customized form (opened editor)
  const step2CompletedKey = `checklist_step2_completed_${slug}`;
  const [hasCustomizedForm, setHasCustomizedForm] = useState(() => {
    try {
      return localStorage.getItem(step2CompletedKey) === 'true';
    } catch {
      return false;
    }
  });
  
  // Mark Step 2 as complete (called when user opens form editor)
  const markStep2Complete = () => {
    if (!hasCustomizedForm) {
      setHasCustomizedForm(true);
      try {
        localStorage.setItem(step2CompletedKey, 'true');
      } catch {}
    }
  };
  
  // Checklist: Step 1 = hasServices, Step 2 = hasCustomizedForm
  // Celebration triggers when BOTH are complete
  const isStep1Complete = hasServices;
  const isStep2Complete = hasCustomizedForm;
  const isBothStepsComplete = isStep1Complete && isStep2Complete;
  
  // Legacy: isSetupComplete still used for some UI elements
  const isSetupComplete = hasBusinessName && hasContactEmail && hasServices;
  
  const celebrationKey = `zipleadpro_setup_celebrated_${slug}`;
  const notificationPopupKey = `notification_popup_shown_${slug}`;
  
  // Fetch the clean public URL for celebration popup
  useEffect(() => {
    if (slug) {
      api.getEmbedCode(slug).then(data => {
        setFormUrl(data.form_url);
      }).catch(() => {
        setFormUrl(`${window.location.origin}/api/share/${slug}`);
      });
    }
  }, [slug]);
  
  // Show celebration when BOTH Step 1 AND Step 2 are complete AND tour is done
  // This prevents the celebration from appearing mid-tour
  useEffect(() => {
    const tourComplete = localStorage.getItem(`zipleadpro_tour_${slug}`) === 'true';
    const tourSkipped = localStorage.getItem(`zipleadpro_tour_skipped_${slug}`) === 'true';
    const tourIsDone = tourComplete || tourSkipped || tourJustCompleted;
    
    if (!loading && isBothStepsComplete && tourIsDone && !localStorage.getItem(celebrationKey)) {
      // Small delay to let tour UI close first
      const timer = setTimeout(() => {
        setShowCelebration(true);
        localStorage.setItem(celebrationKey, "true");
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, isBothStepsComplete, celebrationKey, slug, tourJustCompleted]);
  
  // PWA Install prompt handling
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsAppInstalled(true);
      return;
    }
    
    // Capture the install prompt for Android/Desktop
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
      toast.success("ZipLeadPro installed successfully!");
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);
  
  // Show install reminder on second login only - BUT NOT during tour
  useEffect(() => {
    if (loading || isAppInstalled) return;
    
    // Don't show during tour
    const tourComplete = localStorage.getItem(`zipleadpro_tour_${slug}`) === 'true';
    const tourSkipped = localStorage.getItem(`zipleadpro_tour_skipped_${slug}`) === 'true';
    if (!tourComplete && !tourSkipped) return; // Tour is still active, don't show
    
    const installReminderKey = `zlp_install_reminder_${slug}`;
    const installDismissedKey = `zlp_install_dismissed_${slug}`;
    const loginCountKey = `zlp_login_count_${slug}`;
    
    // Check if user permanently dismissed
    if (localStorage.getItem(installDismissedKey)) return;
    
    // Track login count
    const loginCount = parseInt(localStorage.getItem(loginCountKey) || '0', 10) + 1;
    localStorage.setItem(loginCountKey, loginCount.toString());
    
    // REMOVED: Install reminder popup - now only available in sidebar
    // Keeping the login count tracking for potential future use
  }, [loading, slug, isAppInstalled]);
  
  // Detect platform for install
  const getPlatform = () => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/android/i.test(ua)) return 'android';
    return 'desktop';
  };
  
  const handleInstallClick = async () => {
    const platform = getPlatform();
    
    if (platform === 'ios') {
      setShowIOSInstallModal(true);
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsAppInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback for browsers that don't support beforeinstallprompt
      toast.info("To install: Open browser menu → 'Add to Home Screen'");
    }
  };
  
  const dismissInstallReminderForever = () => {
    localStorage.setItem(`zlp_install_dismissed_${slug}`, 'true');
    setShowInstallReminder(false);
  };
  
  // Show notification reminder popup once after first login (if no email set)
  useEffect(() => {
    if (!loading && business && !hasContactEmail && !localStorage.getItem(notificationPopupKey)) {
      // Delay popup slightly so it doesn't overwhelm user on first load
      const timer = setTimeout(() => {
        setShowNotificationPopup(true);
        localStorage.setItem(notificationPopupKey, "true");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, business, hasContactEmail, notificationPopupKey]);
  
  const handleDismissNotificationPopup = () => {
    setShowNotificationPopup(false);
  };
  
  const handleDismissCelebration = () => {
    setShowCelebration(false);
    setActiveSection("overview");
    setShowPostCelebration(true);
  };

  // Check for Stripe Connect redirect and lead deep-link
  useEffect(() => {
    if (searchParams.get("stripe_connected") === "true") {
      toast.success("Stripe account connected successfully!");
      setActiveSection("stripe");
    }
    // Check for lead deep-link from email
    const leadId = searchParams.get("lead");
    if (leadId) {
      setActiveSection("leads");
      // Store lead ID to open in detail view after data loads
      sessionStorage.setItem("openLeadId", leadId);
    }
  }, [searchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [businessData, leadsData, servicesData] = await Promise.all([
        api.getBusiness(slug),
        api.getLeads(slug),
        api.getServices(slug, true)
      ]);
      setBusiness(businessData);
      setLeads(leadsData);
      setServices(servicesData);
      
      // Check if terms need to be accepted
      if (!businessData.terms_accepted) {
        setShowTermsModal(true);
      }
      
      // Fetch Stripe status
      try {
        const status = await api.getStripeConnectStatus(slug);
        setStripeStatus(status);
      } catch (e) {
        setStripeStatus({ connected: false });
      }
      
      // Check if annual pricing is enabled (from admin stripe config)
      try {
        const adminStripeConfig = await axios.get(`${API}/admin/stripe-config-status`, { headers: getAuthHeaders() });
        setAnnualPricingEnabled(adminStripeConfig.data?.annual_price_configured || false);
      } catch (e) {
        setAnnualPricingEnabled(false);
      }

      // Fetch quotes
      try {
        const quotesData = await api.getQuotes(slug);
        setQuotes(quotesData);
      } catch (e) {
        setQuotes([]);
      }
      
      // Fetch unscheduled jobs
      try {
        const unscheduledData = await api.getUnscheduledJobs(slug);
        setUnscheduledJobs(unscheduledData);
      } catch (e) {
        setUnscheduledJobs([]);
      }
      
      // Fetch scheduled jobs for calendar
      try {
        const scheduledData = await api.getScheduledJobs(slug);
        setScheduledJobs(scheduledData);
      } catch (e) {
        setScheduledJobs([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied");
        localStorage.removeItem('token');
        localStorage.removeItem('businessSlug');
        navigate("/login");
      } else if (error.response?.status === 404) {
        // Business not found - account may have been deleted
        toast.error("Account not found. Please sign up again.");
        localStorage.removeItem('token');
        localStorage.removeItem('businessSlug');
        navigate("/get-started");
      } else if (error.response?.status === 401) {
        // Token invalid/expired
        toast.error("Session expired. Please log in again.");
        localStorage.removeItem('token');
        localStorage.removeItem('businessSlug');
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  // Handle terms acceptance for existing users
  const handleTermsAccept = async () => {
    if (!termsForm.signatory_name.trim() || !termsForm.terms_accepted || !termsForm.disclaimer_accepted) {
      toast.error("Please complete all fields");
      return;
    }
    setTermsSubmitting(true);
    try {
      await api.acceptTerms(slug, {
        signatory_name: termsForm.signatory_name.trim(),
        terms_accepted: true,
        disclaimer_accepted: true,
        terms_version: "2026-03-12"
      });
      setBusiness({ ...business, terms_accepted: true, disclaimer_accepted: true, signatory_name: termsForm.signatory_name.trim() });
      setShowTermsModal(false);
      toast.success("Terms accepted");
    } catch (error) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setTermsSubmitting(false);
    }
  };

  // Handle demo submission - sends a demo request through the normal workflow
  const handleDemoSubmission = async () => {
    if (sendingDemo) return; // Prevent duplicate clicks
    
    setSendingDemo(true);
    try {
      // Get active non-modifier services
      const activeServices = services.filter(s => s.active && !s.is_modifier);
      
      // Randomly select 1-2 services
      const shuffled = [...activeServices].sort(() => Math.random() - 0.5);
      const numServices = Math.min(Math.random() > 0.5 ? 2 : 1, shuffled.length);
      const selectedServices = shuffled.slice(0, Math.max(1, numServices));
      
      // All quantities are 1 for demo mode (no random quantities)
      const quantities = {};
      selectedServices.forEach(s => {
        quantities[s.id] = 1;
      });
      
      // Realistic demo customer names
      const firstNames = ["Sarah", "Michael", "Emily", "James", "Jessica", "David", "Ashley", "Robert", "Amanda", "John"];
      const lastNames = ["Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Martinez", "Wilson", "Taylor"];
      const randomFirst = firstNames[Math.floor(Math.random() * firstNames.length)];
      const randomLast = lastNames[Math.floor(Math.random() * lastNames.length)];
      const demoName = `${randomFirst} ${randomLast}`;
      
      // Realistic phone number
      const areaCode = ["512", "415", "310", "214", "404", "303", "702", "602"][Math.floor(Math.random() * 8)];
      const demoPhone = `(${areaCode}) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`;
      
      // Sample addresses if address is enabled
      const sampleAddresses = [
        "123 Oak Street, Austin, TX 78701",
        "456 Maple Avenue, Denver, CO 80202", 
        "789 Pine Road, Phoenix, AZ 85001",
        "321 Cedar Lane, Atlanta, GA 30301",
        "654 Birch Drive, San Diego, CA 92101"
      ];
      const demoAddress = business?.field_config?.address_enabled 
        ? sampleAddresses[Math.floor(Math.random() * sampleAddresses.length)]
        : undefined;
      
      // Stock photos for demo mode - lawn/construction site images
      const stockPhotoUrls = [
        "https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800",
        "https://images.unsplash.com/photo-1592417817098-8fd3d9eb14a5?w=800",
        "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800"
      ];
      
      // Add demo photos if business has photos enabled (optional or required)
      const photoMode = business?.field_config?.photo_mode || "off";
      const photosEnabled = photoMode !== "off";
      const demoPhotos = photosEnabled ? stockPhotoUrls.slice(0, 2) : [];
      
      // Generate realistic message based on selected services
      const serviceNames = selectedServices.map(s => s.label).join(" and ");
      const messageTemplates = [
        `Hi, I'm interested in getting ${serviceNames}. When is your next available opening?`,
        `I'd like to schedule ${serviceNames} as soon as possible. Please let me know your availability.`,
        `Looking for a quote on ${serviceNames}. My schedule is flexible this week.`,
        `Can you help me with ${serviceNames}? I'm available most afternoons.`,
        `I need ${serviceNames} done soon. What's your turnaround time?`
      ];
      const demoMessage = messageTemplates[Math.floor(Math.random() * messageTemplates.length)];
      
      // Auto-fill custom question answers
      const customAnswers = {};
      const customQuestions = business?.field_config?.custom_questions || [];
      customQuestions.forEach(q => {
        if (q.type === 'radio' && q.options?.length > 0) {
          // Pick a random option for radio buttons
          customAnswers[q.id] = q.options[Math.floor(Math.random() * q.options.length)];
        } else if (q.type === 'checkbox' && q.options?.length > 0) {
          // Pick 1-2 random options for checkboxes
          const shuffledOpts = [...q.options].sort(() => Math.random() - 0.5);
          const numOpts = Math.min(Math.floor(Math.random() * 2) + 1, shuffledOpts.length);
          customAnswers[q.id] = shuffledOpts.slice(0, numOpts);
        } else if (q.type === 'text') {
          // Generate sample text answer
          customAnswers[q.id] = "Sample response for demo";
        }
      });
      
      // Build pricing snapshot (same structure as real intake form)
      const pricingSnapshot = selectedServices.map(service => {
        const qty = quantities[service.id] || 1;
        return {
          service_id: service.id,
          label: service.label || "",
          pricing_type: service.pricing_type || "none",
          price_value: service.price_value || 0,
          unit_label: service.unit_label || null,
          quantity: qty,
          is_modifier: service.is_modifier || false,
          modifier_type: service.modifier_type || null,
          modifier_percent: service.modifier_percent || null,
          modifier_fixed: service.modifier_fixed || null,
        };
      });
      
      // Create demo lead data with realistic info
      // Use business email for demo so owner receives the quote
      const demoCustomerEmail = business?.notify_email || business?.business_email;
      if (!demoCustomerEmail) {
        toast.error("Please set up your notification email in Settings first");
        setSendingDemo(false);
        return;
      }
      
      const demoData = {
        customer_name: demoName,
        customer_email: demoCustomerEmail,
        customer_phone: demoPhone,
        customer_address: demoAddress,
        message: demoMessage,
        selected_service_ids: selectedServices.map(s => s.id),
        quantities: quantities,
        pricing_snapshot: pricingSnapshot,
        custom_answers: Object.keys(customAnswers).length > 0 ? customAnswers : undefined,
        photo_urls: demoPhotos,  // Always pass array (empty or with photos)
        is_demo: true // Flag for backend to add [Demo Request] to subject
      };
      
      await api.createLead(demoData, slug);
      setShowDemoSuccess(true); // Show prominent popup instead of toast
      fetchData(); // Refresh to show the new demo lead
    } catch (error) {
      toast.error("Failed to send demo request");
      console.error("Demo submission error:", error);
    } finally {
      setSendingDemo(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  // If business data failed to load, redirect
  if (!business) {
    localStorage.removeItem('token');
    localStorage.removeItem('businessSlug');
    toast.error("Account not found. Please sign up or log in again.");
    navigate("/get-started");
    return null;
  }

  // Check if trial has expired
  const isTrialExpired = () => {
    if (business.subscription_status !== 'trialing') return false;
    if (!business.trial_ends_at) return false;
    return new Date(business.trial_ends_at) < new Date();
  };

  // Calculate days left in trial
  const getTrialDaysLeft = () => {
    if (!business.trial_ends_at) return 0;
    const endDate = new Date(business.trial_ends_at);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Handle upgrade to paid plan - opens plan picker dialog
  const handleUpgrade = () => {
    setPlanPickerInterval("monthly");
    setShowPlanPicker(true);
  };

  // Execute checkout for the selected plan
  const handlePlanCheckout = async (interval) => {
    try {
      setPlanPickerLoading(true);
      const result = await api.createSubscriptionCheckout(slug, interval);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Unable to start checkout. Please try again.");
    } finally {
      setPlanPickerLoading(false);
    }
  };

  // If trial expired, show upgrade screen instead of dashboard
  if (isTrialExpired()) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center pb-2">
            <div className="text-5xl mb-4">⏰</div>
            <CardTitle className="text-2xl">Your Free Trial Has Ended</CardTitle>
            <CardDescription className="text-base mt-2">
              Your 10-day trial of ZipLeadPro has expired, but your data is safe!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm">
                <strong>Don't worry!</strong> All your leads, quotes, and settings are preserved. 
                Subscribe now to pick up right where you left off.
              </p>
            </div>
            
            {/* Plan toggle */}
            <div className="flex items-center justify-center gap-3 p-3 bg-gray-50 rounded-lg">
              <button
                onClick={() => setPlanPickerInterval("monthly")}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${planPickerInterval === "monthly" ? "bg-slate-900 text-white shadow-md" : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300"}`}
                data-testid="expired-plan-monthly-btn"
              >
                $29/mo
              </button>
              <button
                onClick={() => setPlanPickerInterval("annual")}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${planPickerInterval === "annual" ? "bg-slate-900 text-white shadow-md" : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300"}`}
                data-testid="expired-plan-annual-btn"
              >
                $290/yr
                <span className="ml-1 text-xs text-blue-500 font-normal">Save $58</span>
              </button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-center mb-3">
                <span className="text-3xl font-bold text-gray-900">{planPickerInterval === "annual" ? "$290" : "$29"}</span>
                <span className="text-gray-600">{planPickerInterval === "annual" ? "/year" : "/month"}</span>
                {planPickerInterval === "annual" && (
                  <p className="text-xs text-blue-700 font-medium mt-1">That's just $24.17/mo — save $58/year</p>
                )}
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Custom intake form — collect photos, details & info before you ever show up
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Instant quotes with built-in deposits & payment links
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Job scheduling — book the work, not just the lead
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  One link handles intake to payment — no extra tools needed
                </li>
              </ul>
            </div>
            
            <Button 
              onClick={() => handlePlanCheckout(planPickerInterval)}
              disabled={planPickerLoading}
              className="w-full bg-slate-900 hover:bg-slate-900 text-white py-6 text-lg font-semibold"
              data-testid="upgrade-btn"
            >
              {planPickerLoading ? "Loading..." : `Subscribe Now - ${planPickerInterval === "annual" ? "$290/year" : "$29/month"}`}
            </Button>
            
            <p className="text-center text-xs text-gray-500">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sidebarItems = [
    { id: "overview", label: "Dashboard", icon: LayoutDashboard },
    { id: "leads", label: "Leads & Quotes", icon: Users },
    { id: "calendar", label: "Calendar", icon: CalendarIcon },
    { id: "routes", label: "Routes & Crews", icon: MapPin },
    { id: "stripe", label: "Payment", icon: CreditCard },
    { id: "setup", label: "Set Up / Settings", icon: SettingsIcon, tourId: "setup-menu" },
    ...(business?.chemicals_enabled ? [{ id: "chemicals", label: "Chemicals", icon: FlaskConical }] : []),
    { id: "faq", label: "FAQ", icon: HelpCircle },
  ];
  
  // Tour - show for new users who haven't completed OR skipped the tour
  // Use simpler key without timestamp for consistency
  const tourCompletedKey = `zipleadpro_tour_${slug}`;
  const tourSkippedKey = `zipleadpro_tour_skipped_${slug}`;
  if (!SHOW_SETUP_TOUR && !localStorage.getItem(tourCompletedKey) && !localStorage.getItem(tourSkippedKey)) {
    localStorage.setItem(tourSkippedKey, 'true');
  }
  const shouldShowTour = !localStorage.getItem(tourCompletedKey) && !localStorage.getItem(tourSkippedKey);

  return (
    <div className="dashboard-shell bg-slate-50 flex">
      <SEO 
        title={business?.name ? `${business.name} Dashboard` : "Dashboard"}
        description="Manage your leads, send quotes, and schedule jobs from your ZipLeadPro dashboard."
        path={`/dashboard/${slug}`}
        noIndex={true}
      />
      {/* Product Tour for new users */}
      {SHOW_SETUP_TOUR && <ProductTour 
        key={`tour-${slug}`}
        slug={slug} 
        isNewUser={shouldShowTour} 
        onComplete={() => {
          // Also save completion here as backup
          localStorage.setItem(tourCompletedKey, 'true');
          setShowTour(false);
          setTourJustCompleted(true);
        }}
        onClosePreview={() => setClosePreviewTrigger(t => t + 1)}
        setActiveSection={setActiveSection}
        businessCreatedAt={business?.created_at}
        activeSection={activeSection}
        services={services}
        isPreviewOpen={tourPreviewOpen}
        formMode={tourFormMode}
        formUrl={formUrl}
        isEditorExpanded={tourEditorExpanded}
      />}
      
      {/* Celebration Popup - Shows on ANY section when setup completes */}
      {SHOW_LINK_LIVE_POPUP && showCelebration && (
        <Dialog open={showCelebration} onOpenChange={setShowCelebration}>
          <DialogContent className="max-w-md text-center z-[10002]">
            <div className="py-4">
              <div className="text-5xl mb-4">🎉</div>
              <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">Your Request Link Is Live</DialogTitle>
              <p className="text-gray-600 mb-4">
                This is the link customers use to submit job requests. You'll receive each request instantly by email.
              </p>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-blue-200">
                <p className="text-sm text-gray-600 mb-2 font-medium">Your custom request link:</p>
                <div className="flex gap-2">
                  <Input value={formUrl} readOnly className="text-sm font-medium bg-white" />
                  <Button 
                    onClick={() => {
                      navigator.clipboard.writeText(formUrl);
                      toast.success("Link copied to clipboard!");
                    }}
                    className="bg-slate-900 hover:bg-slate-900"
                  >
                    Copy
                  </Button>
                </div>
              </div>
              
              {SHOW_WATCH_IT_WORK && (<>
              <p className="text-base font-semibold text-gray-800 mb-4">
                Now let's watch it work.
              </p>
              
              <Button 
                onClick={async () => {
                  await handleDemoSubmission();
                  setShowCelebration(false);
                }}
                disabled={sendingDemo}
                className="w-full bg-slate-900 hover:bg-slate-900 text-white font-semibold mb-3"
                data-testid="watch-it-work-popup-btn"
              >
                {sendingDemo ? "Sending Demo..." : "Watch It Work"}
              </Button>
              </>)}
              
              <button 
                onClick={() => setShowCelebration(false)}
                className="text-sm text-gray-500 hover:text-gray-700 underline cursor-pointer"
                data-testid="customize-more-first-btn"
              >
                {SHOW_WATCH_IT_WORK ? "Customize more first" : "Continue"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Demo Success Popup - Prominent notification for first-time users */}
      {showDemoSuccess && (
        <Dialog open={showDemoSuccess} onOpenChange={setShowDemoSuccess}>
          <DialogContent className="max-w-md text-center z-[10002]">
            <div className="py-4">
              <div className="text-5xl mb-4">📬</div>
              <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">Check Your Inbox!</DialogTitle>
              <p className="text-gray-600 mb-4">
                A demo job request was just sent to <span className="font-semibold text-slate-900">{business?.notify_email || business?.business_email}</span>
              </p>
              <p className="text-sm text-gray-500 mb-4">
                This is exactly what you'll receive when a real customer submits a request through your form. Open the email to see the full experience!
              </p>
              <p className="text-xs text-gray-400 mb-6">
                Tip: When you send a quote, it will include any PDF or video links you've configured in the Quote Resources section.
              </p>
              
              <Button 
                onClick={() => setShowDemoSuccess(false)}
                className="w-full bg-slate-900 hover:bg-slate-900 text-white font-semibold"
                data-testid="demo-success-got-it-btn"
              >
                Got It!
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Terms Acceptance Modal for Existing Users */}
      {showTermsModal && (
        <Dialog open={showTermsModal} onOpenChange={() => {}}>
          <DialogContent className="max-w-lg z-[10003]" hideCloseButton={true} onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Terms of Service Agreement</DialogTitle>
            </DialogHeader>
            
            {!showFullTerms ? (
              <div className="space-y-4 py-2">
                <p className="text-sm text-gray-600">
                  We've updated our Terms of Service. Please review and accept to continue using ZipLeadPro.
                </p>
                
                <div className="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3 text-xs text-gray-600 space-y-2">
                  <p><strong>Key Terms:</strong></p>
                  <p>• ZipLeadPro is a software tool and does not guarantee business results</p>
                  <p>• You are responsible for your business operations, services, and customer communications</p>
                  <p>• Payments are processed by third-party processors (Stripe); we do not hold funds</p>
                  <p>• By using the platform, you grant us license to process and store your data</p>
                  <p>• Disputes are resolved through binding arbitration under Iowa law</p>
                  <p className="pt-2"><strong>If you create an account on behalf of a business, organization, or trade name, you represent that you are authorized to accept these Terms on its behalf.</strong></p>
                </div>
                
                <button 
                  onClick={() => setShowFullTerms(true)}
                  className="text-sm text-slate-900 hover:text-slate-900 underline"
                >
                  Read Full Terms of Service and Privacy Policy
                </button>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Your Full Legal Name *</Label>
                  <Input
                    value={termsForm.signatory_name}
                    onChange={(e) => setTermsForm({ ...termsForm, signatory_name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-400">Person authorized to accept terms on behalf of the business</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms_modal_accept"
                      checked={termsForm.terms_accepted}
                      onChange={(e) => setTermsForm({ ...termsForm, terms_accepted: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-gray-300"
                    />
                    <label htmlFor="terms_modal_accept" className="text-sm text-gray-700">
                      I agree to the{" "}
                      <button onClick={() => setShowFullTerms(true)} className="text-slate-900 hover:text-slate-900 underline">
                        Terms of Service and Privacy Policy
                      </button>
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="disclaimer_modal_accept"
                      checked={termsForm.disclaimer_accepted}
                      onChange={(e) => setTermsForm({ ...termsForm, disclaimer_accepted: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-gray-300"
                    />
                    <label htmlFor="disclaimer_modal_accept" className="text-sm text-gray-700">
                      I understand ZipLeadPro is a software tool and does not guarantee business results
                    </label>
                  </div>
                </div>
                
                <Button 
                  onClick={handleTermsAccept}
                  disabled={termsSubmitting || !termsForm.signatory_name.trim() || !termsForm.terms_accepted || !termsForm.disclaimer_accepted}
                  className="w-full"
                >
                  {termsSubmitting ? "Saving..." : "Accept & Continue"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4 py-2">
                <div className="max-h-[60vh] overflow-y-auto prose prose-sm text-gray-700 space-y-3">
                  <p className="text-xs text-gray-500">Effective Date: March 12, 2026 | Platform: ZipLeadPro | Company: Baseline Holdings LLC</p>
                  
                  <p>These Terms of Service and Privacy Policy ("Terms") govern your access to and use of the ZipLeadPro platform ("Platform", "Service"), operated by Baseline Holdings LLC ("Company", "we", "our", or "us").</p>
                  
                  <p><strong>By creating an account, accessing the platform, or using the Service, you ("User", "Business", or "you") agree to be legally bound by these Terms. If you do not agree to these Terms, you must not use the platform.</strong></p>

                  <p><strong>If you create an account on behalf of a business, organization, or trade name, you represent that you are authorized to accept these Terms on its behalf. If no legal entity exists, these Terms apply to you individually.</strong></p>

                  <h4 className="font-semibold mt-3">1. Description of Service</h4>
                  <p className="text-sm">ZipLeadPro is a software platform that provides tools designed to help businesses streamline customer intake, quoting workflows, and optional payment collection. The platform may allow businesses to receive customer inquiries, collect project details and photos, generate quotes, communicate with customers, optionally collect deposits through third-party payment processors, and manage leads and service requests.</p>
                  <p className="text-sm">ZipLeadPro provides software tools only. Baseline Holdings LLC does not act as a contractor, broker, agent, payment processor, or service provider between businesses and their customers.</p>

                  <h4 className="font-semibold mt-3">2. Business Responsibility</h4>
                  <p className="text-sm">Businesses using the platform are solely responsible for their operations including: services offered to customers, quotes, pricing, and agreements, work performed for customers, compliance with all applicable laws, tax reporting and tax collection, and customer communications and obligations.</p>
                  <p className="text-sm"><strong>ZipLeadPro does not guarantee leads, customers, bookings, or revenue. The platform is a productivity tool and not a lead generation guarantee.</strong></p>

                  <h4 className="font-semibold mt-3">3. Payments and Financial Transactions</h4>
                  <p className="text-sm">ZipLeadPro may integrate with third-party payment processors such as Stripe. All payments and deposits are processed directly between the business and the payment processor. Baseline Holdings LLC does not hold customer funds, does not process payments directly, does not control payment processor policies, and is not responsible for payment failures, chargebacks, or financial disputes.</p>

                  <h4 className="font-semibold mt-3">4. Platform Availability</h4>
                  <p className="text-sm">ZipLeadPro is a cloud-based platform and may experience interruptions. Baseline Holdings LLC does not guarantee uninterrupted service availability. Users acknowledge that use of the platform occurs at their own risk.</p>

                  <h4 className="font-semibold mt-3">5. Data Collection</h4>
                  <p className="text-sm">The platform collects and stores information necessary to operate the service including business account information, customer contact information, project descriptions, uploaded photos or documents, usage data and analytics, and payment-related metadata.</p>

                  <h4 className="font-semibold mt-3">6. Data Use and Commercial Rights</h4>
                  <p className="text-sm">By using the platform, you grant Baseline Holdings LLC a perpetual, irrevocable, worldwide, royalty-free license to collect, store, process, analyze, distribute, license, commercialize, share, or sell data collected through the platform. Use of the platform constitutes consent to this data use.</p>

                  <h4 className="font-semibold mt-3">7-9. Platform Usage Data, Security & Retention</h4>
                  <p className="text-sm">The platform may automatically collect technical data including IP addresses, browser type, device type, and activity logs. Baseline Holdings LLC takes reasonable measures to protect stored information but no online system can be guaranteed to be completely secure. Data may be retained even after accounts are closed.</p>

                  <h4 className="font-semibold mt-3">10-11. Acceptable Use & Account Termination</h4>
                  <p className="text-sm">Users may not use the platform to conduct illegal activities, misrepresent services, collect data for fraudulent purposes, or interfere with platform functionality. Accounts violating these rules may be suspended or terminated.</p>

                  <h4 className="font-semibold mt-3">12. Intellectual Property</h4>
                  <p className="text-sm">All software, systems, branding, and technology related to ZipLeadPro are the exclusive property of Baseline Holdings LLC.</p>

                  <h4 className="font-semibold mt-3">13. Limitation of Liability</h4>
                  <p className="text-sm">To the maximum extent permitted by law, Baseline Holdings LLC shall not be liable for any indirect, incidental, special, or consequential damages. If liability is found, the maximum total liability shall not exceed the amount paid for the service during the 30 days preceding the claim.</p>

                  <h4 className="font-semibold mt-3">14-16. Indemnification, Arbitration & Class Action Waiver</h4>
                  <p className="text-sm">Users agree to defend and indemnify Baseline Holdings LLC from any claims arising from their use of the platform. Any disputes shall be resolved through binding arbitration. Users waive the right to participate in any class action against Baseline Holdings LLC.</p>

                  <h4 className="font-semibold mt-3">17-18. Governing Law & Changes</h4>
                  <p className="text-sm">These Terms shall be governed by the laws of the State of Iowa. Baseline Holdings LLC may update these Terms at any time. Continued use constitutes acceptance.</p>

                  <h4 className="font-semibold mt-3">Contact</h4>
                  <p className="text-sm">Questions regarding these Terms may be directed to: <strong>help@zipleadpro.com</strong></p>
                </div>
                
                <Button onClick={() => setShowFullTerms(false)} className="w-full">
                  Back to Agreement
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
      
      {/* Post-Celebration Encouragement Modal */}
      {showPostCelebration && (
        <Dialog open={showPostCelebration} onOpenChange={setShowPostCelebration}>
          <DialogContent className="max-w-md text-center z-[10002]">
            <div className="py-4">
              <div className="text-5xl mb-4">🏆</div>
              <DialogTitle className="text-2xl font-bold text-gray-900 mb-4">You're Already Ahead</DialogTitle>
              <p className="text-gray-600 mb-6">
                Most businesses stop here. But the ones who stand out? They add custom questions that qualify leads instantly, upload a logo that builds trust on sight, and dial in every detail until their form converts like crazy.
              </p>
              
              <Button 
                onClick={() => setShowPostCelebration(false)} 
                className="w-full bg-slate-900 hover:bg-slate-900 text-white font-semibold"
              >
                Keep Exploring
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* iOS Install Instructions Modal */}
      {showIOSInstallModal && (
        <Dialog open={showIOSInstallModal} onOpenChange={setShowIOSInstallModal}>
          <DialogContent className="max-w-sm text-center z-[10002]">
            <div className="py-4">
              <div className="text-5xl mb-4">📲</div>
              <DialogTitle className="text-xl font-bold text-gray-900 mb-4">Add ZipLeadPro to Home Screen</DialogTitle>
              <div className="text-left space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-900 font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Tap the Share button</p>
                    <p className="text-sm text-gray-500">Look for the square (with an arrow pointing) up on your screen</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-900 font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Tap "Add to Home Screen"</p>
                    <p className="text-sm text-gray-500">Scroll down in the share menu to find this option</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-900 font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Tap "Add"</p>
                    <p className="text-sm text-gray-500">ZipLeadPro will appear on your home screen like a regular app</p>
                  </div>
                </div>
              </div>
              <Button 
                onClick={() => setShowIOSInstallModal(false)} 
                className="w-full bg-slate-900 hover:bg-slate-900 text-white"
              >
                Got it!
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Second Login Install Reminder */}
      {showInstallReminder && !isAppInstalled && (
        <Dialog open={showInstallReminder} onOpenChange={setShowInstallReminder}>
          <DialogContent className="max-w-sm text-center z-[10002]">
            <div className="py-4">
              <div className="text-5xl mb-4">📱</div>
              <DialogTitle className="text-xl font-bold text-gray-900 mb-2">Quick Access to Your Leads</DialogTitle>
              <p className="text-gray-600 mb-6">
                Add ZipLeadPro to your home screen for instant access. Open it like any app—no browser needed.
              </p>
              <div className="space-y-2">
                <Button 
                  onClick={() => {
                    setShowInstallReminder(false);
                    handleInstallClick();
                  }} 
                  className="w-full bg-slate-900 hover:bg-slate-900 text-white"
                >
                  Add to Home Screen
                </Button>
                <button 
                  onClick={dismissInstallReminderForever}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
                >
                  Don't show this again
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Notification Reminder Popup - One-time after first login */}
      {showNotificationPopup && !showCelebration && (
        <NotificationReminder 
          variant="popup" 
          onDismiss={() => {
            handleDismissNotificationPopup();
            setActiveSection("setup");
          }} 
        />
      )}

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-slate-600 hover:text-slate-900"
          data-testid="mobile-menu-btn"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          {business?.logo_url && (
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}${business.logo_url}`} 
              alt="" 
              className="w-7 h-7 object-contain rounded"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <div className="flex flex-col items-start">
            <h1 className="font-heading text-base font-semibold text-slate-900 truncate max-w-[150px] tracking-tight">{business?.name || "Dashboard"}</h1>
            <span className="text-[9px] text-slate-400">Powered by <span className="text-blue-800">ZipLeadPro</span></span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600">
          Logout
        </Button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-slate-200 flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            {business?.logo_url && (
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${business.logo_url}`} 
                alt={business?.name || "Logo"} 
                className="w-10 h-10 object-contain rounded-lg bg-slate-50 p-1 flex-shrink-0"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-lg font-semibold text-slate-900 truncate tracking-tight">{business?.name || "Dashboard"}</h1>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 p-2 overflow-y-auto">
          {/* Trial countdown banner */}
          {business?.subscription_status === 'trialing' && getTrialDaysLeft() > 0 && (
            <div className={`mb-3 p-3 rounded-md text-center ${
              getTrialDaysLeft() <= 3 
                ? 'bg-amber-50 border border-amber-200' 
                : 'bg-blue-50 border border-blue-200'
            }`}>
              <p className={`text-xs font-medium ${
                getTrialDaysLeft() <= 3 ? 'text-amber-800' : 'text-slate-900'
              }`}>
                {getTrialDaysLeft()} day{getTrialDaysLeft() !== 1 ? 's' : ''} left in trial
              </p>
              <button
                onClick={handleUpgrade}
                className={`mt-2 w-full text-xs py-1.5 px-3 rounded-md font-medium transition-colors ${
                  getTrialDaysLeft() <= 3
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : 'bg-slate-900 hover:bg-slate-900 text-white'
                }`}
              >
                Upgrade Now
              </button>
            </div>
          )}
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const active = activeSection === item.id;
            return (
              <button
                key={item.id}
                data-tour={item.tourId}
                data-testid={`sidebar-${item.id}-btn`}
                onClick={() => {
                  setActiveSection(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm mb-1 transition-colors ${
                  active
                    ? "bg-blue-50 text-slate-900 font-medium"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <Icon className={`w-[18px] h-[18px] ${active ? 'text-blue-800' : 'text-slate-500'}`} strokeWidth={2} />
                <span>{item.label}</span>
              </button>
            );
          })}
          
          {/* Quick Link Section in Sidebar */}
          {isSetupComplete && formUrl ? (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200" data-tour="share-section">
              <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5 text-blue-800" /> Your Request Link</p>
              <div className="flex gap-1">
                <Input 
                  value={formUrl} 
                  readOnly 
                  className="text-xs h-8 bg-white"
                />
                <Button 
                  size="sm"
                  className="h-8 px-2 bg-slate-900 hover:bg-slate-900 text-white"
                  onClick={() => {
                    navigator.clipboard.writeText(formUrl);
                    toast.success("Copied!");
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Your Request Link</p>
              <p className="text-xs text-slate-400">Complete your setup to unlock your unique link</p>
            </div>
          )}
          
          {/* Watch It Work - Demo Submission Button */}
          {SHOW_WATCH_IT_WORK && isSetupComplete && (
            <button
              onClick={handleDemoSubmission}
              disabled={sendingDemo}
              data-testid="sidebar-watch-it-work-btn"
              className="w-full mt-3 px-4 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-center transition-colors border border-amber-600/20"
            >
              <div className="flex items-center justify-center gap-2 mb-0.5">
                <Zap className="w-4 h-4" strokeWidth={2.5} />
                <span className="font-semibold text-sm tracking-tight">Watch It Work</span>
              </div>
              <div className="text-[11px] font-medium opacity-90">
                {sendingDemo ? "Sending..." : "Process a demo job now"}
              </div>
            </button>
          )}
          
          {/* Add to Home Screen button - visible on all devices */}
          {!isAppInstalled && (
            <button
              onClick={() => {
                handleInstallClick();
                setSidebarOpen(false);
              }}
              className="w-full mt-4 text-sm text-slate-900 hover:text-slate-900 py-2 flex items-center justify-center gap-2 bg-blue-50 rounded-md border border-blue-200 transition-colors"
            >
              <Smartphone className="w-4 h-4" /> Add to Home Screen
            </button>
          )}
        </nav>
        <div className="p-4 border-t border-slate-200 space-y-2">
          {SHOW_SETUP_TOUR && <button
            onClick={() => {
              // Use the same function that clears all tour-related keys
              clearTourStateForSlug(slug);
              setShowTour(true);
              setSidebarOpen(false);
              window.location.reload();
            }}
            className="w-full text-xs text-slate-500 hover:text-blue-800 py-1 flex items-center justify-center gap-1.5"
            data-testid="restart-tutorial-btn"
          >
            <GraduationCap className="w-3.5 h-3.5" /> Restart Tutorial
          </button>}
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
          <p className="text-center text-[10px] text-slate-400 pt-2">Powered by <span className="font-medium text-blue-800">ZipLeadPro</span></p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-16 lg:pt-0" style={{ overscrollBehavior: 'contain' }}>
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
          {/* Dock Navigation - mobile only */}
          <div className="lg:hidden mb-4">
            <DockNavigation 
              items={sidebarItems} 
              activeId={activeSection} 
              onSelect={setActiveSection} 
            />
          </div>
          
          <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
            {activeSection === "overview" && (
              <>
                <DashboardOverview leads={leads} quotes={quotes} services={services} stripeStatus={stripeStatus} slug={slug} business={business} setActiveSection={setActiveSection} unscheduledJobs={unscheduledJobs} onRefresh={fetchData} onSendQuote={() => { setActiveSection("leads"); setOpenQuoteDialog(true); }} onShowPlanPicker={handleUpgrade} />
                <div className="mt-6">
                  <UpcomingChargesCard slug={slug} />
                </div>
              </>
            )}
            {activeSection === "stripe" && (
              <StripeConnectSection slug={slug} stripeStatus={stripeStatus} onRefresh={fetchData} />
            )}
            {activeSection === "setup" && (
              <SetupSection 
                slug={slug} 
                business={business} 
                services={services} 
                onRefresh={fetchData}
                isStep1Complete={isStep1Complete}
                isStep2Complete={isStep2Complete}
                isBothStepsComplete={isBothStepsComplete}
                onStep2Complete={markStep2Complete}
                onPreviewOpen={() => setTourPreviewOpen(true)}
                onFormModeChange={(mode) => setTourFormMode(mode)}
                onEditorExpand={() => setTourEditorExpanded(true)}
                closePreviewTrigger={closePreviewTrigger}
                annualPricingEnabled={annualPricingEnabled}
                onDemoSubmission={handleDemoSubmission}
                demoLoading={sendingDemo}
                tourActive={showTour}
              />
            )}
            {activeSection === "leads" && (
              <LeadsSection slug={slug} leads={leads} quotes={quotes} services={services} stripeStatus={stripeStatus} onRefresh={fetchData} setActiveSection={setActiveSection} business={business} openQuoteDialog={openQuoteDialog} setOpenQuoteDialog={setOpenQuoteDialog} quoteForLeadId={quoteForLeadId} setQuoteForLeadId={setQuoteForLeadId} />
            )}
            {activeSection === "calendar" && (
              <CalendarSection 
                unscheduledJobs={unscheduledJobs} 
                scheduledJobs={scheduledJobs} 
                onRefresh={fetchData}
                slug={slug}
                business={business}
                services={services}
              />
            )}
            {activeSection === "routes" && (
              <RoutesSection 
                scheduledJobs={scheduledJobs}
                slug={slug}
                onRefresh={fetchData}
                initialTab={searchParams.get("tab")}
                searchParams={searchParams}
              />
            )}
            {activeSection === "email-template" && (
              <EmailTemplateSection slug={slug} />
            )}
            {activeSection === "faq" && (
              <div className="space-y-6">
                <h1 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h1>
                <p className="text-gray-600">Quick answers to help you get the most out of ZipLeadPro.</p>
                <FAQSection />
              </div>
            )}
            {activeSection === "chemicals" && business?.chemicals_enabled && (
              <ChemicalsTab slug={slug} business={business} />
            )}
          </div>
        </div>
      </main>

      {/* Subscription Required Modal — replaces the easy-to-miss toast.
          Triggered by:
          (a) Clicking any locked action button (Send Quote / Build Quote / Collect Balance / Manual Approve)
          (b) Any 402 response from the backend subscription gate
          Stays open until the user takes action — no auto-dismiss.
          Copy adapts to the three distinct states (trial ended / paused / canceled)
          so the message feels right for that user's situation — a trial user
          isn't "reactivating", they're starting their plan for the first time. */}
      {(() => {
        const status = business?.subscription_status;
        const trialEnded = status === "trialing"; // we only open this modal when sub is inactive, so trialing here means expired
        const variant = trialEnded
          ? {
              heading: "Your Free Trial Has Ended",
              subhead: "10-day trial complete",
              body: "Your free trial is up. To keep sending quotes, charging cards, and approving on behalf of customers, choose a plan below. Your leads, quotes, customers, and history are all safe.",
              cta: "Choose Your Plan",
            }
          : status === "canceled"
          ? {
              heading: "Subscription Canceled",
              subhead: "Your subscription was canceled",
              body: "Your ZipLeadPro subscription is canceled. To resume sending quotes, charging cards, and approving on behalf of customers, reactivate your plan. Your leads, quotes, customers, and history are all safe and waiting.",
              cta: "Reactivate Subscription",
            }
          : status === "paused"
          ? {
              heading: "Subscription Paused",
              subhead: "Your subscription is paused",
              body: "Your ZipLeadPro subscription is paused. To resume sending quotes, charging cards, and approving on behalf of customers, reactivate your plan. Your leads, quotes, customers, and history are all safe and waiting.",
              cta: "Reactivate Subscription",
            }
          : {
              heading: "Subscription Required",
              subhead: "Your subscription is inactive",
              body: "To send quotes, charge cards, or approve on behalf of customers, please reactivate your ZipLeadPro subscription. Your leads, quotes, customers, and history are all safe and waiting.",
              cta: "Reactivate Subscription",
            };
        return (
          <Dialog open={showSubRequiredModal} onOpenChange={setShowSubRequiredModal}>
            <DialogContent
              className="sm:max-w-md p-0 overflow-hidden"
              data-testid="sub-required-dialog"
              onPointerDownOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <div className="bg-slate-900 px-6 py-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                  <Lock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-white font-heading font-bold text-lg leading-tight" data-testid="sub-required-heading">{variant.heading}</h2>
                  <p className="text-slate-300 text-xs mt-0.5" data-testid="sub-required-subhead">{variant.subhead}</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-slate-700 text-sm leading-relaxed mb-4" data-testid="sub-required-body">
                  {variant.body}
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-5">
                  <p className="text-xs text-slate-600 leading-relaxed">
                    <span className="font-semibold text-slate-900">$29/month</span> or <span className="font-semibold text-slate-900">$290/year</span> · No contracts · Cancel anytime
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => { setShowSubRequiredModal(false); handleUpgrade(); }}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-5"
                    data-testid="sub-required-subscribe-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" strokeWidth={2.5} />
                    {variant.cta}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowSubRequiredModal(false)}
                    data-testid="sub-required-close-btn"
                  >
                    Not now
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* AI Assistant - Hidden until behavior is improved
      <ZipLeadProAssistant 
        slug={slug} 
        onNavigate={(section) => setActiveSection(section)} 
        onRefresh={fetchData} 
      />
      */}
      
      {/* Plan Picker Dialog */}
      <Dialog open={showPlanPicker} onOpenChange={setShowPlanPicker}>
        <DialogContent className="sm:max-w-md" data-testid="plan-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Choose Your Plan</DialogTitle>
            <DialogDescription className="text-center">
              Pick the plan that works best for you
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Plan toggle */}
            <div className="flex items-center justify-center gap-3 p-3 bg-gray-50 rounded-lg">
              <button
                onClick={() => setPlanPickerInterval("monthly")}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${planPickerInterval === "monthly" ? "bg-slate-900 text-white shadow-md" : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300"}`}
                data-testid="plan-picker-monthly-btn"
              >
                $29/mo
              </button>
              <button
                onClick={() => setPlanPickerInterval("annual")}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${planPickerInterval === "annual" ? "bg-slate-900 text-white shadow-md" : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300"}`}
                data-testid="plan-picker-annual-btn"
              >
                $290/yr
                <span className="ml-1 text-xs text-blue-500 font-normal">Save $58</span>
              </button>
            </div>

            {/* Price display */}
            <div className="text-center py-3">
              <span className="text-4xl font-bold text-gray-900">{planPickerInterval === "annual" ? "$290" : "$29"}</span>
              <span className="text-gray-500 text-lg ml-1">{planPickerInterval === "annual" ? "/year" : "/month"}</span>
              {planPickerInterval === "annual" && (
                <p className="text-sm text-blue-700 font-medium mt-1">That's just $24.17/mo — save $58/year</p>
              )}
            </div>

            {/* Features */}
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Custom intake form — collect photos, details & info before you ever show up
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Instant quotes with built-in deposits & payment links
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Job scheduling — book the work, not just the lead
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                One link handles intake to payment — no extra tools needed
              </li>
            </ul>

            {/* Subscribe button */}
            <Button
              onClick={() => handlePlanCheckout(planPickerInterval)}
              disabled={planPickerLoading}
              className="w-full bg-slate-900 hover:bg-slate-900 py-5 text-base font-semibold"
              data-testid="plan-picker-subscribe-btn"
            >
              {planPickerLoading ? "Loading..." : `Subscribe Now - ${planPickerInterval === "annual" ? "$290/year" : "$29/month"}`}
            </Button>
            <p className="text-xs text-center text-gray-500">
              Secure checkout via Stripe. Cancel anytime, no contracts.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== NOTIFICATION REMINDER COMPONENT ==============
// ============== DOCK NAVIGATION COMPONENT (macOS-style) ==============
const DockNavigation = ({ items, activeId, onSelect }) => {
  const [hoverIndex, setHoverIndex] = useState(-1);
  const [touchX, setTouchX] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [isTap, setIsTap] = useState(true);
  const dockRef = useRef(null);
  
  // Calculate scale based on distance from hover/touch point - more subtle, premium feel
  const getScale = (index) => {
    if (hoverIndex === -1 && touchX === null) {
      return activeId === items[index].id ? 1.05 : 1;
    }
    
    const distance = Math.abs(index - hoverIndex);
    if (distance === 0) return 1.3;
    if (distance === 1) return 1.15;
    if (distance === 2) return 1.05;
    return 1;
  };
  
  // Track touch start to detect scrolls vs taps
  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
    setIsTap(true);
  };
  
  // Handle touch move for dock magnification
  const handleTouchMove = (e) => {
    if (!dockRef.current) return;
    const touch = e.touches[0];
    
    // If vertical movement > 10px, it's a scroll not a tap
    if (touchStartY !== null && Math.abs(touch.clientY - touchStartY) > 10) {
      setIsTap(false);
      setHoverIndex(-1);
      setTouchX(null);
      return;
    }
    
    const rect = dockRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const itemWidth = rect.width / items.length;
    const index = Math.floor(x / itemWidth);
    if (index >= 0 && index < items.length) {
      setHoverIndex(index);
      setTouchX(x);
    }
  };
  
  const handleTouchEnd = () => {
    // Only select if it was a tap, not a scroll-through
    if (isTap && hoverIndex >= 0 && hoverIndex < items.length) {
      onSelect(items[hoverIndex].id);
    }
    setHoverIndex(-1);
    setTouchX(null);
    setTouchStartY(null);
    setIsTap(true);
  };
  
  return (
    <div 
      ref={dockRef}
      className="flex items-end justify-center gap-2 px-5 py-3 bg-white rounded-2xl mx-auto border border-slate-200"
      style={{ maxWidth: 'fit-content' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => { setHoverIndex(-1); setTouchX(null); setTouchStartY(null); setIsTap(true); }}
      onMouseLeave={() => setHoverIndex(-1)}
    >
      {items.map((item, index) => {
        const scale = getScale(index);
        const isActive = activeId === item.id;
        const Icon = item.icon;
        
        return (
          <button
            key={item.id}
            onClick={() => onSelect(item.id)}
            onMouseEnter={() => setHoverIndex(index)}
            className="flex flex-col items-center px-2 focus:outline-none"
            data-testid={`dock-${item.id}-btn`}
            style={{
              transform: `scale(${scale}) translateY(${scale > 1 ? -(scale - 1) * 15 : 0}px)`,
              transition: 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transformOrigin: 'bottom center',
            }}
          >
            <span 
              className={`mb-1 transition-all duration-300 ${isActive ? 'text-slate-900' : 'text-slate-500'}`}
            >
              {Icon ? <Icon className="w-6 h-6" strokeWidth={2} /> : null}
            </span>
            {/* Active indicator dot */}
            <div 
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                isActive ? 'bg-slate-900 scale-100' : 'bg-transparent scale-0'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};

const NotificationReminder = ({ variant = "card", onDismiss }) => {
  // Variants: "card" (dashboard), "banner" (settings), "popup" (one-time)
  
  if (variant === "banner") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-xl">🔔</span>
          <div className="flex-1">
            <p className="text-sm text-amber-900">
              <strong>Pro tip:</strong> Enable email notifications to get instant alerts when new leads come in!
            </p>
          </div>
          {onDismiss && (
            <button onClick={onDismiss} className="text-amber-600 hover:text-amber-800 text-sm">✕</button>
          )}
        </div>
      </div>
    );
  }
  
  if (variant === "popup") {
    return (
      <Dialog open={true} onOpenChange={onDismiss}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🔔</span> Don't Miss a Lead!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-700">
              ZipLeadPro works best when you're notified instantly about new leads. Enable notifications to:
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-600">✓</span>
                Get instant email alerts when customers request quotes
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600">✓</span>
                Never miss a time-sensitive opportunity
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600">✓</span>
                Respond faster than your competitors
              </li>
            </ul>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-sm text-slate-900">
                <strong>Quick setup:</strong> Go to Setup → Business Information and add your notification email
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onDismiss}>Maybe Later</Button>
            <Button onClick={() => { onDismiss(); /* navigate to settings handled by parent */ }}>
              Set Up Notifications
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Default: card variant
  return (
    <Card className="border-amber-200 bg-amber-50 mb-4 sm:mb-6" data-testid="notification-reminder-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="text-2xl">🔔</span>
          <div className="flex-1">
            <p className="font-bold text-amber-900 text-sm sm:text-base">Unlock ZipLeadPro's Full Potential!</p>
            <p className="text-xs sm:text-sm text-amber-800 mt-1">
              Enable notifications to get instant alerts for new leads and never miss an opportunity.
            </p>
            <div className="flex items-center gap-3 mt-3">
              <Badge className="bg-amber-600 text-white cursor-pointer hover:bg-amber-700">
                Enable Notifications →
              </Badge>
              {onDismiss && (
                <button onClick={onDismiss} className="text-xs text-amber-600 hover:text-amber-800 underline">
                  Remind me later
                </button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== DASHBOARD OVERVIEW ==============
const DashboardOverview = ({ leads, quotes, services, stripeStatus, slug, business, setActiveSection, unscheduledJobs = [], onRefresh, onSendQuote, onShowPlanPicker }) => {
  const [formUrl, setFormUrl] = useState("");
  const [showNotificationReminder, setShowNotificationReminder] = useState(true);
  const [showQRModal, setShowQRModal] = useState(false);
  
  // Job scheduling modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingJob, setSchedulingJob] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [schedulingLoading, setSchedulingLoading] = useState(false);
  
  const newLeads = leads.filter(l => l.status === "new").length;
  const activeQuotes = quotes.filter(q => q.status === "sent").length;
  const paidQuotes = quotes.filter(q => q.status === "paid").length;
  
  // Setup completion checks (for showing/hiding the link section)
  const hasBusinessName = business?.name && business.name.trim() !== "";
  const hasContactEmail = business?.notify_email && business.notify_email.trim() !== "";
  const hasServices = services.filter(s => s.active).length > 0;
  const isSetupComplete = hasBusinessName && hasContactEmail && hasServices;
  
  // Check if notification tips were dismissed in localStorage (reset every 7 days)
  const tipsDismissedKey = `notification_tips_dismissed_${slug}`;
  const tipsDismissedAt = localStorage.getItem(tipsDismissedKey);
  const tipsDismissedRecently = tipsDismissedAt && (Date.now() - parseInt(tipsDismissedAt)) < 7 * 24 * 60 * 60 * 1000;
  
  // Always show notification importance if email not set, or tips if not dismissed recently
  const shouldShowNotificationAlert = !hasContactEmail;
  const shouldShowNotificationTips = hasContactEmail && !tipsDismissedRecently && showNotificationReminder;
  
  const handleDismissReminder = () => {
    localStorage.setItem(tipsDismissedKey, Date.now().toString());
    setShowNotificationReminder(false);
  };
  
  useEffect(() => {
    // Fetch the clean public URL from the embed-code endpoint
    api.getEmbedCode(slug).then(data => {
      setFormUrl(data.form_url);
    }).catch(() => {
      // Fallback if API fails
      setFormUrl(`${window.location.origin}/api/share/${slug}`);
    });
  }, [slug]);
  
  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Dashboard Overview</h2>
      
      {/* Quick Action - Send Quote Button */}
      <div className="mb-4 sm:mb-6">
        {(() => {
          const locked = isSubInactive(business);
          return (
            <Button 
              onClick={locked ? () => { showSubRequired(); onShowPlanPicker && onShowPlanPicker(); } : onSendQuote}
              disabled={locked}
              aria-disabled={locked}
              title={locked ? SUB_LOCK_TOOLTIP : ""}
              className={`w-full sm:w-auto py-6 px-8 text-lg font-semibold rounded-xl shadow-lg transition-all ${locked ? "bg-slate-400 hover:bg-slate-400 text-white cursor-not-allowed opacity-80" : "bg-slate-900 hover:bg-slate-900 text-white hover:shadow-xl"}`}
              data-testid="quick-send-quote-btn"
            >
              {locked ? <Lock className="w-5 h-5 mr-2" /> : <span className="mr-2">⚡</span>}
              {locked ? "Reactivate to Send Quote" : "Send Quote"}
            </Button>
          );
        })()}
      </div>
      
      {/* Resubscription prompt for lapsed users - NOT for active trials */}
      {business?.subscription_status && 
       business.subscription_status !== "active" && 
       !(business.subscription_status === "trialing" && business.trial_ends_at && new Date(business.trial_ends_at) > new Date()) && (
        <Card className="mb-4 sm:mb-6 border-2 border-amber-300 bg-amber-50">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <span className="text-3xl">⚠️</span>
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 text-lg mb-2">
                  {business.subscription_status === "trialing" ? "Your Trial Has Expired" : "Your Subscription Has Ended"}
                </h3>
                <p className="text-amber-800 mb-3">
                  Your leads are still here, but every day without ZipLeadPro is a day you're quoting slower and leaving money on the table.
                </p>
                <div className="bg-white/70 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-700 mb-2"><strong>What you're missing right now:</strong></p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Intake forms that collect photos and job details before you show up</li>
                    <li>• One-click quotes with deposits that get you paid upfront</li>
                    <li>• Built-in scheduling so the job is booked, not just quoted</li>
                    <li>• A single link that handles everything — no juggling apps</li>
                  </ul>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <Button 
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={onShowPlanPicker}
                    data-testid="resubscribe-plan-picker-btn"
                  >
                    Subscribe Now
                  </Button>
                  <span className="text-xs text-amber-700">Pick up right where you left off — your data is waiting</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setActiveSection("leads")}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold">{leads.length}</div>
            <div className="text-xs sm:text-sm text-gray-500">Total Leads</div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:bg-blue-50 transition-colors"
          onClick={() => setActiveSection("leads")}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold text-blue-600">{newLeads}</div>
            <div className="text-xs sm:text-sm text-gray-500">New Leads</div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:bg-amber-50 transition-colors"
          onClick={() => setActiveSection("leads")}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold text-amber-600">{activeQuotes}</div>
            <div className="text-xs sm:text-sm text-gray-500">Pending Quotes</div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:bg-blue-50 transition-colors"
          onClick={() => setActiveSection("leads")}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold text-blue-700">{paidQuotes}</div>
            <div className="text-xs sm:text-sm text-gray-500">Paid Quotes</div>
          </CardContent>
        </Card>
      </div>

      {/* Stripe Status Alert - Clickable */}
      {!stripeStatus?.connected && (
        <Card 
          className="mb-4 sm:mb-6 border-blue-200 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => setActiveSection("stripe")}
        >
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💳</span>
              <div className="flex-1">
                <p className="font-bold text-slate-900 text-sm sm:text-base">Start Getting Paid Faster with Stripe</p>
                <p className="text-xs sm:text-sm text-slate-900 mt-1">Accept credit cards, collect deposits, and get funds deposited directly to your bank. Setup takes just 5 minutes.</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-slate-900 text-white">Connect Now →</Badge>
                  <span className="text-xs text-slate-900">Free to connect • Only pay when you get paid</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Stripe Pending Verification Alert */}
      {stripeStatus?.connected && !stripeStatus?.charges_enabled && (
        <Card 
          className="mb-4 sm:mb-6 border-blue-200 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => setActiveSection("stripe")}
        >
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
            <div>
              <p className="font-medium text-blue-800 text-sm sm:text-base">⏳ Stripe Verification In Progress</p>
              <p className="text-xs sm:text-sm text-blue-700">Your account is being verified (usually 1-2 business days). You can still send quotes without payment links.</p>
            </div>
            <Badge variant="outline" className="text-blue-700 border-blue-300 self-start sm:self-auto">View Status →</Badge>
          </CardContent>
        </Card>
      )}

      {/* CRITICAL: Notification Alert - Show if no notification email set */}
      {shouldShowNotificationAlert && (
        <Card className="mb-4 sm:mb-6 border-2 border-red-400 bg-red-50">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="text-3xl">🚨</span>
              <div className="flex-1">
                <p className="font-bold text-red-900 text-base sm:text-lg">CRITICAL: No Notification Email Set!</p>
                <p className="text-sm text-red-800 mt-1">
                  You are NOT receiving lead alerts! When customers submit requests, you won't know about it.
                  <strong> This means you're losing business to competitors right now.</strong>
                </p>
                <Button 
                  onClick={() => setActiveSection("setup")}
                  className="mt-3 bg-red-600 hover:bg-red-700"
                >
                  Set Up Notifications Now →
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification Best Practices - Show periodically even when email is set */}
      {shouldShowNotificationTips && (
        <Card className="mb-4 sm:mb-6 border border-amber-300 bg-amber-50">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📬</span>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <p className="font-bold text-amber-900 text-sm sm:text-base">Your Email is Your Command Center</p>
                  <button onClick={handleDismissReminder} className="text-amber-600 hover:text-amber-800 text-xs">Dismiss</button>
                </div>
                <p className="text-sm text-amber-800 mt-2">
                  Every lead lands straight in your inbox. Turn on push notifications so you never miss an opportunity.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setActiveSection("setup")}
        >
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="text-base sm:text-lg">Your Services</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl sm:text-3xl font-bold">{services.filter(s => s.active).length}</div>
            <p className="text-xs sm:text-sm text-gray-500">
              {services.length === 0 
                ? "Add services to speed up quoting →" 
                : `${services.length} total services`}
            </p>
          </CardContent>
        </Card>
        
        {/* Your Custom Request Link - Only show if setup is complete */}
        {isSetupComplete ? (
          // Check if link should be active (paid OR valid trial)
          (() => {
            const isActive = business?.subscription_status === "active";
            const isTrialing = business?.subscription_status === "trialing";
            const trialEndsAt = business?.trial_ends_at ? new Date(business.trial_ends_at) : null;
            const trialValid = isTrialing && trialEndsAt && trialEndsAt > new Date();
            const trialDaysLeft = trialEndsAt ? Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24)) : 0;
            const linkActive = isActive || trialValid;
            
            if (!linkActive) {
              // Trial expired or inactive - show locked link with upgrade prompt
              return (
                <Card className="border-2 border-blue-500 bg-blue-50">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-slate-900">
                      <span>🔒</span> {isTrialing ? "Your Trial Has Expired" : "Your Link is Ready to Go Live!"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="bg-white/80 rounded-lg p-3 mb-3">
                      <p className="text-sm text-gray-700 mb-2">
                        {isTrialing 
                          ? <><strong>Your 10-day trial has ended.</strong> Subscribe now to reactivate your link and keep receiving leads.</>
                          : <><strong>Great news!</strong> Your form is built and ready. Subscribe to unlock your link and start capturing leads.</>
                        }
                      </p>
                      <div className="text-xs text-gray-500 font-mono bg-gray-100 p-2 rounded flex items-center gap-2">
                        <span>🔗</span>
                        <span className="truncate">{formUrl || "Loading..."}</span>
                      </div>
                    </div>
                    
                    <div className="bg-blue-100 rounded-lg p-3 mb-4">
                      <p className="text-sm text-slate-900 font-semibold mb-2">Unlock starting at $24.17/mo:</p>
                      <ul className="text-xs text-slate-900 space-y-1">
                        <li>✓ Your intake link goes live — collect job details & photos 24/7</li>
                        <li>✓ Send quotes with deposits in seconds, not hours</li>
                        <li>✓ Built-in payment links — get paid before you show up</li>
                        <li>✓ Schedule jobs right from your dashboard</li>
                      </ul>
                    </div>
                    
                    <Button 
                      className="w-full bg-slate-900 hover:bg-slate-900 text-white font-semibold py-5"
                      onClick={onShowPlanPicker}
                      data-testid="locked-link-plan-picker-btn"
                    >
                      Subscribe & Go Live
                    </Button>
                    
                    <p className="text-xs text-center text-gray-500 mt-2">
                      Secure payment via Stripe. Cancel anytime.
                    </p>
                  </CardContent>
                </Card>
              );
            }
            
            // Active subscription OR valid trial - show normal link
            return (
              <Card className={isTrialing ? "border-2 border-amber-300" : ""}>
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">Your Custom Request Link</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Trial Banner */}
                  {isTrialing && trialValid && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-sm font-semibold text-amber-800">
                            ⏰ Trial: {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left
                          </p>
                          <p className="text-xs text-amber-700">Your link is live! Subscribe before it expires.</p>
                        </div>
                        <Button 
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={onShowPlanPicker}
                          data-testid="trial-banner-plan-picker-btn"
                        >
                          Subscribe Now
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                    Share this link on your website, social media, or text it to customers:
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input 
                      value={formUrl || "Loading..."}
                      readOnly 
                      className="text-xs flex-1"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => {
                        if (formUrl) {
                          navigator.clipboard.writeText(formUrl);
                          toast.success("Link copied to clipboard!");
                        }
                      }}
                    >
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => setShowQRModal(true)}
                    >
                      QR Code
                    </Button>
                  </div>
                  
                  {/* QR Code Modal */}
                  {showQRModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowQRModal(false)}>
                      <div className="bg-white rounded-lg p-6 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold mb-4 text-center">QR Code for Your Form</h3>
                        <div className="flex justify-center mb-4 p-4 bg-white rounded border">
                          <QRCodeSVG id="qr-code-svg" value={formUrl} size={200} level="H" />
                        </div>
                        <p className="text-xs text-gray-500 text-center mb-4">Scan to open your intake form</p>
                        <div className="flex gap-2">
                          <Button variant="outline" className="flex-1" onClick={() => setShowQRModal(false)}>Close</Button>
                          <Button className="flex-1" onClick={() => {
                            const svg = document.getElementById('qr-code-svg');
                            const svgData = new XMLSerializer().serializeToString(svg);
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            const img = new Image();
                            img.onload = () => {
                              canvas.width = img.width;
                              canvas.height = img.height;
                              ctx.fillStyle = 'white';
                              ctx.fillRect(0, 0, canvas.width, canvas.height);
                              ctx.drawImage(img, 0, 0);
                              const a = document.createElement('a');
                              a.download = `${slug}-qr-code.png`;
                              a.href = canvas.toDataURL('image/png');
                              a.click();
                              toast.success("QR code downloaded!");
                            };
                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                          }}>
                            Download PNG
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    This link is unique to your business. Customers who visit will see your services and can submit requests.
                  </p>
                </CardContent>
              </Card>
            );
          })()
        ) : null /* "Almost There" card is shown at top of page */}
      </div>
      
      {/* Unscheduled Jobs Section */}
      {unscheduledJobs.length > 0 && (
        <Card className="mt-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📅</span>
                <h3 className="font-bold text-amber-900 text-base sm:text-lg">Jobs Ready to Schedule</h3>
                <Badge className="bg-amber-600 text-white">{unscheduledJobs.length}</Badge>
              </div>
            </div>
            <p className="text-sm text-amber-800 mb-4">These customers have paid (deposit or full) and are waiting for their service date.</p>
            
            <div className="space-y-3">
              {unscheduledJobs.slice(0, 5).map((job) => {
                const lead = job.lead;
                const services = lead.pricing_snapshot?.map(ps => ps.label).join(", ") || lead.service_type || "Service";
                const depositStatus = job.deposit_paid > 0 ? 
                  (job.quote_amount > 0 && job.deposit_paid >= job.quote_amount ? "Paid in Full" : `$${job.deposit_paid.toFixed(2)} deposit paid`) : 
                  (job.quote_amount > 0 ? "Awaiting Payment" : "Price TBD");
                
                return (
                  <div key={lead.id} className="bg-white rounded-lg p-3 border border-amber-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{lead.customer_name}</p>
                      <p className="text-xs text-gray-600 truncate">{services}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">${job.quote_amount?.toFixed(2) || '0.00'}</span>
                        <span className="text-xs text-blue-700">• {depositStatus}</span>
                        <span className="text-xs text-gray-400">• {new Date(lead.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
                        onClick={() => {
                          setSchedulingJob(job);
                          setScheduleDate("");
                          setScheduleTime("");
                          setShowScheduleModal(true);
                        }}
                      >
                        📅 Schedule
                      </Button>
                      <button
                        className="text-gray-400 hover:text-red-500 text-sm px-2"
                        title="Remove from queue"
                        onClick={async () => {
                          if (window.confirm("Remove this job from the scheduling queue?")) {
                            try {
                              await api.removeFromQueue(lead.id);
                              toast.success("Removed from queue");
                              if (onRefresh) onRefresh();
                            } catch (e) {
                              toast.error("Failed to remove");
                            }
                          }
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {unscheduledJobs.length > 5 && (
              <p className="text-xs text-amber-700 mt-3 text-center">
                + {unscheduledJobs.length - 5} more jobs waiting to be scheduled
              </p>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Schedule Job Modal */}
      {showScheduleModal && schedulingJob && (
        <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
          <DialogContent className="max-w-md">
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">📅</span>
              Schedule Job
            </DialogTitle>
            <div className="space-y-4 pt-2">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-900">{schedulingJob.lead.customer_name}</p>
                <p className="text-sm text-gray-600">{schedulingJob.lead.pricing_snapshot?.map(ps => ps.label).join(", ") || schedulingJob.lead.service_type || "Service"}</p>
                {schedulingJob.lead.customer_address && (
                  <p className="text-xs text-gray-500 mt-1">📍 {schedulingJob.lead.customer_address}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Date *</label>
                <Input 
                  type="date" 
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time (optional)</label>
                <Input 
                  type="text" 
                  placeholder="e.g. 9:00 AM or 9:00 AM - 12:00 PM"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank to confirm time later</p>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowScheduleModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-blue-700 hover:bg-blue-800"
                  disabled={!scheduleDate || schedulingLoading}
                  onClick={async () => {
                    setSchedulingLoading(true);
                    try {
                      await api.scheduleJob(schedulingJob.lead.id, scheduleDate, scheduleTime);
                      toast.success("Job scheduled");
                      setShowScheduleModal(false);
                      setSchedulingJob(null);
                      // Force refresh data to update the queue
                      if (onRefresh) {
                        await onRefresh();
                      }
                    } catch (error) {
                      console.error("Schedule job error:", error);
                      toast.error("Failed to schedule job");
                    } finally {
                      setSchedulingLoading(false);
                    }
                  }}
                >
                  {schedulingLoading ? "Scheduling..." : "✅ Confirm & Notify Customer"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// ============== STRIPE CONNECT DASHBOARD ==============
const StripeConnectDashboard = ({ slug, stripeStatus }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [payingOut, setPayingOut] = useState(false);
  const [openingSettings, setOpeningSettings] = useState(false);
  const [showAccountManager, setShowAccountManager] = useState(false);
  const [myAccounts, setMyAccounts] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(null);

  const fetchData = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const [data, details] = await Promise.all([
        api.getStripeConnectDashboardData(slug),
        api.getStripeAccountDetails(slug)
      ]);
      if (data.error) {
        console.error("[STRIPE DASHBOARD] API returned error:", data.error);
        toast.error(`Stripe data error: ${data.error}`);
      }
      setDashboardData(data);
      setAccountDetails(details);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch Stripe dashboard data:", error);
      toast.error("Failed to load Stripe data");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchMyAccounts = async () => {
    try {
      const result = await api.listMyStripeAccounts(slug);
      setMyAccounts(result);
    } catch (error) {
      toast.error("Failed to load your Stripe accounts");
    }
  };

  const handleDeleteOrphan = async (accountId) => {
    if (!window.confirm(`Delete this unused Stripe account? This cannot be undone.`)) {
      return;
    }
    
    setDeletingAccount(accountId);
    try {
      await api.deleteOrphanStripeAccount(slug, accountId);
      toast.success("Unused account deleted!");
      fetchMyAccounts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete account"));
    } finally {
      setDeletingAccount(null);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false); // Silent refresh, no loading spinner
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handlePayout = async () => {
    if (!dashboardData?.balance?.available || dashboardData.balance.available <= 0) {
      toast.error("No available balance to payout");
      return;
    }
    
    setPayingOut(true);
    try {
      const result = await api.createStripePayout(slug);
      toast.success(result.message || "Payout initiated successfully!");
      fetchData(true); // Refresh to show new payout
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to initiate payout"));
    } finally {
      setPayingOut(false);
    }
  };

  const handleUpdatePayoutSettings = async () => {
    setOpeningSettings(true);
    try {
      const result = await api.getStripeConnectDashboardLink(slug);
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error("Could not get settings link");
        setOpeningSettings(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to open settings"));
      setOpeningSettings(false);
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading && !dashboardData) {
    return (
      <Card className="mt-4">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            <span className="ml-3 text-gray-600">Loading payment data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">Payment Activity</h3>
          <span className="inline-flex h-2 w-2 rounded-full bg-blue-600 animate-pulse" title="Live updating"></span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {lastUpdated && <span>Updated {lastUpdated.toLocaleTimeString()}</span>}
          <button 
            onClick={() => fetchData(true)} 
            className="text-slate-900 hover:text-slate-900 underline"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Payout Card - Only show if connected and has balance */}
      {dashboardData?.connected && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-slate-900">Available Balance</p>
                <p className="text-2xl sm:text-3xl font-bold text-blue-700">
                  {formatCurrency(dashboardData?.balance?.available || 0)}
                </p>
                {accountDetails?.external_accounts?.[0] && (
                  <p className="text-xs text-blue-800 mt-1">
                    Payout to: {accountDetails.external_accounts[0].bank_name} ••••{accountDetails.external_accounts[0].last4}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  onClick={handlePayout}
                  disabled={payingOut || !dashboardData?.balance?.available || dashboardData.balance.available <= 0}
                  className="bg-blue-700 hover:bg-blue-800 text-white"
                >
                  {payingOut ? "Processing..." : "Payout Now"}
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleUpdatePayoutSettings}
                  disabled={openingSettings}
                  className="border-blue-700 text-blue-800 hover:bg-blue-50"
                >
                  {openingSettings ? "Opening..." : "Update Bank Account"}
                </Button>
              </div>
            </div>
            {dashboardData?.balance?.pending > 0 && (
              <p className="text-xs text-amber-700 mt-2">
                + {formatCurrency(dashboardData.balance.pending)} pending (typically available in 2-7 days)
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Available</p>
            <p className="text-lg sm:text-2xl font-bold text-blue-700">
              {formatCurrency(dashboardData?.balance?.available || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Pending</p>
            <p className="text-lg sm:text-2xl font-bold text-amber-600">
              {formatCurrency(dashboardData?.balance?.pending || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Volume</p>
            <p className="text-lg sm:text-2xl font-bold text-slate-900">
              {formatCurrency(dashboardData?.total_volume || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Payouts</p>
            <p className="text-lg sm:text-2xl font-bold text-gray-700">
              {formatCurrency(dashboardData?.total_payouts || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base sm:text-lg">Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData?.transactions?.length > 0 ? (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 font-medium text-gray-600">Date</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600">Description</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600">Customer</th>
                    <th className="text-right py-2 px-4 font-medium text-gray-600">Amount</th>
                    <th className="text-center py-2 px-4 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.transactions.map((txn) => (
                    <tr key={txn.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 px-4 text-gray-600 whitespace-nowrap">{formatDate(txn.created)}</td>
                      <td className="py-2 px-4 text-gray-900">{txn.description || 'Payment'}</td>
                      <td className="py-2 px-4 text-gray-600">{txn.customer_email || '-'}</td>
                      <td className="py-2 px-4 text-right font-medium">{formatCurrency(txn.amount, txn.currency)}</td>
                      <td className="py-2 px-4 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          txn.status === 'succeeded' ? 'bg-blue-100 text-blue-800' :
                          txn.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {txn.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No transactions yet</p>
              <p className="text-sm mt-1">Transactions will appear here once you receive payments</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payouts */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base sm:text-lg">Payouts to Your Bank</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData?.payouts?.length > 0 ? (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 font-medium text-gray-600">Created</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600">Arrival</th>
                    <th className="text-right py-2 px-4 font-medium text-gray-600">Amount</th>
                    <th className="text-center py-2 px-4 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.payouts.map((payout) => (
                    <tr key={payout.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 px-4 text-gray-600 whitespace-nowrap">{formatDate(payout.created)}</td>
                      <td className="py-2 px-4 text-gray-600 whitespace-nowrap">{formatDate(payout.arrival_date)}</td>
                      <td className="py-2 px-4 text-right font-medium">{formatCurrency(payout.amount, payout.currency)}</td>
                      <td className="py-2 px-4 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          payout.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                          payout.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                          payout.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {payout.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No payouts yet</p>
              <p className="text-sm mt-1">Payouts will appear here once Stripe sends money to your bank</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== STRIPE CONNECT SECTION ==============
const StripeConnectSection = ({ slug, stripeStatus, onRefresh }) => {
  const [connecting, setConnecting] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const result = await api.createStripeConnectAccount(slug);
      if (result.onboarding_url) {
        window.location.href = result.onboarding_url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to connect Stripe"));
    } finally {
      setConnecting(false);
    }
  };

  const handleContinueOnboarding = async () => {
    setConnecting(true);
    try {
      const result = await api.refreshStripeConnectLink(slug);
      if (result.onboarding_url) {
        window.location.href = result.onboarding_url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to get onboarding link"));
    } finally {
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    setLoadingDashboard(true);
    try {
      const result = await api.getStripeConnectDashboardLink(slug);
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error("Could not get Stripe dashboard link");
        setLoadingDashboard(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to open dashboard"));
      setLoadingDashboard(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Accept Payments</h2>
      
      {/* Value-building intro - show when not connected OR connected but not fully enabled */}
      {(!stripeStatus?.connected || !stripeStatus?.charges_enabled) && (
        <Card className="mb-4 sm:mb-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="text-3xl">🚀</div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">Unlock the Full Power of ZipLeadPro</h3>
                <p className="text-slate-900 mb-3">
                  Businesses that accept online payments see <strong>40% faster payment collection</strong> and <strong>67% fewer follow-ups</strong> on outstanding invoices.
                </p>
                <ul className="text-sm text-slate-900 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="text-blue-700">✓</span>
                    <span><strong>Get paid instantly</strong> — Customers pay with one click, funds go directly to your bank</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-700">✓</span>
                    <span><strong>Look more professional</strong> — Branded payment links build trust with customers</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-700">✓</span>
                    <span><strong>Automatic tracking</strong> — Know exactly who paid and when, no spreadsheets needed</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-700">✓</span>
                    <span><strong>Collect deposits</strong> — Secure jobs faster with upfront partial payments</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-base sm:text-lg">Payment Account Status</CardTitle>
        </CardHeader>
        <CardContent>
          {!stripeStatus?.connected ? (
            <div className="space-y-4">
              <div className="flex items-start sm:items-center gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-500 text-sm sm:text-base">💳</span>
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 text-sm sm:text-base">Not Connected</p>
                  <p className="text-xs sm:text-sm text-gray-500">Connect your Stripe account to start accepting payments</p>
                </div>
              </div>
              <Button onClick={handleConnectStripe} disabled={connecting} className="w-full sm:w-auto bg-slate-900 hover:bg-slate-900">
                {connecting ? "Connecting..." : "Connect Stripe Account"}
              </Button>
              <p className="text-xs text-gray-500">
                Don't have a Stripe account? No problem — you'll create one during the setup process.
              </p>
            </div>
          ) : !stripeStatus?.details_submitted ? (
            <div className="space-y-4">
              <div className="flex items-start sm:items-center gap-3 p-3 sm:p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-600 text-sm sm:text-base">⚠️</span>
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-amber-800 text-sm sm:text-base">Almost There!</p>
                  <p className="text-xs sm:text-sm text-amber-700">Complete your Stripe setup to accept payments</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={handleContinueOnboarding} disabled={connecting} className="w-full sm:w-auto bg-slate-900 hover:bg-slate-900" data-testid="continue-stripe-setup-btn">
                  {connecting ? "Loading..." : "Continue Setup"}
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm("Start over with a brand-new Stripe connection? Your current incomplete setup will be cleared.")) return;
                    try {
                      await api.disconnectStripeAccount(slug);
                      toast.success("Cleared. Click Connect Stripe Account to start fresh.");
                      onRefresh && onRefresh();
                    } catch (error) {
                      toast.error(getErrorMessage(error, "Could not reset Stripe connection"));
                    }
                  }}
                  className="w-full sm:w-auto"
                  data-testid="reset-stripe-setup-btn"
                >
                  Start Over
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Stuck? "Start Over" clears the incomplete connection so you can launch the setup wizard again from scratch.
              </p>
            </div>
          ) : !stripeStatus?.charges_enabled ? (
            <div className="space-y-4">
              <div className="flex items-start sm:items-center gap-3 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 text-sm sm:text-base">⏳</span>
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-blue-800 text-sm sm:text-base">Verification in Progress</p>
                  <p className="text-xs sm:text-sm text-blue-700">Stripe is verifying your account. This usually takes 1-2 business days.</p>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-slate-900">
                  <strong>Good news!</strong> You can send quotes now using "Send without Payment Link" — customers will receive the quote and you can collect payment separately.
                </p>
              </div>
              <Button variant="outline" onClick={handleOpenDashboard} disabled={loadingDashboard} className="w-full sm:w-auto">
                {loadingDashboard ? "Opening..." : "Open Stripe Dashboard"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start sm:items-center gap-3 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 text-sm sm:text-base">✓</span>
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 text-sm sm:text-base">Ready to Accept Payments</p>
                  <p className="text-xs sm:text-sm text-blue-800">Your Stripe account is connected and verified</p>
                </div>
              </div>
              {stripeStatus?.account_id && (
                <p className="text-xs text-gray-500 break-all">Account ID: {stripeStatus.account_id}</p>
              )}
              <Button variant="outline" onClick={handleOpenDashboard} disabled={loadingDashboard} className="w-full sm:w-auto">
                {loadingDashboard ? "Opening..." : "Open Full Stripe Dashboard"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Show dashboard for all states - connected or not */}
      <StripeConnectDashboard slug={slug} stripeStatus={stripeStatus} />
    </div>
  );
};

// ============== PRODUCT MANAGEMENT SECTION ==============
const ProductManagementSection = ({ slug }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', price: '', description: '', quantity_limit: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadProducts();
  }, [slug]);

  const loadProducts = async () => {
    try {
      const data = await api.getProducts(slug);
      setProducts(data);
    } catch (error) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleCreateProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      toast.error("Name and price are required");
      return;
    }
    
    const priceCents = Math.round(parseFloat(newProduct.price) * 100);
    if (isNaN(priceCents) || priceCents <= 0) {
      toast.error("Please enter a valid price");
      return;
    }
    
    setCreating(true);
    try {
      let imageUrl = null;
      
      if (imageFile) {
        setUploadingImage(true);
        const uploadResult = await api.uploadProductImage(slug, imageFile);
        imageUrl = uploadResult.image_url;
        setUploadingImage(false);
      }
      
      await api.createStripeProduct(slug, newProduct.name, priceCents, newProduct.description || null, imageUrl, newProduct.quantity_limit ? parseInt(newProduct.quantity_limit) : null);
      toast.success("Product created!");
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create product"));
    } finally {
      setCreating(false);
      setUploadingImage(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    setDeletingId(productId);
    try {
      await api.deleteStripeProduct(slug, productId);
      toast.success("Product deleted");
      loadProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete product"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddAdditionalImage = async (productId, file) => {
    try {
      await api.addProductAdditionalImage(slug, productId, file);
      toast.success("Image added");
      loadProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to add image"));
    }
  };

  const handleRemoveAdditionalImage = async (productId, imageUrl) => {
    try {
      await api.removeProductAdditionalImage(slug, productId, imageUrl);
      toast.success("Image removed");
      loadProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to remove image"));
    }
  };

  const resetForm = () => {
    setNewProduct({ name: '', price: '', description: '', quantity_limit: '' });
    setImageFile(null);
    setImagePreview(null);
    setShowAddForm(false);
    setEditingProduct(null);
  };

  return (
    <Card className="mt-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg">🛍️ Your Products</CardTitle>
          <Button size="sm" onClick={() => showAddForm ? resetForm() : setShowAddForm(true)}>
            {showAddForm ? "Cancel" : "+ Add Product"}
          </Button>
        </div>
        <CardDescription>Products for your Shop intake mode</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Add Product Form */}
        {showAddForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
            <div>
              <Label className="text-sm">Main Product Image (optional)</Label>
              <p className="text-xs text-gray-500 mb-1">This image appears on Stripe and as the primary product photo</p>
              <div className="mt-1 flex items-center gap-4">
                {imagePreview ? (
                  <div className="relative">
                    <img src={imagePreview} alt="Preview" className="w-20 h-20 object-cover rounded-lg border" />
                    <button 
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                    >×</button>
                  </div>
                ) : (
                  <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-2xl text-gray-400">📷</span>
                    <span className="text-xs text-gray-500">Add</span>
                    <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                  </label>
                )}
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Product Name *</Label>
              <Input 
                value={newProduct.name} 
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                placeholder="e.g., Basic Service Package"
              />
            </div>
            <div>
              <Label className="text-sm">Price (USD) *</Label>
              <Input 
                type="number" step="0.01" min="0"
                value={newProduct.price} 
                onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                placeholder="19.99"
              />
            </div>
            <div>
              <Label className="text-sm">Description (optional)</Label>
              <Input 
                value={newProduct.description} 
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                placeholder="Brief description of this product"
              />
            </div>
            <div>
              <Label className="text-sm">Quantity Limit (optional)</Label>
              <p className="text-xs text-gray-500 mb-1">Max quantity per order. Leave empty for unlimited.</p>
              <Input 
                type="number" min="1"
                value={newProduct.quantity_limit} 
                onChange={(e) => setNewProduct({...newProduct, quantity_limit: e.target.value})}
                placeholder="e.g., 10"
              />
            </div>
            <Button onClick={handleCreateProduct} disabled={creating || uploadingImage} className="w-full">
              {uploadingImage ? "Uploading image..." : creating ? "Creating..." : "Create Product"}
            </Button>
          </div>
        )}
        
        {/* Product List */}
        {loading ? (
          <div className="text-center py-4 text-gray-500">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <p className="mb-2">No products yet</p>
            <p className="text-sm">Add products to enable the Shop intake mode</p>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map(product => (
              <ProductCard 
                key={product.id}
                product={product}
                slug={slug}
                onDelete={() => handleDeleteProduct(product.id)}
                onAddImage={(file) => handleAddAdditionalImage(product.id, file)}
                onRemoveImage={(url) => handleRemoveAdditionalImage(product.id, url)}
                deleting={deletingId === product.id}
                onRefresh={loadProducts}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Individual Product Card with edit/delete and additional images
const ProductCard = ({ product, slug, onDelete, onAddImage, onRemoveImage, deleting, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ 
    name: product.name, 
    description: product.description || '',
    price: product.prices?.[0]?.unit_amount ? (product.prices[0].unit_amount / 100).toFixed(2) : ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);
  
  const allImages = [
    ...(product.images || []),
    ...(product.additional_images || [])
  ];

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: editData.name,
        description: editData.description
      };
      // If price changed, include it (backend creates new Stripe price)
      const newPriceCents = editData.price ? Math.round(parseFloat(editData.price) * 100) : null;
      const oldPriceCents = product.prices?.[0]?.unit_amount;
      if (newPriceCents && newPriceCents !== oldPriceCents) {
        updateData.price_cents = newPriceCents;
      }
      await api.updateStripeProduct(slug, product.id, updateData);
      toast.success("Product updated");
      setEditing(false);
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update"));
    } finally {
      setSaving(false);
    }
  };

  const handleAdditionalImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    setUploadingAdditional(true);
    try {
      await onAddImage(file);
    } finally {
      setUploadingAdditional(false);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Main row */}
      <div className="flex items-center gap-3 p-3 bg-gray-50">
        {product.images?.[0] ? (
          <img src={product.images[0]} alt="" className="w-12 h-12 object-cover rounded" />
        ) : (
          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">No img</div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{product.name}</p>
          {product.prices?.[0] && (
            <p className="text-sm text-slate-900 font-semibold">
              ${(product.prices[0].unit_amount / 100).toFixed(2)}
            </p>
          )}
          {product.additional_images?.length > 0 && (
            <p className="text-xs text-gray-500">+{product.additional_images.length} more photos</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)}>
            {expanded ? "▲" : "▼"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(!editing); setExpanded(true); }}>
            ✏️
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} disabled={deleting}>
            {deleting ? "..." : "🗑️"}
          </Button>
        </div>
      </div>
      
      {/* Expanded section */}
      {expanded && (
        <div className="p-3 border-t bg-white space-y-3">
          {/* Edit form */}
          {editing && (
            <div className="space-y-2 p-3 bg-blue-50 rounded-lg">
              {/* Main Image */}
              <div>
                <Label className="text-xs">Main Photo</Label>
                <div className="flex items-center gap-2 mt-1">
                  {product.images?.[0] ? (
                    <img src={product.images[0]} alt="" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">None</div>
                  )}
                  <label className="text-xs text-slate-900 cursor-pointer hover:underline">
                    {uploadingMainImage ? "Uploading..." : "Change Photo"}
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      disabled={uploadingMainImage}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingMainImage(true);
                        try {
                          const uploadResult = await api.uploadProductImage(slug, file);
                          await api.updateStripeProduct(slug, product.id, { image_url: uploadResult.image_url });
                          toast.success("Photo updated");
                          onRefresh();
                        } catch (err) {
                          toast.error("Failed to update photo");
                        } finally {
                          setUploadingMainImage(false);
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs">Name</Label>
                <Input 
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Price (USD)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={editData.price}
                  onChange={(e) => setEditData({...editData, price: e.target.value})}
                  className="h-8 text-sm"
                  placeholder="19.99"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Note: Creates new price in Stripe</p>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input 
                  value={editData.description}
                  onChange={(e) => setEditData({...editData, description: e.target.value})}
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveEdit} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          )}
          
          {/* Additional Images */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Additional Photos</Label>
              <label className="text-xs text-slate-900 cursor-pointer hover:underline">
                {uploadingAdditional ? "Uploading..." : "+ Add Photo"}
                <input type="file" accept="image/*" onChange={handleAdditionalImageSelect} className="hidden" disabled={uploadingAdditional} />
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-2">Extra photos only shown on ZipLeadPro (not Stripe)</p>
            
            {allImages.length > 0 ? (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {allImages.map((img, idx) => (
                  <div key={idx} className="relative flex-shrink-0">
                    <img src={img} alt="" className="w-16 h-16 object-cover rounded" />
                    {idx > 0 && product.additional_images?.includes(img) && (
                      <button 
                        onClick={() => onRemoveImage(img)}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                      >×</button>
                    )}
                    {idx === 0 && <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[8px] text-center">Main</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">No additional photos</p>
            )}
          </div>
          
          {product.description && (
            <div>
              <Label className="text-xs text-gray-500">Description</Label>
              <p className="text-sm">{product.description}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============== SERVICES SECTION ==============
const ServicesSection = ({ slug, services, onRefresh }) => {
  const [editingService, setEditingService] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formUrl, setFormUrl] = useState("");
  const [formData, setFormData] = useState({
    label: "",
    description: "",
    pricing_type: "none",
    price_value: "",
    unit_label: "",
    active: true,
    is_modifier: false,
    modifier_type: "percentage",
    modifier_percent: "",
    modifier_fixed: ""
  });

  useEffect(() => {
    api.getEmbedCode(slug).then(data => setFormUrl(data.form_url)).catch(() => {});
  }, [slug]);

  const resetForm = () => {
    setFormData({
      label: "",
      description: "",
      pricing_type: "none",
      price_value: "",
      unit_label: "",
      active: true,
      is_modifier: false,
      modifier_type: "percentage",
      modifier_percent: "",
      modifier_fixed: ""
    });
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      label: service.label,
      description: service.description || "",
      pricing_type: service.pricing_type || "none",
      price_value: service.price_value || "",
      unit_label: service.unit_label || "",
      active: service.active,
      is_modifier: service.is_modifier || false,
      modifier_type: service.modifier_type || "percentage",
      modifier_percent: service.modifier_percent || "",
      modifier_fixed: service.modifier_fixed || ""
    });
    setShowAddDialog(true);
  };

  const handleSave = async () => {
    if (!formData.label.trim()) {
      toast.error("Service name is required");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        price_value: formData.price_value ? parseFloat(formData.price_value) : null,
        modifier_percent: formData.modifier_percent ? parseFloat(formData.modifier_percent) : null,
        modifier_fixed: formData.modifier_fixed ? parseFloat(formData.modifier_fixed) : null
      };
      
      if (editingService) {
        await api.updateService(slug, editingService.id, data);
        toast.success("Service updated");
      } else {
        await api.createService(slug, data);
        toast.success("Service created");
      }
      setShowAddDialog(false);
      setEditingService(null);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save service"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.deleteService(slug, serviceId);
      toast.success("Service deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const handleReorder = async (serviceId, direction) => {
    const currentIndex = services.findIndex(s => s.id === serviceId);
    if ((direction === -1 && currentIndex === 0) || (direction === 1 && currentIndex === services.length - 1)) {
      return;
    }
    
    const newOrder = [...services];
    const [removed] = newOrder.splice(currentIndex, 1);
    newOrder.splice(currentIndex + direction, 0, removed);
    
    try {
      await api.reorderServices(slug, newOrder.map(s => s.id));
      onRefresh();
    } catch (error) {
      toast.error("Failed to reorder services");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Services</h2>
        <Button onClick={() => { resetForm(); setEditingService(null); setShowAddDialog(true); }} size="sm" className="sm:size-default bg-slate-900 hover:bg-slate-900">
          + Add Service
        </Button>
      </div>
      
      {/* Quick Link Banner */}
      {formUrl && services.length > 0 && (
        <Card className="mb-4 bg-slate-900 text-white border-0">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-sm">🚀 Your services are ready for customers!</p>
                <p className="text-xs opacity-90">Share your link to start receiving requests</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="secondary"
                  className="bg-white text-blue-800 hover:bg-gray-100"
                  onClick={() => {
                    navigator.clipboard.writeText(formUrl);
                    toast.success("Link copied!");
                  }}
                >
                  Copy Link
                </Button>
                <Button 
                  size="sm" 
                  variant="secondary"
                  className="bg-white text-blue-800 hover:bg-gray-100"
                  onClick={() => setShowQRModal(true)}
                >
                  QR Code
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="border-white text-white hover:bg-white/20"
                  onClick={() => window.open(formUrl, '_blank')}
                >
                  Preview
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Helpful tip */}
      <Card className="mb-4 bg-blue-50 border-blue-200">
        <CardContent className="p-3 sm:p-4">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Services become easy one-tap buttons in button mode. Add modifiers like "Rush Service +20%" or "After Hours +$50" for quick price adjustments. Do NOT add modifiers by themselves, They are designed to work alongside your services.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {services.length === 0 ? (
            <div className="text-center py-8 sm:py-12 px-4">
              <div className="text-4xl mb-3">🛠️</div>
              <p className="text-gray-700 font-medium mb-2">No services yet</p>
              <p className="text-sm text-gray-500 mb-4">Add your services to speed up quoting. Each service becomes a one-tap button.</p>
              <Button onClick={() => { resetForm(); setEditingService(null); setShowAddDialog(true); }} className="bg-slate-900 hover:bg-slate-900">
                Add Your First Service
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="sm:hidden divide-y">
                {services.map((service, idx) => (
                  <div key={service.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm flex items-center gap-2">
                          {service.label}
                          {service.is_modifier && (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                              Modifier
                            </Badge>
                          )}
                        </div>
                        {service.description && (
                          <div className="text-xs text-gray-500 mt-0.5">{service.description}</div>
                        )}
                      </div>
                      <Badge variant={service.active ? "default" : "secondary"} className="ml-2 text-xs">
                        {service.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      {service.is_modifier && service.modifier_percent ? (
                        `+${service.modifier_percent}%`
                      ) : service.pricing_type === "none" ? (
                        <span className="text-gray-400">No price</span>
                      ) : service.pricing_type === "fixed" ? (
                        `$${service.price_value?.toFixed(2)}`
                      ) : service.pricing_type === "starting_at" ? (
                        `Starting at $${service.price_value?.toFixed(2)}`
                      ) : (
                        `$${service.price_value?.toFixed(2)} ${service.unit_label || "per unit"}`
                      )}
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleReorder(service.id, -1)}
                          disabled={idx === 0}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-1"
                        >
                          ▲
                        </button>
                        <button 
                          onClick={() => handleReorder(service.id, 1)}
                          disabled={idx === services.length - 1}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-1"
                        >
                          ▼
                        </button>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(service)}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(service.id)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Order</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Pricing</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {services.map((service, idx) => (
                      <TableRow key={service.id}>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <button 
                              onClick={() => handleReorder(service.id, -1)}
                              disabled={idx === 0}
                              className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              ▲
                            </button>
                            <button 
                              onClick={() => handleReorder(service.id, 1)}
                              disabled={idx === services.length - 1}
                              className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              ▼
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{service.label}</div>
                          {service.description && (
                            <div className="text-sm text-gray-500">{service.description}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {service.pricing_type === "none" ? (
                            <span className="text-gray-400">No price</span>
                          ) : service.pricing_type === "fixed" ? (
                            `$${service.price_value?.toFixed(2)}`
                          ) : service.pricing_type === "starting_at" ? (
                            `Starting at $${service.price_value?.toFixed(2)}`
                          ) : (
                            `$${service.price_value?.toFixed(2)} ${service.unit_label || "per unit"}`
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={service.active ? "default" : "secondary"}>
                            {service.active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(service)}>
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(service.id)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingService ? "Edit Service" : "Add Service"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Service Name *</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Website Design"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Pricing Type</Label>
              <Select
                value={formData.pricing_type}
                onValueChange={(v) => setFormData({ ...formData, pricing_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Price</SelectItem>
                  <SelectItem value="fixed">Fixed Price</SelectItem>
                  <SelectItem value="starting_at">Starting At</SelectItem>
                  <SelectItem value="per_unit">Per Unit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.pricing_type !== "none" && (
              <div className="space-y-2">
                <Label>Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price_value}
                  onChange={(e) => setFormData({ ...formData, price_value: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            )}
            {formData.pricing_type === "per_unit" && (
              <div className="space-y-2">
                <Label>Unit Label</Label>
                <Input
                  value={formData.unit_label}
                  onChange={(e) => setFormData({ ...formData, unit_label: e.target.value })}
                  placeholder="e.g., per hour, per page"
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.active}
                onCheckedChange={(v) => setFormData({ ...formData, active: v })}
              />
              <Label>Active</Label>
            </div>
            
            {/* Modifier Settings */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Switch
                  checked={formData.is_modifier}
                  onCheckedChange={(v) => setFormData({ ...formData, is_modifier: v })}
                />
                <Label>This is an add-on (e.g., Rush Service, After Hours)</Label>
              </div>
              {formData.is_modifier && (
                <div className="space-y-4 pl-6">
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600">Add-on Type</Label>
                    <Select
                      value={formData.modifier_type}
                      onValueChange={(v) => setFormData({ ...formData, modifier_type: v })}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage (+%)</SelectItem>
                        <SelectItem value="fixed">Fixed Price (+$)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.modifier_type === "percentage" ? (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-600">Percentage to Add</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">+</span>
                        <Input
                          type="number"
                          step="1"
                          value={formData.modifier_percent}
                          onChange={(e) => setFormData({ ...formData, modifier_percent: e.target.value })}
                          placeholder="e.g., 20"
                          className="w-24"
                        />
                        <span className="text-gray-500">%</span>
                      </div>
                      <p className="text-xs text-gray-500">Adds this percentage of the current quote total</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-600">Fixed Amount to Add</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">+$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.modifier_fixed}
                          onChange={(e) => setFormData({ ...formData, modifier_fixed: e.target.value })}
                          placeholder="e.g., 50"
                          className="w-24"
                        />
                      </div>
                      <p className="text-xs text-gray-500">Adds this fixed dollar amount to the quote</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== SETUP SECTION (Combined Services + Settings) ==============
const SetupSection = ({ slug, business, services, onRefresh, isStep1Complete: propIsStep1Complete, isStep2Complete: propIsStep2Complete, isBothStepsComplete: propIsBothStepsComplete, onStep2Complete, onPreviewOpen, onFormModeChange, onEditorExpand, closePreviewTrigger, annualPricingEnabled = false, onDemoSubmission, demoLoading, tourActive = false }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(true);
  const [servicesFilter, setServicesFilter] = useState("all"); // all | public | internal
  const [showAddServiceDialog, setShowAddServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [savingService, setSavingService] = useState(false);
  
  // State to trigger opening the form preview panel
  const [openFormPreview, setOpenFormPreview] = useState(false);
  
  // Check if tour was skipped (only show checklist for skipped users)
  const tourSkippedKey = `zipleadpro_tour_skipped_${slug}`;
  const tourCompletedKey = `zipleadpro_tour_${slug}`;
  const [tourWasSkipped, setTourWasSkipped] = useState(() => {
    try {
      return localStorage.getItem(tourSkippedKey) === 'true';
    } catch {
      return false;
    }
  });
  const [tourWasCompleted, setTourWasCompleted] = useState(() => {
    try {
      return localStorage.getItem(tourCompletedKey) === 'true';
    } catch {
      return false;
    }
  });
  
  // LOCAL state for Step 2 completion - updates immediately when user opens editor
  const step2Key = `checklist_step2_completed_${slug}`;
  const [localStep2Complete, setLocalStep2Complete] = useState(() => {
    try {
      return localStorage.getItem(step2Key) === 'true';
    } catch {
      return false;
    }
  });
  
  // Use local state OR prop (whichever is true)
  const isStep1Complete = propIsStep1Complete;
  const isStep2Complete = localStep2Complete || propIsStep2Complete;
  const isBothStepsComplete = isStep1Complete && isStep2Complete;
  
  // Wrapper for onStep2Complete that also updates local state
  const handleStep2Complete = () => {
    setLocalStep2Complete(true);
    try {
      localStorage.setItem(step2Key, 'true');
    } catch {}
    if (onStep2Complete) {
      onStep2Complete();
    }
  };
  
  // Custom domain publishing state
  const [publishMode, setPublishMode] = useState(business?.publish_mode || "zipleadpro");
  const [customDomain, setCustomDomain] = useState(business?.custom_domain || "");
  const [publishing, setPublishing] = useState(false);
  const [domainStatus, setDomainStatus] = useState(business?.domain_status || "none");
  const [verifying, setVerifying] = useState(false);
  const ZIPLEADPRO_IP = "76.76.21.21";
  
  // Quote Resources state
  const [quoteResources, setQuoteResources] = useState({
    pdf_url: "",
    pdf_label: "",
    pdf_enabled: false,
    video_url: "",
    video_label: "",
    video_enabled: false
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  
  // Subscription management state
  const [subscriptionExpanded, setSubscriptionExpanded] = useState(false);
  const [businessInfoExpanded, setBusinessInfoExpanded] = useState(false);
  const [quoteResourcesExpanded, setQuoteResourcesExpanded] = useState(false);
  const [publicLinkExpanded, setPublicLinkExpanded] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(null); // 'pause' | 'cancel' | null
  const [selectedBillingInterval, setSelectedBillingInterval] = useState("monthly"); // "monthly" | "annual"
  const [confirmationText, setConfirmationText] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [countdownActive, setCountdownActive] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    notify_email: "",
    reply_to_email: "",
    intake_mode: "simple_form",
    auto_followup_quotes: true,
    field_config: {
      name_required: true,
      email_required: true,
      phone_enabled: true,
      phone_required: false,
      address_enabled: false,
      file_upload_enabled: false,
      message_enabled: true,
      photo_mode: "off",
      photo_instructions: "",
      form_background_color: "",
      form_card_background_color: "",
      color_mode: "light",
      form_text_color: "",
      form_font_family: "",
      logo_size: 96
    }
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formUrl, setFormUrl] = useState("");
  
  // Service form data
  const [serviceFormData, setServiceFormData] = useState({
    label: "",
    description: "",
    pricing_type: "none",
    price_value: "",
    unit_label: "",
    active: true,
    public: true,
    is_modifier: false,
    modifier_type: "percentage",
    modifier_percent: "",
    modifier_fixed: ""
  });

  // Track if user has manually hidden the checklist after completing all steps
  const checklistHiddenKey = `checklist_hidden_${slug}`;
  const [checklistHidden, setChecklistHidden] = useState(() => {
    try {
      return localStorage.getItem(checklistHiddenKey) === 'true';
    } catch {
      return false;
    }
  });

  // Local hasServices check for service count display
  const hasServices = services && services.filter(s => s.active).length > 0;
  
  // isSetupComplete used for embed code and other legacy UI elements
  const hasBusinessName = business?.name && business.name.trim().length > 0;
  const hasContactEmail = business?.notify_email && business.notify_email.trim().length > 0;
  const isSetupComplete = hasBusinessName && hasContactEmail && hasServices;
  
  // Show link card only when both steps complete AND user has hidden checklist
  const showLinkCard = isBothStepsComplete && checklistHidden;
  
  // Name change tracking
  const nameChangesUsed = business?.name_changes_used || 0;
  const nameChangesRemaining = Math.max(0, 2 - nameChangesUsed);
  const nameChangeResetDate = business?.name_changes_reset_date 
    ? new Date(business.name_changes_reset_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;
  const isNameChangeDisabled = nameChangesRemaining === 0;

  useEffect(() => {
    api.getEmbedCode(slug).then(data => setFormUrl(data.form_url)).catch(() => {});
  }, [slug]);

  // Sync publish state from business prop
  useEffect(() => {
    if (business) {
      setPublishMode(business.publish_mode || "zipleadpro");
      setCustomDomain(business.custom_domain || "");
      setDomainStatus(business.domain_status || "none");
      // Load quote resources
      if (business.quote_resources) {
        setQuoteResources({
          pdf_url: business.quote_resources.pdf_url || "",
          pdf_label: business.quote_resources.pdf_label || "",
          pdf_enabled: business.quote_resources.pdf_enabled || false,
          video_url: business.quote_resources.video_url || "",
          video_label: business.quote_resources.video_label || "",
          video_enabled: business.quote_resources.video_enabled || false
        });
      }
    }
  }, [business]);

  // Handle PDF/Image upload for quote resources
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Supported types
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    
    if (!allowedTypes.includes(file.type) && !allowedExts.includes(ext)) {
      toast.error("Please upload a PDF or image file (JPG, PNG, GIF, WebP)");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }
    
    setUploadingPdf(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(`${API}/tenants/${slug}/upload-quote-pdf`, formDataUpload);
      // Store relative URL - backend will prepend correct domain for emails
      const pdfUrl = response.data.pdf_url;
      setQuoteResources(prev => ({ ...prev, pdf_url: pdfUrl, pdf_enabled: true }));
      toast.success("File uploaded! Click Save to apply.");
    } catch (error) {
      toast.error("Failed to upload file");
      console.error("File upload error:", error);
    } finally {
      setUploadingPdf(false);
    }
  };

  // Save quote resources
  const handleSaveQuoteResources = async () => {
    setSaving(true);
    try {
      await api.updateBusiness(slug, { quote_resources: quoteResources });
      toast.success("Quote resources saved");
      onRefresh();
    } catch (error) {
      toast.error("Failed to save quote resources");
    } finally {
      setSaving(false);
    }
  };

  // Handle publish to custom domain
  const handlePublish = async () => {
    if (publishMode === "custom_domain" && !customDomain.trim()) {
      toast.error("Please enter a domain");
      return;
    }
    setPublishing(true);
    try {
      await api.publishDomain(slug, publishMode, publishMode === "custom_domain" ? customDomain : null);
      toast.success(publishMode === "custom_domain" 
        ? "Domain configured! DNS verification in progress..." 
        : "Published to ZipLeadPro URL");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  // Handle manual DNS verification
  const handleVerifyDNS = async () => {
    setVerifying(true);
    try {
      const result = await api.verifyDomain(slug);
      if (result.status === "active") {
        toast.success("Domain verified and active!");
        setDomainStatus("active");
      } else {
        toast.error(result.error || "DNS not yet configured");
      }
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Handle logo file selection with immediate preview
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    } else {
      setLogoPreview(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const result = await api.createBillingPortal(slug);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      // Handle the case where stripe_customer_id is missing
      const errorMsg = error.response?.data?.detail || "Unable to open billing portal";
      if (errorMsg.includes("No subscription found")) {
        toast.error("No billing record found. Please contact support if you believe this is an error.");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoadingPortal(false);
    }
  };
  // Handle subscription action (pause/cancel) with confirmation modal
  const handleSubscriptionAction = async (action) => {
    setLoadingPortal(true);
    try {
      await api.businessManageSubscription(slug, action);
      toast.success(`Subscription ${action === 'pause' ? 'paused' : 'canceled'} successfully`);
      onRefresh();
    } catch (error) {
      toast.error(`Failed to ${action} subscription`);
    } finally {
      setLoadingPortal(false);
      closeSubscriptionModal();
    }
  };

  // Open confirmation modal
  const openSubscriptionModal = (action) => {
    setShowSubscriptionModal(action);
    setConfirmationText('');
    setCountdown(5);
    setCountdownActive(false);
  };

  // Close confirmation modal
  const closeSubscriptionModal = () => {
    setShowSubscriptionModal(null);
    setConfirmationText('');
    setCountdown(5);
    setCountdownActive(false);
  };

  // Start countdown when text matches
  useEffect(() => {
    if (!showSubscriptionModal) return;
    
    const requiredText = showSubscriptionModal.toUpperCase();
    const isMatch = confirmationText.toUpperCase() === requiredText;
    
    if (isMatch && !countdownActive) {
      setCountdownActive(true);
      setCountdown(5);
    } else if (!isMatch && countdownActive) {
      setCountdownActive(false);
      setCountdown(5);
    }
  }, [confirmationText, showSubscriptionModal, countdownActive]);

  // Countdown timer
  useEffect(() => {
    if (!countdownActive || countdown <= 0) return;
    
    const timer = setTimeout(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [countdownActive, countdown]);

  useEffect(() => {
    if (business) {
      setFormData({
        name: business.name || "",
        notify_email: business.notify_email || "",
        reply_to_email: business.reply_to_email || "",
        intake_mode: business.intake_mode || "simple_form",
        auto_followup_quotes: business.auto_followup_quotes !== false,
        chemicals_enabled: !!business.chemicals_enabled,
        state_code: business.state_code || "",
        applicator_license: business.applicator_license || "",
        field_config: {
          name_required: business.field_config?.name_required ?? true,
          email_required: business.field_config?.email_required ?? true,
          phone_enabled: business.field_config?.phone_enabled ?? true,
          phone_required: business.field_config?.phone_required ?? false,
          address_enabled: business.field_config?.address_enabled ?? false,
          address_required: business.field_config?.address_required ?? false,
          file_upload_enabled: business.field_config?.file_upload_enabled ?? false,
          file_upload_mode: business.field_config?.file_upload_mode || "off",
          file_upload_instructions: business.field_config?.file_upload_instructions || "",
          message_enabled: business.field_config?.message_enabled ?? true,
          photo_mode: business.field_config?.photo_mode || "off",
          photo_instructions: business.field_config?.photo_instructions || "",
          form_background_color: business.field_config?.form_background_color || "",
          form_card_background_color: business.field_config?.form_card_background_color || "",
          color_mode: business.field_config?.color_mode || "light",
          form_text_color: business.field_config?.form_text_color || "",
          form_font_family: business.field_config?.form_font_family || "",
          logo_size: business.field_config?.logo_size || 96,
          tagline: business.field_config?.tagline || "",
          submit_button_text: business.field_config?.submit_button_text || "",
          hide_pricing: business.field_config?.hide_pricing ?? false,
          custom_questions: business.field_config?.custom_questions || []
        }
      });
    }
  }, [business]);

  const updateFieldConfig = (key, value) => {
    setFormData(prev => ({
      ...prev,
      field_config: { ...prev.field_config, [key]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await api.updateBusiness(slug, formData);
      
      if (logoFile) {
        await api.uploadLogo(slug, logoFile);
        setLogoFile(null);
        setLogoPreview(null);
      }
      
      // Check if slug changed (name was updated)
      if (result.slug && result.slug !== slug) {
        toast.success(`Settings saved! Your new link: ${window.location.origin}/api/share/${result.slug}`);
        navigate(`/dashboard/${result.slug}`, { replace: true });
      } else {
        toast.success("Settings saved successfully!");
      }
      
      await onRefresh();
    } catch (error) {
      console.error("Save error:", error);
      const errorMsg = getErrorMessage(error, "Failed to save settings");
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  // Service functions
  const resetServiceForm = () => {
    setServiceFormData({
      label: "",
      description: "",
      pricing_type: "none",
      price_value: "",
      unit_label: "",
      active: true,
      public: true,
      is_modifier: false,
      modifier_type: "percentage",
      modifier_percent: "",
      modifier_fixed: ""
    });
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceFormData({
      label: service.label,
      description: service.description || "",
      pricing_type: service.pricing_type || "none",
      price_value: service.price_value || "",
      unit_label: service.unit_label || "",
      active: service.active,
      public: service.public ?? true,
      is_modifier: service.is_modifier || false,
      modifier_type: service.modifier_type || "percentage",
      modifier_percent: service.modifier_percent || "",
      modifier_fixed: service.modifier_fixed || ""
    });
    setShowAddServiceDialog(true);
  };

  const handleSaveService = async () => {
    if (!serviceFormData.label.trim()) {
      toast.error("Service name is required");
      return;
    }

    setSavingService(true);
    try {
      const data = {
        ...serviceFormData,
        price_value: serviceFormData.price_value ? parseFloat(serviceFormData.price_value) : null,
        modifier_percent: serviceFormData.modifier_percent ? parseFloat(serviceFormData.modifier_percent) : null,
        modifier_fixed: serviceFormData.modifier_fixed ? parseFloat(serviceFormData.modifier_fixed) : null
      };
      
      if (editingService) {
        await api.updateService(slug, editingService.id, data);
        toast.success("Service updated");
      } else {
        await api.createService(slug, data);
        toast.success("Service created");
      }
      setShowAddServiceDialog(false);
      setEditingService(null);
      resetServiceForm();
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save service"));
    } finally {
      setSavingService(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.deleteService(slug, serviceId);
      toast.success("Service deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const handleReorderService = async (serviceId, direction) => {
    const currentIndex = services.findIndex(s => s.id === serviceId);
    if ((direction === -1 && currentIndex === 0) || (direction === 1 && currentIndex === services.length - 1)) {
      return;
    }
    
    const newOrder = [...services];
    const [removed] = newOrder.splice(currentIndex, 1);
    newOrder.splice(currentIndex + direction, 0, removed);
    
    try {
      await api.reorderServices(slug, newOrder.map(s => s.id));
      onRefresh();
    } catch (error) {
      toast.error("Failed to reorder services");
    }
  };

  return (
    <>
      {/* Watch It Work - Prominent CTA at top of Setup */}
      {SHOW_WATCH_IT_WORK && <div className="mb-6 flex flex-col items-center" data-testid="setup-watch-it-work">
        <button
          onClick={onDemoSubmission}
          disabled={demoLoading}
          className="w-full sm:w-auto sm:min-w-[340px] px-8 py-6 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-2xl text-white text-center transition-all shadow-xl hover:shadow-2xl hover:scale-[1.03] border-2 border-amber-300/30"
          data-testid="setup-watch-it-work-btn"
        >
          <div className="text-3xl mb-2">⚡️</div>
          <div className="font-black text-2xl tracking-tight">Watch It Work</div>
          <div className="text-sm font-semibold opacity-90 mt-1">
            {demoLoading ? "Sending..." : "Process a demo job now"}
          </div>
        </button>
        <p className="text-sm text-gray-500 mt-4 px-2 text-center max-w-md">
          Use this as you make changes to your form to see how it will affect real-world flow. 
        </p>
      </div>}

      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Setup</h2>
      
      {/* Quick Link Section - show when setup complete OR when tour completed normally */}
      {(showLinkCard || (tourWasCompleted && isBothStepsComplete)) && formUrl ? (
        <Card className="mb-4 sm:mb-6 border-2 border-blue-200 bg-slate-50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="text-lg">🔗</span> Your Customer Request Link
                </p>
                <p className="text-xs text-gray-600 mt-1">Share this link anywhere to receive customer requests</p>
              </div>
              <div className="flex gap-2">
                <Input value={formUrl} readOnly className="text-xs bg-white max-w-xs" />
                <Button size="sm" className="bg-slate-900 hover:bg-slate-900" onClick={() => { navigator.clipboard.writeText(formUrl); toast.success("Link copied!"); }}>Copy</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
      
      <div className="space-y-4 sm:space-y-6">
        {/* Products Section - Only show when business is in Product Catalog mode */}
        {business?.intake_mode === "product_catalog" && (
          <ProductManagementSection slug={slug} />
        )}
        
        {/* Services Section - Collapsible */}
        <Card id="tour-services-card" data-tour="services-card">
          <div 
            onClick={(e) => {
              // Only toggle if clicking on the header itself, not the Add button
              if (e.target.closest('[data-add-service-btn]')) return;
              setServicesExpanded(!servicesExpanded);
            }} 
            className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between text-left hover:bg-gray-50 cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">🛠️</span>
              <div>
                <p className="font-semibold text-gray-900">Services</p>
                <p className="text-xs text-gray-500">{services.filter(s => s.active).length} active</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                data-add-service-btn 
                data-tour="add-service-btn"
                size="sm" 
                className="bg-slate-900 hover:bg-slate-900" 
                onClick={() => { resetServiceForm(); setEditingService(null); setShowAddServiceDialog(true); }}
              >
                + Add
              </Button>
              <svg className={`w-5 h-5 text-gray-500 transition-transform ${servicesExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </div>
          </div>
          
          {servicesExpanded && (
            <CardContent className="pt-0 px-4 pb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> {formData.intake_mode === "service_buttons" 
                    ? "Services become one-tap buttons on your intake form. Add modifiers like \"Rush Service +20%\" or \"After Hours +$50\" for quick price adjustments when quoting."
                    : "Services speed up your quoting process. Add modifiers like \"Rush Service +20%\" or \"After Hours +$50\" for quick price adjustments."}
                </p>
              </div>

              {services.length > 0 && (
                <div className="flex items-center gap-1 mb-3 rounded-lg bg-gray-100 p-1 w-fit" data-testid="services-filter">
                  {[
                    { value: "all", label: "All", count: services.length },
                    { value: "public", label: "Customer-Facing", count: services.filter(s => s.public !== false).length },
                    { value: "internal", label: "Internal", count: services.filter(s => s.public === false).length },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setServicesFilter(opt.value)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${servicesFilter === opt.value ? "bg-white text-slate-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
                      data-testid={`services-filter-${opt.value}`}
                    >
                      {opt.label} <span className="text-gray-400 ml-1">({opt.count})</span>
                    </button>
                  ))}
                </div>
              )}

              {services.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No services yet. Add your first service!</div>
              ) : (() => {
                const filtered = services.filter(s => servicesFilter === "all" || (servicesFilter === "public" && s.public !== false) || (servicesFilter === "internal" && s.public === false));
                if (filtered.length === 0) {
                  return <div className="text-center py-6 text-sm text-gray-500">No {servicesFilter === "public" ? "customer-facing" : "internal"} services yet.</div>;
                }
                return (
                <div className="space-y-2">
                  {filtered.map((service, index) => (
                    <div key={service.id} className={`border rounded-lg p-3 ${!service.active ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-900 truncate">{service.label}{service.public === false && <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">Internal</span>}</h4>
                            {service.is_modifier && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-800">Add-on</Badge>}
                            {!service.active && <Badge variant="outline" className="text-xs">Hidden</Badge>}
                          </div>
                          {service.description && <p className="text-sm text-gray-500 mt-1 line-clamp-1">{service.description}</p>}
                          <p className="text-sm text-slate-900 font-medium mt-1">
                            {service.is_modifier ? (service.modifier_type === "percentage" ? `+${service.modifier_percent || 0}%` : `+$${service.modifier_fixed || 0}`) : (formatPrice(service) || "No price")}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleReorderService(service.id, -1)} disabled={index === 0} className="h-8 w-8 p-0">↑</Button>
                          <Button variant="ghost" size="sm" onClick={() => handleReorderService(service.id, 1)} disabled={index === services.length - 1} className="h-8 w-8 p-0">↓</Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditService(service)} className="h-8 w-8 p-0">✏️</Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteService(service.id)} className="h-8 w-8 p-0 text-red-600">🗑️</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                );
              })()}
            </CardContent>
          )}
        </Card>

        {/* Custom Questions Section */}
        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 cursor-pointer"
            onClick={() => setFormData(prev => ({ ...prev, _customQuestionsExpanded: !prev._customQuestionsExpanded }))}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="text-lg flex-shrink-0">📋</span>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">Custom Questions</p>
                <p className="text-xs text-gray-500 truncate">
                  {formData.field_config?.custom_questions?.length > 0 
                    ? `${formData.field_config.custom_questions.length} question(s) configured`
                    : 'Add multiple-choice questions'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button 
                size="sm" 
                variant="outline"
                className="text-xs px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  const newQ = {
                    id: `q_${Date.now()}`,
                    label: '',
                    type: 'radio',
                    options: ['', ''],
                    required: false
                  };
                  const currentQuestions = formData.field_config?.custom_questions || [];
                  setFormData(prev => ({
                    ...prev,
                    _customQuestionsExpanded: true,
                    field_config: { ...prev.field_config, custom_questions: [...currentQuestions, newQ] }
                  }));
                }}
              >
                + Add
              </Button>
              <svg className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${formData._customQuestionsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          {formData._customQuestionsExpanded && formData.field_config?.custom_questions?.length > 0 && (
            <CardContent className="pt-0 px-3 pb-4 space-y-4 border-t">
              {formData.field_config.custom_questions.map((q, qIdx) => (
                <div key={q.id} className="border rounded-lg p-3 space-y-3 bg-gray-50">
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <Input
                        value={q.label}
                        onChange={(e) => {
                          const updated = [...formData.field_config.custom_questions];
                          updated[qIdx] = { ...updated[qIdx], label: e.target.value };
                          setFormData(prev => ({ ...prev, field_config: { ...prev.field_config, custom_questions: updated } }));
                        }}
                        placeholder="Question"
                        className="flex-1 text-sm min-w-0"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 px-2 flex-shrink-0"
                        onClick={() => {
                          const updated = formData.field_config.custom_questions.filter((_, i) => i !== qIdx);
                          setFormData(prev => ({ ...prev, field_config: { ...prev.field_config, custom_questions: updated } }));
                        }}
                      >
                        ✕
                      </Button>
                    </div>
                    <select
                      value={q.type}
                      onChange={(e) => {
                        const updated = [...formData.field_config.custom_questions];
                        updated[qIdx] = { ...updated[qIdx], type: e.target.value };
                        setFormData(prev => ({ ...prev, field_config: { ...prev.field_config, custom_questions: updated } }));
                      }}
                      className="border rounded px-2 py-1 text-sm w-full"
                    >
                      <option value="radio">Single Select</option>
                      <option value="checkbox">Multi Select</option>
                      <option value="text">Text Answer</option>
                    </select>
                  </div>
                  
                  {/* Individual Option Fields - Only show for radio/checkbox */}
                  {q.type !== 'text' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Options (max 10)</Label>
                    {(q.options || ['', '']).map((opt, optIdx) => (
                      <div key={optIdx} className="flex gap-2">
                        <Input
                          value={opt}
                          onChange={(e) => {
                            const updated = [...formData.field_config.custom_questions];
                            const newOptions = [...(updated[qIdx].options || ['', ''])];
                            newOptions[optIdx] = e.target.value;
                            updated[qIdx] = { ...updated[qIdx], options: newOptions };
                            setFormData(prev => ({ ...prev, field_config: { ...prev.field_config, custom_questions: updated } }));
                          }}
                          placeholder={`Option ${optIdx + 1}`}
                          className="flex-1 text-sm min-w-0"
                        />
                        {(q.options || []).length > 2 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-gray-400 hover:text-red-500 px-2 flex-shrink-0"
                            onClick={() => {
                              const updated = [...formData.field_config.custom_questions];
                              const newOptions = (updated[qIdx].options || []).filter((_, i) => i !== optIdx);
                              updated[qIdx] = { ...updated[qIdx], options: newOptions };
                              setFormData(prev => ({ ...prev, field_config: { ...prev.field_config, custom_questions: updated } }));
                            }}
                          >
                            ✕
                          </Button>
                        )}
                      </div>
                    ))}
                    {(q.options || []).length < 10 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-900 hover:text-slate-900 text-xs"
                        onClick={() => {
                          const updated = [...formData.field_config.custom_questions];
                          const newOptions = [...(updated[qIdx].options || ['', '']), ''];
                          updated[qIdx] = { ...updated[qIdx], options: newOptions };
                          setFormData(prev => ({ ...prev, field_config: { ...prev.field_config, custom_questions: updated } }));
                        }}
                      >
                        + Add Option
                      </Button>
                    )}
                  </div>
                  )}
                  
                  {/* Text input hint for text type */}
                  {q.type === 'text' && (
                    <p className="text-xs text-gray-500 italic">Customers will see a text input field to type their answer.</p>
                  )}
                  
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={q.required || false}
                      onChange={(e) => {
                        const updated = [...formData.field_config.custom_questions];
                        updated[qIdx] = { ...updated[qIdx], required: e.target.checked };
                        setFormData(prev => ({ ...prev, field_config: { ...prev.field_config, custom_questions: updated } }));
                      }}
                    />
                    Required
                  </label>
                </div>
              ))}
              
              <Button onClick={handleSave} disabled={saving} className="w-full">
                {saving ? "Saving..." : "💾 Save Questions"}
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Business Information - Collapsible */}
        <Card className="border border-gray-200">
          <div 
            onClick={() => setBusinessInfoExpanded(!businessInfoExpanded)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">🏢</span>
              <div>
                <p className="font-semibold text-gray-900">Business Information</p>
                <p className="text-xs text-gray-500">{business?.name || 'Set up your business details'}</p>
              </div>
            </div>
            <svg className={`w-5 h-5 text-gray-500 transition-transform ${businessInfoExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          {businessInfoExpanded && (
            <CardContent className="pt-0 px-4 pb-4 space-y-4 border-t">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Business Name</Label>
                  {isNameChangeDisabled ? (
                    <span className="text-xs text-red-500">Limit reached. Resets {nameChangeResetDate}</span>
                  ) : (
                    <span className="text-xs text-gray-500">{nameChangesRemaining} of 2 changes remaining</span>
                  )}
                </div>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} disabled={isNameChangeDisabled && formData.name === business?.name} className={isNameChangeDisabled && formData.name === business?.name ? "bg-gray-100" : ""} />
                {nameChangesRemaining < 2 && <p className="text-xs text-amber-600">⚠️ Changing name will also update your public URL.</p>}
              </div>
              <div className="space-y-2">
                <Label>Notification Email</Label>
                <Input type="email" value={formData.notify_email} onChange={(e) => setFormData({ ...formData, notify_email: e.target.value })} placeholder="Where to receive notifications" />
              </div>
              <div className="space-y-2">
                <Label>Reply-To Email</Label>
                <Input type="email" value={formData.reply_to_email} onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })} placeholder="Customer reply address" />
              </div>
              <ChemicalsSettingsBlock formData={formData} setFormData={setFormData} />
              <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">{saving ? "Saving..." : "💾 Save Business Info"}</Button>
            </CardContent>
          )}
        </Card>

        {/* Add to Website */}
        {isSetupComplete ? (
          <div className="border rounded-lg bg-white">
            <button onClick={() => setShowEmbedCode(!showEmbedCode)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50">
              <span className="text-sm font-medium text-gray-700 flex items-center gap-2"><span>📋</span> Add Form to Your Website</span>
              <svg className={`w-4 h-4 text-gray-500 ${showEmbedCode ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {showEmbedCode && (
              <div className="px-4 pb-4 border-t">
                <p className="text-xs text-gray-500 mt-3 mb-2">Copy this code and paste it into your website:</p>
                <div className="bg-gray-100 p-3 rounded text-xs font-mono break-all mb-2">{`<iframe src="${BACKEND_URL?.replace('/api', '')}/t/${slug}" width="100%" height="700" frameborder="0"></iframe>`}</div>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`<iframe src="${BACKEND_URL?.replace('/api', '')}/t/${slug}" width="100%" height="700" frameborder="0"></iframe>`); toast.success("Copied!"); }}>Copy Code</Button>
              </div>
            )}
          </div>
        ) : (
          <div className="border rounded-lg bg-gray-50 p-4">
            <div className="flex items-center gap-2 text-gray-500"><span>🔒</span><span className="text-sm font-medium">Add to Website</span></div>
            <p className="text-xs text-gray-400 mt-1">Complete setup to unlock</p>
          </div>
        )}

        {/* Quote Resources - Collapsible */}
        <Card className="border border-gray-200">
          <div 
            onClick={() => setQuoteResourcesExpanded(!quoteResourcesExpanded)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">📎</span>
              <div>
                <p className="font-semibold text-gray-900">Quote Resources</p>
                <p className="text-xs text-gray-500">Documents, flyers & videos to include in quote emails</p>
              </div>
            </div>
            <svg className={`w-5 h-5 text-gray-500 transition-transform ${quoteResourcesExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          {quoteResourcesExpanded && (
            <CardContent className="pt-0 px-4 pb-4 space-y-4 border-t">
              {/* PDF/Image Resource */}
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-500">📎</span>
                    <Label className="font-medium text-sm">Document / Flyer</Label>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={quoteResources.pdf_enabled}
                      onChange={(e) => setQuoteResources(prev => ({ ...prev, pdf_enabled: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-xs text-gray-600">Include</span>
                  </label>
                </div>
                <div className="space-y-2">
                  {!quoteResources.pdf_url ? (
                    <>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,image/*,application/pdf"
                        onChange={handlePdfUpload}
                        disabled={uploadingPdf}
                        className="text-xs w-full file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                      />
                      <p className="text-xs text-gray-400">PDF or image (JPG, PNG, GIF, WebP) • Max 10MB</p>
                      {uploadingPdf && <span className="text-xs text-gray-500">Uploading...</span>}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <span>✓ Uploaded</span>
                      <a href={quoteResources.pdf_url.startsWith('/') ? `${BACKEND_URL}${quoteResources.pdf_url}` : quoteResources.pdf_url} target="_blank" rel="noopener noreferrer" className="underline">Preview</a>
                      <button 
                        onClick={() => setQuoteResources(prev => ({ ...prev, pdf_url: "", pdf_label: "" }))}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        ✕ Remove
                      </button>
                    </div>
                  )}
                </div>
                <Input
                  placeholder="Button label (e.g., View Pricing Sheet)"
                  value={quoteResources.pdf_label}
                  onChange={(e) => setQuoteResources(prev => ({ ...prev, pdf_label: e.target.value }))}
                  className="text-sm h-8"
                />
              </div>

              {/* Video Resource */}
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600">🎬</span>
                    <Label className="font-medium text-sm">Video Link</Label>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={quoteResources.video_enabled}
                      onChange={(e) => setQuoteResources(prev => ({ ...prev, video_enabled: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-xs text-gray-600">Include</span>
                  </label>
                </div>
                <Input
                  type="url"
                  placeholder="https://youtube.com/watch?v=..."
                  value={quoteResources.video_url}
                  onChange={(e) => setQuoteResources(prev => ({ ...prev, video_url: e.target.value }))}
                  className="text-sm h-8"
                />
                <Input
                  placeholder="Button label (e.g., Watch How We Work)"
                  value={quoteResources.video_label}
                  onChange={(e) => setQuoteResources(prev => ({ ...prev, video_label: e.target.value }))}
                  className="text-sm h-8"
                />
              </div>

              <Button onClick={handleSaveQuoteResources} disabled={saving} className="w-full">
                {saving ? "Saving..." : "💾 Save Resources"}
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Subscription Management - Collapsible at bottom */}
        <Card className="border border-gray-200">
          <div 
            onClick={() => setSubscriptionExpanded(!subscriptionExpanded)} 
            className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between text-left hover:bg-gray-50 cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">💳</span>
              <div>
                <p className="font-semibold text-gray-900">Subscription</p>
                <p className="text-xs text-gray-500">
                  {business?.subscription_status === 'active' ? '✓ Active' :
                   business?.subscription_status === 'trialing' ? '⏱ Free Trial' :
                   business?.subscription_status === 'paused' ? '⏸ Paused' :
                   '✗ Inactive'}
                </p>
              </div>
            </div>
            <svg className={`w-5 h-5 text-gray-500 transition-transform ${subscriptionExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          
          {subscriptionExpanded && (
            <CardContent className="pt-0 px-4 pb-4 space-y-4">
              {/* Current Status Display */}
              <div className="p-4 rounded-lg bg-gray-50 border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Current Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    business?.subscription_status === 'active' ? 'bg-blue-100 text-slate-900' :
                    business?.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-800' :
                    business?.subscription_status === 'paused' ? 'bg-amber-100 text-amber-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {business?.subscription_status === 'active' ? '✓ Active' :
                     business?.subscription_status === 'trialing' ? '⏱ Free Trial' :
                     business?.subscription_status === 'paused' ? '⏸ Paused' :
                     '✗ Inactive'}
                  </span>
                </div>
                
                {business?.subscription_status === 'trialing' && business?.trial_ends_at && (
                  <p className="text-sm text-gray-600">
                    Trial ends: {new Date(business.trial_ends_at).toLocaleDateString()}
                    {new Date(business.trial_ends_at) > new Date() 
                      ? ` (${Math.ceil((new Date(business.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))} days left)`
                      : ' (Expired)'}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Billing Interval Toggle - for non-active users */}
                {business?.subscription_status !== 'active' && (
                  <div className="flex items-center justify-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <button
                      onClick={() => setSelectedBillingInterval("monthly")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedBillingInterval === "monthly" ? "bg-slate-900 text-white shadow-md" : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300"}`}
                    >
                      $29/mo
                    </button>
                    <button
                      onClick={() => setSelectedBillingInterval("annual")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedBillingInterval === "annual" ? "bg-slate-900 text-white shadow-md" : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300"}`}
                    >
                      $290/yr
                      <span className="ml-1 text-xs text-blue-600 font-normal">Save $58</span>
                    </button>
                  </div>
                )}
                
                {/* Subscribe/Reactivate button - for non-active users */}
                {business?.subscription_status !== 'active' && (
                  <Button 
                    className="w-full bg-slate-900 hover:bg-slate-900 py-5 cursor-pointer"
                    onClick={async () => {
                      try {
                        setLoadingPortal(true);
                        const result = await api.createSubscriptionCheckout(slug, selectedBillingInterval);
                        window.location.href = result.checkout_url;
                      } catch (error) {
                        toast.error("Failed to start checkout");
                      } finally {
                        setLoadingPortal(false);
                      }
                    }}
                    disabled={loadingPortal}
                    data-testid="subscribe-now-btn"
                  >
                    {loadingPortal ? "Loading..." : `🚀 Subscribe Now - ${selectedBillingInterval === "annual" ? "$290/year" : "$29/month"}`}
                  </Button>
                )}

                {/* Manage Billing - for active users */}
                {business?.subscription_status === 'active' && (
                  <Button 
                    onClick={handleManageSubscription} 
                    disabled={loadingPortal} 
                    className="w-full bg-slate-900 hover:bg-slate-900 cursor-pointer"
                    data-testid="manage-billing-btn"
                  >
                    {loadingPortal ? "Loading..." : "📄 Manage Billing & Invoices"}
                  </Button>
                )}

                {/* Upgrade to Annual - for active monthly subscribers */}
                {business?.subscription_status === 'active' && (
                  <Button 
                    onClick={async () => {
                      try {
                        setLoadingPortal(true);
                        const response = await axios.post(`${API}/tenants/${slug}/upgrade-to-annual`, {}, { headers: getAuthHeaders() });
                        window.location.href = response.data.url;
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to open upgrade");
                      } finally {
                        setLoadingPortal(false);
                      }
                    }}
                    disabled={loadingPortal}
                    variant="outline" 
                    className="w-full cursor-pointer border-blue-600 text-blue-700 hover:bg-blue-50"
                    data-testid="upgrade-annual-btn"
                  >
                    {loadingPortal ? "Loading..." : "💰 Switch to Annual (Save $58/yr)"}
                  </Button>
                )}

                {/* Update Payment Method - for active, trialing, paused users */}
                {(business?.subscription_status === 'active' || business?.subscription_status === 'trialing' || business?.subscription_status === 'paused') && (
                  <Button 
                    onClick={handleManageSubscription} 
                    disabled={loadingPortal} 
                    variant="outline"
                    className="w-full cursor-pointer"
                    data-testid="update-payment-btn"
                  >
                    {loadingPortal ? "Loading..." : "💳 Update Payment Method"}
                  </Button>
                )}

                {/* Pause button - opens confirmation modal */}
                {business?.subscription_status === 'active' && (
                  <Button 
                    variant="outline"
                    className="w-full cursor-pointer"
                    onClick={() => openSubscriptionModal('pause')}
                    disabled={loadingPortal}
                    data-testid="pause-subscription-btn"
                  >
                    ⏸ Pause Subscription
                  </Button>
                )}

                {/* Resume button - for paused subscribers */}
                {business?.subscription_status === 'paused' && (
                  <Button 
                    className="w-full bg-blue-700 hover:bg-blue-800 cursor-pointer"
                    onClick={async () => {
                      try {
                        setLoadingPortal(true);
                        await api.businessManageSubscription(slug, 'resume');
                        toast.success("Subscription resumed!");
                        onRefresh();
                      } catch (error) {
                        toast.error("Failed to resume");
                      } finally {
                        setLoadingPortal(false);
                      }
                    }}
                    disabled={loadingPortal}
                    data-testid="resume-subscription-btn"
                  >
                    ▶ Resume Subscription
                  </Button>
                )}

                {/* Cancel button - opens confirmation modal */}
                {(business?.subscription_status === 'active' || business?.subscription_status === 'paused') && (
                  <Button 
                    variant="ghost"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                    onClick={() => openSubscriptionModal('cancel')}
                    disabled={loadingPortal}
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </CardContent>
          )}
        </Card>

      </div>
      
      <MobilePreviewSheet 
        intakeMode={formData.intake_mode} 
        fieldConfig={formData.field_config} 
        services={services} 
        logoUrl={logoPreview || business?.logo_url} 
        businessName={formData.name || business?.name} 
        slug={slug}
        business={business}
        showOnAllScreens={true}
        externalOpen={openFormPreview}
        closePreviewTrigger={closePreviewTrigger}
        onExternalOpenChange={(val) => {
          setOpenFormPreview(val);
          // Notify parent when preview opens (for tour)
          if (val && onPreviewOpen) onPreviewOpen();
        }}
        onStep2Complete={handleStep2Complete}
        onPreviewOpen={onPreviewOpen}
        onEditorExpand={onEditorExpand}
        onIntakeModeChange={(mode) => {
          setFormData(prev => ({ ...prev, intake_mode: mode }));
          // Notify parent of mode change (for tour)
          if (onFormModeChange) onFormModeChange(mode);
          // Changing form mode counts as customization - mark Step 2 complete
          handleStep2Complete();
        }}
        onFieldConfigChange={(key, value) => setFormData(prev => ({ 
          ...prev, 
          field_config: { ...prev.field_config, [key]: value } 
        }))}
        onLogoChange={(file) => {
          if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
          } else {
            setLogoFile(null);
            setLogoPreview(null);
          }
        }}
        onSave={async (config, newLogoFile) => {
          setSaving(true);
          try {
            // Extract shop customization fields to top level
            const { shop_headline, shop_headline_size, shop_headline_color, shop_footer, shop_footer_size, shop_footer_color, ...fieldConfigFields } = config;
            const updatedFormData = { 
              ...formData, 
              field_config: { ...formData.field_config, ...fieldConfigFields },
              shop_headline,
              shop_headline_size,
              shop_headline_color,
              shop_footer,
              shop_footer_size,
              shop_footer_color
            };
            await api.updateBusiness(slug, updatedFormData);
            if (newLogoFile) {
              await api.uploadLogo(slug, newLogoFile);
            }
            await onRefresh();
          } catch (error) {
            console.error("Save error:", error);
            throw error;
          } finally {
            setSaving(false);
          }
        }}
      />
      
      {/* Add/Edit Service Dialog */}
      <Dialog open={showAddServiceDialog} onOpenChange={setShowAddServiceDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingService ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Service Name *</Label>
              <Input value={serviceFormData.label} onChange={(e) => setServiceFormData({ ...serviceFormData, label: e.target.value })} placeholder="e.g., Lawn Mowing" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={serviceFormData.description} onChange={(e) => setServiceFormData({ ...serviceFormData, description: e.target.value })} placeholder="Brief description" rows={2} />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div>
                <Label className="text-sm font-medium">This is a modifier</Label>
                <p className="text-xs text-gray-500">For add-ons like "Rush +20%" or "After Hours +$50"</p>
              </div>
              <Switch checked={serviceFormData.is_modifier} onCheckedChange={(v) => setServiceFormData({ ...serviceFormData, is_modifier: v })} />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <Label className="text-sm font-medium">{serviceFormData.public ? "Public" : "Internal"}</Label>
                <p className="text-xs text-gray-500">{serviceFormData.public ? "Visible on customer intake form" : "Only visible in quote builder"}</p>
              </div>
              <Switch checked={serviceFormData.public} onCheckedChange={(v) => setServiceFormData({ ...serviceFormData, public: v })} />
            </div>
            
            {serviceFormData.is_modifier ? (
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                <Label className="text-sm font-medium">Modifier Type</Label>
                <Select value={serviceFormData.modifier_type} onValueChange={(v) => setServiceFormData({ ...serviceFormData, modifier_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (+%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (+$)</SelectItem>
                  </SelectContent>
                </Select>
                {serviceFormData.modifier_type === "percentage" ? (
                  <div className="flex items-center gap-2">
                    <Input type="number" value={serviceFormData.modifier_percent} onChange={(e) => setServiceFormData({ ...serviceFormData, modifier_percent: e.target.value })} placeholder="20" className="w-24" />
                    <span>%</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>$</span>
                    <Input type="number" value={serviceFormData.modifier_fixed} onChange={(e) => setServiceFormData({ ...serviceFormData, modifier_fixed: e.target.value })} placeholder="50" className="w-24" />
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Pricing Type</Label>
                  <Select value={serviceFormData.pricing_type} onValueChange={(v) => setServiceFormData({ ...serviceFormData, pricing_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No price</SelectItem>
                      <SelectItem value="fixed">Fixed price</SelectItem>
                      <SelectItem value="starting_at">Starting at</SelectItem>
                      <SelectItem value="per_unit">Per unit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {serviceFormData.pricing_type !== "none" && (
                  <div className="flex items-center gap-2">
                    <span>$</span>
                    <Input type="number" value={serviceFormData.price_value} onChange={(e) => setServiceFormData({ ...serviceFormData, price_value: e.target.value })} placeholder="0.00" />
                  </div>
                )}
                {serviceFormData.pricing_type === "per_unit" && (
                  <Input value={serviceFormData.unit_label} onChange={(e) => setServiceFormData({ ...serviceFormData, unit_label: e.target.value })} placeholder="per sq ft, per hour" />
                )}
              </>
            )}
            
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={serviceFormData.active} onCheckedChange={(v) => setServiceFormData({ ...serviceFormData, active: v })} />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowAddServiceDialog(false); setEditingService(null); resetServiceForm(); }}>Cancel</Button>
              <Button onClick={handleSaveService} disabled={savingService}>{savingService ? "Saving..." : (editingService ? "Update" : "Create")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Confirmation Modal */}
      <Dialog open={!!showSubscriptionModal} onOpenChange={() => closeSubscriptionModal()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={showSubscriptionModal === 'cancel' ? 'text-red-600' : 'text-amber-600'}>
              {showSubscriptionModal === 'cancel' ? '⚠️ Cancel Subscription' : '⏸ Pause Subscription'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${showSubscriptionModal === 'cancel' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
              <p className="text-sm text-gray-700">
                {showSubscriptionModal === 'cancel' 
                  ? 'This will permanently cancel your subscription. Your intake link will be disabled and you will lose access to premium features.'
                  : 'This will pause your subscription. Your intake link will be temporarily disabled until you resume.'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Type <span className="font-bold text-gray-900">{showSubscriptionModal?.toUpperCase()}</span> to confirm:
              </Label>
              <Input 
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={`Type ${showSubscriptionModal?.toUpperCase()} here`}
                className="font-mono"
                autoFocus
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={closeSubscriptionModal}>
                Keep Subscription
              </Button>
              <Button 
                variant={showSubscriptionModal === 'cancel' ? 'destructive' : 'default'}
                className={showSubscriptionModal === 'pause' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                disabled={
                  confirmationText.toUpperCase() !== showSubscriptionModal?.toUpperCase() || 
                  countdown > 0 || 
                  loadingPortal
                }
                onClick={() => handleSubscriptionAction(showSubscriptionModal)}
              >
                {loadingPortal 
                  ? 'Processing...' 
                  : countdown > 0 && confirmationText.toUpperCase() === showSubscriptionModal?.toUpperCase()
                    ? `Wait ${countdown}s...`
                    : showSubscriptionModal === 'cancel' 
                      ? 'Cancel Subscription' 
                      : 'Pause Subscription'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Your Public Link - Bottom of Setup */}
      <Card className="mt-2 overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
          onClick={() => setPublicLinkExpanded(!publicLinkExpanded)}
        >
          <div className="flex items-center gap-3">
            <span className="text-xl">🔗</span>
            <div>
              <h3 className="font-semibold text-gray-900">Your Public Link</h3>
              <p className="text-xs text-gray-500">Share this link with customers to access your intake form</p>
            </div>
          </div>
          <svg className={`w-5 h-5 text-gray-500 transition-transform ${publicLinkExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </div>
        {publicLinkExpanded && (
        <CardContent className="space-y-4 pt-0">
          {/* ZipLeadPro Link */}
          <div className="p-3 rounded-lg border-2 border-blue-800 bg-blue-50">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">ZipLeadPro Link</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 break-all">{formUrl || `${window.location.origin}/api/share/${slug}`}</code>
                  <button 
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(formUrl || `${window.location.origin}/api/share/${slug}`, 'Link'); }}
                    className="text-xs text-slate-900 hover:text-slate-900 self-start"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        )}
      </Card>
      
      {/* Spacer for floating Preview button */}
      <div className="h-20 lg:hidden" />
    </>
  );
};


// ============== LEADS SECTION ==============
const LeadsSection = ({ slug, leads, quotes, services, stripeStatus, onRefresh, setActiveSection, business, openQuoteDialog, setOpenQuoteDialog, quoteForLeadId, setQuoteForLeadId }) => {
  const subLocked = isSubInactive(business);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLeadInDialog, setEditingLeadInDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [collectingBalance, setCollectingBalance] = useState(null);
  const [balanceDialogQuote, setBalanceDialogQuote] = useState(null);
  const [finalTotalInput, setFinalTotalInput] = useState("");
  const [paymentLinkSentInfo, setPaymentLinkSentInfo] = useState(null); // {customerEmail, amount, url}
  const [showDepositTip, setShowDepositTip] = useState(() => {
    return sessionStorage.getItem('hideDepositTip') !== 'true';
  });
  // Tab control - switches to quotes tab when openQuoteDialog is triggered.
  // Also URL-synced: on reload (or any window.location.reload triggered by
  // payments, sends, etc.), `?tab=quotes` / `?tab=customers` keeps the user
  // on the sub-tab they were on instead of bouncing back to "leads".
  const [leadsTab, _setLeadsTabRaw] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("tab");
      if (t && ["leads", "quotes", "customers"].includes(t)) return t;
    } catch (_) { /* ignore */ }
    return "leads";
  });
  const setLeadsTab = useCallback((next) => {
    _setLeadsTabRaw((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get("tab") !== value) {
          params.set("tab", value);
          const qs = params.toString();
          const url = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
          window.history.replaceState(window.history.state, "", url);
        }
      } catch (_) { /* ignore */ }
      return value;
    });
  }, []);
  const navigate = useNavigate();
  
  // Mass select state for leads
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [deletingLeads, setDeletingLeads] = useState(false);
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  // Case-insensitive search across name, email, phone, and address — the
  // four fields a business owner is most likely to remember a customer by.
  const filteredCustomers = (() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q) ||
        (c.address || "").toLowerCase().includes(q)
      );
    });
  })();
  const [selectedCustomerEmails, setSelectedCustomerEmails] = useState(new Set());
  const [deletingCustomers, setDeletingCustomers] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", phone: "", address: "" });
  const [editingCustomer, setEditingCustomer] = useState(null);

  // Build a lookup: lead_id -> nicely-truncated service summary from linked quote line items.
  // Falls back from lead.service_type → lead.pricing_snapshot → quote.line_items.
  // This fixes "N/A" on leads that were created via the "send quote to a brand-new
  // customer" flow, where service_type was never captured on the lead row.
  const leadIdToQuoteServices = useMemo(() => {
    const map = {};
    for (const q of (quotes || [])) {
      if (!q.lead_id || map[q.lead_id]) continue;
      const items = (q.line_items || []).map(li => (li.description || "").trim()).filter(Boolean);
      if (items.length === 0) continue;
      map[q.lead_id] = items;
    }
    return map;
  }, [quotes]);

  const getLeadServiceLabel = (lead) => {
    if (lead.service_type) return lead.service_type;
    const ps = (lead.pricing_snapshot || []).map(p => p.label).filter(Boolean);
    if (ps.length > 0) {
      return ps.length <= 2 ? ps.join(", ") : `${ps.slice(0, 2).join(", ")} +${ps.length - 2}`;
    }
    const fromQuote = leadIdToQuoteServices[lead.id];
    if (fromQuote && fromQuote.length > 0) {
      return fromQuote.length <= 2 ? fromQuote.join(", ") : `${fromQuote.slice(0, 2).join(", ")} +${fromQuote.length - 2}`;
    }
    return "—";
  };
  const [editCustomerData, setEditCustomerData] = useState({ name: "", phone: "", address: "", notes: "" });
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [viewingCustomerItem, setViewingCustomerItem] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({ leads: [], quotes: [], measurements: [] });
  const [customerDetailsLoading, setCustomerDetailsLoading] = useState(false);

  const openCustomerDetails = async (customer) => {
    setViewingCustomer(customer);
    setCustomerDetailsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/tenants/${slug}/customers/${encodeURIComponent(customer.email)}/details`, { headers: { Authorization: `Bearer ${token}` } });
      setCustomerDetails(res.data);
    } catch { setCustomerDetails({ leads: [], quotes: [], measurements: [] }); }
    setCustomerDetailsLoading(false);
  };
  const [addingCustomer, setAddingCustomer] = useState(false);
  
  // Switch to quotes tab when openQuoteDialog becomes true
  useEffect(() => {
    if (openQuoteDialog) {
      setLeadsTab("quotes");
    }
  }, [openQuoteDialog]);
  
  const dismissDepositTip = () => {
    sessionStorage.setItem('hideDepositTip', 'true');
    setShowDepositTip(false);
  };
  
  // Get quote for a lead
  const getQuoteForLead = (leadId) => quotes?.find(q => q.lead_id === leadId);

  // Check for deep-link lead ID from email
  useEffect(() => {
    const openLeadId = sessionStorage.getItem("openLeadId");
    if (openLeadId && leads.length > 0) {
      const lead = leads.find(l => l.id === openLeadId);
      if (lead) {
        // Use setTimeout to avoid setState in sync effect
        setTimeout(() => {
          setSelectedLead(lead);
          toast.info("Opening lead from notification email");
        }, 0);
      }
      sessionStorage.removeItem("openLeadId");
    }
  }, [leads]);
  
  // Handle collect balance - instant charge or payment link fallback
  const handleCollectBalance = async (quote) => {
    const quotedTotal = quote.final_total || quote.quoted_total || quote.total || 0;
    const depositPaid = quote.deposit_paid_amount || 0;
    const isTBD = quote.final_price_tbd;
    
    // finalTotalInput now stores the charge amount directly
    const chargeAmount = finalTotalInput !== "" ? parseFloat(finalTotalInput) || 0 : Math.max(0, quotedTotal - depositPaid);
    const finalTotal = chargeAmount + depositPaid;
    
    if (chargeAmount <= 0) {
      toast.error("Amount to charge must be greater than $0");
      return;
    }
    
    if (isTBD && finalTotalInput === "") {
      toast.error("Please enter an amount to charge");
      return;
    }
    
    setCollectingBalance(quote.id);
    try {
      // Get lead info for the notification
      const lead = leads.find(l => l.id === quote.lead_id);
      
      const response = await api.collectBalance(slug, quote.id, {
        final_total: finalTotal,
        send_email: true  // Send receipt/payment link to customer
      });
      
      
      if (response.charged) {
        // Instant charge succeeded!
        toast.success(`Successfully charged $${response.amount_charged.toFixed(2)}! Customer's card has been charged.`, {
          duration: 5000
        });
        setBalanceDialogQuote(null);
        setFinalTotalInput("");
        onRefresh();
      } else if (response.checkout_url) {
        // Fallback to payment link (no saved card) - show prominent popup
        setBalanceDialogQuote(null);
        setFinalTotalInput("");
        // Show popup with all info - DON'T call onRefresh() yet as it resets component state
        setPaymentLinkSentInfo({
          customerEmail: lead?.customer_email || "customer",
          customerName: lead?.customer_name || "Customer",
          amount: response.balance_due,
          url: response.checkout_url,
          onClose: () => {
            setPaymentLinkSentInfo(null);
            onRefresh(); // Refresh AFTER popup is dismissed
          }
        });
      } else {
        toast.error("Unexpected response from server");
      }
    } catch (error) {
      console.error("[COLLECT BALANCE] Error:", error);
      toast.error(error.response?.data?.detail || "Failed to collect balance");
    } finally {
      setCollectingBalance(null);
    }
  };

  const handleDeleteLead = async (leadId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await api.deleteLead(leadId);
      toast.success("Lead deleted");
      setSelectedLead(null);
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  // Mass select helpers for leads
  const toggleLeadSelect = (leadId, e) => {
    if (e) e.stopPropagation();
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      next.has(leadId) ? next.delete(leadId) : next.add(leadId);
      return next;
    });
  };
  const toggleAllLeads = () => {
    if (selectedLeadIds.size === filteredLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(filteredLeads.map(l => l.id)));
    }
  };
  const handleBulkDeleteLeads = async () => {
    if (selectedLeadIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedLeadIds.size} lead${selectedLeadIds.size > 1 ? 's' : ''} and their quotes? This cannot be undone.`)) return;
    setDeletingLeads(true);
    try {
      await api.bulkDeleteLeads(slug, Array.from(selectedLeadIds));
      toast.success(`${selectedLeadIds.size} lead${selectedLeadIds.size > 1 ? 's' : ''} deleted`);
      setSelectedLeadIds(new Set());
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete leads");
    } finally {
      setDeletingLeads(false);
    }
  };

  // Customer management
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const data = await api.getCustomers(slug);
      setCustomers(data);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setCustomersLoading(false);
    }
  };
  const toggleCustomerSelect = (email) => {
    setSelectedCustomerEmails(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };
  const toggleAllCustomers = () => {
    if (selectedCustomerEmails.size === customers.length) {
      setSelectedCustomerEmails(new Set());
    } else {
      setSelectedCustomerEmails(new Set(customers.map(c => c.email)));
    }
  };
  const handleDeleteCustomer = async (email) => {
    if (!window.confirm("Delete this customer and all their leads/quotes? This cannot be undone.")) return;
    try {
      await api.deleteCustomer(slug, email);
      toast.success("Customer deleted");
      fetchCustomers();
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };
  
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setEditCustomerData({
      name: customer.name || "",
      phone: customer.phone || "",
      address: customer.address || "",
      notes: customer.notes || ""
    });
  };
  
  const handleSaveCustomer = async () => {
    if (!editingCustomer) return;
    setSavingCustomer(true);
    try {
      await api.updateCustomer(slug, editingCustomer.email, editCustomerData);
      toast.success("Customer updated");
      setEditingCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update customer"));
    } finally {
      setSavingCustomer(false);
    }
  };
  
  const handleBulkDeleteCustomers = async () => {
    if (selectedCustomerEmails.size === 0) return;
    if (!window.confirm(`Delete ${selectedCustomerEmails.size} customer${selectedCustomerEmails.size > 1 ? 's' : ''} and all their leads/quotes? This cannot be undone.`)) return;
    setDeletingCustomers(true);
    try {
      await api.bulkDeleteCustomers(slug, Array.from(selectedCustomerEmails));
      toast.success(`${selectedCustomerEmails.size} customer${selectedCustomerEmails.size > 1 ? 's' : ''} deleted`);
      setSelectedCustomerEmails(new Set());
      fetchCustomers();
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete customers");
    } finally {
      setDeletingCustomers(false);
    }
  };
  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email) {
      toast.error("Name and email are required");
      return;
    }
    setAddingCustomer(true);
    try {
      await api.createCustomer(slug, newCustomer);
      toast.success("Customer added");
      setNewCustomer({ name: "", email: "", phone: "", address: "" });
      setShowAddCustomer(false);
      fetchCustomers();
      onRefresh();
    } catch (error) {
      toast.error("Failed to add customer");
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleQuoteLead = (lead) => {
    setSelectedLead(null);
    if (setQuoteForLeadId) setQuoteForLeadId(lead.id);
    if (setOpenQuoteDialog) setOpenQuoteDialog(true);
  };

  // Clickable status badge - navigates to relevant action when applicable
  const getStatusBadge = (status, lead = null, clickable = true) => {
    const quote = lead ? getQuoteForLead(lead.id) : null;
    
    // Check for scheduled status first (lifecycle_status)
    if (lead?.lifecycle_status === "scheduled" && lead?.scheduled_date) {
      const dateStr = new Date(lead.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return (
        <Badge 
          className={`bg-blue-100 text-slate-900 ${clickable ? 'cursor-pointer hover:bg-blue-200 transition-colors' : ''}`}
          onClick={clickable ? (e) => { e.stopPropagation(); setActiveSection("calendar"); } : undefined}
          title={clickable ? "View in Calendar" : undefined}
        >
          📅 {dateStr}
        </Badge>
      );
    }
    if (lead?.lifecycle_status === "unscheduled") {
      return (
        <Badge 
          className={`bg-amber-100 text-amber-800 ${clickable ? 'cursor-pointer hover:bg-amber-200 transition-colors' : ''}`}
          onClick={clickable ? (e) => { e.stopPropagation(); setActiveSection("calendar"); } : undefined}
          title={clickable ? "Schedule this job" : undefined}
        >
          ⏳ Needs Scheduling
        </Badge>
      );
    }
    
    const variants = {
      new: "bg-blue-100 text-blue-800",
      contacted: "bg-amber-100 text-amber-800",
      quoted: "bg-blue-100 text-slate-900",
      deposit_paid: "bg-amber-100 text-amber-800",
      paid: "bg-blue-100 text-slate-900",
      completed: "bg-blue-100 text-slate-900",
      closed: "bg-gray-100 text-gray-800",
      cancelled: "bg-gray-100 text-gray-800",
    };
    const labels = {
      new: "New",
      contacted: "Contacted",
      quoted: "Quoted",
      deposit_paid: "Deposit Paid",
      paid: "Paid",
      completed: "Completed",
      closed: "Closed",
      cancelled: "Cancelled",
    };
    
    // Determine if this status should be clickable and what action it triggers
    const getClickHandler = () => {
      if (!clickable || !lead) return undefined;
      
      switch (status) {
        case "new":
          // Open full quote builder dialog with this lead pre-selected
          return (e) => { 
            e.stopPropagation(); 
            if (setQuoteForLeadId) setQuoteForLeadId(lead.id);
            if (setOpenQuoteDialog) setOpenQuoteDialog(true);
          };
        case "deposit_paid":
          // Open collect balance dialog if quote exists
          if (quote) {
            return (e) => { e.stopPropagation(); setBalanceDialogQuote(quote); };
          }
          return undefined;
        default:
          return undefined;
      }
    };
    
    const getTitle = () => {
      if (!clickable) return undefined;
      switch (status) {
        case "new": return "Create quote for this lead";
        case "deposit_paid": return quote ? "Collect remaining balance" : undefined;
        default: return undefined;
      }
    };
    
    const clickHandler = getClickHandler();
    const isClickable = clickable && !!clickHandler;
    
    return (
      <Badge 
        className={`${variants[status] || "bg-gray-100 text-gray-800"} ${isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
        onClick={clickHandler}
        title={getTitle()}
      >
        {labels[status] || status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleString();

  const filteredLeads = selectedStatus && selectedStatus !== "all"
    ? leads.filter(l => l.status === selectedStatus)
    : leads;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Leads & Quotes</h2>
      </div>

      <Tabs value={leadsTab} onValueChange={(v) => { setLeadsTab(v); if (v === "customers") fetchCustomers(); }} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
        </TabsList>
        
        <TabsContent value="leads" className="mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-800">All Leads</h3>
              {selectedLeadIds.size > 0 && (
                <Button 
                  size="sm" variant="destructive" 
                  onClick={handleBulkDeleteLeads} 
                  disabled={deletingLeads}
                  data-testid="bulk-delete-leads-btn"
                >
                  {deletingLeads ? "Deleting..." : `Delete ${selectedLeadIds.size} selected`}
                </Button>
              )}
            </div>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="quoted">Quoted</SelectItem>
                <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              {filteredLeads.length === 0 ? (
                <div className="text-center py-8 sm:py-12 text-gray-500 text-sm sm:text-base">No leads yet</div>
              ) : (
                <>
                  {/* Mobile Card View */}
                  <div className="sm:hidden divide-y">
                    {filteredLeads.map((lead) => (
                      <div 
                        key={lead.id} 
                        className={`p-4 space-y-2 cursor-pointer hover:bg-gray-50 ${selectedLeadIds.has(lead.id) ? 'bg-blue-50' : ''}`}
                        onClick={() => setSelectedLead(lead)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <input 
                              type="checkbox" 
                              checked={selectedLeadIds.has(lead.id)}
                              onChange={(e) => toggleLeadSelect(lead.id, e)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-4 w-4 rounded border-gray-300 accent-blue-800 shrink-0"
                              data-testid={`lead-checkbox-${lead.id}`}
                            />
                            <div className="min-w-0">
                              <div className="font-medium text-sm flex items-center gap-1.5">
                                {lead.customer_name || "N/A"}
                                {lead.is_demo && <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">Demo</Badge>}
                              </div>
                              <div className="text-xs text-gray-500">{lead.customer_email}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(lead.status, lead)}
                            <button
                              onClick={(e) => handleDeleteLead(lead.id, e)}
                              className="text-gray-400 hover:text-red-500 p-1"
                              title="Delete lead"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{getLeadServiceLabel(lead)}</span>
                          <span>{formatDate(lead.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Desktop Table View */}
                  <div className="hidden sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <input 
                              type="checkbox" 
                              checked={filteredLeads.length > 0 && selectedLeadIds.size === filteredLeads.length}
                              onChange={toggleAllLeads}
                              className="h-4 w-4 rounded border-gray-300 accent-blue-800"
                              data-testid="lead-select-all"
                            />
                          </TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLeads.map((lead) => (
                          <TableRow key={lead.id} className={`cursor-pointer hover:bg-gray-50 ${selectedLeadIds.has(lead.id) ? 'bg-blue-50' : ''}`} onClick={() => setSelectedLead(lead)}>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox" 
                                checked={selectedLeadIds.has(lead.id)}
                                onChange={(e) => toggleLeadSelect(lead.id, e)}
                                className="h-4 w-4 rounded border-gray-300 accent-blue-800"
                                data-testid={`lead-checkbox-${lead.id}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              <span className="flex items-center gap-1.5">
                                {lead.customer_name || "N/A"}
                                {lead.is_demo && <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">Demo</Badge>}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div>{lead.customer_email}</div>
                              {lead.customer_phone && <div className="text-sm text-gray-500">{lead.customer_phone}</div>}
                            </TableCell>
                            <TableCell>{getLeadServiceLabel(lead)}</TableCell>
                            <TableCell>{getStatusBadge(lead.status, lead)}</TableCell>
                            <TableCell className="text-sm">{formatDate(lead.created_at)}</TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={(e) => handleDeleteLead(lead.id, e)}
                                className="text-gray-400 hover:text-red-500 p-2"
                                title="Delete lead"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="quotes" className="mt-4">
          <QuotesSection slug={slug} quotes={quotes} leads={leads} services={services} stripeStatus={stripeStatus} onRefresh={onRefresh} openDialog={openQuoteDialog} onDialogOpened={() => setOpenQuoteDialog && setOpenQuoteDialog(false)} quoteForLeadId={quoteForLeadId} onLeadConsumed={() => setQuoteForLeadId && setQuoteForLeadId(null)} business={business} />
        </TabsContent>

        <TabsContent value="customers" className="mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-800">
                Customers <span className="text-sm font-normal text-gray-500" data-testid="customer-count">({customers.length.toLocaleString()})</span>
              </h3>
              {selectedCustomerEmails.size > 0 && (
                <Button 
                  size="sm" variant="destructive" 
                  onClick={handleBulkDeleteCustomers} 
                  disabled={deletingCustomers}
                  data-testid="bulk-delete-customers-btn"
                >
                  {deletingCustomers ? "Deleting..." : `Delete ${selectedCustomerEmails.size} selected`}
                </Button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="relative">
                <input
                  type="search"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Search by name, email, phone, address..."
                  className="w-full sm:w-72 pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid="customer-search-input"
                />
                <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <Button size="sm" onClick={() => setShowAddCustomer(true)} data-testid="add-customer-btn" className="w-full sm:w-auto">
                + Add Customer
              </Button>
            </div>
          </div>
          {customerSearch.trim() && customers.length > 0 && (
            <p className="text-xs text-gray-500 mb-2" data-testid="customer-search-results">
              {filteredCustomers.length} of {customers.length} matching "{customerSearch.trim()}"
            </p>
          )}

          {/* Add Customer Form */}
          {showAddCustomer && (
            <Card className="mb-4 border-blue-200">
              <CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Name *</Label>
                    <Input value={newCustomer.name} onChange={(e) => setNewCustomer(p => ({...p, name: e.target.value}))} placeholder="Customer name" data-testid="new-customer-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer(p => ({...p, email: e.target.value}))} placeholder="customer@email.com" data-testid="new-customer-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={newCustomer.phone} onChange={(e) => setNewCustomer(p => ({...p, phone: e.target.value}))} placeholder="(555) 123-4567" data-testid="new-customer-phone" />
                  </div>
                  <div>
                    <Label className="text-xs">Address</Label>
                    <Input value={newCustomer.address} onChange={(e) => setNewCustomer(p => ({...p, address: e.target.value}))} placeholder="123 Main St" data-testid="new-customer-address" />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => { setShowAddCustomer(false); setNewCustomer({ name: "", email: "", phone: "", address: "" }); }}>Cancel</Button>
                  <Button size="sm" onClick={handleAddCustomer} disabled={addingCustomer} data-testid="save-customer-btn">
                    {addingCustomer ? "Adding..." : "Add Customer"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Edit Customer Dialog */}
          <Dialog open={!!editingCustomer} onOpenChange={() => setEditingCustomer(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Customer</DialogTitle>
              </DialogHeader>
              {editingCustomer && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-gray-500">Email (cannot be changed)</Label>
                    <p className="text-sm font-medium">{editingCustomer.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input 
                      value={editCustomerData.name} 
                      onChange={(e) => setEditCustomerData(p => ({...p, name: e.target.value}))} 
                      placeholder="Customer name" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input 
                      value={editCustomerData.phone} 
                      onChange={(e) => setEditCustomerData(p => ({...p, phone: e.target.value}))} 
                      placeholder="(555) 123-4567" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Address</Label>
                    <Input 
                      value={editCustomerData.address} 
                      onChange={(e) => setEditCustomerData(p => ({...p, address: e.target.value}))} 
                      placeholder="123 Main St" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Notes</Label>
                    <Textarea 
                      value={editCustomerData.notes} 
                      onChange={(e) => setEditCustomerData(p => ({...p, notes: e.target.value}))} 
                      placeholder="Add notes about this customer..."
                      rows={3}
                      data-testid="customer-notes-input"
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingCustomer(null)}>Cancel</Button>
                    <Button size="sm" onClick={handleSaveCustomer} disabled={savingCustomer}>
                      {savingCustomer ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Customer Detail Dialog */}
          <Dialog open={!!viewingCustomer} onOpenChange={() => { setViewingCustomer(null); setViewingCustomerItem(null); }}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{viewingCustomer?.name || "Customer Details"}</DialogTitle>
              </DialogHeader>
              {viewingCustomer && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Email:</span> <span className="font-medium">{viewingCustomer.email}</span></div>
                    <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{viewingCustomer.phone || "—"}</span></div>
                    {viewingCustomer.address && <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium">{viewingCustomer.address}</span></div>}
                  </div>

                  {customerDetailsLoading ? (
                    <p className="text-center text-gray-400 py-4">Loading...</p>
                  ) : viewingCustomerItem ? (
                    /* Expanded item view */
                    <div className="space-y-3">
                      <button onClick={() => setViewingCustomerItem(null)} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                        ← Back to list
                      </button>
                      {viewingCustomerItem.type === 'lead' ? (
                        <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-semibold">{viewingCustomerItem.data.service_type || "General Inquiry"}</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-gray-500">Status:</span> <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                              viewingCustomerItem.data.status === 'new' ? 'bg-blue-100 text-blue-700' :
                              viewingCustomerItem.data.status === 'quoted' ? 'bg-amber-100 text-amber-700' :
                              viewingCustomerItem.data.status === 'won' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>{viewingCustomerItem.data.status}</span></div>
                            <div><span className="text-gray-500">Date:</span> {new Date(viewingCustomerItem.data.created_at).toLocaleString()}</div>
                            {viewingCustomerItem.data.customer_name && <div><span className="text-gray-500">Name:</span> {viewingCustomerItem.data.customer_name}</div>}
                            {viewingCustomerItem.data.customer_phone && <div><span className="text-gray-500">Phone:</span> {viewingCustomerItem.data.customer_phone}</div>}
                            {viewingCustomerItem.data.customer_address && <div className="col-span-2"><span className="text-gray-500">Address:</span> {viewingCustomerItem.data.customer_address}</div>}
                          </div>
                          {viewingCustomerItem.data.message && (
                            <div>
                              <span className="text-gray-500 text-sm">Message:</span>
                              <p className="mt-1 text-sm whitespace-pre-wrap bg-white p-3 rounded border">{viewingCustomerItem.data.message}</p>
                            </div>
                          )}
                          {viewingCustomerItem.data.pricing_snapshot?.length > 0 && (
                            <div>
                              <span className="text-gray-500 text-sm">Services Requested:</span>
                              <div className="mt-1 space-y-1">
                                {viewingCustomerItem.data.pricing_snapshot.map((ps, i) => (
                                  <div key={i} className="flex justify-between text-sm bg-white p-2 rounded border">
                                    <span>{ps.name || ps.service}</span>
                                    <span className="font-medium">${ps.price || ps.amount || 0}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-center">
                            <h4 className="font-semibold">{viewingCustomerItem.data.quote_number}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              viewingCustomerItem.data.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                              viewingCustomerItem.data.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{viewingCustomerItem.data.status}</span>
                          </div>
                          <div className="text-sm space-y-1">
                            <div><span className="text-gray-500">Date:</span> {new Date(viewingCustomerItem.data.created_at).toLocaleString()}</div>
                            {viewingCustomerItem.data.valid_until && <div><span className="text-gray-500">Valid Until:</span> {new Date(viewingCustomerItem.data.valid_until).toLocaleDateString()}</div>}
                          </div>
                          <div>
                            <span className="text-gray-500 text-sm">Line Items:</span>
                            <div className="mt-1 space-y-1">
                              {(viewingCustomerItem.data.line_items || []).map((item, i) => (
                                <div key={i} className="flex justify-between text-sm bg-white p-2 rounded border">
                                  <span>{item.description} {item.quantity > 1 ? `x${item.quantity}` : ''}</span>
                                  <span className="font-medium">${(item.total || item.unit_price * item.quantity).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="border-t pt-2 space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>${(viewingCustomerItem.data.subtotal || viewingCustomerItem.data.quoted_total || 0).toFixed(2)}</span></div>
                            {viewingCustomerItem.data.tax_state && viewingCustomerItem.data.tax_rate > 0 && (
                              <div className="flex justify-between"><span className="text-gray-500">{viewingCustomerItem.data.tax_state} Tax ({viewingCustomerItem.data.tax_rate}%)</span><span>${(viewingCustomerItem.data.tax_amount || 0).toFixed(2)}</span></div>
                            )}
                            <div className="flex justify-between font-bold text-base"><span>Total</span><span>${(viewingCustomerItem.data.quoted_total || viewingCustomerItem.data.total || 0).toFixed(2)}</span></div>
                            {viewingCustomerItem.data.deposit_amount > 0 && (
                              <div className="flex justify-between text-gray-500"><span>Deposit</span><span>${viewingCustomerItem.data.deposit_amount.toFixed(2)}</span></div>
                            )}
                          </div>
                          {viewingCustomerItem.data.notes && (
                            <div>
                              <span className="text-gray-500 text-sm">Notes:</span>
                              <p className="mt-1 text-sm whitespace-pre-wrap bg-white p-3 rounded border">{viewingCustomerItem.data.notes}</p>
                            </div>
                          )}
                          {/* Quick text-to-customer action */}
                          {viewingCustomerItem.type === 'quote' && (
                            <div className="pt-2 border-t flex justify-end">
                              <TextQuoteButton slug={slug} quoteId={viewingCustomerItem.data.id} variant="primary" size="md" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Leads */}
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Leads ({customerDetails.leads.length})</h4>
                        {customerDetails.leads.length === 0 ? (
                          <p className="text-xs text-gray-400">No leads</p>
                        ) : (
                          <div className="space-y-2">
                            {customerDetails.leads.map(lead => (
                              <div key={lead.id} className="p-3 bg-gray-50 rounded-lg text-sm cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setViewingCustomerItem({ type: 'lead', data: lead })}>
                                <div className="flex justify-between items-start">
                                  <div>
                                    <span className="font-medium">{lead.service_type || "General Inquiry"}</span>
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                      lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                      lead.status === 'quoted' ? 'bg-amber-100 text-amber-700' :
                                      lead.status === 'won' ? 'bg-blue-100 text-blue-800' :
                                      lead.status === 'lost' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>{lead.status}</span>
                                  </div>
                                  <span className="text-xs text-gray-400">{new Date(lead.created_at).toLocaleDateString()}</span>
                                </div>
                                {lead.message && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{lead.message}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Quotes */}
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Quotes ({customerDetails.quotes.length})</h4>
                        {customerDetails.quotes.length === 0 ? (
                          <p className="text-xs text-gray-400">No quotes</p>
                        ) : (
                          <div className="space-y-2">
                            {customerDetails.quotes.map(quote => (
                              <div key={quote.id} className="p-3 bg-gray-50 rounded-lg text-sm cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setViewingCustomerItem({ type: 'quote', data: quote })}>
                                <div className="flex justify-between items-start">
                                  <div>
                                    <span className="font-medium">{quote.quote_number}</span>
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                      quote.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                                      quote.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                      quote.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                                      quote.status === 'expired' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>{quote.status}</span>
                                  </div>
                                  <span className="text-xs text-gray-400">{new Date(quote.created_at).toLocaleDateString()}</span>
                                </div>
                                <div className="flex justify-between mt-1">
                                  <div className="text-xs text-gray-500 truncate mr-2">
                                    {(quote.line_items || []).map(i => i.description).join(", ")}
                                  </div>
                                  <span className="font-semibold whitespace-nowrap">${(quote.quoted_total || quote.total || 0).toFixed(2)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Measurements */}
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Measurements ({(customerDetails.measurements || []).length})</h4>
                        {(customerDetails.measurements || []).length === 0 ? (
                          <p className="text-xs text-gray-400">No measurements saved for this customer</p>
                        ) : (
                          <div className="space-y-2">
                            {customerDetails.measurements.map(m => (
                              <div key={m.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium truncate">{m.address}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {(m.total_sqft || 0).toLocaleString()} sq ft &middot; {(m.areas || []).length} area{(m.areas || []).length === 1 ? '' : 's'}
                                      {m.total_perimeter_ft ? ` · ${Math.round(m.total_perimeter_ft).toLocaleString()} ft perimeter` : ''}
                                    </div>
                                  </div>
                                  <span className="text-xs text-gray-400 flex-shrink-0">{new Date(m.created_at).toLocaleDateString()}</span>
                                </div>
                                {(m.areas || []).length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {m.areas.slice(0, 4).map((a, i) => (
                                      <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-white border border-gray-200">
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: a.color || '#3B82F6' }} />
                                        {a.name}: {(a.area_sqft || 0).toLocaleString()} sf
                                      </span>
                                    ))}
                                    {m.areas.length > 4 && (
                                      <span className="text-[11px] text-gray-400 px-2 py-0.5">+{m.areas.length - 4} more</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>


          <Card>
            <CardContent className="p-0">
              {customersLoading ? (
                <div className="text-center py-8 text-gray-500">Loading customers...</div>
              ) : customers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No customers yet. Customers are created when leads come in or you add them manually.</div>
              ) : (
                <>
                  {/* Mobile Customer Cards */}
                  <div className="sm:hidden divide-y">
                    {filteredCustomers.map((customer) => (
                      <div key={customer.email} className={`p-4 space-y-1 cursor-pointer hover:bg-gray-50 ${selectedCustomerEmails.has(customer.email) ? 'bg-blue-50' : ''}`} onClick={() => openCustomerDetails(customer)}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <input 
                              type="checkbox" 
                              checked={selectedCustomerEmails.has(customer.email)}
                              onChange={() => toggleCustomerSelect(customer.email)}
                              className="h-4 w-4 rounded border-gray-300 accent-blue-800 shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="font-medium text-sm flex items-center gap-1.5">
                                {customer.name || "N/A"}
                                {customer.is_demo && <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">Demo</Badge>}
                              </div>
                              <div className="text-xs text-gray-500">{customer.email}</div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => handleEditCustomer(customer)} className="text-gray-400 hover:text-blue-800 p-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button onClick={() => handleDeleteCustomer(customer.email)} className="text-gray-400 hover:text-red-500 p-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 pl-6">
                          <span>{customer.lead_count} lead{customer.lead_count !== 1 ? 's' : ''}</span>
                          <span>{customer.quote_count} quote{customer.quote_count !== 1 ? 's' : ''}</span>
                        </div>
                        {customer.notes && (
                          <div className="text-xs text-gray-600 pl-6 mt-1 italic">
                            📝 {customer.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Desktop Customer Table */}
                  <div className="hidden sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <input 
                              type="checkbox" 
                              checked={customers.length > 0 && selectedCustomerEmails.size === customers.length}
                              onChange={toggleAllCustomers}
                              className="h-4 w-4 rounded border-gray-300 accent-blue-800"
                              data-testid="customer-select-all"
                            />
                          </TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead>Leads</TableHead>
                          <TableHead>Quotes</TableHead>
                          <TableHead>Last Activity</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCustomers.map((customer) => (
                          <TableRow key={customer.email} className={`cursor-pointer hover:bg-gray-50 ${selectedCustomerEmails.has(customer.email) ? 'bg-blue-50' : ''}`} onClick={() => openCustomerDetails(customer)}>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox" 
                                checked={selectedCustomerEmails.has(customer.email)}
                                onChange={() => toggleCustomerSelect(customer.email)}
                                className="h-4 w-4 rounded border-gray-300 accent-blue-800"
                                data-testid={`customer-checkbox-${customer.email}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              <span className="flex items-center gap-1.5">
                                {customer.name || "N/A"}
                                {customer.is_demo && <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">Demo</Badge>}
                              </span>
                            </TableCell>
                            <TableCell>{customer.email}</TableCell>
                            <TableCell>{customer.phone || "—"}</TableCell>
                            <TableCell className="max-w-[150px] truncate" title={customer.notes || ""}>
                              {customer.notes ? <span className="text-gray-600 italic">{customer.notes}</span> : "—"}
                            </TableCell>
                            <TableCell>{customer.lead_count}</TableCell>
                            <TableCell>{customer.quote_count}</TableCell>
                            <TableCell className="text-sm">{formatDate(customer.last_lead_at)}</TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end gap-1">
                                <button onClick={() => handleEditCustomer(customer)} className="text-gray-400 hover:text-blue-800 p-2" title="Edit customer">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button onClick={() => handleDeleteCustomer(customer.email)} className="text-gray-400 hover:text-red-500 p-2" title="Delete customer">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => { setSelectedLead(null); setEditingLeadInDialog(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{editingLeadInDialog ? "Edit Lead" : "Lead Details"}</DialogTitle>
          </DialogHeader>
          {selectedLead && editingLeadInDialog && (
            <EditLeadForm 
              lead={selectedLead} 
              onSave={async (updatedData) => {
                try {
                  await api.updateLead(selectedLead.id, updatedData);
                  toast.success("Lead updated");
                  setEditingLeadInDialog(false);
                  setSelectedLead(null);
                  onRefresh();
                } catch (error) {
                  toast.error(getErrorMessage(error, "Failed to update lead"));
                }
              }}
              onCancel={() => setEditingLeadInDialog(false)}
            />
          )}
          {selectedLead && !editingLeadInDialog && (
            <div className="space-y-4">
              {/* Deposit Tip */}
              {showDepositTip && !getQuoteForLead(selectedLead.id) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                  <span className="text-amber-500 text-lg flex-shrink-0">💡</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-amber-800">
                      <strong>Pro tip:</strong> Requiring a deposit increases job commitment, reduces no-shows, and improves your cash flow.
                    </p>
                  </div>
                  <button 
                    onClick={dismissDepositTip}
                    className="text-amber-400 hover:text-amber-600 text-lg flex-shrink-0"
                    aria-label="Dismiss tip"
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Name</Label>
                  <p className="font-medium text-sm sm:text-base flex items-center gap-1.5">
                    {selectedLead.customer_name || "N/A"}
                    {selectedLead.is_demo && <Badge className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0">Demo</Badge>}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Email</Label>
                  <p className="font-medium text-sm sm:text-base break-all">{selectedLead.customer_email}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Phone</Label>
                  <p className="font-medium text-sm sm:text-base">{selectedLead.customer_phone || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Status</Label>
                  <div>{getStatusBadge(selectedLead.status)}</div>
                </div>
              </div>
              {selectedLead.customer_address && (
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Address</Label>
                  <p className="font-medium text-sm sm:text-base">{selectedLead.customer_address}</p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => { navigator.clipboard.writeText(selectedLead.customer_address); toast.success("Address copied!"); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      Copy Address
                    </button>
                    <button
                      onClick={() => { navigate(`/dashboard/${slug}?section=routes&tab=measure&t=${Date.now()}`); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                      Measure Property
                    </button>
                  </div>
                </div>
              )}
              {selectedLead.message && (
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Message</Label>
                  <p className="font-medium text-sm sm:text-base bg-gray-50 p-2 sm:p-3 rounded">{selectedLead.message}</p>
                </div>
              )}
              {selectedLead.service_type && (
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Service</Label>
                  <p className="font-medium text-sm sm:text-base">{selectedLead.service_type}</p>
                </div>
              )}
              {selectedLead.pricing_snapshot?.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Selected Services</Label>
                  <div className="space-y-1 mt-1">
                    {selectedLead.pricing_snapshot.map((s, idx) => (
                      <div key={idx} className="text-xs sm:text-sm bg-gray-50 p-2 rounded flex justify-between">
                        <span>{s.label}</span>
                        {s.price_value && <span>${s.price_value.toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Custom Question Answers */}
              {selectedLead.custom_answers && Object.keys(selectedLead.custom_answers).length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Custom Answers</Label>
                  <div className="space-y-2 mt-1">
                    {Object.entries(selectedLead.custom_answers).map(([questionId, answer], idx) => {
                      // Try to find the question label from business config
                      const question = business?.field_config?.custom_questions?.find(q => q.id === questionId);
                      const label = question?.label || `Question ${idx + 1}`;
                      const displayAnswer = Array.isArray(answer) ? answer.join(", ") : answer;
                      return (
                        <div key={questionId} className="text-xs sm:text-sm bg-amber-50 p-2 rounded">
                          <span className="text-gray-600">{label}:</span>
                          <span className="ml-2 font-medium">{displayAnswer}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-gray-500 text-xs sm:text-sm">Received</Label>
                <p className="font-medium text-sm sm:text-base">{formatDate(selectedLead.created_at)}</p>
              </div>
              {/* Photos Section */}
              {selectedLead.photo_urls?.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs sm:text-sm">Photos ({selectedLead.photo_urls.length})</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedLead.photo_urls.map((url, idx) => (
                      <a 
                        key={idx} 
                        href={`${BACKEND_URL}${url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img 
                          src={`${BACKEND_URL}${url}`} 
                          alt={`Photo ${idx + 1}`}
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border hover:ring-2 ring-blue-800 transition-all"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Quote Information */}
              {(() => {
                const quote = getQuoteForLead(selectedLead.id);
                if (!quote) return null;
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Label className="text-slate-900 text-sm font-medium">Quote Details</Label>
                    <div className="mt-2 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Quote Total:</span>
                        <span className="font-medium">${(quote.quoted_total || quote.total)?.toFixed(2)}</span>
                      </div>
                      {quote.deposit_amount > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Deposit Required:</span>
                            <span className="font-medium">${quote.deposit_amount?.toFixed(2)}</span>
                          </div>
                          {quote.deposit_paid_amount > 0 && (
                            <div className="flex justify-between text-sm text-blue-700">
                              <span>Deposit Paid:</span>
                              <span className="font-medium">${quote.deposit_paid_amount?.toFixed(2)}</span>
                            </div>
                          )}
                          {quote.status === "deposit_paid" && (
                            <div className="flex justify-between text-sm text-amber-600 font-medium">
                              <span>Balance Due:</span>
                              <span>${((quote.quoted_total || quote.total) - (quote.deposit_paid_amount || 0))?.toFixed(2)}</span>
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium ${
                          quote.status === 'paid' ? 'text-blue-700' : 
                          quote.status === 'deposit_paid' ? 'text-amber-600' : 
                          'text-gray-600'
                        }`}>
                          {quote.status === 'paid' ? '✓ Paid' : 
                           quote.status === 'deposit_paid' ? '⏳ Deposit Paid' : 
                           quote.status === 'sent' ? '📤 Sent' : 
                           quote.status === 'viewed' ? '👁 Viewed' : 
                           quote.status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              <div className="flex flex-col sm:flex-row justify-between gap-2 pt-4 border-t">
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setEditingLeadInDialog(true)}
                    className="w-full sm:w-auto"
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleDeleteLead(selectedLead.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full sm:w-auto"
                  >
                    Delete
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setSelectedLead(null)} className="w-full sm:w-auto">Close</Button>
                  {(() => {
                    const quote = getQuoteForLead(selectedLead.id);
                    if (quote?.status === "deposit_paid") {
                      return (
                        <Button 
                          onClick={subLocked ? () => showSubRequired() : () => {
                            setFinalTotalInput(""); // Reset to allow auto-calculation
                            setBalanceDialogQuote(quote);
                          }} 
                          disabled={subLocked}
                          title={subLocked ? SUB_LOCK_TOOLTIP : ""}
                          className={`w-full sm:w-auto ${subLocked ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed opacity-80" : "bg-amber-600 hover:bg-amber-700"}`}
                        >
                          {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1.5" /> Locked</> : "Collect Balance"}
                        </Button>
                      );
                    }
                    if (quote?.status === "paid") {
                      return (
                        <span className="text-blue-700 font-medium px-3 py-2">✓ Paid in Full</span>
                      );
                    }
                    if (!quote) {
                      return (
                        <Button
                          onClick={subLocked ? () => showSubRequired() : () => handleQuoteLead(selectedLead)}
                          disabled={subLocked}
                          title={subLocked ? SUB_LOCK_TOOLTIP : ""}
                          className={`w-full sm:w-auto ${subLocked ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed opacity-80" : "bg-slate-900 hover:bg-slate-900"}`}
                        >
                          {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1.5" /> Locked</> : "Create Quote"}
                        </Button>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Collect Balance Dialog */}
      <Dialog open={!!balanceDialogQuote} onOpenChange={() => setBalanceDialogQuote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collect Remaining Balance</DialogTitle>
          </DialogHeader>
          {balanceDialogQuote && (() => {
            const quotedTotal = balanceDialogQuote.final_total || balanceDialogQuote.quoted_total || balanceDialogQuote.total || 0;
            const depositPaid = balanceDialogQuote.deposit_paid_amount || 0;
            const isTBD = balanceDialogQuote.final_price_tbd;
            const calculatedBalance = Math.max(0, quotedTotal - depositPaid);
            const chargeAmount = finalTotalInput !== "" ? parseFloat(finalTotalInput) || 0 : calculatedBalance;
            
            return (
            <div className="space-y-4">
              {/* Show deposit already collected */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-blue-800">Deposit Collected</span>
                  <span className="text-slate-900 font-semibold">${depositPaid.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Amount to charge input */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Amount to Charge</Label>
                {!isTBD && quotedTotal > 0 && (
                  <p className="text-xs text-gray-500">
                    Suggested: ${calculatedBalance.toFixed(2)} (from ${quotedTotal.toFixed(2)} quote)
                  </p>
                )}
                {isTBD && (
                  <p className="text-xs text-amber-600">
                    Final price was marked as TBD - enter the amount to charge
                  </p>
                )}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={finalTotalInput !== "" ? finalTotalInput : (isTBD ? "" : calculatedBalance.toString())}
                    onChange={(e) => {
                      setFinalTotalInput(e.target.value);
                    }}
                    placeholder="Enter amount..."
                    className="text-2xl font-bold pl-8 h-14"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Instant charge:</strong> Customer's saved card will be charged immediately. If no card is on file, an email with a payment link will be sent.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setBalanceDialogQuote(null)}>Cancel</Button>
                <Button 
                  onClick={() => handleCollectBalance(balanceDialogQuote)}
                  disabled={collectingBalance === balanceDialogQuote.id || chargeAmount <= 0}
                  className="bg-amber-600 hover:bg-amber-700 text-lg px-6"
                >
                  {collectingBalance === balanceDialogQuote.id ? "Charging..." : `Charge $${chargeAmount.toFixed(2)}`}
                </Button>
              </div>
            </div>
          );})()}
        </DialogContent>
      </Dialog>
      
      {/* Payment Link Sent Notification Dialog */}
      <Dialog open={!!paymentLinkSentInfo} onOpenChange={(open) => {
        if (!open && paymentLinkSentInfo?.onClose) {
          paymentLinkSentInfo.onClose();
        } else if (!open) {
          setPaymentLinkSentInfo(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Payment Link Sent
            </DialogTitle>
          </DialogHeader>
          {paymentLinkSentInfo && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium mb-2">
                  No saved card was found for this customer.
                </p>
                <p className="text-amber-700 text-sm">
                  An email with a payment link for <strong>${paymentLinkSentInfo.amount?.toFixed(2)}</strong> has been sent to:
                </p>
                <p className="text-amber-900 font-semibold mt-1">{paymentLinkSentInfo.customerEmail}</p>
              </div>
              
              <p className="text-sm text-gray-500">
                The customer will need to complete payment using the link. Once paid, the status will update automatically.
              </p>
              
              <div className="flex justify-end pt-2">
                <Button onClick={() => {
                  if (paymentLinkSentInfo?.onClose) {
                    paymentLinkSentInfo.onClose();
                  } else {
                    setPaymentLinkSentInfo(null);
                  }
                }}>
                  Got it
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== QUOTES SECTION ==============
const QuotesSection = ({ slug, quotes, leads, services, stripeStatus, onRefresh, openDialog = false, onDialogOpened, quoteForLeadId, onLeadConsumed, business }) => {
  const subLocked = isSubInactive(business);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [newCustomerEmail, setNewCustomerEmail] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);
  const [collectingBalance, setCollectingBalance] = useState(null);
  const [balanceDialogQuote, setBalanceDialogQuote] = useState(null);
  const [finalTotalInput, setFinalTotalInput] = useState("");
  const [paymentLinkSentInfo, setPaymentLinkSentInfo] = useState(null); // {customerEmail, amount, url}
  const [selectedQuoteIds, setSelectedQuoteIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [recordPaymentQuote, setRecordPaymentQuote] = useState(null); // quote object when modal open
  const [recordPaymentMethod, setRecordPaymentMethod] = useState("cash");
  const [recordPaymentAmount, setRecordPaymentAmount] = useState("");
  const [recordingPayment, setRecordingPayment] = useState(false);

  const PAYMENT_METHOD_LABEL = {
    cash: "Cash",
    check: "Check",
    venmo: "Venmo",
    cashapp: "CashApp",
    paypal: "PayPal",
    zelle: "Zelle",
    other: "Other",
  };

  const computeBalanceDue = (q) => {
    const total = q.quoted_total || q.total || 0;
    const stripePaid = q.deposit_paid_amount || 0;
    const offlineSum = (q.offline_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
    return Math.max(0, total - stripePaid - offlineSum);
  };

  const openRecordPayment = (q) => {
    setRecordPaymentQuote(q);
    setRecordPaymentMethod("cash");
    setRecordPaymentAmount(computeBalanceDue(q).toFixed(2));
  };

  const submitRecordPayment = async () => {
    if (!recordPaymentQuote) return;
    const amt = parseFloat(recordPaymentAmount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setRecordingPayment(true);
    try {
      const res = await api.recordOfflinePayment(slug, recordPaymentQuote.id, recordPaymentMethod, amt);
      toast.success(`Payment recorded · ${PAYMENT_METHOD_LABEL[recordPaymentMethod] || recordPaymentMethod}`);
      // Refresh modal contents so the user sees the new entry + can undo if needed
      if (res && res.quote) {
        setRecordPaymentQuote(res.quote);
        setRecordPaymentAmount(computeBalanceDue(res.quote).toFixed(2));
      }
      onRefresh();
    } catch {
      toast.error("Failed to record payment");
    } finally {
      setRecordingPayment(false);
    }
  };

  const deleteOfflinePayment = async (quoteId, paymentId) => {
    if (!window.confirm("Remove this payment?")) return;
    try {
      const res = await api.deleteOfflinePayment(slug, quoteId, paymentId);
      toast.success("Payment removed");
      // Reflect updated quote in the open modal so the user can keep working
      if (res && res.quote) {
        setRecordPaymentQuote(res.quote);
        setRecordPaymentAmount(computeBalanceDue(res.quote).toFixed(2));
      }
      onRefresh();
    } catch {
      toast.error("Failed to remove payment");
    }
  };

  const handleManualApprove = async (quote) => {
    if (!window.confirm("Mark this quote as approved on the customer's behalf?\n\nThis is silent — the customer is not notified.")) return;
    try {
      await api.manualApproveQuote(slug, quote.id);
      toast.success("Approved manually");
      onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to approve";
      toast.error(typeof msg === "string" ? msg : "Failed to approve");
    }
  };
  const handleManualDecline = async (quote) => {
    if (!window.confirm("Mark this quote as declined on the customer's behalf?\n\nThis is silent — the customer is not notified.")) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "";
      await axios.post(`${API_URL}/tenants/${slug}/quotes/${quote.id}/manual-decline`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });
      toast.success("Declined manually");
      onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to decline";
      toast.error(typeof msg === "string" ? msg : "Failed to decline");
    }
  };
  const handleUnapprove = async (quote) => {
    if (!window.confirm("Change this quote's status back to 'Sent'?\n\nThis lets you approve or decline it again. The customer is not notified.")) return;
    try {
      await api.unapproveQuote(slug, quote.id);
      toast.success("Status changed");
      onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to change status";
      toast.error(typeof msg === "string" ? msg : "Failed to change status");
    }
  };


  const toggleQuoteSelected = (id) => {
    setSelectedQuoteIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAllQuotes = () => {
    if (selectedQuoteIds.length === quotes.length) setSelectedQuoteIds([]);
    else setSelectedQuoteIds(quotes.map(q => q.id));
  };
  const handleBulkDelete = async () => {
    if (selectedQuoteIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedQuoteIds.length} quote${selectedQuoteIds.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await api.bulkDeleteQuotes(slug, selectedQuoteIds);
      toast.success(`Deleted ${selectedQuoteIds.length} quote${selectedQuoteIds.length === 1 ? "" : "s"}`);
      setSelectedQuoteIds([]);
      onRefresh();
    } catch {
      toast.error("Failed to delete quotes");
    } finally {
      setBulkDeleting(false);
    }
  };

  const [formData, setFormData] = useState({
    line_items: [],
    configurable_items: [],
    choice_groups: [],
    notes: "",
    valid_days: 30,
    deposit_amount: 0,
    payment_type: "full",
    final_price_tbd: false,
    tax_state: "",
    tax_rate: 0,
    // Whole-quote discount: percent 0–100 applied to subtotal before tax.
    // `discount_label` lets the business name the discount (e.g. "Spring Promo",
    // "Repeat Customer", "Senior Discount"). The label + amount persist on the
    // saved quote and surface on the customer-facing invoice so it reads as
    // a marketing/value signal, not a hidden line item.
    discount_percent: 0,
    discount_label: ""
  });

  // US State tax rates (state-level base rates, 2025-2026)
  const STATE_TAX_RATES = [
    { state: "Alabama", abbr: "AL", rate: 4.00 }, { state: "Alaska", abbr: "AK", rate: 0 },
    { state: "Arizona", abbr: "AZ", rate: 5.60 }, { state: "Arkansas", abbr: "AR", rate: 6.50 },
    { state: "California", abbr: "CA", rate: 7.25 }, { state: "Colorado", abbr: "CO", rate: 2.90 },
    { state: "Connecticut", abbr: "CT", rate: 6.35 }, { state: "Delaware", abbr: "DE", rate: 0 },
    { state: "Florida", abbr: "FL", rate: 6.00 }, { state: "Georgia", abbr: "GA", rate: 4.00 },
    { state: "Hawaii", abbr: "HI", rate: 4.00 }, { state: "Idaho", abbr: "ID", rate: 6.00 },
    { state: "Illinois", abbr: "IL", rate: 6.25 }, { state: "Indiana", abbr: "IN", rate: 7.00 },
    { state: "Iowa", abbr: "IA", rate: 6.00 }, { state: "Kansas", abbr: "KS", rate: 6.50 },
    { state: "Kentucky", abbr: "KY", rate: 6.00 }, { state: "Louisiana", abbr: "LA", rate: 5.00 },
    { state: "Maine", abbr: "ME", rate: 5.50 }, { state: "Maryland", abbr: "MD", rate: 6.00 },
    { state: "Massachusetts", abbr: "MA", rate: 6.25 }, { state: "Michigan", abbr: "MI", rate: 6.00 },
    { state: "Minnesota", abbr: "MN", rate: 6.875 }, { state: "Mississippi", abbr: "MS", rate: 7.00 },
    { state: "Missouri", abbr: "MO", rate: 4.225 }, { state: "Montana", abbr: "MT", rate: 0 },
    { state: "Nebraska", abbr: "NE", rate: 5.50 }, { state: "Nevada", abbr: "NV", rate: 4.60 },
    { state: "New Hampshire", abbr: "NH", rate: 0 }, { state: "New Jersey", abbr: "NJ", rate: 6.625 },
    { state: "New Mexico", abbr: "NM", rate: 4.875 }, { state: "New York", abbr: "NY", rate: 4.00 },
    { state: "North Carolina", abbr: "NC", rate: 4.75 }, { state: "North Dakota", abbr: "ND", rate: 5.00 },
    { state: "Ohio", abbr: "OH", rate: 5.75 }, { state: "Oklahoma", abbr: "OK", rate: 4.50 },
    { state: "Oregon", abbr: "OR", rate: 0 }, { state: "Pennsylvania", abbr: "PA", rate: 6.00 },
    { state: "Rhode Island", abbr: "RI", rate: 7.00 }, { state: "South Carolina", abbr: "SC", rate: 6.00 },
    { state: "South Dakota", abbr: "SD", rate: 4.50 }, { state: "Tennessee", abbr: "TN", rate: 7.00 },
    { state: "Texas", abbr: "TX", rate: 6.25 }, { state: "Utah", abbr: "UT", rate: 4.85 },
    { state: "Vermont", abbr: "VT", rate: 6.00 }, { state: "Virginia", abbr: "VA", rate: 4.30 },
    { state: "Washington", abbr: "WA", rate: 6.50 }, { state: "West Virginia", abbr: "WV", rate: 6.00 },
    { state: "Wisconsin", abbr: "WI", rate: 5.00 }, { state: "Wyoming", abbr: "WY", rate: 4.00 }
  ];
  const [taxSearch, setTaxSearch] = useState("");
  const [showTaxDropdown, setShowTaxDropdown] = useState(false);
  const filteredStates = STATE_TAX_RATES.filter(s => 
    taxSearch && (s.state.toLowerCase().includes(taxSearch.toLowerCase()) || s.abbr.toLowerCase().includes(taxSearch.toLowerCase()))
  );
  useEffect(() => {
    if (openDialog && !showCreateDialog) {
      resetForm();
      // Pre-populate from lead if quoteForLeadId is set
      if (quoteForLeadId) {
        const lead = leads.find(l => l.id === quoteForLeadId);
        if (lead) {
          setSelectedLeadId(lead.id);
          // Auto-populate line items from lead's pricing snapshot
          if (lead.pricing_snapshot && lead.pricing_snapshot.length > 0) {
            const baseItems = lead.pricing_snapshot.filter(ps => !ps.is_modifier);
            const modifierItems = lead.pricing_snapshot.filter(ps => ps.is_modifier);
            const baseSubtotal = baseItems.reduce((sum, ps) => sum + ((ps.quantity || 1) * (ps.price_value || ps.price || 0)), 0);
            const autoItems = lead.pricing_snapshot.map(ps => {
              let price = ps.is_modifier
                ? (ps.modifier_type === "percentage" && ps.modifier_percent
                  ? Math.round(baseSubtotal * (ps.modifier_percent / 100))
                  : (ps.modifier_fixed || 0))
                : (ps.price_value || ps.price || 0);
              return {
                description: ps.label || ps.service_name || "Service",
                quantity: ps.quantity || 1,
                unit_price: price
              };
            });
            setFormData(prev => ({ ...prev, line_items: autoItems }));
          }
        }
        if (onLeadConsumed) onLeadConsumed();
      }
      setShowCreateDialog(true);
      if (onDialogOpened) onDialogOpened();
    }
  }, [openDialog]);

  const resetForm = () => {
    setFormData({ line_items: [], configurable_items: [], choice_groups: [], notes: "", valid_days: 30, deposit_amount: 0, payment_type: "full", final_price_tbd: false, tax_state: "", tax_rate: 0, discount_percent: 0, discount_label: "" });
    setSelectedLeadId("");
    setNewCustomerMode(false);
    setNewCustomerEmail("");
    setNewCustomerName("");
    setEditingQuote(null);
    setTaxSearch("");
  };

  const handleAddLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        { description: "", quantity: 1, unit_price: 0 }
      ]
    });
  };

  const handleUpdateLineItem = (idx, field, value) => {
  const newItems = [...formData.line_items];

  newItems[idx][field] =
    field === "quantity" || field === "unit_price"
      ? value
      : value;

  setFormData({
    ...formData,
    line_items: newItems,
  });
};

  const handleRemoveLineItem = (idx) => {
    const newItems = formData.line_items.filter((_, i) => i !== idx);
    setFormData({ ...formData, line_items: newItems });
  };

  const handlePrefillFromService = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (!service) return;
    handleAddLineItem();
    const idx = formData.line_items.length;
    setTimeout(() => {
      handleUpdateLineItem(idx, "description", service.label);
      handleUpdateLineItem(idx, "unit_price", service.price_value || 0);
    }, 0);
  };

  // Subtotal from line items only (pre-discount). Used to compute the discount
  // amount and the discount-strike display.
  const calculateSubtotal = () => {
    return formData.line_items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  // `calculateTotal` is the *discounted* subtotal — the working number every
  // downstream calculation (tax, deposit-pct, total-with-tax) should use.
  // Keeping the function name unchanged means we only had to edit one place
  // instead of dozens.
  const calculateTotal = () => {
    const sub = calculateSubtotal();
    const pct = Math.max(0, Math.min(100, parseFloat(formData.discount_percent) || 0));
    return sub * (1 - pct / 100);
  };

  const handleSaveQuote = async (sendImmediately = false) => {
    // Validate customer selection
    if (!selectedLeadId && !newCustomerMode) {
      toast.error("Please select a customer");
      return;
    }
    
    if (newCustomerMode && !newCustomerEmail) {
      toast.error("Please enter customer email");
      return;
    }
    
    // Allow sending if: has line items, OR configurable items, OR choice groups, OR deposit amount
    const hasAnyItems = formData.line_items.length > 0 || formData.configurable_items.length > 0 || formData.choice_groups.some(cg => cg.options && cg.options.length > 0);
    if (!hasAnyItems && formData.deposit_amount <= 0) {
      toast.error("Add at least one service or set a deposit amount");
      return;
    }
    
    // Validate each line item - convert to numbers for comparison (only if there are items)
    for (let i = 0; i < formData.line_items.length; i++) {
      const item = formData.line_items[i];
      if (!item.description || item.description.trim() === "") {
        toast.error(`Line item ${i + 1}: Description is required`);
        return;
      }
      const qty = parseFloat(item.quantity);
      if (isNaN(qty) || qty <= 0) {
        toast.error(`Line item ${i + 1}: Quantity must be greater than 0`);
        return;
      }
      const price = parseFloat(item.unit_price);
      if (isNaN(price) || price < 0) {
        toast.error(`Line item ${i + 1}: Price cannot be negative`);
        return;
      }
    }
    
    const total = calculateTotal();
    // Allow $0 total when configurable items exist (customer selects and pays at approval)
    const hasConfigurables = formData.configurable_items.length > 0;
    if (total <= 0 && formData.payment_type !== "deposit" && !hasConfigurables) {
      toast.error("Quote total must be greater than $0");
      return;
    }

    setSaving(true);
    try {
      let leadId = selectedLeadId;
      
      // Create new lead if new customer mode (skip notification - quote email will be sent instead)
      if (newCustomerMode) {
        const firstSvc = (formData.line_items || []).find(li => (li.description || "").trim())?.description?.trim() || "";
        const newLead = await api.createLead({
          customer_name: newCustomerName || newCustomerEmail.split('@')[0],
          customer_email: newCustomerEmail,
          service_type: firstSvc || undefined,
          source: "manual_quote"
        }, slug, true);
        leadId = newLead.id;
      }
      
      let depositAmount = formData.payment_type === "deposit" ? parseFloat(formData.deposit_amount) || 0 : 0;
      
      // Validate deposit only if deposit type selected
      if (formData.payment_type === "deposit") {
        if (depositAmount <= 0) {
          toast.error("Please enter a deposit amount or choose Full Payment");
          setSaving(false);
          return;
        }
        // Heavily discounted / tiny totals: a deposit at or above the total just becomes full payment
        if (total > 0 && depositAmount >= total) {
          depositAmount = 0;
        }
      }
      
      const data = {
        lead_id: leadId,
        line_items: formData.line_items.map(item => ({
          description: item.description.trim(),
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        configurable_items: formData.configurable_items.map(item => ({
          id: item.id || undefined,
          description: item.description.trim(),
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        choice_groups: formData.choice_groups.map(cg => ({
          id: cg.id || undefined,
          group_name: cg.group_name,
          options: (cg.options || []).map(opt => ({
            id: opt.id || undefined,
            description: opt.description.trim(),
            quantity: parseFloat(opt.quantity) || 1,
            unit_price: parseFloat(opt.unit_price) || 0
          }))
        })),
        notes: formData.notes,
        valid_days: formData.valid_days,
        deposit_amount: depositAmount,
        tax_state: formData.tax_state || null,
        tax_rate: formData.tax_rate || 0,
        tax_amount: formData.tax_rate > 0 ? parseFloat((total * formData.tax_rate / 100).toFixed(2)) : 0,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        discount_label: (formData.discount_label || "").trim() || null,
        // Auto-enable final_price_tbd when total is $0 and deposit is being used
        final_price_tbd: formData.final_price_tbd || (formData.payment_type === "deposit" && total === 0),
      };

      let quoteId = editingQuote?.id;
      
      if (editingQuote) {
        await api.updateQuote(slug, editingQuote.id, data);
        if (!sendImmediately) toast.success("Quote updated");
      } else {
        const newQuote = await api.createQuote(slug, data);
        quoteId = newQuote.id;
        if (!sendImmediately) toast.success("Quote created");
      }
      
      // If sendImmediately, send the quote (all quotes use approve/decline flow now)
      if (sendImmediately && quoteId) {
        try {
          setSending(quoteId);
          await api.sendQuoteWithoutPayment(slug, quoteId);
          toast.success(formData.configurable_items.length > 0 ? "Estimate sent to customer!" : "Quote sent to customer!");
        } catch (sendError) {
          toast.error(getErrorMessage(sendError, "Quote saved but failed to send"));
        } finally {
          setSending(null);
        }
      }
      
      setShowCreateDialog(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save quote"));
    } finally {
      setSaving(false);
    }
  };

  const handleSendQuote = async (quoteId) => {
    setSending(quoteId);
    try {
      // All quotes now use approve/decline flow - no Stripe payment link at send time
      await api.sendQuoteWithoutPayment(slug, quoteId);
      const quote = quotes.find(q => q.id === quoteId);
      const hasConfigurables = quote && (quote.configurable_items || []).length > 0;
      toast.success(hasConfigurables ? "Estimate sent to customer!" : "Quote sent to customer!");
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to send quote"));
    } finally {
      setSending(null);
    }
  };

  const handleSendWithoutPayment = async (quoteId) => {
    setSending(quoteId);
    try {
      await api.sendQuoteWithoutPayment(slug, quoteId);
      toast.success("Quote sent (without payment link)");
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to send quote"));
    } finally {
      setSending(null);
    }
  };

  // Save the quote, then open the user's native Messages app with a pre-filled
  // SMS containing the estimate link. No Twilio / 10DLC required.
  // If `alsoEmail` is true, also fires the email send before opening Messages.
  const handleSaveAndText = async (opts = {}) => {
    const alsoEmail = !!opts.alsoEmail;
    // Validate the same way handleSaveQuote does
    if (!selectedLeadId && !newCustomerMode) {
      toast.error("Please select a customer");
      return;
    }
    if (newCustomerMode && !newCustomerEmail) {
      toast.error("Please enter customer email");
      return;
    }
    const hasAnyItems = formData.line_items.length > 0 || formData.configurable_items.length > 0 || formData.choice_groups.some(cg => cg.options && cg.options.length > 0);
    if (!hasAnyItems && formData.deposit_amount <= 0) {
      toast.error("Add at least one service or set a deposit amount");
      return;
    }

    setSaving(true);
    try {
      let leadId = selectedLeadId;
      if (newCustomerMode) {
        if (!newCustomerPhone) {
          toast.error("A phone number is required to text the customer");
          setSaving(false);
          return;
        }
        const newLead = await api.createLead(slug, {
          customer_name: newCustomerName || newCustomerEmail,
          customer_email: newCustomerEmail,
          customer_phone: newCustomerPhone,
          customer_address: newCustomerAddress || "",
          notes: "",
          skip_notification: true,
        });
        leadId = newLead.id;
      } else {
        // Make sure the picked customer has a phone before we try to text
        const lead = leads.find(l => l.id === selectedLeadId);
        if (!lead?.customer_phone) {
          toast.error("This customer has no phone number on file. Add one to text them.");
          setSaving(false);
          return;
        }
      }
      const total = calculateTotal();
      let depositAmount = formData.payment_type === "deposit" ? Math.max(parseFloat(formData.deposit_amount) || 0, 0) : 0;
      if (total > 0 && depositAmount >= total) depositAmount = 0;
      const data = {
        lead_id: leadId,
        line_items: formData.line_items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        configurable_items: formData.configurable_items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        choice_groups: formData.choice_groups.map(g => ({
          id: g.id || undefined,
          group_name: g.group_name || g.name || "Options",
          options: (g.options || []).map(opt => ({
            id: opt.id || undefined,
            description: opt.description || "",
            quantity: parseFloat(opt.quantity) || 1,
            unit_price: parseFloat(opt.unit_price) || 0
          }))
        })),
        notes: formData.notes,
        valid_days: formData.valid_days,
        deposit_amount: depositAmount,
        tax_state: formData.tax_state || null,
        tax_rate: formData.tax_rate || 0,
        tax_amount: formData.tax_rate > 0 ? parseFloat((total * formData.tax_rate / 100).toFixed(2)) : 0,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        discount_label: (formData.discount_label || "").trim() || null,
        final_price_tbd: formData.final_price_tbd || (formData.payment_type === "deposit" && total === 0),
      };
      let quoteId = editingQuote?.id;
      if (editingQuote) {
        await api.updateQuote(slug, editingQuote.id, data);
      } else {
        const newQuote = await api.createQuote(slug, data);
        quoteId = newQuote.id;
      }
      // If user picked "Send both", fire the email send first
      if (alsoEmail && quoteId) {
        try {
          await api.sendQuoteWithoutPayment(slug, quoteId);
        } catch (sendErr) {
          toast.error("Quote saved but email send failed.");
        }
      }
      // Fetch the SMS payload + open Messages
      const token = localStorage.getItem("token");
      const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API_URL}/tenants/${slug}/quotes/${quoteId}/sms-payload`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error("Quote saved, but could not prepare the text message.");
      } else {
        const payload = await res.json();
        const cleanPhone = (payload.phone || "").replace(/[^\d+]/g, "");
        const body = encodeURIComponent(payload.body || "");
        // iOS and Android both accept `sms:{phone}?body={text}` (Apple
        // documented this form since iOS 8). Using `&` as the separator
        // works on most iOS builds but a few carriers / Messages forks
        // mangle it — `?` is the safer cross-platform choice.
        const href = `sms:${cleanPhone}?body=${body}`;
        window.location.href = href;
        toast.success(alsoEmail ? "Email sent — opening Messages..." : "Quote saved — opening Messages...");
      }
      setShowCreateDialog(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save quote"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSendWithoutPayment = async () => {
    // Validate
    if (!selectedLeadId && !newCustomerMode) {
      toast.error("Please select a customer");
      return;
    }
    if (newCustomerMode && !newCustomerEmail) {
      toast.error("Please enter customer email");
      return;
    }
    const hasItems = formData.line_items.length > 0 || formData.configurable_items.length > 0 || formData.choice_groups.some(cg => cg.options && cg.options.length > 0);
    if (!hasItems) {
      toast.error("Please add at least one item to the quote");
      return;
    }
    
    // Validate line items
    for (let i = 0; i < formData.line_items.length; i++) {
      const item = formData.line_items[i];
      if (!item.description || item.description.trim() === "") {
        toast.error(`Line item ${i + 1}: Description is required`);
        return;
      }
      const qty = parseFloat(item.quantity);
      if (isNaN(qty) || qty <= 0) {
        toast.error(`Line item ${i + 1}: Quantity must be greater than 0`);
        return;
      }
    }
    
    setSaving(true);
    try {
      let leadId = selectedLeadId;
      
      // Create new lead if new customer mode (skip notification - quote email will be sent instead)
      if (newCustomerMode) {
        const firstSvc = (formData.line_items || []).find(li => (li.description || "").trim())?.description?.trim() || "";
        const newLead = await api.createLead({
          customer_name: newCustomerName || newCustomerEmail.split('@')[0],
          customer_email: newCustomerEmail,
          service_type: firstSvc || undefined,
          source: "manual_quote"
        }, slug, true);
        leadId = newLead.id;
      }
      
      const draftTotal = calculateTotal();
      let depositAmount = formData.payment_type === "deposit" ? (parseFloat(formData.deposit_amount) || 0) : 0;
      if (draftTotal > 0 && depositAmount >= draftTotal) depositAmount = 0;
      
      const data = {
        lead_id: leadId,
        line_items: formData.line_items.map(item => ({
          description: item.description.trim(),
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        configurable_items: formData.configurable_items.map(item => ({
          id: item.id || undefined,
          description: item.description.trim(),
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        choice_groups: formData.choice_groups.map(cg => ({
          id: cg.id || undefined,
          group_name: cg.group_name,
          options: (cg.options || []).map(opt => ({
            id: opt.id || undefined,
            description: opt.description.trim(),
            quantity: parseFloat(opt.quantity) || 1,
            unit_price: parseFloat(opt.unit_price) || 0
          }))
        })),
        notes: formData.notes,
        valid_days: formData.valid_days,
        deposit_amount: depositAmount,
        tax_state: formData.tax_state || null,
        tax_rate: formData.tax_rate || 0,
        tax_amount: formData.tax_rate > 0 ? parseFloat((calculateTotal() * formData.tax_rate / 100).toFixed(2)) : 0,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        discount_label: (formData.discount_label || "").trim() || null,
        final_price_tbd: formData.final_price_tbd
      };

      let quoteId = editingQuote?.id;
      
      if (editingQuote) {
        await api.updateQuote(slug, editingQuote.id, data);
        quoteId = editingQuote.id;
      } else {
        const newQuote = await api.createQuote(slug, data);
        quoteId = newQuote.id;
      }
      
      // Send without payment link
      await api.sendQuoteWithoutPayment(slug, quoteId);
      toast.success("Quote sent (without payment link)");
      
      setShowCreateDialog(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save/send quote"));
    } finally {
      setSaving(false);
    }
  };

  const handleCollectBalance = async (quote) => {
    const quotedTotal = quote.final_total || quote.quoted_total || quote.total || 0;
    const depositPaid = quote.deposit_paid_amount || 0;
    const isTBD = quote.final_price_tbd;
    
    // finalTotalInput now stores the charge amount directly
    const chargeAmount = finalTotalInput !== "" ? parseFloat(finalTotalInput) || 0 : Math.max(0, quotedTotal - depositPaid);
    const finalTotal = chargeAmount + depositPaid;
    
    if (chargeAmount <= 0) {
      toast.error("Amount to charge must be greater than $0");
      return;
    }
    
    if (isTBD && finalTotalInput === "") {
      toast.error("Please enter an amount to charge");
      return;
    }
    
    // Get lead info for the notification
    const lead = leads.find(l => l.id === quote.lead_id);
    
    setCollectingBalance(quote.id);
    try {
      const result = await api.collectBalance(slug, quote.id, {
        final_total: finalTotal,
        send_email: true  // Send receipt/payment link to customer
      });
      
      if (result.charged) {
        // Instant charge succeeded!
        toast.success(`Successfully charged $${result.amount_charged.toFixed(2)}! Customer's card has been charged.`, {
          duration: 5000
        });
        setBalanceDialogQuote(null);
        setFinalTotalInput("");
        onRefresh();
      } else if (result.checkout_url) {
        // Fallback to payment link (no saved card) - show prominent popup
        setBalanceDialogQuote(null);
        setFinalTotalInput("");
        // Show popup - DON'T call onRefresh() yet as it resets component state
        setPaymentLinkSentInfo({
          customerEmail: lead?.customer_email || "customer",
          customerName: lead?.customer_name || "Customer",
          amount: result.balance_due,
          url: result.checkout_url,
          onClose: () => {
            setPaymentLinkSentInfo(null);
            onRefresh(); // Refresh AFTER popup is dismissed
          }
        });
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to collect balance"));
    } finally {
      setCollectingBalance(null);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await api.deleteQuote(slug, quoteId);
      toast.success("Quote deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };

  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    setSelectedLeadId(quote.lead_id);
    setFormData({
      line_items: quote.line_items || [],
      configurable_items: quote.configurable_items || [],
      choice_groups: quote.choice_groups || [],
      notes: quote.notes || "",
      valid_days: quote.valid_days || 30,
      deposit_amount: quote.deposit_amount || 0,
      payment_type: (quote.deposit_amount && quote.deposit_amount > 0) ? "deposit" : "full",
      tax_state: quote.tax_state || "",
      tax_rate: parseFloat(quote.tax_rate) || 0,
      final_price_tbd: !!quote.final_price_tbd,
      discount_percent: parseFloat(quote.discount_percent) || 0,
      discount_label: quote.discount_label || "",
    });
    setShowCreateDialog(true);
  };

  const getStatusBadge = (status, quote = null) => {
    const variants = {
      draft: "bg-gray-100 text-gray-800",
      sent: "bg-blue-100 text-blue-800",
      viewed: "bg-blue-100 text-slate-900",
      approved: "bg-blue-100 text-slate-900",
      declined: "bg-red-50 text-red-700",
      deposit_paid: "bg-amber-100 text-amber-800",
      paid: "bg-blue-100 text-slate-900",
      expired: "bg-red-100 text-red-800",
    };
    const labels = {
      draft: "Draft",
      sent: "Sent",
      viewed: "Viewed",
      approved: "Approved",
      declined: "Declined",
      deposit_paid: "Deposit Paid",
      paid: "Paid",
      expired: "Expired",
    };
    let label = labels[status] || status;
    // Suffix the offline method when fully paid via cash/check/etc.
    if (status === "paid" && quote && quote.last_offline_method) {
      label = `Paid · ${PAYMENT_METHOD_LABEL[quote.last_offline_method] || quote.last_offline_method}`;
    } else if (status === "deposit_paid" && quote && (quote.offline_payments || []).length > 0) {
      label = "Partially Paid";
    } else if (status === "approved" && quote && quote.manually_approved) {
      label = "Approved · Manual";
    }
    return <Badge className={variants[status] || "bg-gray-100 text-gray-800"}>{label}</Badge>;
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString();

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Quotes</h2>
        {(() => {
          const locked = isSubInactive(business);
          return (
            <Button
              onClick={locked ? () => showSubRequired() : () => { resetForm(); setShowCreateDialog(true); }}
              disabled={locked}
              aria-disabled={locked}
              title={locked ? SUB_LOCK_TOOLTIP : ""}
              size="sm"
              className={`w-full sm:w-auto ${locked ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed opacity-80" : ""}`}
              data-testid="create-quote-btn"
            >
              {locked ? <><Lock className="w-3.5 h-3.5 mr-1.5" /> Reactivate to Create</> : "+ Create Quote"}
            </Button>
          );
        })()}
      </div>

      {(!stripeStatus?.connected || !stripeStatus?.charges_enabled) && (
        <Card className="mb-4 sm:mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-3 sm:p-4">
            <p className="text-amber-800">Connect and verify your Stripe account to send quotes with payment links.</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {quotes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No quotes yet. Create your first quote from a lead.
            </div>
          ) : (
            <>
              {/* Bulk action bar */}
              {selectedQuoteIds.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2 bg-blue-50 border-b border-blue-200" data-testid="quotes-bulk-bar">
                  <span className="text-sm font-medium text-slate-900">
                    {selectedQuoteIds.length} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedQuoteIds([])}
                      data-testid="quotes-bulk-clear"
                    >
                      Clear
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                      disabled={bulkDeleting}
                      data-testid="quotes-bulk-delete"
                    >
                      {bulkDeleting ? "Deleting..." : `Delete ${selectedQuoteIds.length}`}
                    </Button>
                  </div>
                </div>
              )}

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-blue-800 focus:ring-blue-700"
                          checked={quotes.length > 0 && selectedQuoteIds.length === quotes.length}
                          onChange={toggleAllQuotes}
                          aria-label="Select all quotes"
                          data-testid="quotes-select-all"
                        />
                      </TableHead>
                      <TableHead>Quote #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.map((quote) => {
                      const lead = leads.find(l => l.id === quote.lead_id);
                      const quotedTotal = quote.quoted_total || quote.total;
                      const depositAmount = quote.deposit_amount || 0;
                      const depositPaid = quote.deposit_paid_amount || 0;
                      const finalTotal = quote.final_total || quotedTotal;
                      const balanceDue = finalTotal - depositPaid;
                      const isDeposit = depositAmount > 0 && depositAmount < quotedTotal;
                      
                      return (
                        <TableRow key={quote.id} className={selectedQuoteIds.includes(quote.id) ? "bg-blue-50/50" : ""}>
                          <TableCell className="w-10">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-blue-800 focus:ring-blue-700"
                              checked={selectedQuoteIds.includes(quote.id)}
                              onChange={() => toggleQuoteSelected(quote.id)}
                              aria-label={`Select quote ${quote.quote_number || quote.id.slice(0, 8)}`}
                              data-testid={`quote-select-${quote.id}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{quote.quote_number || quote.id.slice(0, 8)}</TableCell>
                          <TableCell>
                            <div>{lead?.customer_name || lead?.customer_email || "Unknown"}</div>
                            {/* Email read/unread indicator — only visible when the quote
                                has actually been emailed (quote_sent_at exists). Kept
                                ultra-small + grey so it doesn't crowd the row. */}
                            {quote.quote_sent_at && (
                              quote.email_opened_at ? (
                                <div className="text-[10px] text-blue-700 mt-0.5" title={`Opened ${new Date(quote.email_opened_at).toLocaleString()}`} data-testid={`email-read-${quote.id}`}>
                                  • Email read
                                </div>
                              ) : (
                                <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`email-unread-${quote.id}`}>
                                  • Email unread
                                </div>
                              )
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">${quotedTotal?.toFixed(2)}</div>
                            {isDeposit && quote.status !== "draft" && (
                              <div className="text-xs text-gray-500">
                                Deposit: ${depositAmount?.toFixed(2)}
                                {depositPaid > 0 && <span className="text-blue-700 ml-1">(Paid)</span>}
                              </div>
                            )}
                            {quote.status === "deposit_paid" && (
                              <div className="text-xs text-amber-600 font-medium">
                                Balance: ${balanceDue?.toFixed(2)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{getStatusBadge(quote.status, quote)}</TableCell>
                          <TableCell className="text-sm">{formatDate(quote.created_at)}</TableCell>
                          <TableCell className="text-right">
                            {/* Edit/Resend is always available — re-opens the editor and customer gets a fresh email when saved+sent */}
                            {quote.status !== "draft" && (
                              <Button variant="ghost" size="sm" onClick={() => handleEditQuote(quote)} data-testid={`edit-quote-${quote.id}`}>
                                Edit/Resend
                              </Button>
                            )}
                            {quote.status === "draft" && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleEditQuote(quote)}>
                                  Edit
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={subLocked ? () => showSubRequired() : () => handleSendQuote(quote.id)}
                                  disabled={sending === quote.id || subLocked}
                                  title={subLocked ? SUB_LOCK_TOOLTIP : ""}
                                  className={subLocked ? "text-slate-400 cursor-not-allowed" : ""}
                                >
                                  {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1" /> Locked</> : (sending === quote.id ? "Sending..." : "Send")}
                                </Button>
                              </>
                            )}
                            {quote.status === "sent" && (
                              <span className="text-xs text-gray-500 mr-2">Quote sent ✓</span>
                            )}
                            {quote.status === "deposit_paid" && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className={subLocked ? "text-slate-400 border-slate-200 cursor-not-allowed opacity-80" : "text-amber-600 border-amber-200"}
                                disabled={subLocked}
                                title={subLocked ? SUB_LOCK_TOOLTIP : ""}
                                onClick={subLocked ? () => showSubRequired() : () => {
                                  setFinalTotalInput(""); // Reset to allow auto-calculation
                                  setBalanceDialogQuote(quote);
                                }}
                              >
                                {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1" /> Locked</> : "Collect Balance"}
                              </Button>
                            )}
                            {quote.status === "paid" && (
                              <span className="text-sm text-blue-700">✓ Paid</span>
                            )}
                            {/* Manual approve — available on ANY non-approved/non-paid status (incl. draft).
                                Use case: create a quote for an elderly customer in-person and approve on their behalf without ever sending it. */}
                            {quote.status !== "approved" && quote.status !== "declined" && quote.status !== "paid" && quote.status !== "deposit_paid" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={subLocked ? "text-slate-400 cursor-not-allowed" : "text-slate-900 hover:text-slate-900 hover:bg-blue-50"}
                                  disabled={subLocked}
                                  title={subLocked ? SUB_LOCK_TOOLTIP : "Approve on customer's behalf (silent — no email)"}
                                  onClick={subLocked ? () => showSubRequired() : () => handleManualApprove(quote)}
                                  data-testid={`manual-approve-${quote.id}`}
                                >
                                  {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1" /> Locked</> : "Approve"}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={subLocked ? "text-slate-400 cursor-not-allowed" : "text-red-700 hover:text-red-800 hover:bg-red-50"}
                                  disabled={subLocked}
                                  title={subLocked ? SUB_LOCK_TOOLTIP : "Decline on customer's behalf (silent — no email)"}
                                  onClick={subLocked ? () => showSubRequired() : () => handleManualDecline(quote)}
                                  data-testid={`manual-decline-${quote.id}`}
                                >
                                  {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1" /> Locked</> : "Decline"}
                                </Button>
                              </>
                            )}
                            {/* Change status — works on ANY approved or declined quote
                                (whether the customer or the owner triggered it). Sends
                                the quote back to "sent" so it can be re-decided. Tiny
                                ghost button keeps the UI uncrowded. */}
                            {(quote.status === "approved" || quote.status === "declined") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-500 hover:text-gray-700"
                                onClick={() => handleUnapprove(quote)}
                                title="Change status — revert to sent (re-decide approve or decline)"
                                data-testid={`change-status-${quote.id}`}
                              >
                                Change status
                              </Button>
                            )}
                            {/* Record / manage offline payments — available on every non-draft quote
                                so the owner can view past payments and undo if needed (incl. fully paid). */}
                            {quote.status !== "draft" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-800 hover:text-slate-900 hover:bg-blue-50"
                                onClick={() => openRecordPayment(quote)}
                                data-testid={`record-payment-${quote.id}`}
                              >
                                {(quote.offline_payments || []).length > 0 ? "Payments" : "Record Payment"}
                              </Button>
                            )}
                            {/* Delete available for all statuses */}
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteQuote(quote.id)} data-testid={`delete-quote-${quote.id}`}>
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              
              {/* Mobile Card View */}
              <div className="md:hidden divide-y">
                {quotes.map((quote) => {
                  const lead = leads.find(l => l.id === quote.lead_id);
                  const quotedTotal = quote.quoted_total || quote.total;
                  const depositAmount = quote.deposit_amount || 0;
                  const depositPaid = quote.deposit_paid_amount || 0;
                  const finalTotal = quote.final_total || quotedTotal;
                  const balanceDue = finalTotal - depositPaid;
                  const isDeposit = depositAmount > 0 && depositAmount < quotedTotal;
                  
                  return (
                    <div key={quote.id} className={`p-4 space-y-3 ${selectedQuoteIds.includes(quote.id) ? "bg-blue-50/40" : ""}`}>
                      {/* Header row */}
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-800 focus:ring-blue-700 shrink-0"
                          checked={selectedQuoteIds.includes(quote.id)}
                          onChange={() => toggleQuoteSelected(quote.id)}
                          aria-label={`Select quote ${quote.quote_number || quote.id.slice(0, 8)}`}
                          data-testid={`quote-select-mobile-${quote.id}`}
                        />
                        <div className="flex-1 flex items-start justify-between">
                          <div>
                            <div className="font-medium text-gray-900">
                              {lead?.customer_name || lead?.customer_email || "Unknown"}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">
                              #{quote.quote_number || quote.id.slice(0, 8)}
                            </div>
                            {quote.quote_sent_at && (
                              quote.email_opened_at ? (
                                <div className="text-[10px] text-blue-700 mt-0.5" title={`Opened ${new Date(quote.email_opened_at).toLocaleString()}`} data-testid={`email-read-mobile-${quote.id}`}>
                                  • Email read
                                </div>
                              ) : (
                                <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`email-unread-mobile-${quote.id}`}>
                                  • Email unread
                                </div>
                              )
                            )}
                          </div>
                          {getStatusBadge(quote.status, quote)}
                        </div>
                      </div>
                      
                      {/* Amount row */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-lg font-semibold">${quotedTotal?.toFixed(2)}</div>
                          {isDeposit && quote.status !== "draft" && (
                            <div className="text-xs text-gray-500">
                              Deposit: ${depositAmount?.toFixed(2)}
                              {depositPaid > 0 && <span className="text-blue-700 ml-1">(Paid)</span>}
                            </div>
                          )}
                          {quote.status === "deposit_paid" && (
                            <div className="text-xs text-amber-600 font-medium">
                              Balance due: ${balanceDue?.toFixed(2)}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{formatDate(quote.created_at)}</div>
                      </div>
                      
                      {/* Actions row */}
                      <div className="flex flex-wrap gap-2 pt-2">
                        {quote.status !== "draft" && (
                          <Button variant="outline" size="sm" onClick={() => handleEditQuote(quote)} data-testid={`edit-quote-mobile-${quote.id}`}>
                            Edit/Resend
                          </Button>
                        )}
                        {quote.status === "draft" && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handleEditQuote(quote)}>
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={subLocked ? () => showSubRequired() : () => handleSendQuote(quote.id)}
                              disabled={sending === quote.id || subLocked}
                              title={subLocked ? SUB_LOCK_TOOLTIP : ""}
                              className={subLocked ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed opacity-80" : ""}
                            >
                              {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1" /> Locked</> : (sending === quote.id ? "Sending..." : "Send")}
                            </Button>
                          </>
                        )}
                        {quote.status === "deposit_paid" && (
                          <Button 
                            size="sm"
                            className={subLocked ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed opacity-80" : "bg-amber-500 hover:bg-amber-600"}
                            disabled={subLocked}
                            title={subLocked ? SUB_LOCK_TOOLTIP : ""}
                            onClick={subLocked ? () => showSubRequired() : () => {
                              setFinalTotalInput(""); // Reset to allow auto-calculation
                              setBalanceDialogQuote(quote);
                            }}
                          >
                            {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1" /> Locked</> : "Collect Balance"}
                          </Button>
                        )}
                        {quote.status === "paid" && (
                          <span className="text-sm text-blue-700 font-medium">✓ Paid in full</span>
                        )}
                        {/* Manual approve / decline — works on any non-approved/non-paid quote (incl. drafts) */}
                        {quote.status !== "approved" && quote.status !== "declined" && quote.status !== "paid" && quote.status !== "deposit_paid" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className={subLocked ? "text-slate-400 border-slate-200 cursor-not-allowed opacity-80" : "text-slate-900 border-blue-300 hover:bg-blue-50"}
                              disabled={subLocked}
                              title={subLocked ? SUB_LOCK_TOOLTIP : ""}
                              onClick={subLocked ? () => showSubRequired() : () => handleManualApprove(quote)}
                              data-testid={`manual-approve-mobile-${quote.id}`}
                            >
                              {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1" /> Locked</> : "Approve"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className={subLocked ? "text-slate-400 border-slate-200 cursor-not-allowed opacity-80" : "text-red-700 border-red-200 hover:bg-red-50"}
                              disabled={subLocked}
                              title={subLocked ? SUB_LOCK_TOOLTIP : ""}
                              onClick={subLocked ? () => showSubRequired() : () => handleManualDecline(quote)}
                              data-testid={`manual-decline-mobile-${quote.id}`}
                            >
                              {subLocked ? <><Lock className="w-3.5 h-3.5 mr-1" /> Locked</> : "Decline"}
                            </Button>
                          </>
                        )}
                        {/* Change status — works on any approved or declined quote */}
                        {(quote.status === "approved" || quote.status === "declined") && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-gray-600 border-gray-300"
                            onClick={() => handleUnapprove(quote)}
                            data-testid={`change-status-mobile-${quote.id}`}
                          >
                            Change status
                          </Button>
                        )}
                        {/* Record / manage offline payments (cash/check/Venmo/etc.) — always available
                            on non-draft so user can view and undo past payments even after full pay. */}
                        {quote.status !== "draft" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-blue-800 border-blue-200 hover:bg-blue-50"
                            onClick={() => openRecordPayment(quote)}
                            data-testid={`record-payment-mobile-${quote.id}`}
                          >
                            {(quote.offline_payments || []).length > 0 ? "Payments" : "Record Payment"}
                          </Button>
                        )}
                        {/* Delete available for all statuses */}
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteQuote(quote.id)} data-testid={`delete-quote-mobile-${quote.id}`}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Record Offline Payment Modal */}
      <Dialog open={!!recordPaymentQuote} onOpenChange={(o) => { if (!o) setRecordPaymentQuote(null); }}>
        <DialogContent className="max-w-sm">
          <DialogTitle className="text-base">Record Payment</DialogTitle>
          {recordPaymentQuote && (
            <div className="space-y-4 pt-2">
              <div className="text-xs text-gray-500">
                Quote #{recordPaymentQuote.quote_number || recordPaymentQuote.id.slice(0, 8)}
                {(() => {
                  const total = recordPaymentQuote.quoted_total || recordPaymentQuote.total || 0;
                  const stripePaid = recordPaymentQuote.deposit_paid_amount || 0;
                  const offlineSum = (recordPaymentQuote.offline_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                  const balance = Math.max(0, total - stripePaid - offlineSum);
                  return (
                    <div className="mt-1">
                      <span className="text-gray-700">Total: </span>${total.toFixed(2)}
                      <span className="ml-3 text-gray-700">Balance Due: </span>
                      <span className="font-semibold text-slate-900">${balance.toFixed(2)}</span>
                    </div>
                  );
                })()}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700">Payment Method</label>
                <select
                  value={recordPaymentMethod}
                  onChange={(e) => setRecordPaymentMethod(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-700"
                  data-testid="record-payment-method"
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">CashApp</option>
                  <option value="paypal">PayPal</option>
                  <option value="zelle">Zelle</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700">Amount</label>
                <Input
                  type="number"
                  step="0.01"
                  value={recordPaymentAmount}
                  onChange={(e) => setRecordPaymentAmount(e.target.value)}
                  className="mt-1"
                  data-testid="record-payment-amount"
                  autoFocus
                  disabled={recordPaymentQuote.status === "paid"}
                />
                {recordPaymentQuote.status === "paid" && (
                  <p className="text-[11px] text-blue-800 mt-1">Fully paid. Remove a payment below to add more.</p>
                )}
              </div>

              {/* Existing offline payments list — undo links */}
              {(recordPaymentQuote.offline_payments || []).length > 0 && (
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs font-medium text-gray-700 mb-1.5">Recorded Payments</p>
                  <div className="space-y-1">
                    {recordPaymentQuote.offline_payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5">
                        <div className="text-gray-700">
                          <span className="font-medium">${(p.amount || 0).toFixed(2)}</span>
                          <span className="text-gray-500 ml-1.5">{PAYMENT_METHOD_LABEL[p.method] || p.method}</span>
                          <span className="text-gray-400 ml-1.5">· {new Date(p.recorded_at).toLocaleDateString()}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteOfflinePayment(recordPaymentQuote.id, p.id)}
                          className="text-red-600 hover:text-red-800 underline"
                          data-testid={`delete-offline-payment-${p.id}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-1">
                <Button variant="ghost" size="sm" onClick={() => setRecordPaymentQuote(null)}>Close</Button>
                <Button
                  size="sm"
                  className="bg-blue-800 hover:bg-slate-900 text-white"
                  onClick={submitRecordPayment}
                  disabled={recordingPayment || recordPaymentQuote.status === "paid"}
                  data-testid="record-payment-submit"
                >
                  {recordingPayment ? "Saving..." : "Record Payment"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Quote Dialog - Looks EXACTLY like the intake form */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent hideCloseButton className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden p-0 gap-0">
          {/* Form container - matches intake form styling */}
          <div 
            className="min-h-full"
            style={{ background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)' }}
          >
            {/* Single bold close button */}
            <button 
              onClick={() => { setShowCreateDialog(false); resetForm(); }}
              className="absolute top-3 right-3 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="p-5 space-y-4">
              {/* Header - like intake form */}
              <div className="text-center pb-2">
                <h2 className="text-lg font-bold text-gray-900">
                  {editingQuote ? (editingQuote.status === "draft" ? "Edit Quote" : `Edit Quote · ${editingQuote.quote_number || ""}`) : "Build Quote"}
                </h2>
                {editingQuote && editingQuote.status !== "draft" && (
                  <p className="text-xs text-slate-500 mt-1">Current status: <span className="font-medium text-slate-700">{editingQuote.status}</span></p>
                )}
              </div>

              {/* Customer Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Customer *</label>
                  {!editingQuote && (
                    <button
                      type="button"
                      onClick={() => {
                        setNewCustomerMode(!newCustomerMode);
                        setSelectedLeadId("");
                        setNewCustomerEmail("");
                        setNewCustomerName("");
                      }}
                      className="text-xs text-slate-900 hover:text-slate-900"
                    >
                      {newCustomerMode ? "Select existing" : "New customer"}
                    </button>
                  )}
                </div>
                
                {newCustomerMode ? (
                  <div className="space-y-2">
                    <Input
                      type="text"
                      placeholder="Customer name"
                      value={newCustomerName}
                      onChange={(e) => setNewCustomerName(e.target.value)}
                      className="bg-white"
                    />
                    <Input
                      type="email"
                      placeholder="Customer email *"
                      value={newCustomerEmail}
                      onChange={(e) => setNewCustomerEmail(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                ) : (
                  <Select 
                    value={selectedLeadId} 
                    onValueChange={(val) => {
                      setSelectedLeadId(val);
                      const lead = leads.find(l => l.id === val);
                      if (lead && lead.pricing_snapshot && lead.pricing_snapshot.length > 0) {
                        const baseTotal = lead.pricing_snapshot.reduce((sum, ps) => {
                          if (ps.is_modifier) return sum;
                          return sum + ((ps.price_value || 0) * (ps.quantity || 1));
                        }, 0);
                        
                        const autoItems = lead.pricing_snapshot.map(ps => {
                          let price = ps.price_value || 0;
                          if (ps.is_modifier && ps.modifier_type === 'percentage' && ps.modifier_percent) {
                            price = Math.round(baseTotal * (ps.modifier_percent / 100) * 100) / 100;
                          }
                          return {
                            description: ps.label || ps.service_name || "Service",
                            quantity: ps.quantity || 1,
                            unit_price: price
                          };
                        });
                        setFormData(prev => ({ ...prev, line_items: autoItems }));
                      }
                    }} 
                    disabled={!!editingQuote}
                  >
                    <SelectTrigger className="bg-white border-gray-200 rounded-lg">
                      <SelectValue placeholder="Select customer..." />
                    </SelectTrigger>
                    <SelectContent>
                      {leads.map((lead) => (
                        <SelectItem key={lead.id} value={lead.id}>
                          {lead.customer_name || lead.customer_email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Selected customer info */}
              {selectedLeadId && !newCustomerMode && (() => {
                const lead = leads.find(l => l.id === selectedLeadId);
                return lead ? (
                  <div className="bg-white/60 rounded-lg p-3 text-sm">
                    <div className="font-medium text-gray-900">{lead.customer_name}</div>
                    <div className="text-gray-600">{lead.customer_email}</div>
                    {lead.customer_phone && <div className="text-gray-600">{lead.customer_phone}</div>}
                  </div>
                ) : null;
              })()}

              {/* Services - searchable picker */}
              {services.filter(s => s.active && !s.is_modifier).length > 0 && (
                <ServicePicker
                  services={services}
                  selectedLabels={formData.line_items.map(li => li.description)}
                  disabled={formData.configurable_items.length > 0}
                  onToggle={(service) => {
                    const isAdded = formData.line_items.some(item => item.description === service.label);
                    if (isAdded) {
                      setFormData(prev => ({
                        ...prev,
                        line_items: prev.line_items.filter(item => item.description !== service.label)
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        line_items: [...prev.line_items, {
                          description: service.label,
                          quantity: 1,
                          unit_price: service.price_value || 0
                        }]
                      }));
                    }
                  }}
                  onAddCustom={() => {
                    setFormData(prev => ({
                      ...prev,
                      line_items: [...prev.line_items, { description: "", quantity: 1, unit_price: 0 }]
                    }));
                  }}
                />
              )}

              {/* Modifiers - only show when services selected and no configurable items */}
              {services.filter(s => s.active && s.is_modifier).length > 0 && formData.line_items.length > 0 && formData.configurable_items.length === 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Add-ons</label>
                  <div className="space-y-2">
                    {services.filter(s => s.active && s.is_modifier).map((service) => {
                      const isAdded = formData.line_items.some(item => item.description === service.label);
                      let priceDisplay = "";
                      if (service.modifier_type === "percentage") priceDisplay = `+${service.modifier_percent}%`;
                      else if (service.modifier_type === "fixed") priceDisplay = `+$${service.modifier_fixed}`;
                      
                      return (
                        <div
                          key={service.id}
                          onClick={() => {
                            if (isAdded) {
                              setFormData(prev => ({
                                ...prev,
                                line_items: prev.line_items.filter(item => item.description !== service.label)
                              }));
                            } else {
                              let amount = service.price_value || 0;
                              if (service.modifier_type === "percentage" && service.modifier_percent) {
                                const total = formData.line_items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
                                amount = parseFloat((total * service.modifier_percent / 100).toFixed(2));
                              } else if (service.modifier_type === "fixed") {
                                amount = service.modifier_fixed || 0;
                              }
                              setFormData(prev => ({
                                ...prev,
                                line_items: [...prev.line_items, { description: service.label, quantity: 1, unit_price: amount }]
                              }));
                            }
                          }}
                          className={`border rounded-lg p-3 cursor-pointer transition-all ${
                            isAdded 
                              ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600'
                              : 'border-gray-200 bg-white hover:border-blue-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <div className={`h-4 w-4 rounded border flex items-center justify-center ${
                                isAdded ? 'bg-blue-700 border-blue-700' : 'border-gray-300 bg-white'
                              }`}>
                                {isAdded && (
                                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className="font-medium text-gray-900 text-sm">{service.label}</span>
                            </div>
                            <span className="font-medium text-blue-700 text-sm">{priceDisplay}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Line Items - Editable */}
              {formData.line_items.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Line Items</label>
                  <div className="bg-white rounded-lg border border-gray-200 divide-y">
                    {formData.line_items.map((item, idx) => (
                      <div key={idx} className="p-3 flex items-center gap-2">
                        <Input
                          value={item.description}
                          onChange={(e) => handleUpdateLineItem(idx, "description", e.target.value)}
                          className="flex-1 border-0 p-0 h-auto text-base md:text-sm font-medium focus:ring-0"
                        />
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleUpdateLineItem(idx, "quantity", e.target.value === '' ? '' : parseFloat(e.target.value))}
                          onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) handleUpdateLineItem(idx, "quantity", 1); }}
                          className="w-12 text-center text-base md:text-sm"
                          min="1"
                        />
                        <span className="text-gray-400">×</span>
                        <div className="flex items-center">
                          <span className="text-gray-400">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => handleUpdateLineItem(idx, "unit_price", e.target.value === '' ? '' : parseFloat(e.target.value))}
                            onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) handleUpdateLineItem(idx, "unit_price", 0); }}
                            className="w-16 text-base md:text-sm"
                          />
                        </div>
                        <button onClick={() => handleRemoveLineItem(idx)} className="text-red-400 hover:text-red-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Custom Item Button */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  line_items: [...prev.line_items, { description: "", quantity: 1, unit_price: 0 }]
                }))}
                className="w-full border-2 border-dashed border-gray-300 bg-white/50 rounded-lg p-3 text-gray-500 hover:border-blue-500 hover:text-slate-900 transition-colors text-sm"
              >
                + Add Custom Service
              </button>

              {/* Configurable Items - services the customer can choose */}
              {formData.configurable_items.length > 0 && (
                <div className="bg-amber-50/50 rounded-lg border border-amber-200 divide-y divide-amber-100">
                  {formData.configurable_items.map((item, idx) => (
                    <div key={idx} className="p-3 flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      {services.filter(s => s.active).length > 0 ? (
                        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{item.description}</span>
                      ) : (
                        <Input
                          value={item.description}
                          onChange={(e) => {
                            const updated = [...formData.configurable_items];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setFormData(prev => ({ ...prev, configurable_items: updated }));
                          }}
                          placeholder="Service name..."
                          className="flex-1 min-w-0 border-0 p-0 h-auto text-sm font-medium focus:ring-0 bg-transparent"
                        />
                      )}
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={item.quantity}
                        onChange={(e) => {
                          const updated = [...formData.configurable_items];
                          updated[idx] = { ...updated[idx], quantity: e.target.value === '' ? '' : parseFloat(e.target.value) };
                          setFormData(prev => ({ ...prev, configurable_items: updated }));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                            const updated = [...formData.configurable_items];
                            updated[idx] = { ...updated[idx], quantity: 1 };
                            setFormData(prev => ({ ...prev, configurable_items: updated }));
                          }
                        }}
                        className="w-12 shrink-0 text-center text-sm"
                        style={{ fontSize: '16px' }}
                        min="1"
                      />
                      <span className="text-gray-400 shrink-0">x $</span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => {
                          const updated = [...formData.configurable_items];
                          updated[idx] = { ...updated[idx], unit_price: e.target.value === '' ? '' : parseFloat(e.target.value) };
                          setFormData(prev => ({ ...prev, configurable_items: updated }));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                            const updated = [...formData.configurable_items];
                            updated[idx] = { ...updated[idx], unit_price: 0 };
                            setFormData(prev => ({ ...prev, configurable_items: updated }));
                          }
                        }}
                        className="w-16 shrink-0 text-sm"
                        style={{ fontSize: '16px' }}
                      />
                      <button onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          configurable_items: prev.configurable_items.filter((_, i) => i !== idx)
                        }));
                      }} className="text-red-400 hover:text-red-600 shrink-0">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Service Picker for configurable items */}
              {(() => {
                const usedDescs = formData.configurable_items.map(i => i.description);
                const available = services.filter(s => s.active && !usedDescs.includes(s.label));
                if (available.length > 0) {
                  return (
                    <select
                      value=""
                      onChange={(e) => {
                        const svc = services.find(s => s.id === e.target.value);
                        if (!svc) return;
                        let price = svc.price_value || 0;
                        if (svc.is_modifier && svc.modifier_type === 'fixed') price = svc.modifier_fixed || 0;
                        setFormData(prev => ({
                          ...prev,
                          configurable_items: [...prev.configurable_items, {
                            id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
                            description: svc.label,
                            quantity: 1,
                            unit_price: price
                          }]
                        }));
                      }}
                      className="w-full border-2 border-dashed border-amber-300 bg-amber-50/30 rounded-lg p-3 text-amber-600 text-sm appearance-none cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-colors"
                      data-testid="add-configurable-item-btn"
                    >
                      <option value="" disabled>+ Add Optional Service </option>
                      {available.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.label}{s.price_value > 0 ? ` — $${s.price_value}` : s.modifier_fixed > 0 ? ` — $${s.modifier_fixed}` : ''}
                        </option>
                      ))}
                    </select>
                  );
                }
                if (services.filter(s => s.active).length === 0) {
                  return (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        configurable_items: [...prev.configurable_items, {
                          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
                          description: "",
                          quantity: 1,
                          unit_price: 0
                        }]
                      }))}
                      className="w-full border-2 border-dashed border-amber-300 bg-amber-50/30 rounded-lg p-3 text-amber-600 text-sm hover:border-amber-400 hover:bg-amber-50 transition-colors"
                      data-testid="add-configurable-item-btn"
                    >
                      + Add Configurable Service
                    </button>
                  );
                }
                return formData.configurable_items.length > 0 ? (
                  <p className="text-xs text-gray-400 text-center py-1">All services added</p>
                ) : null;
              })()}

              {/* Tax State Selector */}
              <div className="relative">
                <label className="text-xs text-gray-500 mb-1 block">Sales Tax (optional)</label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={formData.tax_state ? `${formData.tax_state} (${formData.tax_rate}%)` : taxSearch}
                      onChange={(e) => {
                        setTaxSearch(e.target.value);
                        setShowTaxDropdown(true);
                        if (formData.tax_state) setFormData({ ...formData, tax_state: "", tax_rate: 0 });
                      }}
                      onFocus={() => { if (taxSearch || formData.tax_state) setShowTaxDropdown(true); }}
                      placeholder="Search state..."
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      data-testid="tax-state-input"
                    />
                    {formData.tax_state && (
                      <button onClick={() => { setFormData({ ...formData, tax_state: "", tax_rate: 0 }); setTaxSearch(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">&times;</button>
                    )}
                    {showTaxDropdown && filteredStates.length > 0 && (
                      <div className="absolute z-50 top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                        {filteredStates.map(s => (
                          <button key={s.abbr} className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex justify-between"
                            onClick={() => {
                              setFormData({ ...formData, tax_state: s.state, tax_rate: s.rate });
                              setTaxSearch("");
                              setShowTaxDropdown(false);
                            }}>
                            <span>{s.state} ({s.abbr})</span>
                            <span className="text-gray-500">{s.rate}%</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Discount (per-quote) — slider 0–100% with custom label.
                  Hidden if there are no line items, since a discount on $0
                  is meaningless and would just add UI noise. */}
              {formData.line_items.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <label className="text-sm font-medium text-gray-700 shrink-0">Discount</label>
                      <input
                        type="text"
                        value={formData.discount_label}
                        onChange={(e) => setFormData({ ...formData, discount_label: e.target.value })}
                        placeholder="Label (optional)"
                        maxLength={40}
                        className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        data-testid="discount-label-input"
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0" data-testid="discount-percent-label">
                      {parseFloat(formData.discount_percent) || 0}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={formData.discount_percent || 0}
                    onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value, 10) })}
                    className="w-full accent-blue-600"
                    aria-label="Discount percentage"
                    data-testid="discount-slider"
                  />
                  {(parseFloat(formData.discount_percent) || 0) > 0 && (
                    <p className="text-xs text-blue-700" data-testid="discount-amount-display">
                      − ${(calculateSubtotal() * (parseFloat(formData.discount_percent) || 0) / 100).toFixed(2)} off
                    </p>
                  )}
                </div>
              )}

              {/* Total with Tax */}
              <div className="bg-gray-900 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center text-white/70 text-sm">
                  <span>Subtotal</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>
                {(parseFloat(formData.discount_percent) || 0) > 0 && (
                  <div className="flex justify-between items-center text-white/70 text-sm" data-testid="totals-discount-row">
                    <span>{(formData.discount_label || "Discount")} ({parseFloat(formData.discount_percent) || 0}%)</span>
                    <span>− ${(calculateSubtotal() - calculateTotal()).toFixed(2)}</span>
                  </div>
                )}
                {formData.tax_rate > 0 && (
                  <div className="flex justify-between items-center text-white/70 text-sm">
                    <span>{formData.tax_state} Tax ({formData.tax_rate}%)</span>
                    <span>${(calculateTotal() * formData.tax_rate / 100).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t border-white/20 pt-2">
                  <span className="text-white font-semibold">Total</span>
                  <span className="text-2xl font-bold text-blue-500">${(calculateTotal() * (1 + formData.tax_rate / 100)).toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Message</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Write a personal note to your customer. Tip: Include your Venmo, Cash App, or payment link if you're not using Stripe."
                  className="w-full bg-white border border-gray-200 rounded-lg p-3 text-base md:text-sm resize-none"
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-2">
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    onClick={() => handleSaveQuote(true)}
                    disabled={saving || sending || (formData.line_items.length === 0 && formData.configurable_items.length === 0) || (!selectedLeadId && !newCustomerEmail)}
                    className="bg-slate-900 hover:bg-slate-900 text-white py-4 font-semibold"
                    data-testid="send-email-btn"
                  >
                    {sending || saving ? "..." : editingQuote && editingQuote.status !== "draft" ? "Resend Email" : "Send Email"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleSaveAndText({ alsoEmail: false })}
                    disabled={saving || sending || (formData.line_items.length === 0 && formData.configurable_items.length === 0) || (!selectedLeadId && !newCustomerEmail)}
                    variant="outline"
                    className="py-4 font-semibold border-blue-300 text-slate-900 hover:bg-blue-50"
                    data-testid="send-text-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1.5" />
                    {saving ? "..." : editingQuote && editingQuote.status !== "draft" ? "Resend Text" : "Send Text"}
                  </Button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => handleSaveAndText({ alsoEmail: true })}
                    disabled={saving || sending || (formData.line_items.length === 0 && formData.configurable_items.length === 0) || (!selectedLeadId && !newCustomerEmail)}
                    data-testid="send-both-link"
                    className="text-xs text-blue-800 hover:text-slate-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingQuote && editingQuote.status !== "draft" ? "Resend both email and text" : "Send both email and text"}
                  </button>
                </div>

                <Button
                  onClick={() => handleSaveQuote(false)}
                  disabled={saving || (!selectedLeadId && !newCustomerEmail)}
                  variant="outline"
                  className="w-full py-3 text-sm"
                  data-testid="save-changes-btn"
                >
                  {editingQuote && editingQuote.status !== "draft" ? "Save Changes (don't notify)" : "Save Draft"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Collect Balance Dialog */}
      <Dialog open={!!balanceDialogQuote} onOpenChange={() => setBalanceDialogQuote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collect Remaining Balance</DialogTitle>
          </DialogHeader>
          {balanceDialogQuote && (() => {
            const quotedTotal = balanceDialogQuote.final_total || balanceDialogQuote.quoted_total || balanceDialogQuote.total || 0;
            const depositPaid = balanceDialogQuote.deposit_paid_amount || 0;
            const isTBD = balanceDialogQuote.final_price_tbd;
            const calculatedBalance = Math.max(0, quotedTotal - depositPaid);
            const chargeAmount = finalTotalInput !== "" ? parseFloat(finalTotalInput) || 0 : calculatedBalance;
            
            return (
            <div className="space-y-4">
              {/* Show deposit already collected */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-blue-800">Deposit Collected</span>
                  <span className="text-slate-900 font-semibold">${depositPaid.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Amount to charge input */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Amount to Charge</Label>
                {!isTBD && quotedTotal > 0 && (
                  <p className="text-xs text-gray-500">
                    Suggested: ${calculatedBalance.toFixed(2)} (from ${quotedTotal.toFixed(2)} quote)
                  </p>
                )}
                {isTBD && (
                  <p className="text-xs text-amber-600">
                    Final price was marked as TBD - enter the amount to charge
                  </p>
                )}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={finalTotalInput !== "" ? finalTotalInput : (isTBD ? "" : calculatedBalance.toString())}
                    onChange={(e) => {
                      setFinalTotalInput(e.target.value);
                    }}
                    placeholder="Enter amount..."
                    className="text-2xl font-bold pl-8 h-14"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Instant charge:</strong> Customer's saved card will be charged immediately. If no card is on file, an email with a payment link will be sent.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setBalanceDialogQuote(null)}>Cancel</Button>
                <Button 
                  onClick={() => handleCollectBalance(balanceDialogQuote)}
                  disabled={collectingBalance === balanceDialogQuote.id || chargeAmount <= 0}
                  className="bg-amber-600 hover:bg-amber-700 text-lg px-6"
                >
                  {collectingBalance === balanceDialogQuote.id ? "Charging..." : `Charge $${chargeAmount.toFixed(2)}`}
                </Button>
              </div>
            </div>
          );})()}
        </DialogContent>
      </Dialog>
      
      {/* Payment Link Sent Notification Dialog */}
      <Dialog open={!!paymentLinkSentInfo} onOpenChange={(open) => {
        if (!open && paymentLinkSentInfo?.onClose) {
          paymentLinkSentInfo.onClose();
        } else if (!open) {
          setPaymentLinkSentInfo(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Payment Link Sent
            </DialogTitle>
          </DialogHeader>
          {paymentLinkSentInfo && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium mb-2">
                  No saved card was found for this customer.
                </p>
                <p className="text-amber-700 text-sm">
                  An email with a payment link for <strong>${paymentLinkSentInfo.amount?.toFixed(2)}</strong> has been sent to:
                </p>
                <p className="text-amber-900 font-semibold mt-1">{paymentLinkSentInfo.customerEmail}</p>
              </div>
              
              <p className="text-sm text-gray-500">
                The customer will need to complete payment using the link. Once paid, the status will update automatically.
              </p>
              
              <div className="flex justify-end pt-2">
                <Button onClick={() => {
                  if (paymentLinkSentInfo?.onClose) {
                    paymentLinkSentInfo.onClose();
                  } else {
                    setPaymentLinkSentInfo(null);
                  }
                }}>
                  Got it
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== DIRECT QUOTE BUILDER PAGE ==============
// Legacy route. The standalone builder was outdated and missing features
// (configurable add-ons, deposit removal, etc.). We now redirect every visit
// to the dashboard's full quote builder dialog, pre-loaded for this lead.
const DirectQuoteBuilder = () => {
  const { slug, leadId } = useParams();
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate(`/dashboard/${slug}?openQuote=${leadId}`, { replace: true });
  }, [slug, leadId, navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="inline-block w-8 h-8 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600">Opening quote builder…</p>
      </div>
    </div>
  );
};

// Map bounds updater component for Leaflet
const MapBoundsUpdater = ({ stops }) => {
  const map = useMap();
  
  useEffect(() => {
    if (stops && stops.length > 0) {
      const bounds = L.latLngBounds(stops.map(s => [s.lat, s.lon]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [stops, map]);
  
  return null;
};


// ============== PROPERTY MEASUREMENT TAB ==============
const MeasureTab = ({ slug }) => <MeasureTabInner slug={slug} />;

const geocodeAddress = async (API, q) => {
  const res = await axios.get(`${API}/geocode-address`, { params: { q } });
  return [res.data.lat, res.data.lon];
};

const MeasureTabInner = ({ slug }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [address, setAddress] = useState("");
  const [mapCenter, setMapCenter] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [areas, setAreas] = useState([{ name: "Area 1", pins: [], color: "#3B82F6" }]);
  const [activeAreaIdx, setActiveAreaIdx] = useState(0);
  const [savedMeasurements, setSavedMeasurements] = useState([]);
  const [customersByAddress, setCustomersByAddress] = useState({});
  const [measuring, setMeasuring] = useState(false);
  const [editingMeasurementId, setEditingMeasurementId] = useState(null);

  // ============== CUBIC YARD CALCULATOR ==============
  // Sits beside the measuring tool so the moment a user finishes measuring an
  // area they can convert it to cubic yards of mulch / soil / gravel in one
  // glance. Beats Yardbook because there is no shape-picker step and no
  // "Calculate" button — square footage auto-flows from the measurement and
  // the result updates live as the user types depth.
  // Formula (industry standard): cubic_yards = (sqft × depth_inches) / 324
  // Bag estimate uses standard 2 cu ft bags: bags = cubic_yards × 13.5
  const [calcArea, setCalcArea] = useState("");
  const [calcDepth, setCalcDepth] = useState("");
  const [calcCollapsed, setCalcCollapsed] = useState(() => {
    try { return localStorage.getItem("zlp_calc_collapsed") === "1"; } catch (e) { return false; }
  });
  const toggleCalcCollapsed = () => {
    setCalcCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("zlp_calc_collapsed", next ? "1" : "0"); } catch (e) {}
      return next;
    });
  };

  const AREA_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

  // Load saved measurements
  useEffect(() => {
    const loadMeasurements = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/tenants/${slug}/measurements`, { headers: { Authorization: `Bearer ${token}` } });
        setSavedMeasurements(res.data);
      } catch {}
    };
    loadMeasurements();
  }, [API, slug]);

  // Load customers and build an address → customer lookup so saved measurements can show the customer name
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/tenants/${slug}/customers`, { headers: { Authorization: `Bearer ${token}` } });
        const map = {};
        (res.data || []).forEach(c => {
          if (c.address) map[c.address.trim().toLowerCase()] = c;
        });
        setCustomersByAddress(map);
      } catch {}
    };
    loadCustomers();
  }, [API, slug]);

  const goToAddress = async () => {
    if (!address.trim()) return;
    try {
      setMapCenter(await geocodeAddress(API, address));
      setMapKey(k => k + 1);
      setMeasuring(true);
    } catch {
      toast.error("Address not found");
    }
  };

  // Shoelace formula for polygon area in sq meters, then convert to sq ft
  const calculateArea = (pins) => {
    if (pins.length < 3) return { areaSqft: 0, perimeterFt: 0 };
    
    // Convert lat/lon to approximate meters using equirectangular projection
    const toMeters = (lat, lon, refLat) => {
      const x = (lon * Math.PI / 180) * 6371000 * Math.cos(refLat * Math.PI / 180);
      const y = (lat * Math.PI / 180) * 6371000;
      return { x, y };
    };
    
    const refLat = pins[0].lat;
    const coords = pins.map(p => toMeters(p.lat, p.lon, refLat));
    
    // Shoelace formula for area
    let area = 0;
    for (let i = 0; i < coords.length; i++) {
      const j = (i + 1) % coords.length;
      area += coords[i].x * coords[j].y;
      area -= coords[j].x * coords[i].y;
    }
    area = Math.abs(area) / 2;
    
    // Perimeter
    let perimeter = 0;
    for (let i = 0; i < coords.length; i++) {
      const j = (i + 1) % coords.length;
      perimeter += Math.sqrt(Math.pow(coords[j].x - coords[i].x, 2) + Math.pow(coords[j].y - coords[i].y, 2));
    }
    
    return {
      areaSqft: Math.round(area * 10.7639),
      perimeterFt: Math.round(perimeter * 3.28084)
    };
  };

  const addPin = (lat, lon) => {
    const updated = [...areas];
    updated[activeAreaIdx].pins.push({ lat, lon });
    setAreas(updated);
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate(15); } catch {}
    }
  };

  // Track the live map center for the mobile aim-reticle precision mode
  const gmapInstanceRef = useRef(null);
  const [aimMode, setAimMode] = useState(true); // Mobile precision mode: reticle in middle, button drops pin
  const dropPinAtReticle = () => {
    const m = gmapInstanceRef.current;
    if (!m) return;
    const c = m.getCenter();
    if (!c) return;
    addPin(c.lat, c.lng);
  };

  const updatePin = (areaIdx, pinIdx, lat, lon) => {
    const updated = [...areas];
    updated[areaIdx].pins[pinIdx] = { lat, lon };
    setAreas(updated);
  };

  const removeLastPin = () => {
    const updated = [...areas];
    updated[activeAreaIdx].pins.pop();
    setAreas(updated);
  };

  const clearArea = () => {
    const updated = [...areas];
    updated[activeAreaIdx].pins = [];
    setAreas(updated);
  };

  const addNewArea = () => {
    const idx = areas.length;
    setAreas([...areas, { name: `Area ${idx + 1}`, pins: [], color: AREA_COLORS[idx % AREA_COLORS.length] }]);
    setActiveAreaIdx(idx);
  };

  const deleteArea = (idx) => {
    if (areas.length <= 1) return;
    const updated = areas.filter((_, i) => i !== idx);
    setAreas(updated);
    setActiveAreaIdx(Math.min(activeAreaIdx, updated.length - 1));
  };

  // Calculate totals
  const areasWithCalc = areas.map(a => ({ ...a, ...calculateArea(a.pins) }));
  const totalSqft = areasWithCalc.reduce((sum, a) => sum + a.areaSqft, 0);
  const totalPerimeter = areasWithCalc.reduce((sum, a) => sum + a.perimeterFt, 0);

  // Auto-feed the calculator's Area field with the SUM of all measured areas.
  // Re-runs any time `totalSqft` changes — i.e. when a pin is moved, an area
  // is added, deleted, or a saved measurement is loaded. Always reflects the
  // current total so a multi-area measurement (front lawn + bed + side strip)
  // calculates cubic yards for the whole job, not just the first area.
  useEffect(() => {
    if (totalSqft > 0) {
      setCalcArea(String(Math.round(totalSqft)));
    }
  }, [totalSqft]);

  // ===== Live calculation =====
  const calcAreaNum = parseFloat(calcArea) || 0;
  const calcDepthNum = parseFloat(calcDepth) || 0;
  const cubicYards = (calcAreaNum > 0 && calcDepthNum > 0)
    ? (calcAreaNum * calcDepthNum) / 324
    : 0;
  const bagCount = cubicYards > 0 ? Math.ceil(cubicYards * 13.5) : 0;

  // Save measurement (create or update)
  const saveMeasurement = async () => {
    try {
      const token = localStorage.getItem("token");
      const payload = {
        address,
        areas: areasWithCalc.map(a => ({ name: a.name, pins: a.pins, area_sqft: a.areaSqft, perimeter_ft: a.perimeterFt, color: a.color })),
        total_sqft: totalSqft,
        total_perimeter_ft: totalPerimeter
      };
      if (editingMeasurementId) {
        await axios.delete(`${API}/tenants/${slug}/measurements/${editingMeasurementId}`, { headers: { Authorization: `Bearer ${token}` } });
      }
      await axios.post(`${API}/tenants/${slug}/measurements`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(editingMeasurementId ? "Measurement updated!" : "Measurement saved!");
      const res = await axios.get(`${API}/tenants/${slug}/measurements`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedMeasurements(res.data);
      resetMeasureState();
    } catch {
      toast.error("Failed to save measurement");
    }
  };

  const resetMeasureState = () => {
    setMeasuring(false);
    setEditingMeasurementId(null);
    setAreas([{ name: "Area 1", pins: [], color: "#3B82F6" }]);
    setActiveAreaIdx(0);
    setAddress("");
    setMapCenter(null);
  };

  // Load a saved measurement for editing
  const loadMeasurement = (m) => {
    setAddress(m.address);
    setEditingMeasurementId(m.id);
    setAreas(m.areas.map(a => ({ name: a.name, pins: a.pins, color: a.color })));
    setActiveAreaIdx(0);
    const firstPin = m.areas?.flatMap(a => a.pins || [])[0];
    geocodeAddress(API, m.address)
      .catch(() => (firstPin ? [firstPin.lat, firstPin.lon] : Promise.reject()))
      .then(center => {
        setMapCenter(center);
        setMapKey(k => k + 1);
        setMeasuring(true);
      }).catch(() => {});
  };

  const deleteMeasurement = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/tenants/${slug}/measurements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedMeasurements(s => s.filter(m => m.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Leaflet overlay component for markers, polygons, and polylines
  const MeasureOverlays = ({ mapCenter: mc, areasData, onPinDragEnd, addressText, onMapReady, onMapClick }) => {
    const map = useMap();

    useEffect(() => {
      if (map && onMapReady) onMapReady(map);
    }, [map, onMapReady]);

    useEffect(() => {
      const handler = (e) => onMapClick(e.latlng.lat, e.latlng.lng);
      map.on('click', handler);
      return () => map.off('click', handler);
    }, [map, onMapClick]);

    useEffect(() => {
      const layers = [];
      layers.push(L.marker(mc, {
        title: addressText,
        interactive: false,
        icon: L.divIcon({
          className: '',
          html: '<svg width="27" height="43" viewBox="0 0 27 43"><path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 43 13.5 43S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#EA4335" stroke="#B31412" stroke-width="1"/><circle cx="13.5" cy="13.5" r="4.5" fill="#B31412"/></svg>',
          iconSize: [27, 43],
          iconAnchor: [13.5, 43]
        })
      }).addTo(map));

      areasData.forEach((area, areaIdx) => {
        const latlngs = area.pins.map(p => [p.lat, p.lon]);
        if (area.pins.length >= 3) {
          layers.push(L.polygon(latlngs, { color: area.color, fillColor: area.color, fillOpacity: 0.25, weight: 2, interactive: false }).addTo(map));
        }
        if (area.pins.length === 2) {
          layers.push(L.polyline(latlngs, { color: area.color, weight: 2, interactive: false }).addTo(map));
        }
        area.pins.forEach((pin, pinIdx) => {
          const marker = L.marker([pin.lat, pin.lon], {
            draggable: true,
            zIndexOffset: 1000,
            icon: L.divIcon({
              className: '',
              html: `<div style="width:24px;height:24px;border-radius:50%;background:${area.color};border:2px solid white;color:white;font:bold 11px sans-serif;display:flex;align-items:center;justify-content:center;box-sizing:border-box">${pinIdx + 1}</div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12]
            })
          }).addTo(map);
          marker.on('dragend', () => {
            const pos = marker.getLatLng();
            onPinDragEnd(areaIdx, pinIdx, pos.lat, pos.lng);
          });
          layers.push(marker);
        });
      });

      return () => layers.forEach(l => l.remove());
    }, [map, mc, areasData, addressText, onPinDragEnd]);

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Address Input */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && goToAddress()}
              placeholder="Enter property address..."
              className="flex-1 px-3 py-2 border rounded-lg text-sm"
              data-testid="measure-address-input"
            />
            <Button onClick={goToAddress} data-testid="measure-go-btn">Go</Button>
          </div>
        </CardContent>
      </Card>

      {/* Map + Measurement Tools */}
      {measuring && mapCenter && (
        <Card>
          <CardContent className="pt-4 pb-4 space-y-3">
            {/* Area tabs */}
            <div className="flex items-center gap-2 flex-wrap">
              {areas.map((area, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveAreaIdx(idx)}
                  className={`px-3 py-1 text-xs rounded-full border flex items-center gap-1 ${
                    activeAreaIdx === idx ? 'ring-2 ring-offset-1' : ''
                  }`}
                  style={{ borderColor: area.color, color: activeAreaIdx === idx ? 'white' : area.color, backgroundColor: activeAreaIdx === idx ? area.color : 'transparent' }}
                >
                  {area.name}
                  {areasWithCalc[idx].areaSqft > 0 && ` (${areasWithCalc[idx].areaSqft.toLocaleString()} sqft)`}
                  {areas.length > 1 && (
                    <span onClick={(e) => { e.stopPropagation(); deleteArea(idx); }} className="ml-1 hover:text-red-500 cursor-pointer">&times;</span>
                  )}
                </button>
              ))}
              <button onClick={addNewArea} className="px-3 py-1 text-xs rounded-full border border-dashed border-gray-400 text-gray-500 hover:border-gray-600">
                + New Area
              </button>
            </div>

            {/* Map */}
            <div className="relative rounded-lg overflow-hidden border" style={{ height: '400px' }}>
                <style>{`
                  .measure-map .leaflet-container { cursor: crosshair !important; background: #1f2937; }
                  .measure-map .leaflet-marker-draggable { cursor: grab !important; }
                  .measure-map .leaflet-control-attribution { font-size: 9px; line-height: 1.2; max-width: 30%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                  .measure-map .leaflet-dragging .leaflet-container { cursor: grabbing !important; }
                `}</style>
                <div className="measure-map" style={{ height: '100%', width: '100%', isolation: 'isolate' }} data-testid="measure-google-map">
                <MapContainer
                  key={mapKey}
                  center={mapCenter}
                  zoom={20}
                  maxZoom={22}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    maxNativeZoom={19}
                    maxZoom={22}
                    attribution="Imagery &copy; Esri, Maxar, Earthstar Geographics"
                  />
                  <MeasureOverlays
                    mapCenter={mapCenter}
                    areasData={areasWithCalc}
                    onPinDragEnd={updatePin}
                    addressText={address}
                    onMapReady={(m) => { gmapInstanceRef.current = m; }}
                    onMapClick={(lat, lng) => {
                      // On mobile we use the reticle + Drop Pin button instead of tap-to-place.
                      const isCoarse = typeof window !== "undefined" && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
                      if (isCoarse && aimMode) return;
                      addPin(lat, lng);
                    }}
                  />
                </MapContainer>
                </div>

                {/* MOBILE PRECISION AIM RETICLE — fixed crosshair in center, map pans under it */}
                {aimMode && (
                  <>
                    {/* Crosshair reticle — only visible on touch devices via media query */}
                    <div className="hidden max-[1023px]:flex pointer-events-none absolute inset-0 items-center justify-center z-10" data-testid="measure-reticle">
                      <div className="relative w-12 h-12">
                        {/* Outer ring */}
                        <div className="absolute inset-0 rounded-full border border-white/90 drop-shadow-[0_0_1px_rgba(0,0,0,0.7)]" />
                        {/* Vertical hairline — split with center gap */}
                        <div className="absolute left-1/2 top-0 w-px h-4 -translate-x-1/2 bg-white drop-shadow-[0_0_1px_rgba(0,0,0,0.8)]" />
                        <div className="absolute left-1/2 bottom-0 w-px h-4 -translate-x-1/2 bg-white drop-shadow-[0_0_1px_rgba(0,0,0,0.8)]" />
                        {/* Horizontal hairline — split with center gap */}
                        <div className="absolute top-1/2 left-0 h-px w-4 -translate-y-1/2 bg-white drop-shadow-[0_0_1px_rgba(0,0,0,0.8)]" />
                        <div className="absolute top-1/2 right-0 h-px w-4 -translate-y-1/2 bg-white drop-shadow-[0_0_1px_rgba(0,0,0,0.8)]" />
                        {/* Center bullseye dot */}
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-blue-500 ring-1 ring-slate-900/80" />
                      </div>
                    </div>
                    {/* Drop Pin button — only on touch devices */}
                    <button
                      type="button"
                      onClick={dropPinAtReticle}
                      data-testid="measure-drop-pin-btn"
                      className="hidden max-[1023px]:flex absolute bottom-3 left-1/2 -translate-x-1/2 z-20 items-center gap-2 whitespace-nowrap px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-lg active:scale-95 transition-transform"
                    >
                      <span className="w-2 h-2 rounded-full bg-blue-300 animate-pulse" />
                      Drop Pin Here
                    </button>
                  </>
                )}

                {/* Aim mode toggle — only on touch devices, top-right corner */}
                <button
                  type="button"
                  onClick={() => setAimMode(v => !v)}
                  data-testid="measure-aim-mode-toggle"
                  className="hidden max-[1023px]:flex absolute top-2 right-2 z-20 items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/95 text-slate-700 text-[11px] font-medium shadow-md border border-slate-200 active:scale-95 transition-transform"
                  title="Toggle precision aim mode"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${aimMode ? 'bg-blue-700' : 'bg-slate-400'}`} />
                  {aimMode ? 'Aim: ON' : 'Aim: OFF'}
                </button>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={removeLastPin} disabled={areas[activeAreaIdx].pins.length === 0}>
                  Undo Pin
                </Button>
                <Button variant="outline" size="sm" onClick={clearArea}>
                  Clear Area
                </Button>
              </div>
              <div className="text-right text-sm space-y-0.5">
                <p className="font-bold text-lg">{totalSqft.toLocaleString()} sq ft</p>
                <p className="text-gray-500">{totalPerimeter.toLocaleString()} ft perimeter</p>
              </div>
            </div>

            {/* Save */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={resetMeasureState}>
                Cancel
              </Button>
              <Button onClick={saveMeasurement} disabled={totalSqft === 0} data-testid="save-measurement-btn">
                {editingMeasurementId ? 'Update Measurement' : 'Save Measurement'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cubic Yard Calculator — converts square footage + depth to cubic yards
          (and approximate 2 cu ft bag count). Always available; auto-fills
          from the live measurement when one is being drawn. */}
      <Card data-testid="cubic-yard-calculator">
        <CardContent className="pt-4 pb-4">
          <button
            type="button"
            onClick={toggleCalcCollapsed}
            className="w-full flex items-center justify-between text-left"
            data-testid="calc-toggle"
          >
            <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
              <span aria-hidden="true">📦</span>
              Mulch / Soil / Gravel Calculator
            </h3>
            <span className="text-slate-500 text-xs">{calcCollapsed ? "Show" : "Hide"}</span>
          </button>

          {!calcCollapsed && (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-slate-500 leading-snug">
                Enter the area and how deep the material needs to go. Result updates as you type.
                {totalSqft > 0 && areas.length > 1 && (
                  <> Area is auto-filled from the sum of all {areas.length} measured areas.</>
                )}
                {totalSqft > 0 && areas.length === 1 && (
                  <> Area is auto-filled from your current measurement.</>
                )}
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-600 font-medium mb-1">Area</label>
                  <div className="relative">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="any"
                      value={calcArea}
                      onChange={(e) => setCalcArea(e.target.value)}
                      placeholder="0"
                      className="w-full px-3 py-2 pr-10 border rounded-lg text-sm"
                      data-testid="calc-area-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">sq ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 font-medium mb-1">Depth</label>
                  <div className="relative">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="any"
                      value={calcDepth}
                      onChange={(e) => setCalcDepth(e.target.value)}
                      placeholder="0"
                      className="w-full px-3 py-2 pr-10 border rounded-lg text-sm"
                      data-testid="calc-depth-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">in</span>
                  </div>
                </div>
              </div>

              <div
                className={`rounded-lg border p-3 transition-colors ${cubicYards > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}
                data-testid="calc-result"
              >
                {cubicYards > 0 ? (
                  <div>
                    <p className="text-2xl font-bold text-slate-900 leading-tight" data-testid="calc-cubic-yards">
                      {cubicYards.toFixed(1)} <span className="text-base font-medium text-slate-700">cubic yards</span>
                    </p>
                    <p className="text-xs text-slate-600 mt-1" data-testid="calc-bag-count">
                      ≈ {bagCount.toLocaleString()} bags <span className="text-slate-400">(2 cu ft)</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Enter an area and depth above to see the result.</p>
                )}
              </div>

              {(calcArea || calcDepth) && (
                <button
                  type="button"
                  onClick={() => { setCalcArea(""); setCalcDepth(""); }}
                  className="text-xs text-slate-500 hover:text-slate-900 underline"
                  data-testid="calc-reset"
                >
                  Reset
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Saved Measurements */}
      {savedMeasurements.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-700 text-sm">Saved Measurements</h3>
          {savedMeasurements.map((m) => {
            const matchedCustomer = customersByAddress[(m.address || "").trim().toLowerCase()];
            return (
              <Card key={m.id} className="cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all" onClick={() => loadMeasurement(m)}>
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      {matchedCustomer?.name && (
                        <p className="font-semibold text-sm text-slate-900 truncate">{matchedCustomer.name}</p>
                      )}
                      <p className={`text-sm truncate ${matchedCustomer?.name ? 'text-gray-600' : 'font-medium text-gray-900'}`}>{m.address}</p>
                      <p className="text-xs text-gray-500">
                        {m.total_sqft?.toLocaleString()} sq ft &middot; {m.areas?.length || 0} area(s) &middot; {new Date(m.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteMeasurement(m.id); }}>
                      🗑️
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============== ROUTES SECTION (Phase 1) ==============
const RoutesSection = ({ scheduledJobs = [], slug, onRefresh, initialTab, searchParams }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  // Initialize tab from URL so a refresh in Measure/Crews/Dispatch stays put.
  const [activeTab, _setActiveTabRaw] = useState(() => searchParams?.get?.("tab") || initialTab || "routes");

  // Wrapper: every tab click also pushes ?tab=... into the URL via
  // replaceState, so window.location.reload() (or any deploy refresh) lands
  // the user back on the same sub-tab inside Routes.
  const setActiveTab = useCallback((next) => {
    _setActiveTabRaw((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get("tab") !== value) {
          params.set("tab", value);
          const qs = params.toString();
          const url = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
          window.history.replaceState(window.history.state, "", url);
        }
      } catch (_) { /* ignore */ }
      return value;
    });
  }, []);

  // Keep state in sync when parent updates ?tab= via navigate() (deep links).
  useEffect(() => {
    const tab = searchParams?.get?.("tab");
    if (tab) _setActiveTabRaw(tab);
  }, [searchParams]);
  const [routes, setRoutes] = useState([]);
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCrewModal, setShowCrewModal] = useState(false);
  const [routeName, setRouteName] = useState("");
  const [expandedRoute, setExpandedRoute] = useState(null);
  const [newCrewName, setNewCrewName] = useState("");
  const [newCrewColor, setNewCrewColor] = useState("#3B82F6");
  const [editingCrew, setEditingCrew] = useState(null);
  const [selectedCrewId, setSelectedCrewId] = useState("");
  const [selectedMapsApp, setSelectedMapsApp] = useState("apple");
  
  // New state for manual route creation
  const [addressInput, setAddressInput] = useState("");
  const [geocodedStops, setGeocodedStops] = useState([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  // Get current location on modal open
  const getCurrentLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            address: "Current Location",
            isCurrentLocation: true
          });
          setLocationLoading(false);
          toast.success("Location detected!");
        },
        (error) => {
          console.error("Geolocation error:", error);
          toast.error("Could not get your location. Please enable location services.");
          setLocationLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      toast.error("Geolocation is not supported by your browser");
      setLocationLoading(false);
    }
  };

  // Fetch routes
  const fetchRoutes = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/tenants/${slug}/routes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate }
      });
      setRoutes(response.data);
    } catch (error) {
      console.error("Error fetching routes:", error);
    } finally {
      setLoading(false);
    }
  }, [API, slug, selectedDate]);

  // Fetch crews
  const fetchCrews = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/tenants/${slug}/crews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrews(response.data);
    } catch (error) {
      console.error("Error fetching crews:", error);
    }
  }, [API, slug]);

  useEffect(() => {
    fetchRoutes();
    fetchCrews();
  }, [fetchRoutes, fetchCrews]);

  // Create crew
  const handleCreateCrew = async () => {
    if (!newCrewName.trim()) {
      toast.error("Please enter a crew name");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/tenants/${slug}/crews`, {
        name: newCrewName,
        color: newCrewColor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Crew created!");
      setShowCrewModal(false);
      setNewCrewName("");
      setNewCrewColor("#3B82F6");
      fetchCrews();
    } catch (error) {
      toast.error("Failed to create crew");
    }
  };

  // Update crew
  const handleUpdateCrew = async () => {
    if (!editingCrew || !newCrewName.trim()) return;
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/tenants/${slug}/crews/${editingCrew.id}`, {
        name: newCrewName,
        color: newCrewColor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Crew updated!");
      setEditingCrew(null);
      setNewCrewName("");
      setNewCrewColor("#3B82F6");
      fetchCrews();
    } catch (error) {
      toast.error("Failed to update crew");
    }
  };

  // Delete crew
  const handleDeleteCrew = async (crewId) => {
    if (!window.confirm("Delete this crew?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/tenants/${slug}/crews/${crewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Crew deleted");
      fetchCrews();
    } catch (error) {
      toast.error("Failed to delete crew");
    }
  };

  // Assign crew to route
  const handleAssignCrew = async (routeId, crewId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/tenants/${slug}/routes/${routeId}`, {
        crew_id: crewId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(crewId ? "Crew assigned!" : "Crew unassigned");
      fetchRoutes();
    } catch (error) {
      toast.error("Failed to assign crew");
    }
  };

  // Get crew by ID
  const getCrewById = (crewId) => crews.find(c => c.id === crewId);

  // Geocode addresses
  const handleGeocodeAddresses = async () => {
    if (!currentLocation) {
      toast.error("Please wait for your location to be detected");
      return;
    }
    
    const addresses = addressInput.split('\n').filter(a => a.trim());
    if (addresses.length === 0) {
      toast.error("Please enter at least one address");
      return;
    }

    setIsGeocoding(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/geocode`, {
        addresses: addresses
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const results = response.data.results;
      const validStops = results.filter(r => r.lat && r.lon);
      const failedAddresses = results.filter(r => r.error);
      
      if (validStops.length === 0) {
        toast.error("Could not find any of those addresses. Please check the format.");
        return;
      }
      
      if (failedAddresses.length > 0) {
        const failedNames = failedAddresses.map(f => f.address).join(', ');
        toast.error(`Could not find: ${failedNames}`, { duration: 5000 });
      }
      
      // Optimize route starting from current location
      const optimized = optimizeStopsFromLocation(currentLocation, validStops);
      setGeocodedStops(optimized);
      setMapReady(true);
      toast.success(`Route optimized with ${validStops.length} stops!`);
    } catch (error) {
      console.error("Geocoding error:", error);
      toast.error("Failed to geocode addresses");
    } finally {
      setIsGeocoding(false);
    }
  };

  // Optimize route starting from current location using nearest-neighbor
  const optimizeStopsFromLocation = (startLocation, stops) => {
    if (stops.length === 0) return [];
    if (stops.length === 1) return [{ ...stops[0], order: 1 }];
    
    // Start from current location, find nearest stops
    const optimized = [];
    const remaining = [...stops];
    let current = startLocation;
    
    while (remaining.length > 0) {
      let nearestIdx = 0;
      let nearestDist = Infinity;
      
      remaining.forEach((stop, idx) => {
        const dist = Math.sqrt(
          Math.pow(stop.lat - current.lat, 2) + 
          Math.pow(stop.lon - current.lon, 2)
        );
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestIdx = idx;
        }
      });
      
      const nearest = remaining[nearestIdx];
      optimized.push({ ...nearest, order: optimized.length + 1 });
      current = nearest;
      remaining.splice(nearestIdx, 1);
    }
    
    return optimized;
  };

  // Create route and export to Google Maps
  const handleCreateRoute = async () => {
    if (!routeName.trim()) {
      toast.error("Please enter a route name");
      return;
    }
    if (geocodedStops.length === 0) {
      toast.error("Please add and optimize addresses first");
      return;
    }
    if (!currentLocation) {
      toast.error("Current location not available");
      return;
    }

    // Build URLs for both map options
    const stopsForUrl = geocodedStops.map(s => s.address.replace(/ /g, '+'));
    const appleMapsUrl = `https://maps.apple.com/?saddr=Current+Location&daddr=${stopsForUrl.join('+to:')}&dirflg=d`;
    const googleMapsUrl = `https://www.google.com/maps/dir/My+Location/${stopsForUrl.join('/')}`;
    
    // Open the selected maps app FIRST before async to avoid popup blocker
    const mapsWindow = window.open(selectedMapsApp === 'apple' ? appleMapsUrl : googleMapsUrl, '_blank');

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/tenants/${slug}/routes`, {
        name: routeName,
        date: selectedDate,
        stops: geocodedStops.map(s => ({
          address: s.address,
          lat: s.lat,
          lon: s.lon,
          display_name: s.display_name
        })),
        crew_id: selectedCrewId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Route created!");
      
      // Reset modal
      resetCreateModal();
      fetchRoutes();
    } catch (error) {
      console.error("Error creating route:", error);
      toast.error("Failed to create route");
      // Close the maps window if route creation failed
      if (mapsWindow) mapsWindow.close();
    }
  };

  // Reset create modal
  const resetCreateModal = () => {
    setShowCreateModal(false);
    setAddressInput("");
    setGeocodedStops([]);
    setMapReady(false);
    setCurrentLocation(null);
    setRouteName("");
    setSelectedCrewId("");
  };

  // Delete route
  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm("Delete this route?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/tenants/${slug}/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Route deleted");
      fetchRoutes();
    } catch (error) {
      toast.error("Failed to delete route");
    }
  };

  // Open route in Google Maps
  const openInGoogleMaps = (routeId, existingStops = null) => {
    if (existingStops && existingStops.length > 0) {
      const sorted = existingStops
        .filter(s => s.customer_address)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (sorted.length > 0) {
        const stops = sorted.map(s => s.customer_address.replace(/ /g, '+'));
        window.open(`https://www.google.com/maps/dir/My+Location/${stops.join('/')}`, '_blank');
        return;
      }
    }
    toast.error("No stops found for this route");
  };

  // Open route in Apple Maps
  const openInAppleMaps = (routeId, existingStops = null) => {
    if (existingStops && existingStops.length > 0) {
      const sorted = existingStops
        .filter(s => s.customer_address)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (sorted.length > 0) {
        const stops = sorted.map(s => s.customer_address.replace(/ /g, '+'));
        const daddr = stops.join('+to:');
        window.open(`https://maps.apple.com/?saddr=Current+Location&daddr=${daddr}&dirflg=d`, '_blank');
        return;
      }
    }
    toast.error("No stops found for this route");
  };

  const crewColors = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-gray-900">🚗 Routes & Crews</h1>
        
        {/* Tab Switcher */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab("routes")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "routes" 
                ? "border-blue-600 text-blue-600" 
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid="routes-tab"
          >
            Routes
          </button>
          <button
            onClick={() => setActiveTab("crews")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "crews" 
                ? "border-blue-600 text-blue-600" 
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid="crews-tab"
          >
            Crews ({crews.length})
          </button>
          <button
            onClick={() => setActiveTab("dispatch")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "dispatch" 
                ? "border-blue-600 text-blue-600" 
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid="dispatch-tab"
          >
            Dispatch
          </button>
          <button
            onClick={() => setActiveTab("measure")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "measure" 
                ? "border-blue-600 text-blue-600" 
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid="measure-tab"
          >
            Measure
          </button>
        </div>
      </div>

      {/* Routes Tab */}
      {activeTab === "routes" && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3">
            <div 
              className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => document.getElementById('route-date-input')?.showPicker?.()}
            >
              <span className="text-blue-600 text-lg">📅</span>
              <input
                id="route-date-input"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-transparent border-none outline-none text-blue-700 font-medium flex-1 cursor-pointer"
                data-testid="route-date-picker"
              />
            </div>
            <Button 
              onClick={() => setShowCreateModal(true)}
              size="sm"
              className="w-full"
              data-testid="create-route-btn"
            >
              + Create Route
            </Button>
          </div>

          {/* Routes List */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading routes...</div>
          ) : routes.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500 mb-4">No routes for {selectedDate}</p>
              <p className="text-sm text-gray-400">Click "Create Route" to add addresses and optimize your route.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {routes.map((route) => {
                const assignedCrew = getCrewById(route.crew_id);
                return (
                <Card key={route.id} className="overflow-visible" data-testid={`route-card-${route.id}`}>
                  <div 
                    className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                    onClick={() => setExpandedRoute(expandedRoute === route.id ? null : route.id)}
                  >
                    <div className="flex items-center gap-3">
                      {assignedCrew ? (
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: assignedCrew.color }}
                          title={assignedCrew.name}
                        >
                          {assignedCrew.name.charAt(0).toUpperCase()}
                        </div>
                      ) : (
                        <span className="text-2xl">🚗</span>
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900">{route.name}</h3>
                        <p className="text-sm text-gray-500">
                          {route.stops?.length || 0} stops
                          {assignedCrew && <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: assignedCrew.color + '20', color: assignedCrew.color }}>• {assignedCrew.name}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const menu = e.currentTarget.nextElementSibling;
                            menu.classList.toggle('hidden');
                          }}
                          data-testid={`nav-menu-${route.id}`}
                        >
                          🗺️ Navigate
                        </Button>
                        <div className="hidden absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 w-44">
                          <button className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-t-lg flex items-center gap-2" 
                            onClick={() => { openInAppleMaps(route.id, route.stops); }}>
                            🍎 Apple Maps
                          </button>
                          <button className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
                            onClick={() => { openInGoogleMaps(route.id, route.stops); }}>
                            🗺️ Google Maps
                          </button>
                          <button className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-b-lg flex items-center gap-2 border-t"
                            onClick={() => { 
                              navigator.clipboard.writeText(`${window.location.origin}/route/${route.id}`);
                              toast.success("Share link copied!");
                            }}>
                            📋 Copy Share Link
                          </button>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteRoute(route.id); }}
                        data-testid={`delete-route-${route.id}`}
                      >
                        🗑️
                      </Button>
                      <span className="text-gray-400">{expandedRoute === route.id ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  
                  {expandedRoute === route.id && (
                    <div className="p-4 border-t space-y-4">
                      {/* Crew Assignment */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Assign Crew:</label>
                        <select
                          value={route.crew_id || ""}
                          onChange={(e) => handleAssignCrew(route.id, e.target.value)}
                          className="w-full p-2 border rounded-lg text-sm"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`assign-crew-${route.id}`}
                        >
                          <option value="">-- No Crew Assigned --</option>
                          {crews.map((crew) => (
                            <option key={crew.id} value={crew.id}>{crew.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Stops */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Stop Order:</h4>
                        <div className="space-y-2">
                          {route.stops?.map((stop, idx) => (
                            <div 
                              key={stop.lead_id} 
                              className="flex items-center gap-3 p-3 bg-white border rounded-lg"
                              data-testid={`route-stop-${idx}`}
                            >
                              <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                                {idx + 1}
                              </span>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{stop.customer_name}</p>
                                <p className="text-sm text-gray-500">{stop.customer_address || "No address"}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );})}
            </div>
          )}
        </div>
      )}

      {/* Crews Tab */}
      {activeTab === "crews" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => setShowCrewModal(true)}
              size="sm"
              data-testid="create-crew-btn"
            >
              + Add Crew
            </Button>
          </div>

          {crews.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500 mb-2">No crews yet</p>
              <p className="text-sm text-gray-400">Create crews to assign to your routes.</p>
            </Card>
          ) : (
            <div className="grid gap-3">
              {crews.map((crew) => (
                <Card key={crew.id} className="p-4" data-testid={`crew-card-${crew.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: crew.color || "#3B82F6" }}
                      >
                        {crew.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{crew.name}</h3>
                        <p className="text-xs text-gray-500">Created {new Date(crew.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setEditingCrew(crew);
                          setNewCrewName(crew.name);
                          setNewCrewColor(crew.color || "#3B82F6");
                        }}
                        data-testid={`edit-crew-${crew.id}`}
                      >
                        ✏️
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteCrew(crew.id)}
                        data-testid={`delete-crew-${crew.id}`}
                      >
                        🗑️
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dispatch View Tab */}
      {activeTab === "dispatch" && (
        <div className="space-y-4">
          <div 
            className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-blue-100 transition-colors"
            onClick={() => document.getElementById('dispatch-date-input')?.showPicker?.()}
          >
            <span className="text-blue-600 text-lg">📅</span>
            <input
              id="dispatch-date-input"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent border-none outline-none text-blue-700 font-medium flex-1 cursor-pointer"
              data-testid="dispatch-date-picker"
            />
          </div>

          <h3 className="text-lg font-semibold text-gray-900">Dispatch Overview - {selectedDate}</h3>
          
          {routes.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500">No routes scheduled for this date</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {crews.map((crew) => {
                const crewRoutes = routes.filter(r => r.crew_id === crew.id);
                return (
                  <Card key={crew.id} className="overflow-hidden" data-testid={`dispatch-crew-${crew.id}`}>
                    <div className="p-3 flex items-center gap-3" style={{ backgroundColor: crew.color + '15' }}>
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: crew.color }}
                      >
                        {crew.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{crew.name}</h4>
                        <p className="text-sm text-gray-500">{crewRoutes.length} route(s) assigned</p>
                      </div>
                    </div>
                    {crewRoutes.length > 0 ? (
                      <div className="p-3 space-y-2">
                        {crewRoutes.map((route) => (
                          <div key={route.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-sm">{route.name}</p>
                              <p className="text-xs text-gray-500">{route.stops?.length || 0} stops</p>
                            </div>
                            <div className="relative">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  const menu = e.currentTarget.nextElementSibling;
                                  menu.classList.toggle('hidden');
                                }}
                                className="text-xs"
                              >
                                🗺️
                              </Button>
                              <div className="hidden absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 w-40">
                                <button className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 rounded-t-lg" 
                                  onClick={() => openInAppleMaps(route.id, route.stops)}>
                                  🍎 Apple Maps
                                </button>
                                <button className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 border-t"
                                  onClick={() => openInGoogleMaps(route.id, route.stops)}>
                                  🗺️ Google Maps
                                </button>
                                <button className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 rounded-b-lg border-t"
                                  onClick={() => { 
                                    navigator.clipboard.writeText(`${window.location.origin}/route/${route.id}`);
                                    toast.success("Share link copied!");
                                  }}>
                                  📋 Share Link
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 text-sm text-gray-400 text-center">No routes assigned</div>
                    )}
                  </Card>
                );
              })}
              
              {/* Unassigned Routes */}
              {(() => {
                const unassignedRoutes = routes.filter(r => !r.crew_id);
                if (unassignedRoutes.length === 0) return null;
                return (
                  <Card className="overflow-hidden border-dashed" data-testid="dispatch-unassigned">
                    <div className="p-3 bg-gray-100 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-bold">
                        ?
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-700">Unassigned</h4>
                        <p className="text-sm text-gray-500">{unassignedRoutes.length} route(s)</p>
                      </div>
                    </div>
                    <div className="p-3 space-y-2">
                      {unassignedRoutes.map((route) => (
                        <div key={route.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{route.name}</p>
                            <p className="text-xs text-gray-500">{route.stops?.length || 0} stops</p>
                          </div>
                          <select
                            value=""
                            onChange={(e) => handleAssignCrew(route.id, e.target.value)}
                            className="text-xs p-1 border rounded"
                            data-testid={`quick-assign-${route.id}`}
                          >
                            <option value="">Assign...</option>
                            {crews.map((crew) => (
                              <option key={crew.id} value={crew.id}>{crew.name}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Measure Tab */}
      {activeTab === "measure" && (
        <MeasureTab slug={slug} />
      )}

      {/* Create Route Modal - EZRoute Style */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) resetCreateModal(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Create Route</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {!mapReady ? (
              /* Step 1: Enter Addresses */
              <div className="space-y-4">
                {/* Current Location */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-700 text-lg">📍</span>
                      <div>
                        <p className="font-medium text-slate-900">Starting Point</p>
                        {currentLocation ? (
                          <p className="text-xs text-blue-700">Location detected ✓</p>
                        ) : (
                          <p className="text-xs text-gray-500">Your current location will be the start</p>
                        )}
                      </div>
                    </div>
                    {!currentLocation && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={getCurrentLocation}
                        disabled={locationLoading}
                      >
                        {locationLoading ? "Detecting..." : "Get Location"}
                      </Button>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label>Route Name</Label>
                  <Input
                    value={routeName}
                    onChange={(e) => setRouteName(e.target.value)}
                    placeholder="e.g., Morning Route, North Side"
                    data-testid="route-name-input"
                  />
                </div>
                
                <div>
                  <Label>Assign to Crew (Optional)</Label>
                  <select
                    value={selectedCrewId}
                    onChange={(e) => setSelectedCrewId(e.target.value)}
                    className="w-full p-2 border rounded-lg text-sm mt-1"
                    data-testid="route-crew-select"
                  >
                    <option value="">-- No Crew --</option>
                    {crews.map((crew) => (
                      <option key={crew.id} value={crew.id}>{crew.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label>Enter Stop Addresses (one per line)</Label>
                  <Textarea
                    value={addressInput}
                    onChange={(e) => setAddressInput(e.target.value)}
                    placeholder={"123 Main St, City, State\n456 Oak Ave, City, State\n789 Pine Rd, City, State"}
                    rows={8}
                    className="mt-1 font-mono text-sm"
                    data-testid="address-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste addresses from Excel or CRM. Each address on a new line.
                  </p>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={resetCreateModal}>Cancel</Button>
                  <Button 
                    onClick={handleGeocodeAddresses} 
                    disabled={isGeocoding || !addressInput.trim() || !currentLocation}
                    data-testid="optimize-route-btn"
                  >
                    {isGeocoding ? "Mapping addresses..." : "Optimize Route →"}
                  </Button>
                </div>
              </div>
            ) : (
              /* Step 2: Map View with Optimized Route */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{routeName || "New Route"}</h3>
                    <p className="text-sm text-gray-500">📍 Start → {geocodedStops.length} stops • Optimized</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setMapReady(false)}>
                    ← Edit Addresses
                  </Button>
                </div>
                
                {/* Map */}
                <div className="h-[350px] rounded-lg overflow-hidden border">
                  <MapContainer
                    center={[currentLocation?.lat || geocodedStops[0]?.lat || 39.8283, currentLocation?.lon || geocodedStops[0]?.lon || -98.5795]}
                    zoom={12}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      attribution="Esri"
                    />
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
                      attribution="CartoDB"
                    />
                    
                    {/* Route line - from current location through all stops */}
                    <Polyline
                      positions={[
                        [currentLocation.lat, currentLocation.lon],
                        ...geocodedStops.map(s => [s.lat, s.lon])
                      ]}
                      color="#3B82F6"
                      weight={3}
                      opacity={0.8}
                    />
                    
                    {/* Current Location Marker - Start */}
                    <Marker
                      position={[currentLocation.lat, currentLocation.lon]}
                      icon={L.divIcon({
                        className: 'custom-marker',
                        html: `<div style="
                          background: #10B981;
                          color: white;
                          width: 32px;
                          height: 32px;
                          border-radius: 50%;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          font-weight: bold;
                          font-size: 14px;
                          border: 3px solid white;
                          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                        ">📍</div>`,
                        iconSize: [32, 32],
                        iconAnchor: [16, 16]
                      })}
                    >
                      <Popup>
                        <strong>Start - Your Location</strong>
                      </Popup>
                    </Marker>
                    
                    {/* Stop Markers */}
                    {geocodedStops.map((stop, idx) => (
                      <Marker
                        key={idx}
                        position={[stop.lat, stop.lon]}
                        icon={L.divIcon({
                          className: 'custom-marker',
                          html: `<div style="
                            background: ${idx === geocodedStops.length - 1 ? '#EF4444' : '#3B82F6'};
                            color: white;
                            width: 28px;
                            height: 28px;
                            border-radius: 50%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: bold;
                            font-size: 12px;
                            border: 2px solid white;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                          ">${idx + 1}</div>`,
                          iconSize: [28, 28],
                          iconAnchor: [14, 14]
                        })}
                      >
                        <Popup>
                          <strong>Stop {idx + 1}{idx === geocodedStops.length - 1 ? ' (Final)' : ''}</strong><br />
                          {stop.address}
                        </Popup>
                      </Marker>
                    ))}
                    
                    <MapBoundsUpdater stops={[currentLocation, ...geocodedStops]} />
                  </MapContainer>
                </div>
                
                {/* Stop List */}
                <div className="max-h-[150px] overflow-y-auto space-y-1">
                  <div className="flex items-center gap-2 p-2 bg-blue-50 rounded text-sm border border-blue-200">
                    <span className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs">📍</span>
                    <span className="flex-1 font-medium text-slate-900">Start - Your Location</span>
                  </div>
                  {geocodedStops.map((stop, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm"
                    >
                      <span 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ 
                          backgroundColor: idx === geocodedStops.length - 1 ? '#EF4444' : '#3B82F6' 
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span className="flex-1 truncate">{stop.address}</span>
                      {idx === geocodedStops.length - 1 && <span className="text-xs text-red-500 font-medium">Final</span>}
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Navigate with:</span>
                    <button
                      onClick={() => setSelectedMapsApp('apple')}
                      className={`px-3 py-1 text-sm rounded-full border ${selectedMapsApp === 'apple' ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-300'}`}
                    >
                      🍎 Apple Maps
                    </button>
                    <button
                      onClick={() => setSelectedMapsApp('google')}
                      className={`px-3 py-1 text-sm rounded-full border ${selectedMapsApp === 'google' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    >
                      🗺️ Google Maps
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={resetCreateModal}>Cancel</Button>
                  <Button onClick={handleCreateRoute} data-testid="create-export-btn">
                    Create & Start Navigation
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Crew Modal */}
      <Dialog open={showCrewModal || editingCrew !== null} onOpenChange={(open) => { 
        if (!open) { 
          setShowCrewModal(false); 
          setEditingCrew(null); 
          setNewCrewName(""); 
          setNewCrewColor("#3B82F6"); 
        } 
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingCrew ? "Edit Crew" : "Add New Crew"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Crew Name</Label>
              <Input
                value={newCrewName}
                onChange={(e) => setNewCrewName(e.target.value)}
                placeholder="e.g., Team Alpha, Morning Crew"
                data-testid="crew-name-input"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {crewColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewCrewColor(color)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      newCrewColor === color ? "border-gray-900 scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: color }}
                    data-testid={`crew-color-${color}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowCrewModal(false); setEditingCrew(null); }}>
                Cancel
              </Button>
              <Button onClick={editingCrew ? handleUpdateCrew : handleCreateCrew} disabled={!newCrewName.trim()}>
                {editingCrew ? "Save" : "Add Crew"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== CALENDAR SECTION ==============
const CalendarSection = ({ unscheduledJobs = [], scheduledJobs = [], onRefresh, slug, business, services = [] }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [viewDate, setViewDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [showQueue, setShowQueue] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [jobInstances, setJobInstances] = useState([]);
  const [recurringJobs, setRecurringJobs] = useState([]);
  const [crews, setCrews] = useState([]);
  const [schedulingJob, setSchedulingJob] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [schedulingLoading, setSchedulingLoading] = useState(false);
  const [dragJob, setDragJob] = useState(null);
  const [chemLogPrefill, setChemLogPrefill] = useState(null);
  const [knownCustomers, setKnownCustomers] = useState([]);

  // Fetch existing customers for autocomplete on the job form
  useEffect(() => {
    const fetchKnownCustomers = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/tenants/${slug}/customers`, { headers: { Authorization: `Bearer ${token}` } });
        setKnownCustomers(res.data || []);
      } catch {}
    };
    fetchKnownCustomers();
  }, [API, slug]);

  const DEFAULT_SEASONAL_CONFIG = { program_name: null, steps: [] };
  const [jobForm, setJobForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", address: "", service_description: "", frequency: "weekly", days_of_week: [], monthly_week: 1, monthly_day: 1, start_date: "", end_date: "", time_slot: "", notes: "", isRecurring: false, crew_id: "", _editId: null, _editInstanceId: null, _linkedCustomerEmail: null, active_months: null, seasonal_config: { program_name: null, steps: [] } });

  const SEASON_OPTIONS = [
    { value: "early_spring", label: "Early Spring (Mar–Apr)" },
    { value: "late_spring", label: "Late Spring (May–Jun)" },
    { value: "early_summer", label: "Early Summer (Jun–Jul)" },
    { value: "late_summer", label: "Late Summer (Aug–Sep)" },
    { value: "fall", label: "Fall (Oct–Nov)" },
    { value: "custom", label: "Custom Date Range" },
  ];

  const DAY_NAMES_SHORT = ["S", "M", "T", "W", "T", "F", "S"];
  const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const today = new Date(); today.setHours(0,0,0,0);

  const fetchCalendarData = useCallback(async () => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [rjRes, iiRes, crewRes] = await Promise.all([
        axios.get(`${API}/tenants/${slug}/recurring-jobs`, { headers }),
        axios.get(`${API}/tenants/${slug}/job-instances`, { headers }),
        axios.get(`${API}/tenants/${slug}/crews`, { headers }).catch(() => ({ data: [] }))
      ]);
      setRecurringJobs(rjRes.data || []);
      setJobInstances(iiRes.data || []);
      setCrews(crewRes.data || []);
    } catch (err) {
      console.error("Calendar fetch failed:", err);
    }
  }, [API, slug]);

  useEffect(() => { fetchCalendarData(); }, [fetchCalendarData]);

  const getMonthDays = () => {
    const y = viewDate.getFullYear(), m = viewDate.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const days = [];
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    for (let i = 1; i <= last.getDate(); i++) days.push(new Date(y, m, i));
    return days;
  };

  const getWeekDays = () => {
    const d = new Date(viewDate);
    const start = new Date(d); start.setDate(d.getDate() - d.getDay());
    return Array.from({ length: 7 }, (_, i) => { const dd = new Date(start); dd.setDate(start.getDate() + i); return dd; });
  };

  const calDays = view === "month" ? getMonthDays() : getWeekDays();

  const getCrewColor = (crewId) => {
    if (!crewId) return null;
    const crew = crews.find(c => c.id === crewId);
    return crew ? crew.color : null;
  };

  const getJobsForDate = (date) => {
    if (!date) return [];
    const ds = date.toISOString().split('T')[0];
    const leadJobs = scheduledJobs.filter(j => j.lead?.scheduled_date === ds).map(j => ({
      type: "lead", id: j.lead?.id, name: j.lead?.customer_name, service: j.lead?.pricing_snapshot?.map(p => p.label).join(", ") || j.lead?.service_type || "Service",
      time: j.lead?.scheduled_time, status: "scheduled", crewId: null, raw: j, address: j.lead?.customer_address
    }));
    const recurInstances = jobInstances.filter(i => i.date === ds).map(i => ({
      type: "recurring", id: i.id, name: i.customer_name, service: i.service_description,
      time: i.time_slot, status: i.status, crewId: i.crew_id, recurringId: i.recurring_job_id, raw: i, address: i.address
    }));
    return [...leadJobs, ...recurInstances];
  };

  const statusColor = (s) => s === "completed" ? "bg-blue-600" : s === "cancelled" ? "bg-gray-400" : "bg-blue-500";
  const statusBorder = (s, crewId) => {
    const cc = getCrewColor(crewId);
    if (cc) return { borderLeft: `3px solid ${cc}` };
    return {};
  };

  const handleScheduleFromQueue = async () => {
    if (!scheduleDate || !schedulingJob) return;
    setSchedulingLoading(true);
    try {
      await api.scheduleJob(schedulingJob.lead.id, scheduleDate, scheduleTime);
      toast.success("Job scheduled!");
      setSchedulingJob(null);
      if (onRefresh) await onRefresh();
      fetchCalendarData();
    } catch { toast.error("Failed to schedule"); }
    finally { setSchedulingLoading(false); }
  };

  const handleCreateJob = async () => {
    if (!jobForm.customer_name || !jobForm.service_description || !jobForm.start_date) {
      toast.error("Fill in customer, service, and start date"); return;
    }
    setSchedulingLoading(true);
    try {
      const token = localStorage.getItem("token");
      const isEdit = !!jobForm._editId;
      const isInstanceEdit = !!jobForm._editInstanceId;
      
      if (isInstanceEdit) {
        // Update single instance (date, time, notes, crew)
        await axios.patch(`${API}/tenants/${slug}/job-instances/${jobForm._editInstanceId}`, {
          date: jobForm.start_date,
          time_slot: jobForm.time_slot,
          notes: jobForm.notes,
          crew_id: jobForm.crew_id || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Job updated!");
      } else if (isEdit) {
        // Same silent-failure guard as the create path — clearing all
        // weekday checkboxes during an edit would wipe the schedule and
        // produce zero new instances.
        let editDaysOfWeek = jobForm.days_of_week || [];
        if (["weekly", "biweekly"].includes(jobForm.frequency) && editDaysOfWeek.length === 0 && jobForm.start_date) {
          const startDow = new Date(jobForm.start_date + 'T12:00:00').getDay();
          editDaysOfWeek = [startDow];
          toast.message(`No weekday selected — auto-using ${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][startDow]}.`);
        }
        // Update existing recurring job
        await axios.patch(`${API}/tenants/${slug}/recurring-jobs/${jobForm._editId}`, {
          service_description: jobForm.service_description,
          frequency: jobForm.frequency,
          days_of_week: editDaysOfWeek,
          monthly_week: jobForm.monthly_week,
          monthly_day: jobForm.monthly_day,
          end_date: jobForm.end_date || null,
          time_slot: jobForm.time_slot,
          notes: jobForm.notes,
          crew_id: jobForm.crew_id || null,
          active: true,
          seasonal_config: jobForm.frequency === "seasonal" ? jobForm.seasonal_config : null,
          active_months: (jobForm.active_months && jobForm.active_months.length > 0 && jobForm.active_months.length < 12) ? jobForm.active_months : null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Job updated!");
      } else if (jobForm.isRecurring) {
        // Silent-failure safety net: if user picks Weekly/Biweekly and forgets
        // to tick any day-of-week, the backend would save the recurring job
        // and generate ZERO instances — toast says success, calendar stays
        // empty. Default to the start-date's day-of-week so the very first
        // visit is guaranteed to land on the calendar.
        let daysOfWeek = jobForm.days_of_week || [];
        if (["weekly", "biweekly"].includes(jobForm.frequency) && daysOfWeek.length === 0 && jobForm.start_date) {
          const startDow = new Date(jobForm.start_date + 'T12:00:00').getDay();
          daysOfWeek = [startDow];
          toast.message(`No weekday selected — auto-using ${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][startDow]} from the start date.`);
        }
        const payload = {
          customer_name: jobForm.customer_name, customer_email: jobForm.customer_email || null, customer_phone: jobForm.customer_phone || null, address: jobForm.address, service_description: jobForm.service_description,
          frequency: jobForm.frequency, days_of_week: daysOfWeek, monthly_week: jobForm.monthly_week, monthly_day: jobForm.monthly_day,
          start_date: jobForm.start_date, end_date: jobForm.end_date || null, time_slot: jobForm.time_slot, notes: jobForm.notes, crew_id: jobForm.crew_id || null,
        };
        if (jobForm.frequency === "seasonal") {
          payload.seasonal_config = jobForm.seasonal_config;
        }
        if (jobForm.active_months && jobForm.active_months.length > 0 && jobForm.active_months.length < 12) {
          payload.active_months = jobForm.active_months;
        }
        await axios.post(`${API}/tenants/${slug}/recurring-jobs`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Recurring job created!");
      } else {
        await axios.post(`${API}/tenants/${slug}/recurring-jobs`, {
          customer_name: jobForm.customer_name, customer_email: jobForm.customer_email || null, customer_phone: jobForm.customer_phone || null, address: jobForm.address, service_description: jobForm.service_description,
          frequency: "weekly", days_of_week: [new Date(jobForm.start_date + 'T12:00:00').getDay()],
          start_date: jobForm.start_date, end_date: jobForm.start_date, time_slot: jobForm.time_slot, notes: jobForm.notes, crew_id: jobForm.crew_id || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Job scheduled!");
      }
      setShowJobForm(false);
      setJobForm({ customer_name: "", customer_email: "", customer_phone: "", address: "", service_description: "", frequency: "weekly", days_of_week: [], monthly_week: 1, monthly_day: 1, start_date: "", end_date: "", time_slot: "", notes: "", isRecurring: false, crew_id: "", _editId: null, _editInstanceId: null, _linkedCustomerEmail: null, active_months: null, seasonal_config: DEFAULT_SEASONAL_CONFIG });
      fetchCalendarData();
    } catch { toast.error("Failed to save job"); }
    finally { setSchedulingLoading(false); }
  };

  const updateInstance = async (instanceId, status) => {
    const token = localStorage.getItem("token");
    try {
      if (status === "cancelled") {
        // Remove from calendar entirely
        await axios.delete(`${API}/tenants/${slug}/job-instances/${instanceId}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Removed from calendar");
      } else {
        await axios.patch(`${API}/tenants/${slug}/job-instances/${instanceId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Marked complete");
        // If chemicals tracking is on, offer to log application
        if (status === "completed" && business?.chemicals_enabled) {
          if (window.confirm("Did you apply chemicals on this job? Log it now?")) {
            try {
              const r = await axios.get(`${API}/tenants/${slug}/chemical-applications/prefill-from-job/${instanceId}`, { headers: { Authorization: `Bearer ${token}` } });
              setChemLogPrefill(r.data);
            } catch { setChemLogPrefill({ job_instance_id: instanceId }); }
          }
        }
      }
      fetchCalendarData();
    } catch { toast.error("Failed to update"); }
  };

  // Drag-and-drop reschedule (desktop only)
  const handleDrop = async (targetDate) => {
    if (!dragJob || !targetDate) return;
    const newDate = targetDate.toISOString().split('T')[0];
    const token = localStorage.getItem("token");
    try {
      if (dragJob.type === "recurring") {
        await axios.patch(`${API}/tenants/${slug}/job-instances/${dragJob.id}`, { date: newDate }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Job moved!");
      } else if (dragJob.type === "lead") {
        await api.rescheduleJob(dragJob.id, newDate, dragJob.time || "");
        toast.success("Job rescheduled!");
        if (onRefresh) onRefresh();
      }
      fetchCalendarData();
    } catch { toast.error("Failed to move job"); }
    setDragJob(null);
  };

  const navPrev = () => { const d = new Date(viewDate); view === "month" ? d.setMonth(d.getMonth() - 1) : d.setDate(d.getDate() - 7); setViewDate(d); };
  const navNext = () => { const d = new Date(viewDate); view === "month" ? d.setMonth(d.getMonth() + 1) : d.setDate(d.getDate() + 7); setViewDate(d); };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900" data-testid="calendar-header">Calendar</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowQueue(!showQueue)} className="relative p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700" data-testid="toggle-queue-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            {unscheduledJobs.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{unscheduledJobs.length}</span>}
          </button>
          <button onClick={() => { setJobForm({ customer_name: "", customer_email: "", customer_phone: "", address: "", service_description: "", frequency: "weekly", days_of_week: [], monthly_week: 1, monthly_day: 1, start_date: new Date().toISOString().split('T')[0], end_date: "", time_slot: "", notes: "", isRecurring: false, crew_id: "", _editId: null, _editInstanceId: null, _linkedCustomerEmail: null, active_months: null, seasonal_config: DEFAULT_SEASONAL_CONFIG }); setShowJobForm(true); }} className="p-2 rounded-lg bg-slate-900 text-white" data-testid="add-job-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          </button>
        </div>
      </div>

      {/* Month/Week Nav */}
      <div className="flex items-center justify-between bg-white rounded-xl border px-3 py-2">
        <button onClick={navPrev} className="p-1.5 rounded-lg hover:bg-gray-100"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
        <div className="text-center">
          <p className="font-semibold text-gray-900 text-sm">{MONTH_NAMES[viewDate.getMonth()]} {viewDate.getFullYear()}</p>
          <div className="flex gap-1 mt-1 justify-center">
            {["month", "week"].map(v => <button key={v} onClick={() => setView(v)} className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${view === v ? "bg-blue-100 text-slate-900" : "text-gray-400"}`}>{v}</button>)}
          </div>
        </div>
        <button onClick={navNext} className="p-1.5 rounded-lg hover:bg-gray-100"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
      </div>

      {/* Queue */}
      {showQueue && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2" data-testid="unscheduled-queue">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-amber-900 text-sm">Needs Scheduling ({unscheduledJobs.length})</p>
            <button onClick={() => setShowQueue(false)} className="text-amber-600 text-xs">Close</button>
          </div>
          {unscheduledJobs.length === 0 ? <p className="text-xs text-gray-500 text-center py-3">All caught up!</p> : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {unscheduledJobs.map((job) => (
                <div key={job.lead?.id} className="bg-white rounded-lg p-2.5 border border-amber-100 flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-sm truncate">{job.lead?.customer_name}</p>
                    <p className="text-xs text-gray-500 truncate">{job.lead?.pricing_snapshot?.map(p => p.label).join(", ") || "Service"}</p>
                  </div>
                  <button onClick={() => { setSchedulingJob(job); setScheduleDate(""); setScheduleTime(""); }} className="shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs rounded-lg font-medium">Schedule</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Crew Legend */}
      {crews.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {crews.map(c => (
            <span key={c.id} className="flex items-center gap-1 text-xs text-gray-600">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.color || '#3B82F6' }} />
              {c.name}
            </span>
          ))}
        </div>
      )}

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="grid grid-cols-7 border-b">
          {(view === "month" ? DAY_NAMES_SHORT : DAY_NAMES).map((d, i) => (
            <div key={i} className="text-center text-xs font-medium text-gray-400 py-2">{d}</div>
          ))}
        </div>
        <div className={`grid grid-cols-7 ${view === "week" ? "min-h-[200px]" : ""}`}>
          {calDays.map((date, idx) => {
            const jobs = getJobsForDate(date);
            const isToday = date && date.toDateString() === today.toDateString();
            const isPast = date && date < today;
            const isDragOver = dragJob && date && !isPast;
            return (
              <div
                key={idx}
                onClick={() => { if (date) { if (dragJob && !isPast) { handleDrop(date); } else { setSelectedDay(date); } } }}
                onDragOver={(e) => { if (dragJob) e.preventDefault(); }}
                onDrop={() => { if (dragJob && date && !isPast) handleDrop(date); }}
                className={`border-r border-b p-1 transition-colors ${view === "week" ? "min-h-[120px]" : "min-h-[52px] sm:min-h-[72px]"} ${
                  !date ? "bg-gray-50/50" : isDragOver ? "bg-blue-100 ring-2 ring-blue-500 ring-inset" : selectedDay && date && date.toDateString() === selectedDay.toDateString() ? "bg-blue-50" : isToday ? "bg-blue-50" : isPast ? "bg-gray-50/50" : "hover:bg-gray-50 cursor-pointer"
                }`}
                data-testid={date ? `cal-day-${date.getDate()}` : undefined}
              >
                {date && (<>
                  <div className={`text-xs font-medium text-center ${isToday ? "bg-slate-900 text-white w-6 h-6 rounded-full flex items-center justify-center mx-auto" : isPast ? "text-gray-300" : "text-gray-700"}`}>{date.getDate()}</div>
                  <div className="mt-0.5 space-y-0.5">
                    {/* No cap — render every job for the day. Cells grow
                        vertically as needed so a busy day with 6 stops is
                        fully visible at a glance instead of being hidden
                        behind a "+N more" indicator. */}
                    {jobs.map((job, jIdx) => (
                      <div
                        key={jIdx}
                        draggable={job.status === "scheduled"}
                        onDragStart={() => setDragJob(job)}
                        onDragEnd={() => setDragJob(null)}
                        className={`${statusColor(job.status)} text-white text-[10px] leading-tight px-1 py-0.5 rounded overflow-hidden ${job.status === "scheduled" ? "cursor-grab active:cursor-grabbing" : ""}`}
                        style={statusBorder(job.status, job.crewId)}
                        title={`${job.name}${job.service ? ` — ${job.service}` : ""}`}
                      >
                        {/* Two-line pill: full customer name + service so the
                            owner can identify the job at a glance. We use
                            `break-normal` (overflow-wrap: normal) so wrapping
                            only happens at spaces — "Test Customer" becomes
                            "Test" / "Customer", never "Test Cus" / "tomer".
                            overflow-hidden on the pill keeps any truly oversized
                            single word from spilling into the neighboring cell;
                            the full string is always available in the tooltip. */}
                        <div className="font-medium break-normal whitespace-normal">{job.name}</div>
                        {job.service && <div className="opacity-80 break-normal whitespace-normal">{job.service}</div>}
                      </div>
                    ))}
                  </div>
                </>)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Drag indicator */}
      {dragJob && <div className="bg-blue-100 border border-blue-300 rounded-lg p-2 text-center text-xs text-slate-900 font-medium">Drop <strong>{dragJob.name}</strong> on a day to reschedule</div>}

      {/* Selected Day Detail */}
      {selectedDay && !dragJob && (() => {
        const dayJobs = getJobsForDate(selectedDay);
        const dayStr = selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
        const closeDay = () => setSelectedDay(null);
        return (
          <Dialog open={true} onOpenChange={(v) => { if (!v) closeDay(); }}>
            <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="day-detail">
              <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200">
                <DialogTitle className="font-heading text-lg font-semibold text-slate-900 tracking-tight text-left">{dayStr}</DialogTitle>
                <p className="text-xs text-slate-500 text-left">{dayJobs.length === 0 ? "No jobs scheduled" : `${dayJobs.length} job${dayJobs.length !== 1 ? "s" : ""}`}</p>
              </DialogHeader>
              <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                {dayJobs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center bg-slate-50 border border-dashed border-slate-200 rounded-lg">
                    <CalendarIcon className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600 font-medium mb-1">Nothing on the books</p>
                    <p className="text-xs text-slate-500">Tap "+ Add Job" to schedule something for this day.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dayJobs.map((job) => {
                      const cc = getCrewColor(job.crewId);
                      const crewName = job.crewId ? crews.find(c => c.id === job.crewId)?.name : null;
                      return (
                        <div key={job.id} className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-200" style={cc ? { borderLeft: `4px solid ${cc}` } : {}}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div className={`w-2 h-2 rounded-full shrink-0 ${statusColor(job.status)}`} />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{job.name}</p>
                                <p className="text-xs text-slate-500 truncate">{job.service}{job.time ? ` · ${job.time}` : ""}{crewName ? ` · ${crewName}` : ""}</p>
                                {job.address && <p className="text-xs text-slate-400 truncate">{job.address}</p>}
                              </div>
                            </div>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${job.status === "completed" ? "bg-blue-100 text-slate-900" : job.status === "cancelled" ? "bg-slate-200 text-slate-600" : "bg-blue-100 text-blue-800"}`}>{job.status}</span>
                          </div>
                          {job.status === "scheduled" && (
                            <div className="flex gap-1.5">
                              {job.type === "recurring" && (
                                <>
                                  <button onClick={() => updateInstance(job.id, "completed")} className="flex-1 py-2 bg-slate-900 hover:bg-slate-900 text-white text-xs rounded-md font-medium transition-colors" data-testid={`complete-${job.id}`}>Done</button>
                                  <button onClick={() => {
                                    setJobForm({
                                      customer_name: job.name || "", customer_email: job.raw?.customer_email || "", customer_phone: job.raw?.customer_phone || "", address: job.address || "", service_description: job.service || "",
                                      frequency: "weekly", days_of_week: [], monthly_week: 1, monthly_day: 1,
                                      start_date: job.raw?.date || selectedDay.toISOString().split('T')[0],
                                      end_date: job.raw?.date || "", time_slot: job.time || "", notes: job.raw?.notes || "",
                                      isRecurring: false, crew_id: job.crewId || "", _editInstanceId: job.id, _editId: null, _linkedCustomerEmail: job.raw?.customer_email || null, active_months: null, seasonal_config: DEFAULT_SEASONAL_CONFIG
                                    });
                                    setShowJobForm(true);
                                    closeDay();
                                  }} className="px-3 py-2 bg-blue-100 text-slate-900 hover:bg-blue-200 text-xs rounded-md transition-colors">Edit</button>
                                  <button onClick={() => updateInstance(job.id, "cancelled")} className="px-3 py-2 bg-slate-200 text-slate-700 hover:bg-slate-300 text-xs rounded-md transition-colors">Skip</button>
                                  <button onClick={() => {
                                    if (window.confirm(`Delete "${job.name}" recurring job and all future instances?`)) {
                                      (async () => {
                                        const token = localStorage.getItem("token");
                                        await axios.delete(`${API}/tenants/${slug}/recurring-jobs/${job.recurringId}`, { headers: { Authorization: `Bearer ${token}` } });
                                        toast.success("Job deleted");
                                        fetchCalendarData();
                                        closeDay();
                                      })();
                                    }
                                  }} className="px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 text-xs rounded-md transition-colors">Delete</button>
                                </>
                              )}
                              {job.type === "lead" && (
                                <>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem("token");
                                        await axios.patch(`${API}/leads/${job.raw.lead?.id || job.id}`, { lifecycle_status: "completed" }, { headers: { Authorization: `Bearer ${token}` } });
                                        toast.success("Marked complete");
                                        fetchCalendarData();
                                        if (onRefresh) onRefresh();
                                        closeDay();
                                      } catch { toast.error("Failed to update"); }
                                    }}
                                    className="flex-1 py-2 bg-slate-900 hover:bg-slate-900 text-white text-xs rounded-md font-medium transition-colors"
                                    data-testid={`complete-lead-${job.id}`}
                                  >
                                    Done
                                  </button>
                                  <button onClick={() => { setSchedulingJob(job.raw); setScheduleDate(job.raw.lead?.scheduled_date || ""); setScheduleTime(job.raw.lead?.scheduled_time || ""); closeDay(); }} className="px-3 py-2 bg-blue-100 text-slate-900 hover:bg-blue-200 text-xs rounded-md transition-colors">Edit</button>
                                </>
                              )}
                            </div>
                          )}
                          {job.status !== "scheduled" && job.type === "recurring" && (
                            <div className="flex gap-1.5">
                              <button onClick={() => updateInstance(job.id, "cancelled")} className="flex-1 py-2 bg-slate-200 text-slate-700 hover:bg-slate-300 text-xs rounded-md transition-colors">Remove</button>
                              <button onClick={() => {
                                if (window.confirm(`Delete "${job.name}" recurring job and all future instances?`)) {
                                  (async () => {
                                    const token = localStorage.getItem("token");
                                    await axios.delete(`${API}/tenants/${slug}/recurring-jobs/${job.recurringId}`, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success("Job deleted");
                                    fetchCalendarData();
                                    closeDay();
                                  })();
                                }
                              }} className="px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 text-xs rounded-md transition-colors">Delete All</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeDay}
                  data-testid="day-detail-close-btn"
                  className="px-3 py-2 text-sm font-medium bg-white text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  data-testid="day-detail-add-job-btn"
                  onClick={() => {
                    setJobForm({ customer_name: "", customer_email: "", customer_phone: "", address: "", service_description: "", frequency: "weekly", days_of_week: [], monthly_week: 1, monthly_day: 1, start_date: selectedDay.toISOString().split('T')[0], end_date: "", time_slot: "", notes: "", isRecurring: false, crew_id: "", _editId: null, _editInstanceId: null, _linkedCustomerEmail: null, active_months: null, seasonal_config: DEFAULT_SEASONAL_CONFIG });
                    setShowJobForm(true);
                    closeDay();
                  }}
                  className="px-3 py-2 text-sm font-medium bg-slate-900 hover:bg-slate-900 text-white rounded-md transition-colors"
                >
                  + Add Job
                </button>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Recurring Jobs List — hide one-off jobs (start_date == end_date) since
          those aren't actually recurring and already appear as calendar pills. */}
      {(() => {
        const trueRecurring = recurringJobs.filter(rj => !(rj.end_date && rj.start_date && rj.end_date === rj.start_date));
        if (trueRecurring.length === 0) return null;
        return (
        <div className="bg-white rounded-xl border p-3 space-y-2">
          <p className="font-semibold text-gray-900 text-sm">Recurring Jobs</p>
          <div className="space-y-2">
            {trueRecurring.map(rj => {
              const cc = getCrewColor(rj.crew_id);
              const crewName = rj.crew_id ? crews.find(c => c.id === rj.crew_id)?.name : null;
              return (
                <div key={rj.id} className={`flex items-center justify-between rounded-lg p-2.5 ${rj.active ? 'bg-blue-50' : 'bg-gray-50'}`} style={cc ? { borderLeft: `4px solid ${cc}` } : {}}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{rj.customer_name}{!rj.active && <span className="text-xs text-gray-400 ml-1">(inactive)</span>}</p>
                    <p className="text-xs text-gray-500">{rj.service_description} · {rj.frequency === "seasonal" ? (rj.seasonal_config?.program_name || (rj.seasonal_config?.steps?.length ? `Seasonal (${rj.seasonal_config.steps.length} step${rj.seasonal_config.steps.length !== 1 ? "s" : ""})` : "Seasonal")) : rj.frequency}{rj.time_slot ? ` · ${rj.time_slot}` : ""}{crewName ? ` · ${crewName}` : ""}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0 ml-2">
                    <button onClick={() => {
                      // Auto-migrate legacy "seasonal" jobs to the new month-pill model on edit:
                      // convert seasonal steps → active_months and treat as weekly.
                      let migratedFreq = rj.frequency || "weekly";
                      let migratedMonths = rj.active_months || null;
                      let migratedDays = rj.days_of_week || [];
                      if (rj.frequency === "seasonal") {
                        const SEASON_MONTHS = {
                          early_spring: [3, 4], late_spring: [5, 6],
                          early_summer: [6, 7], late_summer: [7, 8],
                          summer: [6, 7, 8],
                          early_fall: [9, 10], late_fall: [10, 11], fall: [9, 10, 11],
                          winter: [12, 1, 2],
                        };
                        const set = new Set();
                        (rj.seasonal_config?.steps || []).forEach(s => {
                          (SEASON_MONTHS[s.season] || []).forEach(m => set.add(m));
                        });
                        if (set.size > 0 && set.size < 12) {
                          migratedMonths = Array.from(set).sort((a,b)=>a-b);
                        }
                        migratedFreq = "weekly";
                        if (!migratedDays || migratedDays.length === 0) {
                          // Default to Monday so the job has at least one day-of-week
                          migratedDays = [1];
                        }
                      }
                      setJobForm({
                        customer_name: rj.customer_name || "",
                        customer_email: rj.customer_email || "",
                        customer_phone: rj.customer_phone || "",
                        address: rj.address || "",
                        service_description: rj.service_description || "",
                        frequency: migratedFreq,
                        days_of_week: migratedDays,
                        monthly_week: rj.monthly_week || 1,
                        monthly_day: rj.monthly_day || 1,
                        start_date: rj.start_date || "",
                        end_date: rj.end_date || "",
                        time_slot: rj.time_slot || "",
                        notes: rj.notes || "",
                        // A one-off job is stored in recurring_jobs with start_date == end_date.
                        // Detect that here so the edit form opens as a single-date job, not as
                        // a recurring template.
                        isRecurring: !(rj.start_date && rj.end_date && rj.start_date === rj.end_date),
                        crew_id: rj.crew_id || "",
                        _editId: rj.id,
                        _editInstanceId: null,
                        _linkedCustomerEmail: rj.customer_email || null,
                        active_months: migratedMonths,
                        seasonal_config: { ...DEFAULT_SEASONAL_CONFIG, ...(rj.seasonal_config || {}) }
                      });
                      setShowJobForm(true);
                    }} className="text-xs text-slate-900 font-medium">Edit</button>
                    <button onClick={async () => {
                    const token = localStorage.getItem("token");
                    await axios.delete(`${API}/tenants/${slug}/recurring-jobs/${rj.id}`, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success("Recurring job removed");
                    fetchCalendarData();
                  }} className="text-xs text-red-500 font-medium">Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}

      {/* Schedule from Queue Modal */}
      {schedulingJob && (
        <Dialog open={!!schedulingJob} onOpenChange={() => setSchedulingJob(null)}>
          <DialogContent className="max-w-sm mx-auto">
            <DialogTitle className="text-base">Schedule Job</DialogTitle>
            <div className="space-y-3 pt-1">
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="font-medium text-sm">{schedulingJob.lead?.customer_name || schedulingJob.name}</p>
                <p className="text-xs text-gray-500">{schedulingJob.lead?.pricing_snapshot?.map(p => p.label).join(", ") || schedulingJob.service || "Service"}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Date</label>
                <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Time (optional)</label>
                <Input type="text" placeholder="e.g. 9:00 AM" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="mt-1" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setSchedulingJob(null)}>Cancel</Button>
                <Button className="flex-1 bg-blue-700 hover:bg-blue-800" disabled={!scheduleDate || schedulingLoading} onClick={handleScheduleFromQueue}>{schedulingLoading ? "..." : "Confirm"}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* New Job Form */}
      {showJobForm && (
        <Dialog open={showJobForm} onOpenChange={setShowJobForm}>
          <DialogContent className="max-w-sm mx-auto max-h-[85vh] overflow-y-auto overflow-x-hidden">
            <DialogTitle className="text-base">{jobForm._editId ? "Edit Job" : "New Job"}</DialogTitle>
            <div className="space-y-3 pt-1">
              <div>
                <label className="text-xs font-medium text-gray-600">Customer Name *</label>
                <CustomerAutocompleteInput
                  value={jobForm.customer_name}
                  onChange={(val) => setJobForm(f => ({ ...f, customer_name: val }))}
                  onPickCustomer={(c) => {
                    setJobForm(f => ({
                      ...f,
                      customer_name: c.name || f.customer_name,
                      customer_email: c.email || "",
                      customer_phone: c.phone || "",
                      address: c.address || f.address || "",
                    }));
                  }}
                  customers={knownCustomers}
                  placeholder="John Smith"
                  className="mt-1"
                  testIdPrefix="job-customer"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-gray-600">Email</label>
                  <Input
                    type="email"
                    value={jobForm.customer_email}
                    onChange={(e) => setJobForm(f => ({ ...f, customer_email: e.target.value }))}
                    placeholder="name@email.com"
                    className="mt-1"
                    data-testid="job-customer-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Phone</label>
                  <Input
                    type="tel"
                    value={jobForm.customer_phone}
                    onChange={(e) => setJobForm(f => ({ ...f, customer_phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                    data-testid="job-customer-phone"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Address</label>
                <Input value={jobForm.address} onChange={(e) => setJobForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main St" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Service *</label>
                <ServiceCombobox
                  services={services}
                  value={jobForm.service_description}
                  onChange={(val) => setJobForm(f => ({ ...f, service_description: val }))}
                  placeholder="Pick a service or type custom..."
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Start Date *</label>
                <Input type="date" value={jobForm.start_date} onChange={(e) => setJobForm(f => ({ ...f, start_date: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Time</label>
                <Input type="text" placeholder="9:00 AM" value={jobForm.time_slot} onChange={(e) => setJobForm(f => ({ ...f, time_slot: e.target.value }))} className="mt-1" />
              </div>

              {/* Crew Assignment */}
              {crews.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-600">Assign Crew</label>
                  <select value={jobForm.crew_id} onChange={(e) => setJobForm(f => ({ ...f, crew_id: e.target.value }))} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="">No crew</option>
                    {crews.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              {/* Recurring Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={jobForm.isRecurring} onChange={(e) => setJobForm(f => ({ ...f, isRecurring: e.target.checked }))} className="w-4 h-4 rounded text-slate-900" />
                <span className="text-sm font-medium">Repeat this job</span>
              </label>

              {jobForm.isRecurring && (
                <div className="space-y-3 bg-blue-50 rounded-lg p-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Frequency</label>
                    <select value={jobForm.frequency} onChange={(e) => setJobForm(f => ({ ...f, frequency: e.target.value }))} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {(jobForm.frequency === "weekly" || jobForm.frequency === "biweekly") && (
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Days</label>
                      <div className="flex gap-1">
                        {DAY_NAMES_SHORT.map((d, i) => (
                          <button key={i} type="button" onClick={() => setJobForm(f => ({ ...f, days_of_week: f.days_of_week.includes(i) ? f.days_of_week.filter(x => x !== i) : [...f.days_of_week, i] }))} className={`w-9 h-9 rounded-full text-xs font-medium ${jobForm.days_of_week.includes(i) ? "bg-slate-900 text-white" : "bg-white text-gray-600 border"}`}>{d}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {jobForm.frequency === "monthly" && (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-600">Week</label>
                        <select value={jobForm.monthly_week} onChange={(e) => setJobForm(f => ({ ...f, monthly_week: parseInt(e.target.value) }))} className="mt-1 w-full border rounded-lg px-2 py-2 text-sm">
                          <option value={1}>1st</option><option value={2}>2nd</option><option value={3}>3rd</option><option value={4}>4th</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-600">Day</label>
                        <select value={jobForm.monthly_day} onChange={(e) => setJobForm(f => ({ ...f, monthly_day: parseInt(e.target.value) }))} className="mt-1 w-full border rounded-lg px-2 py-2 text-sm">
                          {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                  <div className="bg-white rounded-md border border-blue-200 p-2.5">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs font-medium text-gray-700">Limit to certain months?</span>
                      <input
                        type="checkbox"
                        checked={!!(jobForm.active_months && jobForm.active_months.length > 0 && jobForm.active_months.length < 12)}
                        onChange={(e) => {
                          setJobForm(f => ({ ...f, active_months: e.target.checked ? [new Date().getMonth() + 1] : null }));
                        }}
                        className="w-4 h-4 accent-blue-800"
                        data-testid="limit-months-toggle"
                      />
                    </label>
                    {jobForm.active_months && jobForm.active_months.length > 0 && jobForm.active_months.length < 12 && (
                      <div className="mt-2">
                        <div className="text-[10px] text-gray-500 mb-1.5">Tap the months this job should run:</div>
                        <div className="grid grid-cols-6 gap-1">
                          {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((name, idx) => {
                            const m = idx + 1;
                            const on = (jobForm.active_months || []).includes(m);
                            return (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setJobForm(f => ({
                                  ...f,
                                  active_months: on
                                    ? (f.active_months || []).filter(x => x !== m)
                                    : [...(f.active_months || []), m].sort((a,b)=>a-b)
                                }))}
                                className={`h-8 rounded-full text-[11px] font-medium transition-colors ${on ? "bg-slate-900 text-white" : "bg-white text-gray-500 border border-gray-200 hover:border-blue-300"}`}
                                data-testid={`month-pill-${m}`}
                              >
                                {name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600">End Date (blank = ongoing)</label>
                    <Input type="date" value={jobForm.end_date} onChange={(e) => setJobForm(f => ({ ...f, end_date: e.target.value }))} className="mt-1" />
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-gray-600">Notes</label>
                <Input value={jobForm.notes} onChange={(e) => setJobForm(f => ({ ...f, notes: e.target.value }))} placeholder="Special instructions..." className="mt-1" />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setShowJobForm(false)}>Cancel</Button>
                <Button className="flex-1 bg-slate-900 hover:bg-slate-900" disabled={schedulingLoading} onClick={handleCreateJob}>{schedulingLoading ? "..." : jobForm._editId ? "Save Changes" : jobForm.isRecurring ? "Create Recurring" : "Schedule"}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Quick-Log Chemical Application prompted after Done */}
      {chemLogPrefill && (
        <CalendarChemLog
          slug={slug}
          business={business}
          prefill={chemLogPrefill}
          onClose={() => setChemLogPrefill(null)}
        />
      )}
    </div>
  );
};

// Small helper component that lazy-loads chemicals/mixtures for the calendar-triggered log modal
const CalendarChemLog = ({ slug, business, prefill, onClose }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [chems, setChems] = useState([]);
  const [mixes, setMixes] = useState([]);
  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    Promise.all([
      axios.get(`${API}/tenants/${slug}/chemicals`, { headers: h }).catch(() => ({ data: [] })),
      axios.get(`${API}/tenants/${slug}/chemical-mixtures`, { headers: h }).catch(() => ({ data: [] })),
    ]).then(([c, m]) => { setChems(c.data || []); setMixes(m.data || []); });
  }, [slug, API]);
  return (
    <LogApplicationModal
      slug={slug}
      chemicals={chems}
      mixtures={mixes}
      tenantState={business?.state_code || ""}
      tenantLicense={business?.applicator_license || ""}
      prefill={prefill}
      onClose={onClose}
      onSaved={onClose}
    />
  );
};

// ============== SUPPORT SECTION ==============
// ============== FAQ COMPONENT ==============
const FAQSection = () => {
  const [openItem, setOpenItem] = useState(null);
  
  const faqs = [
    {
      question: "How do I get started?",
      answer: "Go to Set Up / Settings, add at least one service, then grab your public link from the 'Your Public Link' card at the bottom of Set Up. Share that link anywhere — website, social media, texts — and leads start landing in your Leads & Quotes tab."
    },
    SHOW_WATCH_IT_WORK && {
      question: "What is 'Watch It Work'?",
      answer: "A safe, self-test button at the top of Set Up / Settings. Tap 'Watch It Work' and the app sends YOU a fake customer request. You'll walk through receiving the lead, building a quote, sending it, and seeing the exact email your real customers will see — end to end, no real customer touched."
    },
    {
      question: "What happens when someone submits a request?",
      answer: "You get an instant email notification with their name, contact info, selected services, uploaded photos, and notes. The lead appears in the Leads & Quotes tab with a 'New' badge. Tap it to view details and create a quote."
    },
    {
      question: "How do I send a quote?",
      answer: "Open any lead from the Leads & Quotes tab and tap 'Create Quote'. Add line items, adjust quantities, and optionally add 'Configurable Items' (optional add-ons your customer can toggle on or off themselves). Choose deposit or pay-in-full, then tap 'Send Quote'. The customer gets a professional email with a 'Review Your Estimate' button that opens their invoice page."
    },
    {
      question: "How does the customer approve or decline a quote?",
      answer: "For every quote. You can choose to email or text the invoice link to the customer. At the bottom they see two buttons: 'Yes! Approve' and 'Politely Decline'. Approving prompts them to pay the deposit (or full amount). Declining notifies you and closes the quote — no more back-and-forth emails."
    },
    {
      question: "What are Optional Services?",
      answer: "Optional add-ons you attach to a quote. The customer sees them on their invoice page with checkboxes and a live running total. They pick what they want before approving, so the final quote reflects exactly what they agreed to. Great for 'Want us to also trim the hedges? +$45' style upsells."
    },
    {
      question: "How do deposits work?",
      answer: "ZipLeadPro auto-calculates a 25% deposit, rounded up to the nearest $10, tax-free. Example: a $130 quote → $40 deposit. The remaining balance plus tax is collected when the customer pays the rest."
    },
    {
      question: "What happens if an auto-charge fails?",
      answer: "If the customer's card on file can't be charged, they automatically receive an email with a one-tap 'Complete Payment' button pointing back to their invoice page. ZipLeadPro retries automatically every 3 days (up to 3 attempts) and you'll see a flag on the Dashboard's 'Scheduled Charges' card so you know to follow up if needed."
    },
    {
      question: "How do I collect the remaining balance after a deposit?",
      answer: "From the Leads & Quotes tab, open the lead and tap the orange 'Collect Balance' button. ZipLeadPro automatically charges the customer's card on file for the remaining balance."
    },
    {
      question: "How does scheduling work?",
      answer: "Paid jobs show up in the Calendar tab under 'Needs Scheduling'. Tap one to pick a date and time. You can also schedule a job directly by tapping the '+' button on the calendar header."
    },
    {
      question: "Can I create recurring jobs?",
      answer: "Yes. In the Calendar, tap '+', fill in the details, and flip on 'Recurring'. Choose the custom frequency."
    },
    {
      question: "Can I drag and drop jobs to reschedule?",
      answer: "Yes. On desktop, drag any scheduled job card on the Calendar to a new day to reschedule it instantly. On mobile, tap the day, then tap Edit on the job to change its date."
    },
    {
      question: "What are Crews and how do I use them?",
      answer: "Go to the Routes & Crews tab → 'Crews'. Create a named, color-coded crew (e.g., 'North Team — Blue'). When creating or editing a job in the Calendar, assign it to a crew. Each crew's jobs show up color-coded on the calendar so you can see who's doing what at a glance."
    },
    {
      question: "How do I measure a property?",
      answer: "From a lead or a quote line item, tap 'Measure Property'. A Google satellite view opens — drop points around the area you want to measure, and the square footage is calculated automatically. You can save the measurement to that customer."
    },
    {
      question: "Can I hide a service from the public intake form but still use it for quotes?",
      answer: "Yes. When creating or editing a service, flip the visibility toggle. 'Public — visible on intake form' means customers can pick it themselves. 'Internal — quote builder only' keeps it private, usable only when you build a quote manually. Services show a gray 'Internal' badge in the service list when hidden."
    },
    {
      question: "What do the status badges mean on leads?",
      answer: "New (just submitted), Quoted (quote sent), Deposit Paid (awaiting final balance), Paid (fully paid), Needs Scheduling (paid, calendar entry needed), Scheduled (on the calendar), Completed (job done). Tap an actionable badge to jump to the next step."
    },
    {
      question: "How do I customize my intake form?",
      answer: "In Set Up / Settings, scroll down to the 'Preview & Customize' button. Tap it to open the live editor where you can upload your logo, switch light/dark mode, pick fonts, edit your tagline and CTA button text, and choose which fields are required — all with a real-time preview."
    },
    {
      question: "How does Stripe connect?",
      answer: "Go to the Payment tab and tap 'Connect Stripe'. You'll be redirected to Stripe to authorize, then bounced back here. Once connected, all quotes include secure payment buttons and funds land directly in your Stripe account. You can't collect deposits or remaining balances through the app until Stripe is connected."
    },
    {
      question: "Can I send a quote without a payment link?",
      answer: "Yes. If you take cash, CashApp, Venmo, or check, just paste your preferred payment link or instructions into the message field when building the quote. You don't need Stripe connected to send the quote — the customer will still see 'Yes! Approve' and 'Politely Decline' buttons on their invoice page."
    },
    {
      question: "How do I add my form to my website?",
      answer: "Go to Set Up / Settings → scroll to 'Your Public Link' at the bottom. Copy the short link to paste into a button on your site, OR copy the embed code to drop the whole intake form inline on any page (Wix, Squarespace, WordPress, etc.)."
    },
    {
      question: "What's included in my subscription?",
      answer: "Everything: unlimited leads, unlimited quotes, scheduling, recurring/seasonal jobs, crew management, route planning, drag-and-drop calendar, auto quote follow-ups, chemical tracking (for licensed pros), property measuring, Stripe payments, custom branding, photo uploads, email notifications, and every future feature we ship. $29/month, no contracts, cancel anytime."
    }
  ];

  return (
    <div className="space-y-3">
      {faqs.filter(Boolean).map((faq, index) => (
        <div key={index} className="border rounded-lg overflow-hidden">
          <button
            onClick={() => setOpenItem(openItem === index ? null : index)}
            className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
          >
            <span className="font-medium text-gray-900">{faq.question}</span>
            <svg 
              className={`w-5 h-5 text-gray-500 transition-transform ${openItem === index ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {openItem === index && (
            <div className="px-4 pb-4 text-gray-600 text-sm border-t bg-gray-50">
              <p className="pt-3">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ============== EMAIL TEMPLATE SECTION ==============
const EmailTemplateSection = ({ slug }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState({
    subject: "",
    body: ""
  });

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Clean up any existing {{variable}} syntax for display - convert to human-readable
  const cleanForDisplay = (text) => {
    if (!text) return "";
    return text
      .replace(/\{\{customer_name\}\}/g, "[Customer Name]")
      .replace(/\{\{business_name\}\}/g, "[Your Business]")
      .replace(/\{\{service_names\}\}/g, "[Services]")
      .replace(/\{\{quote_amount\}\}/g, "[Quote Total]")
      .replace(/\{\{payment_link\}\}/g, "")
      .replace(/\{\{valid_until\}\}/g, "[Expiration Date]")
      .replace(/\{\{business_contact\}\}/g, "[Your Contact Info]");
  };
  
  // Convert human-readable placeholders back for storage (backend needs {{payment_link}})
  const prepareForSave = (text) => {
    if (!text) return "";
    // The backend auto-adds the payment button, so we just return the message as-is
    // We need to ensure {{payment_link}} is present for the backend validation
    // We'll add it invisibly at the end since the UI abstracts this away
    let cleaned = text
      .replace(/\[Customer Name\]/gi, "{{customer_name}}")
      .replace(/\[Your Business\]/gi, "{{business_name}}")
      .replace(/\[Services\]/gi, "{{service_names}}")
      .replace(/\[Quote Total\]/gi, "{{quote_amount}}")
      .replace(/\[Expiration Date\]/gi, "{{valid_until}}")
      .replace(/\[Your Contact Info\]/gi, "{{business_contact}}");
    
    // Always ensure payment_link is present (backend requirement)
    if (!cleaned.includes("{{payment_link}}")) {
      cleaned += "\n\n{{payment_link}}";
    }
    return cleaned;
  };

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const data = await api.getQuoteEmailTemplate(slug);
      setTemplate({
        subject: cleanForDisplay(data.subject || ""),
        body: cleanForDisplay(data.body || "")
      });
    } catch (error) {
      // Use user-friendly defaults
      setTemplate({
        subject: "Your service quote is ready",
        body: "Hi there,\n\nThanks for reaching out! Your service quote is ready.\n\nYou can review the details and accept the quote using the button below.\n\nIf you have any questions, just reply to this email.\n\nBest regards,"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateQuoteEmailTemplate(slug, {
        subject: template.subject,
        body: prepareForSave(template.body)
      });
      toast.success("Email template saved!");
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Quote Email Template</h2>
      <p className="text-gray-600 mb-6">Personalize the message your customers receive with their quote.</p>
      
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <span>✉️</span> Customize Your Email
          </CardTitle>
          <CardDescription className="text-sm">
            Write a warm, personal message. The quote details and payment button are added automatically!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Email Subject</Label>
            <Input
              value={template.subject}
              onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
              placeholder="Your service quote is ready"
              className="text-base"
              data-testid="email-subject-input"
            />
            <p className="text-xs text-gray-500">This is what customers see in their inbox</p>
          </div>
          
          <div className="space-y-2">
            <Label className="font-medium">Your Message</Label>
            <Textarea
              value={template.body}
              onChange={(e) => setTemplate({ ...template, body: e.target.value })}
              rows={8}
              className="text-base"
              placeholder="Hi there,&#10;&#10;Thanks for reaching out! Your quote is ready for review.&#10;&#10;Best regards,"
              data-testid="email-body-input"
            />
            <p className="text-xs text-gray-500">
              Write your message naturally. Quote details and a payment button will appear below this automatically.
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-slate-900 font-medium mb-2">✨ What your customer will see:</p>
            <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
              <li>Your personal message (above)</li>
              <li>A detailed breakdown of services & pricing</li>
              <li>A big, clear "View Quote & Pay" button</li>
              <li>Quote expiration date</li>
            </ul>
          </div>
          
          <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto" data-testid="save-email-template-btn">
            {saving ? "Saving..." : "💾 Save Email Template"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== MOBILE PREVIEW SHEET ==============
const MobilePreviewSheet = ({
  intakeMode,
  fieldConfig,
  services,
  logoUrl,
  businessName,
  onIntakeModeChange,
  showOnAllScreens = false,
  onFieldConfigChange,
  onLogoChange,
  onSave,
  slug,
  business, // Add business prop for persisted data
  externalOpen = false, // Allow external control of open state
  closePreviewTrigger = 0, // Trigger to close preview from parent
  onExternalOpenChange, // Callback when open state changes
  onStep2Complete, // Callback to mark Step 2 complete when user opens editor
  onPreviewOpen, // Callback for tour when preview expands
  onEditorExpand, // Callback for tour when editor panel is expanded
}) => {

  const [isOpen, setIsOpen] = useState(false);
  
  // Close preview when triggered by parent (e.g., tour completion)
  useEffect(() => {
    if (closePreviewTrigger > 0) {
      setIsOpen(false);
    }
  }, [closePreviewTrigger]);
  
  // Sync with external open state
  useEffect(() => {
    if (externalOpen) {
      setIsOpen(true);
      // Reset external state after opening
      if (onExternalOpenChange) {
        onExternalOpenChange(false);
      }
    }
  }, [externalOpen, onExternalOpenChange]);
  
  // Notify tour when preview opens
  useEffect(() => {
    if (isOpen && onPreviewOpen) {
      onPreviewOpen();
    }
  }, [isOpen, onPreviewOpen]);
  
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [forceShowTour, setForceShowTour] = useState(false);
  const startY = useRef(0);
  const contentRef = useRef(null);
  
  // Editor collapsed state - ALWAYS start collapsed when preview opens
  const storageKey = slug ? `editor_collapsed_${slug}` : 'editor_collapsed';
  const [editorCollapsed, setEditorCollapsed] = useState(true); // Always start collapsed
  
  // Reset editor to collapsed whenever preview opens
  useEffect(() => {
    if (isOpen) {
      setEditorCollapsed(true);
    }
  }, [isOpen]);
  
  // Track if user has ever opened the editor (for animation)
  const editorOpenedKey = slug ? `editor_opened_${slug}` : 'editor_opened';
  const [hasOpenedEditor, setHasOpenedEditor] = useState(() => {
    try {
      return localStorage.getItem(editorOpenedKey) === 'true';
    } catch {
      return false;
    }
  });
  
  // Toggle editor collapsed state
  const toggleEditorCollapsed = () => {
    const newState = !editorCollapsed;
    setEditorCollapsed(newState);
    
    // When user EXPANDS the editor (newState = false), mark Step 2 complete
    if (!newState) {
      // Mark Step 2 as complete - user has opened the editor
      if (onStep2Complete) {
        onStep2Complete();
      }
      
      // Notify tour that preview is open
      if (onPreviewOpen) {
        onPreviewOpen();
      }
      
      // Notify tour that editor is expanded
      if (onEditorExpand) {
        onEditorExpand();
      }
      
      if (!hasOpenedEditor) {
        setHasOpenedEditor(true);
        try {
          localStorage.setItem(editorOpenedKey, 'true');
        } catch {}
      }
    }
    
    try {
      localStorage.setItem(storageKey, String(newState));
    } catch {
      // localStorage not available
    }
  };
  
  // CRITICAL: Initialize localConfig from persisted business data (source of truth)
  // This ensures preview survives deployments/reloads
  const getPersistedConfig = () => {
    const persisted = business?.field_config || {};
    return {
      form_background_color: persisted.form_background_color || "",
      form_card_background_color: persisted.form_card_background_color || "",
      form_text_color: persisted.form_text_color || "",
      form_font_family: persisted.form_font_family || "",
      color_mode: persisted.color_mode || "light",
      logo_size: persisted.logo_size || 96,
      tagline: persisted.tagline || "",
      submit_button_text: persisted.submit_button_text || "",
      phone_enabled: persisted.phone_enabled ?? true,
      phone_required: persisted.phone_required ?? false,
      address_enabled: persisted.address_enabled ?? false,
      address_required: persisted.address_required ?? false,
      photo_mode: persisted.photo_mode || "off",
      photo_instructions: persisted.photo_instructions || "",
      name_required: persisted.name_required ?? true,
      email_required: persisted.email_required ?? true,
      message_enabled: persisted.message_enabled ?? true,
      file_upload_enabled: persisted.file_upload_enabled ?? false,
      file_upload_mode: persisted.file_upload_mode || "off",
      file_upload_instructions: persisted.file_upload_instructions || "",
      hide_pricing: persisted.hide_pricing ?? false,
      custom_questions: persisted.custom_questions || [],
      // Shop customization fields (stored at business top level, not field_config)
      shop_headline: business?.shop_headline || "",
      shop_headline_size: business?.shop_headline_size || "24",
      shop_headline_color: business?.shop_headline_color || "#111827",
      shop_footer: business?.shop_footer || "",
      shop_footer_size: business?.shop_footer_size || "14",
      shop_footer_color: business?.shop_footer_color || "#6b7280",
    };
  };
  
  const [localConfig, setLocalConfig] = useState(getPersistedConfig);
  
  // Local logo state for immediate preview updates
  const [localLogoUrl, setLocalLogoUrl] = useState(business?.logo_url || logoUrl);
  
  // Re-hydrate from business data when it changes (e.g., after save/refresh)
  useEffect(() => {
    if (business) {
      setLocalConfig(getPersistedConfig());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.field_config, business?.field_config?.custom_questions, business?.shop_headline, business?.shop_footer]);
  
  // Sync custom_questions from fieldConfig prop for live preview updates (before save)
  const customQuestionsJson = JSON.stringify(fieldConfig?.custom_questions);
  useEffect(() => {
    if (fieldConfig?.custom_questions) {
      setLocalConfig(prev => ({ ...prev, custom_questions: fieldConfig.custom_questions }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customQuestionsJson]);
  
  // Sync logo URL from business data
  useEffect(() => {
    setLocalLogoUrl(business?.logo_url || logoUrl);
  }, [business?.logo_url, logoUrl]);
  
  const visibilityClass = showOnAllScreens ? "" : "lg:hidden";
  
  // Update local config IMMEDIATELY (optimistic UI) and notify parent
  const updateLocalConfig = (key, value) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    // Notify parent for syncing (but preview already updated)
    if (onFieldConfigChange) {
      onFieldConfigChange(key, value);
    }
  };
  
  // Handle logo file selection - IMMEDIATE preview update
  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLogoFile(file);
      setLocalLogoUrl(previewUrl); // Immediate preview update
      setHasChanges(true);
      if (onLogoChange) {
        onLogoChange(file);
      }
    }
  };
  
  // Remove logo - IMMEDIATE preview update with background save
  const handleRemoveLogo = async () => {
    // Immediately update preview (optimistic UI)
    setLocalLogoUrl(null);
    setLogoFile(null);
    setHasChanges(true);
    
    // Notify parent immediately
    if (onLogoChange) {
      onLogoChange(null);
    }
    
    // Background API call to persist removal
    if (slug) {
      try {
        await api.deleteLogo(slug);
        toast.success("Logo removed");
      } catch (error) {
        // Revert on failure
        setLocalLogoUrl(business?.logo_url || logoUrl);
        toast.error("Failed to remove logo");
      }
    }
  };
  
  // Handle save
  const handleSave = async () => {
    if (!onSave) {
      toast.error("Save not available");
      return;
    }
    setSaving(true);
    try {
      await onSave(localConfig, logoFile);
      setHasChanges(false);
      setLogoFile(null);
      
      // Try to copy link to clipboard (may fail on some browsers)
      let linkCopied = false;
      if (slug) {
        const formLink = `${window.location.origin}/api/share/${slug}`;
        try {
          await navigator.clipboard.writeText(formLink);
          linkCopied = true;
        } catch (e) {
          // Clipboard access denied - that's ok
        }
      }
      toast.success(linkCopied ? "Saved! Link copied 📋" : "Saved!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };
  
  // Current logo URL for preview (local state takes precedence)
  const currentLogoUrl = localLogoUrl;
  
  // Lock body scroll when preview is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  const handleTouchStart = (e) => {
    if (contentRef.current && contentRef.current.contains(e.target)) {
      if (contentRef.current.scrollTop > 0) {
        return;
      }
    }
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };
  
  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0) {
      setDragY(diff);
    }
  };
  
  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragY > 100) {
      setIsOpen(false);
    }
    setDragY(0);
  };
  
  return createPortal(
    <>
      {/* Floating Preview Button */}
      <div 
        className={visibilityClass}
        style={{
          position: 'fixed',
          bottom: '24px',
          left: '16px',
          right: '16px',
          zIndex: 40
        }}
      >
        <Button 
          onClick={() => setIsOpen(true)}
          className="w-full bg-slate-900 hover:bg-slate-900 text-white shadow-xl py-5 text-base font-medium animate-shine-glow"
          data-testid="mobile-preview-btn"
          data-tour="preview-button"
        >
          <span className="mr-2">👁️</span> Preview & Customize 👆 (Tap here anytime)
        </Button>
        <style>{`
          @keyframes shine-glow {
            0%, 100% { 
              box-shadow: 0 4px 25px rgba(99, 102, 241, 0.6),
                          0 0 20px rgba(251, 191, 36, 0.8),
                          0 0 40px rgba(251, 191, 36, 0.4),
                          0 0 0 0 rgba(255, 215, 0, 1);
            }
            50% { 
              box-shadow: 0 4px 30px rgba(99, 102, 241, 0.8),
                          0 0 35px rgba(251, 191, 36, 1),
                          0 0 60px rgba(251, 191, 36, 0.6),
                          0 0 8px rgba(255, 215, 0, 0.9);
            }
          }
          .animate-shine-glow {
            animation: shine-glow 1.2s ease-in-out infinite;
          }
        `}</style>
      </div>
      
      {/* Slide-up Preview Panel */}
      {isOpen && (
        <div 
          className={visibilityClass}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Backdrop */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              opacity: isDragging ? 1 - (dragY / 300) : 1
            }}
            onClick={() => setIsOpen(false)}
          />
          
          {/* Phone-like container for desktop, full-width for mobile */}
          <div 
            className="relative w-full sm:w-[390px] h-full sm:h-[85vh] sm:max-h-[844px] sm:rounded-[40px] sm:my-4"
            style={{
              backgroundColor: '#1a1a1a',
              padding: window.innerWidth >= 640 ? '12px' : '0',
              boxShadow: window.innerWidth >= 640 ? '0 25px 80px rgba(0,0,0,0.5)' : 'none'
            }}
          >
            {/* Phone notch for desktop */}
            <div className="hidden sm:block absolute top-4 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-20" />
            
            {/* Panel content */}
            <div 
              style={{
                height: '100%',
                backgroundColor: 'white',
                borderRadius: window.innerWidth >= 640 ? '32px' : '16px 16px 0 0',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transform: `translateY(${dragY}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                position: 'relative'
              }}
            >
              {/* Handle bar */}
              <div 
                className="flex-shrink-0 bg-white pt-3 pb-2 px-4 border-b cursor-grab active:cursor-grabbing rounded-t-[32px] sm:rounded-t-[32px]"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-2" />
                <p className="text-xs text-center text-gray-400 mb-2 sm:hidden">Swipe down to close</p>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 text-sm">Preview & Customize</h3>
                  <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="text-gray-500">✕</Button>
                </div>
              </div>
              
              {/* Preview Content - Scrollable */}
              <div 
                ref={contentRef}
                className="flex-1 overflow-y-auto overscroll-contain p-3"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <IntakePreview
                  intakeMode={intakeMode}
                  fieldConfig={localConfig}
                  services={services}
                  logoUrl={currentLogoUrl}
                  businessName={businessName}
                  backgroundColor={localConfig?.form_background_color}
                  cardBackgroundColor={localConfig?.form_card_background_color}
                  textColor={localConfig?.form_text_color}
                  fontFamily={localConfig?.form_font_family}
                  logoSize={localConfig?.logo_size}
                  hideLabel={true}
                  onIntakeModeChange={onIntakeModeChange}
                  slug={slug}
                  shopHeadline={localConfig?.shop_headline}
                  shopHeadlineSize={localConfig?.shop_headline_size}
                  shopHeadlineColor={localConfig?.shop_headline_color}
                  shopFooter={localConfig?.shop_footer}
                  shopFooterSize={localConfig?.shop_footer_size}
                  shopFooterColor={localConfig?.shop_footer_color}
                />
                <div className="h-4" />
              </div>
              
              {/* Bottom Toolbar - With Minimize/Expand Toggle */}
              <div className="flex-shrink-0 bg-white border-t" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
              {/* Collapse/Expand Header - More visually obvious */}
              <div 
                className={`flex items-center justify-between px-4 py-3 cursor-pointer border-b transition-all ${
                  editorCollapsed 
                    ? 'bg-slate-900 hover:bg-slate-900 animate-customize-bounce' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
                onClick={toggleEditorCollapsed}
                data-tour="customize-expand"
              >
                <span className={`text-sm font-semibold ${editorCollapsed ? 'text-white' : 'text-gray-700'}`}>
                  {editorCollapsed 
                    ? "⚙️ Customize Your Form" 
                    : (intakeMode === "product_catalog" ? "🛍️ Shop Editor" : "⚙️ Form Settings")}
                </span>
                <div className="flex items-center gap-2">
                  {editorCollapsed ? (
                    <span className="text-sm bg-white text-slate-900 px-3 py-1 rounded-full font-bold shadow-sm">
                      Open ▼
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500 px-3 py-1 rounded-full border border-gray-300">
                      Close ▲
                    </span>
                  )}
                  {hasChanges && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Unsaved</span>}
                </div>
              </div>
              <style>{`
                @keyframes customize-bounce {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-4px); }
                }
                .animate-customize-bounce {
                  animation: customize-bounce 2s ease-in-out infinite;
                }
              `}</style>
              
              {/* Collapsible Editor Content */}
              {!editorCollapsed && (
                <>
                  {/* Row 1: Visual customization */}
                  <div className="flex flex-wrap items-center gap-2 p-2 border-b">
                    <div className="flex items-center gap-1">
                      <label className="cursor-pointer flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">
                        {currentLogoUrl ? "Change Logo" : "Upload Logo"}
                        <input type="file" accept="image/*" onChange={handleLogoSelect} className="hidden" />
                      </label>
                      {currentLogoUrl && (
                        <button onClick={handleRemoveLogo} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">
                          Remove
                        </button>
                      )}
                    </div>
                    {currentLogoUrl && (
                      <input type="range" min="48" max="400" value={localConfig.logo_size || 96} onChange={(e) => updateLocalConfig("logo_size", parseInt(e.target.value))} className="w-14 h-2 bg-gray-200 rounded-lg cursor-pointer" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} />
                    )}
                    <div className="flex items-center gap-2" data-tour="color-settings">
                      <span className="text-xs text-gray-500">Theme:</span>
                      <button 
                        onClick={() => {
                          updateLocalConfig("color_mode", "light");
                          updateLocalConfig("form_background_color", "#f9fafb");
                          updateLocalConfig("form_card_background_color", "#ffffff");
                          updateLocalConfig("form_text_color", "#111827");
                        }}
                        className={`px-2 py-1 text-xs rounded ${localConfig.color_mode !== 'dark' ? 'bg-gray-200 text-gray-800 font-medium' : 'bg-gray-100 text-gray-500'}`}
                      >
                        Light
                      </button>
                      <button 
                        onClick={() => {
                          updateLocalConfig("color_mode", "dark");
                          updateLocalConfig("form_background_color", "#000000");
                          updateLocalConfig("form_card_background_color", "#0a0a0a");
                          updateLocalConfig("form_text_color", "#ffffff");
                        }}
                        className={`px-2 py-1 text-xs rounded ${localConfig.color_mode === 'dark' ? 'bg-gray-800 text-white font-medium' : 'bg-gray-100 text-gray-500'}`}
                      >
                        Dark
                      </button>
                    </div>
                    <select value={localConfig.form_font_family || ""} onChange={(e) => updateLocalConfig("form_font_family", e.target.value)} className="text-xs border rounded px-1 py-1">
                      <option value="">Font</option>
                      <option value="serif">Serif</option>
                      <option value="monospace">Mono</option>
                    </select>
                  </div>
                  
                  {/* Row 2: Text customization - Hide in Shop mode */}
                  {intakeMode !== "product_catalog" && (
                    <div className="flex items-center gap-2 p-2 border-b">
                      <input 
                        type="text" 
                        value={localConfig.tagline || ""} 
                        onChange={(e) => updateLocalConfig("tagline", e.target.value)} 
                        placeholder="Tagline (e.g., Request a quote today)"
                        className="flex-1 text-xs border rounded px-2 py-1"
                        style={{ fontSize: '16px' }}
                        onFocus={(e) => { const el = e.target; setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350); }}
                      />
                      <input 
                        type="text" 
                        value={localConfig.submit_button_text || ""} 
                        onChange={(e) => updateLocalConfig("submit_button_text", e.target.value)} 
                        placeholder="Button text"
                        className="w-24 text-xs border rounded px-2 py-1"
                        style={{ fontSize: '16px' }}
                        onFocus={(e) => { const el = e.target; setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350); }}
                      />
                    </div>
                  )}
                  
                  {/* Row 3: Field visibility and requirements - Hide in Shop mode */}
                  {intakeMode !== "product_catalog" && (
                    <div className="flex items-center justify-between gap-2 p-2 border-b text-xs" data-tour="field-settings">
                      <span className="text-gray-500">Show:</span>
                      <label className="flex items-center gap-1">
                        <input type="checkbox" checked={localConfig.phone_enabled ?? true} onChange={(e) => updateLocalConfig("phone_enabled", e.target.checked)} className="w-3 h-3" />
                        Phone
                      </label>
                      <label className="flex items-center gap-1">
                        <input type="checkbox" checked={localConfig.address_enabled ?? false} onChange={(e) => updateLocalConfig("address_enabled", e.target.checked)} className="w-3 h-3" />
                        Address
                      </label>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-500" data-tour="required-settings">Required:</span>
                      <label className="flex items-center gap-1">
                        <input type="checkbox" checked={localConfig.phone_required ?? false} onChange={(e) => updateLocalConfig("phone_required", e.target.checked)} className="w-3 h-3" />
                        Phone
                      </label>
                      <label className="flex items-center gap-1">
                        <input type="checkbox" checked={localConfig.address_required ?? false} onChange={(e) => updateLocalConfig("address_required", e.target.checked)} className="w-3 h-3" />
                        Address
                      </label>
                    </div>
                  )}
                  
                  {/* Row 4: Photo Upload Settings - Hide in Shop mode */}
                  {intakeMode !== "product_catalog" && (
                    <div className="flex items-center gap-3 p-2 border-b text-xs">
                      <span className="text-gray-500">📷 Photos:</span>
                      <select 
                        value={localConfig.photo_mode || "off"} 
                        onChange={(e) => updateLocalConfig("photo_mode", e.target.value)}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value="off">Off</option>
                        <option value="optional">Optional</option>
                        <option value="required">Required</option>
                      </select>
                      {localConfig.photo_mode && localConfig.photo_mode !== "off" && (
                        <input 
                          type="text"
                          value={localConfig.photo_instructions || ""}
                          onChange={(e) => updateLocalConfig("photo_instructions", e.target.value)}
                          placeholder="Photo instructions..."
                          className="flex-1 text-xs border rounded px-2 py-1"
                          style={{ fontSize: '16px' }}
                          onFocus={(e) => { const el = e.target; setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350); }}
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Row 5: Hide Pricing - Hide in Shop mode */}
                  {intakeMode !== "product_catalog" && (
                    <div className="flex items-center justify-between p-2 border-b text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">💰 Hide Pricing:</span>
                        <span className="text-gray-400">(for custom quotes)</span>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={localConfig.hide_pricing || false} 
                          onChange={(e) => updateLocalConfig("hide_pricing", e.target.checked)} 
                          className="w-4 h-4 rounded"
                        />
                        <span className={localConfig.hide_pricing ? "text-slate-900 font-medium" : "text-gray-500"}>
                          {localConfig.hide_pricing ? "On" : "Off"}
                        </span>
                      </label>
                    </div>
                  )}
                  
                  {/* Row 6: File Upload (for large project files) - Hidden */}
                  <div className="flex items-center gap-3 p-2 border-b text-xs" style={{ display: "none" }}>
                    <span className="text-gray-500">📁 Files:</span>
                    <select 
                      value={localConfig.file_upload_mode || "off"} 
                      onChange={(e) => updateLocalConfig("file_upload_mode", e.target.value)}
                      className="text-xs border rounded px-2 py-1"
                    >
                      <option value="off">Off</option>
                      <option value="optional">Optional</option>
                      <option value="required">Required</option>
                    </select>
                    {localConfig.file_upload_mode && localConfig.file_upload_mode !== "off" && (
                      <input 
                        type="text"
                        value={localConfig.file_upload_instructions || ""}
                        onChange={(e) => updateLocalConfig("file_upload_instructions", e.target.value)}
                        placeholder="e.g., Upload your project files"
                        className="flex-1 text-xs border rounded px-2 py-1"
                      />
                    )}
                  </div>
                  
                  {/* Shop Customization - Only show in product_catalog mode */}
                  {intakeMode === "product_catalog" && (
                    <>
                      <div className="p-2 bg-blue-50 border-b">
                        <span className="text-xs font-semibold text-slate-900">🛍️ Shop Page Customization</span>
                      </div>
                      <div className="p-2 border-b space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-16">Headline:</span>
                          <input 
                            type="text"
                            value={localConfig.shop_headline || ""}
                            onChange={(e) => updateLocalConfig("shop_headline", e.target.value)}
                            placeholder="Welcome to our shop"
                            className="flex-1 text-xs border rounded px-2 py-1"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-16">Size/Color:</span>
                          <select 
                            value={localConfig.shop_headline_size || "24"}
                            onChange={(e) => updateLocalConfig("shop_headline_size", e.target.value)}
                            className="text-xs border rounded px-1 py-1"
                          >
                            <option value="18">Small</option>
                            <option value="24">Medium</option>
                            <option value="32">Large</option>
                          </select>
                          <input 
                            type="color" 
                            value={localConfig.shop_headline_color || "#111827"} 
                            onChange={(e) => updateLocalConfig("shop_headline_color", e.target.value)} 
                            className="w-6 h-6 rounded border cursor-pointer"
                          />
                        </div>
                      </div>
                      <div className="p-2 border-b space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-16">Footer:</span>
                          <input 
                            type="text"
                            value={localConfig.shop_footer || ""}
                            onChange={(e) => updateLocalConfig("shop_footer", e.target.value)}
                            placeholder="Thank you for shopping with us"
                            className="flex-1 text-xs border rounded px-2 py-1"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-16">Size/Color:</span>
                          <select 
                            value={localConfig.shop_footer_size || "14"}
                            onChange={(e) => updateLocalConfig("shop_footer_size", e.target.value)}
                            className="text-xs border rounded px-1 py-1"
                          >
                            <option value="12">Small</option>
                            <option value="14">Medium</option>
                            <option value="16">Large</option>
                          </select>
                          <input 
                            type="color" 
                            value={localConfig.shop_footer_color || "#6b7280"} 
                            onChange={(e) => updateLocalConfig("shop_footer_color", e.target.value)} 
                            className="w-6 h-6 rounded border cursor-pointer"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              
              {/* Save Button - Always visible */}
              {onSave && (
                <div className="p-3 space-y-2" data-tour="save-button">
                  <Button className="w-full bg-blue-700 hover:bg-blue-800 py-4 text-base font-semibold" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : (hasChanges ? "💾 Save Changes" : "✓ Saved")}
                  </Button>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

// ============== SETTINGS SECTION ==============
const SettingsSection = ({ slug, business, services, onRefresh }) => {
  const [saving, setSaving] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  
  // Custom domain publishing state
  const [publishMode, setPublishMode] = useState(business?.publish_mode || "zipleadpro");
  const [customDomain, setCustomDomain] = useState(business?.custom_domain || "");
  const [publishing, setPublishing] = useState(false);
  const [domainStatus, setDomainStatus] = useState(business?.domain_status || "none");
  
  // Subscription management state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(null); // 'pause' | 'cancel' | null
  const [confirmationText, setConfirmationText] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [countdownActive, setCountdownActive] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    notify_email: "",
    reply_to_email: "",
    intake_mode: "simple_form",
    auto_followup_quotes: true,
    field_config: {
      name_required: true,
      email_required: true,
      phone_enabled: true,
      phone_required: false,
      address_enabled: false,
      file_upload_enabled: false,
      message_enabled: true,
      photo_mode: "off",
      photo_instructions: "",
      form_background_color: "",
      form_card_background_color: "",
      color_mode: "light",
      form_text_color: "",
      form_font_family: "",
      logo_size: 96
    }
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formUrl, setFormUrl] = useState("");
  
  // Quote Resources state
  const [quoteResources, setQuoteResources] = useState({
    pdf_url: "",
    pdf_label: "",
    pdf_enabled: false,
    video_url: "",
    video_label: "",
    video_enabled: false
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);

  // Check if setup is complete (needed for gating embed code)
  const hasBusinessName = business?.name && business.name.trim().length > 0;
  const hasContactEmail = business?.notify_email && business.notify_email.trim().length > 0;
  const hasServices = services && services.filter(s => s.active).length > 0;
  const isSetupComplete = hasBusinessName && hasContactEmail && hasServices;
  
  // ZipLeadPro IP for DNS records (placeholder - should come from config)
  const ZIPLEADPRO_IP = "76.76.21.21";

  useEffect(() => {
    api.getEmbedCode(slug).then(data => setFormUrl(data.form_url)).catch(() => {});
  }, [slug]);

  // Handle logo file selection with immediate preview
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    } else {
      setLogoPreview(null);
    }
  };

  // Sync publish state from business prop
  useEffect(() => {
    if (business) {
      setPublishMode(business.publish_mode || "zipleadpro");
      setCustomDomain(business.custom_domain || "");
      setDomainStatus(business.domain_status || "none");
      // Load quote resources
      if (business.quote_resources) {
        setQuoteResources({
          pdf_url: business.quote_resources.pdf_url || "",
          pdf_label: business.quote_resources.pdf_label || "",
          pdf_enabled: business.quote_resources.pdf_enabled || false,
          video_url: business.quote_resources.video_url || "",
          video_label: business.quote_resources.video_label || "",
          video_enabled: business.quote_resources.video_enabled || false
        });
      }
    }
  }, [business]);

  // Handle publish to custom domain
  const handlePublish = async () => {
    if (publishMode === "custom_domain" && !customDomain.trim()) {
      toast.error("Please enter a domain");
      return;
    }
    
    setPublishing(true);
    try {
      await api.publishDomain(slug, publishMode, publishMode === "custom_domain" ? customDomain : null);
      toast.success(publishMode === "custom_domain" 
        ? "Domain configured! DNS verification in progress..." 
        : "Published to ZipLeadPro URL");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  // Handle manual DNS verification
  const [verifying, setVerifying] = useState(false);
  const handleVerifyDNS = async () => {
    setVerifying(true);
    try {
      const result = await api.verifyDomain(slug);
      if (result.status === "active") {
        toast.success("Domain verified and active!");
        setDomainStatus("active");
      } else {
        toast.error(result.error || "DNS not yet configured");
      }
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const result = await api.createBillingPortal(slug);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      // Handle the case where stripe_customer_id is missing
      const errorMsg = error.response?.data?.detail || "Unable to open billing portal";
      if (errorMsg.includes("No subscription found")) {
        toast.error("No billing record found. Please contact support if you believe this is an error.");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoadingPortal(false);
    }
  };

  // Handle PDF upload for quote resources
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error("Please upload a PDF file");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error("File size must be under 10MB");
      return;
    }
    
    setUploadingPdf(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(`${API}/tenants/${slug}/upload-quote-pdf`, formDataUpload);
      // Store relative URL - backend will prepend correct domain for emails
      const pdfUrl = response.data.pdf_url;
      setQuoteResources(prev => ({ ...prev, pdf_url: pdfUrl, pdf_enabled: true }));
      toast.success("PDF uploaded! Click Save to apply.");
    } catch (error) {
      toast.error("Failed to upload PDF");
      console.error("PDF upload error:", error);
    } finally {
      setUploadingPdf(false);
    }
  };

  // Save quote resources
  const handleSaveQuoteResources = async () => {
    setSaving(true);
    try {
      await api.updateBusiness(slug, { quote_resources: quoteResources });
      toast.success("Quote resources saved");
      onRefresh();
    } catch (error) {
      toast.error("Failed to save quote resources");
    } finally {
      setSaving(false);
    }
  };

  // Handle subscription action (pause/cancel) with confirmation modal
  const handleSubscriptionAction = async (action) => {
    setLoadingPortal(true);
    try {
      await api.adminManageSubscription(slug, action);
      toast.success(`Subscription ${action === 'pause' ? 'paused' : 'canceled'} successfully`);
      onRefresh();
    } catch (error) {
      toast.error(`Failed to ${action} subscription`);
    } finally {
      setLoadingPortal(false);
      closeSubscriptionModal();
    }
  };

  // Open confirmation modal
  const openSubscriptionModal = (action) => {
    setShowSubscriptionModal(action);
    setConfirmationText('');
    setCountdown(5);
    setCountdownActive(false);
  };

  // Close confirmation modal
  const closeSubscriptionModal = () => {
    setShowSubscriptionModal(null);
    setConfirmationText('');
    setCountdown(5);
    setCountdownActive(false);
  };

  // Start countdown when text matches
  useEffect(() => {
    if (!showSubscriptionModal) return;
    
    const requiredText = showSubscriptionModal.toUpperCase();
    const isMatch = confirmationText.toUpperCase() === requiredText;
    
    if (isMatch && !countdownActive) {
      setCountdownActive(true);
      setCountdown(5);
    } else if (!isMatch && countdownActive) {
      setCountdownActive(false);
      setCountdown(5);
    }
  }, [confirmationText, showSubscriptionModal, countdownActive]);

  // Countdown timer
  useEffect(() => {
    if (!countdownActive || countdown <= 0) return;
    
    const timer = setTimeout(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [countdownActive, countdown]);

  useEffect(() => {
    if (business) {
      setFormData({
        name: business.name || "",
        notify_email: business.notify_email || "",
        reply_to_email: business.reply_to_email || "",
        intake_mode: business.intake_mode || "simple_form",
        auto_followup_quotes: business.auto_followup_quotes !== false,
        chemicals_enabled: !!business.chemicals_enabled,
        state_code: business.state_code || "",
        applicator_license: business.applicator_license || "",
        field_config: {
          name_required: business.field_config?.name_required ?? true,
          email_required: business.field_config?.email_required ?? true,
          phone_enabled: business.field_config?.phone_enabled ?? true,
          phone_required: business.field_config?.phone_required ?? false,
          address_enabled: business.field_config?.address_enabled ?? false,
          address_required: business.field_config?.address_required ?? false,
          file_upload_enabled: business.field_config?.file_upload_enabled ?? false,
          file_upload_mode: business.field_config?.file_upload_mode || "off",
          file_upload_instructions: business.field_config?.file_upload_instructions || "",
          message_enabled: business.field_config?.message_enabled ?? true,
          photo_mode: business.field_config?.photo_mode || "off",
          photo_instructions: business.field_config?.photo_instructions || "",
          form_background_color: business.field_config?.form_background_color || "",
          form_card_background_color: business.field_config?.form_card_background_color || "",
          color_mode: business.field_config?.color_mode || "light",
          form_text_color: business.field_config?.form_text_color || "",
          form_font_family: business.field_config?.form_font_family || "",
          logo_size: business.field_config?.logo_size || 96,
          tagline: business.field_config?.tagline || "",
          submit_button_text: business.field_config?.submit_button_text || "",
          hide_pricing: business.field_config?.hide_pricing ?? false,
          custom_questions: business.field_config?.custom_questions || []
        }
      });
    }
  }, [business]);

  const updateFieldConfig = (key, value) => {
    setFormData(prev => ({
      ...prev,
      field_config: { ...prev.field_config, [key]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateBusiness(slug, formData);
      
      if (logoFile) {
        await api.uploadLogo(slug, logoFile);
        setLogoFile(null);
        setLogoPreview(null);
      }
      
      toast.success("Settings saved successfully!");
      await onRefresh();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Business Settings</h2>
      
      {/* Publish Section - Simplified */}
      <Card className="mb-4 sm:mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Your Public Link</CardTitle>
          <CardDescription>Share this link with customers to access your intake form</CardDescription>
        </CardHeader>
        <CardContent>
          {/* ZipLeadPro Link */}
          <div className="p-3 rounded-lg border-2 border-blue-800 bg-blue-50">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">ZipLeadPro Link</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 break-all">{formUrl || `${window.location.origin}/api/share/${slug}`}</code>
                  <button 
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(formUrl || `${window.location.origin}/api/share/${slug}`, 'Link'); }}
                    className="text-xs text-slate-900 hover:text-slate-900 self-start"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="space-y-4 sm:space-y-6">
        {/* Intake Form Settings FIRST - with side-by-side preview */}
        <Card>
          <CardHeader>
            <CardTitle>Intake Form Settings</CardTitle>
            <CardDescription>Customize your customer request form - preview updates in real-time</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Desktop: Side-by-side layout with fixed preview */}
            <div className="hidden lg:flex lg:gap-6" style={{ minHeight: '600px' }}>
              {/* Settings Column - Scrollable */}
              <div className="flex-1 space-y-4 overflow-y-auto pr-4" style={{ maxHeight: '70vh' }}>
                <div className="space-y-2">
                  <Label>Form Style</Label>
                  <Select
                    value={formData.intake_mode}
                    onValueChange={(v) => setFormData({ ...formData, intake_mode: v })}
                  >
                    <SelectTrigger data-testid="intake-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simple_form">Simple Contact Form</SelectItem>
                      <SelectItem value="service_buttons">Pick Services + Get Quote</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Logo Section */}
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    data-testid="logo-upload-input"
                  />
                  {(logoPreview || business?.logo_url) && (
                    <div className="flex items-center gap-3 mt-2">
                      <img 
                        src={logoPreview || `${STATIC_URL}${business.logo_url}`} 
                        alt="Logo preview" 
                        className="object-contain" 
                        style={{ height: `${formData.field_config.logo_size || 96}px` }}
                        onError={(e) => e.target.style.display = 'none'}
                        data-testid="logo-preview-img"
                      />
                      {business?.logo_url && !logoPreview && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={async () => {
                            if (window.confirm("Remove your logo?")) {
                              try {
                                await api.deleteLogo(slug);
                                toast.success("Logo removed");
                                onRefresh();
                              } catch (err) {
                                toast.error("Failed to remove logo");
                              }
                            }
                          }}
                          data-testid="remove-logo-btn"
                        >
                          Remove Logo
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Logo Size Slider */}
                <div className="space-y-2">
                  <Label>Logo Size: {formData.field_config.logo_size || 96}px</Label>
                  <input
                    type="range"
                    min="48"
                    max="400"
                    value={formData.field_config.logo_size || 96}
                    onChange={(e) => updateFieldConfig("logo_size", parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    data-testid="logo-size-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Small</span>
                    <span>Large</span>
                  </div>
                </div>
                
                {/* Photo Upload Mode */}
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
                  <Label className="flex items-center gap-2">
                    <span>📷</span> Photo Upload
                  </Label>
                  <Select
                    value={formData.field_config.photo_mode}
                    onValueChange={(v) => updateFieldConfig("photo_mode", v)}
                  >
                    <SelectTrigger data-testid="photo-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off - No photo upload</SelectItem>
                      <SelectItem value="optional">Optional - Customers can upload photos</SelectItem>
                      <SelectItem value="required">Required - Customers must upload photos</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.field_config.photo_mode !== "off" && (
                    <div className="space-y-2 mt-2">
                      <Label className="text-sm">Photo Instructions</Label>
                      <Textarea
                        value={formData.field_config.photo_instructions}
                        onChange={(e) => updateFieldConfig("photo_instructions", e.target.value)}
                        placeholder="e.g., 'Please upload photos of the area showing current condition'"
                        rows={2}
                        className="text-sm"
                        data-testid="photo-instructions-input"
                      />
                    </div>
                  )}
                </div>
                
                <Separator className="my-4" />
                
                {/* Appearance Section */}
                <p className="text-sm font-medium text-gray-700">Appearance</p>
                
                {/* Background Color */}
                <div className="space-y-2">
                  <Label>Form Background Color</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.field_config.form_background_color || "#eff6ff"}
                      onChange={(e) => updateFieldConfig("form_background_color", e.target.value)}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                    <span className="text-sm text-gray-500">
                      {formData.field_config.form_background_color || "Default"}
                    </span>
                    {formData.field_config.form_background_color && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateFieldConfig("form_background_color", "")}
                        className="text-xs"
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Text Color */}
                <div className="space-y-2">
                  <Label>Text Color</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.field_config.form_text_color || "#111827"}
                      onChange={(e) => updateFieldConfig("form_text_color", e.target.value)}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                    <span className="text-sm text-gray-500">
                      {formData.field_config.form_text_color || "Default"}
                    </span>
                    {formData.field_config.form_text_color && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateFieldConfig("form_text_color", "")}
                        className="text-xs"
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Font Style */}
                <div className="space-y-2">
                  <Label>Font Style</Label>
                  <Select
                    value={formData.field_config.form_font_family || "default"}
                    onValueChange={(v) => updateFieldConfig("form_font_family", v === "default" ? "" : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Default (System)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default (System)</SelectItem>
                      <SelectItem value="Inter, sans-serif">Inter (Modern)</SelectItem>
                      <SelectItem value="Georgia, serif">Georgia (Classic)</SelectItem>
                      <SelectItem value="Verdana, sans-serif">Verdana (Clean)</SelectItem>
                      <SelectItem value="'Trebuchet MS', sans-serif">Trebuchet (Friendly)</SelectItem>
                      <SelectItem value="'Courier New', monospace">Courier (Technical)</SelectItem>
                      <SelectItem value="Arial, sans-serif">Arial (Simple)</SelectItem>
                      <SelectItem value="'Times New Roman', serif">Times (Traditional)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Save Button */}
                <Button onClick={handleSave} disabled={saving} className="w-full mt-4" data-testid="save-form-settings-btn">
                  {saving ? "Saving..." : "💾 Save Form Settings"}
                </Button>
              </div>
              
              {/* Preview Column - Fixed position on right */}
              <div className="w-[400px] flex-shrink-0">
                <div className="sticky top-4">
                  <IntakePreview
                    intakeMode={formData.intake_mode}
                    fieldConfig={formData.field_config}
                    services={services}
                    logoUrl={logoPreview || business?.logo_url}
                    businessName={formData.name || business?.name}
                    backgroundColor={formData.field_config.form_background_color}
                    textColor={formData.field_config.form_text_color}
                    fontFamily={formData.field_config.form_font_family}
                    logoSize={formData.field_config.logo_size}
                    slug={slug}
                    shopHeadline={formData.shop_headline}
                    shopHeadlineSize={formData.shop_headline_size}
                    shopHeadlineColor={formData.shop_headline_color}
                    shopFooter={formData.shop_footer}
                    shopFooterSize={formData.shop_footer_size}
                    shopFooterColor={formData.shop_footer_color}
                  />
                </div>
              </div>
            </div>

            {/* Mobile: Settings with floating preview button */}
            <div className="lg:hidden space-y-4">
              <div className="space-y-2">
                <Label>Form Style</Label>
                <Select
                  value={formData.intake_mode}
                  onValueChange={(v) => setFormData({ ...formData, intake_mode: v })}
                >
                  <SelectTrigger data-testid="intake-mode-select-mobile">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple_form">Simple Contact Form</SelectItem>
                    <SelectItem value="service_buttons">Pick Services + Get Quote</SelectItem>
                    <SelectItem value="product_catalog">Online Store</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Product Management - Show when Product Catalog mode is selected */}
              {formData.intake_mode === "product_catalog" && (
                <ProductManagementSection slug={slug} />
              )}
              
              {/* Logo Section */}
              <div className="space-y-2">
                <Label>Logo</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                />
                {(logoPreview || business?.logo_url) && (
                  <div className="flex items-center gap-3 mt-2">
                    <img 
                      src={logoPreview || `${STATIC_URL}${business.logo_url}`} 
                      alt="Logo preview" 
                      className="object-contain" 
                      style={{ height: `${formData.field_config.logo_size || 96}px` }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    {business?.logo_url && !logoPreview && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={async () => {
                          if (window.confirm("Remove your logo?")) {
                            try {
                              await api.deleteLogo(slug);
                              toast.success("Logo removed");
                              onRefresh();
                            } catch (err) {
                              toast.error("Failed to remove logo");
                            }
                          }
                        }}
                      >
                        Remove Logo
                      </Button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Logo Size Slider */}
              <div className="space-y-2">
                <Label>Logo Size: {formData.field_config.logo_size || 96}px</Label>
                <input
                  type="range"
                  min="48"
                  max="400"
                  value={formData.field_config.logo_size || 96}
                  onChange={(e) => updateFieldConfig("logo_size", parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Small</span>
                  <span>Large</span>
                </div>
              </div>
              
              {/* Photo Upload Mode */}
              <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
                <Label className="flex items-center gap-2">
                  <span>📷</span> Photo Upload
                </Label>
                <Select
                  value={formData.field_config.photo_mode}
                  onValueChange={(v) => updateFieldConfig("photo_mode", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Off - No photo upload</SelectItem>
                    <SelectItem value="optional">Optional - Customers can upload photos</SelectItem>
                    <SelectItem value="required">Required - Customers must upload photos</SelectItem>
                  </SelectContent>
                </Select>
                {formData.field_config.photo_mode !== "off" && (
                  <div className="space-y-2 mt-2">
                    <Label className="text-sm">Photo Instructions</Label>
                    <Textarea
                      value={formData.field_config.photo_instructions}
                      onChange={(e) => updateFieldConfig("photo_instructions", e.target.value)}
                      placeholder="e.g., 'Please upload photos of the area'"
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                )}
              </div>
              
              <Separator className="my-4" />
              
              {/* Appearance Section */}
              <p className="text-sm font-medium text-gray-700">Appearance</p>
              
              {/* Background Color */}
              <div className="space-y-2">
                <Label>Form Background Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={formData.field_config.form_background_color || "#eff6ff"}
                    onChange={(e) => updateFieldConfig("form_background_color", e.target.value)}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                  <span className="text-sm text-gray-500">
                    {formData.field_config.form_background_color || "Default"}
                  </span>
                  {formData.field_config.form_background_color && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => updateFieldConfig("form_background_color", "")}
                      className="text-xs"
                    >
                      Reset
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Text Color */}
              <div className="space-y-2">
                <Label>Text Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={formData.field_config.form_text_color || "#111827"}
                    onChange={(e) => updateFieldConfig("form_text_color", e.target.value)}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                  <span className="text-sm text-gray-500">
                    {formData.field_config.form_text_color || "Default"}
                  </span>
                  {formData.field_config.form_text_color && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => updateFieldConfig("form_text_color", "")}
                      className="text-xs"
                    >
                      Reset
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Font Style */}
              <div className="space-y-2">
                <Label>Font Style</Label>
                <Select
                  value={formData.field_config.form_font_family || "default"}
                  onValueChange={(v) => updateFieldConfig("form_font_family", v === "default" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Default (System)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default (System)</SelectItem>
                    <SelectItem value="Inter, sans-serif">Inter (Modern)</SelectItem>
                    <SelectItem value="Georgia, serif">Georgia (Classic)</SelectItem>
                    <SelectItem value="Verdana, sans-serif">Verdana (Clean)</SelectItem>
                    <SelectItem value="'Trebuchet MS', sans-serif">Trebuchet (Friendly)</SelectItem>
                    <SelectItem value="'Courier New', monospace">Courier (Technical)</SelectItem>
                    <SelectItem value="Arial, sans-serif">Arial (Simple)</SelectItem>
                    <SelectItem value="'Times New Roman', serif">Times (Traditional)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Save Button */}
              <Button onClick={handleSave} disabled={saving} className="w-full mt-4">
                {saving ? "Saving..." : "💾 Save Form Settings"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Business Information - Secondary card */}
        <Card>
          <CardHeader>
            <CardTitle>Business Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Notification reminder banner */}
            {!formData.notify_email && (
              <NotificationReminder variant="banner" />
            )}
            <div className="space-y-2">
              <Label>Business Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Notification Email</Label>
              <Input
                type="email"
                value={formData.notify_email}
                onChange={(e) => setFormData({ ...formData, notify_email: e.target.value })}
                placeholder="Where to receive lead notifications"
              />
            </div>
            <div className="space-y-2">
              <Label>Reply-To Email</Label>
              <Input
                type="email"
                value={formData.reply_to_email}
                onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })}
                placeholder="Email customers can reply to"
              />
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50">
              <input
                id="auto-followup-quotes-toggle"
                type="checkbox"
                className="mt-1 h-4 w-4 accent-blue-800"
                checked={formData.auto_followup_quotes !== false}
                onChange={(e) => setFormData({ ...formData, auto_followup_quotes: e.target.checked })}
                data-testid="auto-followup-quotes-toggle"
              />
              <label htmlFor="auto-followup-quotes-toggle" className="text-sm text-gray-700 leading-snug">
                <span className="font-medium text-gray-900">Auto follow-ups on pending quotes</span>
                <br />
                Automatically email customers a friendly reminder with a one-tap link back to their estimate on <strong>Day 3, 7, and 14</strong>. Stops when the quote is approved, declined, or expires.
              </label>
            </div>
            <ChemicalsSettingsBlock formData={formData} setFormData={setFormData} />
            <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
              {saving ? "Saving..." : "💾 Save Business Info"}
            </Button>
          </CardContent>
        </Card>

        {/* Add Form to Website - Compact collapsible - Only show if setup complete */}
        {isSetupComplete ? (
          <div className="border rounded-lg bg-white">
            <button
              onClick={() => setShowEmbedCode(!showEmbedCode)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <span>📋</span> Add Form to Your Website
              </span>
              <svg 
                className={`w-4 h-4 text-gray-500 transition-transform ${showEmbedCode ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showEmbedCode && (
              <div className="px-4 pb-4 border-t">
                <p className="text-xs text-gray-500 mt-3 mb-2">Copy this code and paste it into your website:</p>
                <div className="bg-gray-100 p-3 rounded text-xs font-mono break-all mb-2">
                  {`<iframe src="${BACKEND_URL?.replace('/api', '')}/t/${slug}" width="100%" height="700" frameborder="0"></iframe>`}
                </div>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`<iframe src="${BACKEND_URL?.replace('/api', '')}/t/${slug}" width="100%" height="700" frameborder="0"></iframe>`);
                    toast.success("Code copied!");
                  }}
                >
                  Copy Code
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="border rounded-lg bg-gray-50 p-4">
            <div className="flex items-center gap-2 text-gray-500">
              <span>🔒</span>
              <span className="text-sm font-medium">Add to Website</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Complete your setup first</p>
          </div>
        )}

        {/* Quote Resources - PDF and Video for quote emails */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span>📎</span> Quote Resources
            </CardTitle>
            <CardDescription>Add a PDF or video link to include in all quote emails</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* PDF Resource */}
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-red-500">📄</span>
                  <Label className="font-medium">PDF Document</Label>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quoteResources.pdf_enabled}
                    onChange={(e) => setQuoteResources(prev => ({ ...prev, pdf_enabled: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-gray-600">Include in emails</span>
                </label>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-gray-500">Upload PDF (max 10MB)</Label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    disabled={uploadingPdf}
                    className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-red-50 file:text-red-600 hover:file:bg-red-100"
                  />
                  {uploadingPdf && <span className="text-xs text-gray-500">Uploading...</span>}
                </div>
                {quoteResources.pdf_url && (
                  <div className="flex items-center gap-2 text-xs text-blue-700">
                    <span>✓ PDF uploaded</span>
                    <a href={quoteResources.pdf_url} target="_blank" rel="noopener noreferrer" className="underline">Preview</a>
                  </div>
                )}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Button Label</Label>
                <Input
                  placeholder="e.g., View Service Agreement"
                  value={quoteResources.pdf_label}
                  onChange={(e) => setQuoteResources(prev => ({ ...prev, pdf_label: e.target.value }))}
                  className="text-sm"
                />
              </div>
            </div>

            {/* Video Resource */}
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-blue-600">🎬</span>
                  <Label className="font-medium">Video Link</Label>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quoteResources.video_enabled}
                    onChange={(e) => setQuoteResources(prev => ({ ...prev, video_enabled: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-gray-600">Include in emails</span>
                </label>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Video URL (YouTube, Vimeo, etc.)</Label>
                <Input
                  type="url"
                  placeholder="https://youtube.com/watch?v=..."
                  value={quoteResources.video_url}
                  onChange={(e) => setQuoteResources(prev => ({ ...prev, video_url: e.target.value }))}
                  className="text-sm"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Button Label</Label>
                <Input
                  placeholder="e.g., Watch How We Work"
                  value={quoteResources.video_label}
                  onChange={(e) => setQuoteResources(prev => ({ ...prev, video_label: e.target.value }))}
                  className="text-sm"
                />
              </div>
            </div>

            <Button onClick={handleSaveQuoteResources} disabled={saving} className="w-full">
              {saving ? "Saving..." : "💾 Save Quote Resources"}
            </Button>
          </CardContent>
        </Card>

        {/* Subscription Management - Only show if stripe_customer_id exists */}
        {business?.stripe_customer_id && (
          <Card>
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Manage your ZipLeadPro subscription, update payment methods, or view invoices.
              </p>
              <Button 
                onClick={handleManageSubscription} 
                disabled={loadingPortal}
                variant="outline"
              >
                {loadingPortal ? "Loading..." : "Manage Subscription"}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Bottom spacer for mobile floating button */}
      <div className="lg:hidden h-24" />
    
      {/* Mobile Preview Floating Button - OUTSIDE scrollable area, truly fixed */}
      <MobilePreviewSheet
        intakeMode={formData.intake_mode}
        fieldConfig={formData.field_config}
        services={services}
        logoUrl={logoPreview || business?.logo_url}
        businessName={formData.name || business?.name}
        slug={slug}
        business={business}
        showOnAllScreens={true}
        onIntakeModeChange={(mode) => setFormData(prev => ({ ...prev, intake_mode: mode }))}
        onFieldConfigChange={(key, value) => setFormData(prev => ({ 
          ...prev, 
          field_config: { ...prev.field_config, [key]: value } 
        }))}
        onLogoChange={(file) => {
          if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
          } else {
            setLogoFile(null);
            setLogoPreview(null);
          }
        }}
        onSave={async (config, newLogoFile) => {
          setSaving(true);
          try {
            // Extract shop customization fields to top level
            const { shop_headline, shop_headline_size, shop_headline_color, shop_footer, shop_footer_size, shop_footer_color, ...fieldConfigFields } = config;
            const updatedFormData = { 
              ...formData, 
              field_config: { ...formData.field_config, ...fieldConfigFields },
              shop_headline,
              shop_headline_size,
              shop_headline_color,
              shop_footer,
              shop_footer_size,
              shop_footer_color
            };
            await api.updateBusiness(slug, updatedFormData);
            if (newLogoFile) {
              await api.uploadLogo(slug, newLogoFile);
            }
            toast.success("Settings saved!");
            await onRefresh();
          } catch (error) {
            toast.error("Failed to save");
          } finally {
            setSaving(false);
          }
        }}
      />
    </>
  );
};

// ============== PUBLIC FAQ PAGE ==============
const PublicFAQPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-900">
      <SEO 
        title="FAQ - Frequently Asked Questions"
        description="Get answers to common questions about ZipLeadPro: intake forms, quote building, deposits, Stripe payments, job scheduling, and more."
        path="/faq"
      />
      <nav className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-between">
        <div className="text-2xl font-bold text-white cursor-pointer" onClick={() => navigate("/")}>ZipLeadPro</div>
        <div className="flex gap-4">
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate("/login")}>Log In</Button>
          <Button className="bg-white text-slate-900 hover:bg-gray-100" onClick={() => navigate("/get-started")}>Get Started</Button>
        </div>
      </nav>
      
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h1>
          <p className="text-slate-300">Everything you need to know about ZipLeadPro</p>
        </div>
        
        <Card>
          <CardContent className="pt-6">
            <FAQSection />
          </CardContent>
        </Card>
        
        <div className="mt-8 text-center">
          <p className="text-slate-300 mb-4">Still have questions?</p>
          <Button className="bg-white text-slate-900 hover:bg-gray-100" onClick={() => window.location.href = "mailto:Admin@baselineholdingsllc.com"}>
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== HOME PAGE ==============

// Thin rotating social-proof strip placed directly under the Hero CTA.
// Three honest, founder-approved trust messages that fade through every 4
// seconds (paused on hover). Slot chosen because trust signals immediately
// after the first CTA validate click intent — the same pattern used by
// Stripe, Linear, and Cal.com.
const SOCIAL_PROOF_MESSAGES = [
  "Trusted by service businesses across multiple countries.",
  "Built by a service business owner — shaped by the people doing the work.",
  "New service businesses are subscribing every month.",
];

const SocialProofTicker = () => {
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const [fade, setFade] = React.useState(true);

  React.useEffect(() => {
    if (paused) return undefined;
    const tick = setInterval(() => {
      setFade(false);
      // brief opacity dip, then swap message, then fade back in
      setTimeout(() => {
        setIndex((i) => (i + 1) % SOCIAL_PROOF_MESSAGES.length);
        setFade(true);
      }, 250);
    }, 4000);
    return () => clearInterval(tick);
  }, [paused]);

  return (
    <div
      className="mt-10 max-w-3xl mx-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="social-proof-ticker"
    >
      <div className="border-t border-b border-white/10 bg-white/[0.03] backdrop-blur-sm py-3 px-4 rounded-md">
        <p
          key={index}
          className={`text-center text-sm sm:text-base text-blue-200/90 font-medium tracking-wide transition-opacity duration-300 ${fade ? "opacity-100" : "opacity-0"}`}
          data-testid="social-proof-message"
        >
          {SOCIAL_PROOF_MESSAGES[index]}
        </p>
      </div>
      <div className="flex justify-center gap-1.5 mt-2" aria-hidden="true">
        {SOCIAL_PROOF_MESSAGES.map((_, i) => (
          <span
            key={i}
            className={`h-1 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-blue-500" : "w-1.5 bg-white/20"}`}
          />
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <SEO 
        title="Lead Capture, Quotes & Scheduling for Service Businesses"
        description="Get jobs booked, deposits paid, and scheduled before you ever step on site. Send one link to collect photos, service details, and customer info. Review, quote, and get paid."
        path="/"
      />
      {/* Nav */}
      <nav className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <img src="/zlp-logo.png" alt="ZipLeadPro" className="h-10 sm:h-20 w-auto object-contain shrink-0" />
          <span className="text-base sm:text-2xl font-bold whitespace-nowrap">ZipLeadPro</span>
        </div>
        <div className="flex gap-1 sm:gap-3 shrink-0">
          <Button variant="ghost" className="text-white hover:bg-white/10 text-xs sm:text-base px-2 sm:px-4" onClick={() => navigate("/login")}>Log In</Button>
          <Button className="bg-white text-slate-900 hover:bg-gray-100 text-xs sm:text-base px-2.5 sm:px-4" onClick={() => navigate("/get-started")}>Get Started</Button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Get Jobs Booked, Deposits Paid, and Scheduled Before You Ever Step On Site.</h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8">
            Send one link. Collect photos, service details, and customer info upfront. Review it. Send the quote. Get paid and schedule the job.
          </p>
          
          {/* Video Explainer — hidden for now per user request */}
          {false && (
          <div className="relative w-full max-w-2xl mx-auto mb-8 rounded-xl overflow-hidden shadow-2xl">
            <div className="aspect-video">
              <iframe
                src="https://www.youtube.com/embed/ko-IdWK7xRI"
                title="ZipLeadPro Explainer"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
          )}
          <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100" onClick={() => navigate("/get-started")}>
            Start Your 10-Day Free Trial
          </Button>
          <p className="text-sm text-gray-400 mt-3">No website required. $29/month or $290/year after trial. Cancel anytime.</p>
        </div>
        <SocialProofTicker />
      </div>

      {/* How It Works Section */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-3xl font-bold text-center mb-8">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-2xl mx-auto mb-3 font-bold">1</div>
            <h3 className="text-lg font-bold mb-1.5">Send Your Link</h3>
            <p className="text-gray-300 text-sm">Text it. Post it. Put it in your bio. No website or app needed.</p>
          </div>
          <div className="text-center">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-2xl mx-auto mb-3 font-bold">2</div>
            <h3 className="text-lg font-bold mb-1.5">Get Notified Instantly</h3>
            <p className="text-gray-300 text-sm">Every request arrives organized with photos, details, and contact info.</p>
          </div>
          <div className="text-center">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-2xl mx-auto mb-3 font-bold">3</div>
            <h3 className="text-lg font-bold mb-1.5">Send. Get Paid. Schedule.</h3>
            <p className="text-gray-300 text-sm">Send the quote, collect the deposit, and the job lands in your scheduling queue.</p>
          </div>
        </div>
      </div>

      {/* Competitor Comparison */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">Why ZipLeadPro Beats Jobber + Yardbook Combined</h2>
        <p className="text-gray-400 text-center mb-8 max-w-3xl mx-auto">One simple tool that takes you from the first customer request all the way to "job done" — without extra add-ons, or extra apps.</p>

        {(() => {
          const rows = [
            ["Monthly price", "$29 flat", "$50 to $130+", "Free"],
            ["Customers can request a job from a public link with photos", "Yes — built in", "Available on higher plans", "Not Supported"],
            ["Customer must pay a deposit or full price upon approval", "Yes — built in, saves time", "Limited", "You send a payment request separately, more clicks"],
            ["Paid jobs automatically land in a list to schedule", "Yes, Tap & Schedule", "Not Supported", "Not Supported"],
            ["Satellite property measuring", "Yes — Easy to use on mobile", "Costs $19/month extra, Clunky", "Included, Poor on mobile"],
            ["Send the quote through your phone's regular text app", "Yes", "Not Supported", "Not Supported"],
            ["Tracks chemical applications + state rules for pesticide pros", "Yes — built in, Mobile Friendly", "Desktop Only", "Limited/Poor mobile experience"],
            ["How long it takes to get set up", "Minutes", "Days", "Hours"],
            ["Handles the whole job from request → quote → payment → scheduling → done", "Yes — one tool", "Yes, but across several tools and add-ons", "Quoting and basic record-keeping"],
          ];
          return (
            <>
              {/* Desktop / tablet table */}
              <div className="hidden md:block rounded-2xl border border-white/15 bg-white/5 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-700/20 text-white">
                      <th className="text-left font-semibold p-4 w-1/2">Feature</th>
                      <th className="text-center font-bold p-4 text-blue-300 whitespace-nowrap">ZipLeadPro</th>
                      <th className="text-center font-medium p-4 text-gray-300 whitespace-nowrap">Jobber</th>
                      <th className="text-center font-medium p-4 text-gray-300 whitespace-nowrap">Yardbook</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-200">
                    {rows.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white/0" : "bg-white/[0.04]"}>
                        <td className="p-3.5 sm:p-4 font-medium text-white border-t border-white/10">{row[0]}</td>
                        <td className="p-3.5 sm:p-4 text-center text-blue-300 font-semibold border-t border-white/10">{row[1]}</td>
                        <td className="p-3.5 sm:p-4 text-center text-gray-400 border-t border-white/10">{row[2]}</td>
                        <td className="p-3.5 sm:p-4 text-center text-gray-400 border-t border-white/10">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile — one card per feature, no scrolling needed */}
              <div className="md:hidden space-y-3">
                {rows.map((row, i) => (
                  <div key={i} className="rounded-xl border border-white/15 bg-white/5 p-4">
                    <p className="text-white font-semibold mb-3 leading-snug">{row[0]}</p>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-start justify-between gap-3 bg-blue-700/15 rounded-lg px-3 py-2">
                        <span className="text-blue-300 font-bold">ZipLeadPro</span>
                        <span className="text-blue-200 text-right">{row[1]}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 px-3 py-1.5">
                        <span className="text-gray-400">Jobber</span>
                        <span className="text-gray-400 text-right">{row[2]}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 px-3 py-1.5">
                        <span className="text-gray-400">Yardbook</span>
                        <span className="text-gray-400 text-right">{row[3]}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          );
        })()}

        <div className="text-center mt-10">
          <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100" onClick={() => navigate("/get-started")}>
            Try ZipLeadPro Free for 10 Days
          </Button>
          <p className="text-sm text-gray-400 mt-3">No credit card. Cancel anytime.</p>
        </div>
      </div>

      {/* Also Included — table-stakes features without the competitor comparison */}
      <div className="max-w-5xl mx-auto px-4 pb-10">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
          <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-1">Also included, no add-ons required</h3>
          <p className="text-gray-400 text-center text-sm mb-4">The basics every service business needs — already in the box.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {[
              "Record cash, check, Venmo, Zelle, or any other offline payment",
              "Approve a quote on a customer's behalf for in-person and phone sales",
              "Returning customer info auto-fills on the next quote or job",
              "Drag-and-drop scheduling on desktop",
              "Recurring jobs you can run only in certain months",
              "Custom branded link with your logo",
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-2 bg-white/5 rounded-lg px-3 py-2.5">
                <span className="text-blue-500 font-bold mt-0.5">✓</span>
                <span className="text-gray-200">{line}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Core Features Section — tap-to-reveal cards keep the page tight.
          Positioned AFTER comparison + "Also included" so high-intent visitors
          have already converted; this section serves the researcher persona
          who wants feature-depth confirmation before reading the FAQ. */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-3xl font-bold text-center mb-2">Built for Service Businesses</h2>
        <p className="text-gray-400 text-center mb-6 text-sm">Everything you need to quote, get paid and schedule — without the chaos. Tap any card to see how it works.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { icon: "💳", title: "Deposits That Lock In the Job", body: "When a customer approves a quote, they pay a 25% deposit or the full amount right then — no exceptions, no \"I'll send it later.\" Healthier cash flow, no more chasing payments." },
            { icon: "🚀", title: "Paid Jobs Auto-Queue for Scheduling", body: "The moment a customer approves and pays, the job drops into a \"Needs Scheduling\" queue. Tap the customer, pick the date, time, and frequency — booked in seconds. No copy-pasting, no missed jobs." },
            { icon: "📥", title: "Leads Straight to Your Inbox", body: "Every submission arrives as a professional email with all details and photos attached. Never miss a job because you were on a roof or behind a mower." },
            { icon: "📋", title: "Structured Intake Forms", body: "Stop chasing customers for photos and addresses. Your link asks for exactly what you need before they hit submit. You get to choose." },
            { icon: "📅", title: "Smart Job Calendar", body: "Drag-and-drop scheduling for one-off or recurring jobs. Pick the months a service runs. Mark jobs done in one tap." },
            { icon: "💵", title: "Record Cash, Check, CashApp, Venmo, Zelle & More", body: "Got paid offline? Mark the quote paid in two taps. Tracks partial payments, shows balance due, and stays out of your way." },
            { icon: "✍️", title: "Manual Approvals", body: "Approve a quote on the customer's behalf for in-person and over-the-phone sales — silently, without sending another email." },
            { icon: "📱", title: "Click-to-Text Estimates", body: "Send quote links via your own phone. Auto-generates and launches your text app — you just tap send. Replies come straight to your number, no third-party SMS to set up." },
            { icon: "🛰️", title: "Satellite Measuring Tool", body: "Measure lawns, driveways, and properties from satellite imagery. Save measurements to a customer so you never re-measure twice." },
            { icon: "👥", title: "Customer Records & Auto-Fill", body: "Returning customer? Their name, email, phone, and address auto-fill on the next quote or job. No re-typing." },
            { icon: "🔗", title: "Your Custom Branded Link", body: "Upload your logo. Customers see your brand, not ours." },
            { icon: "🧪", title: "Chemical Application Tracking", body: "Log applications with REI hours, EPA #, and 50-state compliance fields." },
          ].map((f, i) => (
            <details key={i} className="group bg-white/10 border border-white/20 rounded-xl hover:bg-white/15 hover:border-blue-500/40 transition-all cursor-pointer">
              <summary className="list-none p-4 flex items-center gap-3 select-none">
                <span className="text-2xl">{f.icon}</span>
                <span className="text-white font-semibold text-sm sm:text-base flex-1">{f.title}</span>
                <span className="text-blue-300 group-open:rotate-45 transition-transform text-xl leading-none shrink-0">+</span>
              </summary>
              <p className="text-gray-300 text-sm leading-relaxed px-4 pb-4 pt-0">{f.body}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">
  <details className="group bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
    <summary className="list-none cursor-pointer px-5 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-4 hover:bg-white/[0.05] transition-colors">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white">
          Frequently Asked Questions
        </h2>
        <p className="text-gray-400 text-sm mt-1">Tap to see common questions and answers.</p>
      </div>
      <span className="text-blue-300 text-3xl leading-none group-open:rotate-45 transition-transform shrink-0">+</span>
    </summary>

    <div className="px-5 sm:px-8 pb-6 sm:pb-8">

  <div className="space-y-4">

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Do customers have to pay when they approve a quote?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        Yes — when a customer approves a quote, they pay a 25% deposit or the full amount right then. No "I'll send it later." It locks in the job and means you stop chasing payments after the work is done.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">What happens after a customer pays?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        The job lands automatically in a "Needs Scheduling" list. Tap the customer, pick the date, time, and how often the job repeats — done. No copy-pasting, no jobs falling through the cracks.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Does ZipLeadPro remember my returning customers?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        Yes. When you start a new quote or job, the customer's name, email, phone, and address auto-fill the moment you pick them. No re-typing the same info every season.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Can I record cash, check, Venmo or Zelle payments?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        Yes. Every quote has a "Record Payment" option. Pick the method (Cash, Check, Venmo, CashApp, PayPal, Zelle, or Other), enter the amount, done. Partial payments are tracked automatically and the quote shows the remaining balance until it's paid in full.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Can I approve a quote for a customer myself?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        Yes. For elderly customers, in-person sales, or phone quotes, you can approve a quote on the customer's behalf in one tap. The status badge shows "Approved · Manual" so you always know which approvals were yours and which came from the customer.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Can I measure properties without leaving my truck?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        Yes. The built-in satellite measuring tool lets you outline lawns, driveways, and any area straight from satellite imagery. Save the measurement to a customer record so you never have to measure the same property twice.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Do you track chemical applications for licensed applicators?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        Yes. Log each application with EPA number, REI hours, rate, and 50-state compliance fields.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">How does texting estimates work?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        ZipLeadPro generates a clean short-link you can send through your phone's regular text app. The customer taps it, opens the quote, and replies come directly to your number — no third-party SMS service to set up or pay for.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Do I need Stripe to use ZipLeadPro?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        No. You can still send quotes and include your own payment link. Connecting Stripe unlocks built-in payment buttons, deposits, card-on-file checkout, auto-charging at job completion, and the smoother payment experience.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Do customers need to create an account?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        No. Customers simply open your link, provide job details, upload photos if needed, review the quote, and pay if required.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Will customers actually fill out the form?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        Yes. Customers already send photos and job details through text all the time. ZipLeadPro simply organizes that information in a cleaner and more professional way.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">What types of businesses is ZipLeadPro designed for?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        ZipLeadPro works best for service businesses like lawn care, junk removal, hauling, pressure washing, snow removal, and similar jobs that can be quoted from photos or straightforward job details.
      </p>
    </details>

    <details className="bg-white/10 border border-white/20 rounded-xl p-5">
      <summary className="cursor-pointer text-white font-semibold">Do I have to use the link for every job?</summary>
      <p className="text-slate-300 mt-3 text-sm leading-relaxed">
        No. You can still add customers manually, create quotes yourself, and send them the traditional way whenever you prefer.
      </p>
    </details>

  </div>
    </div>
  </details>
</div>

      {/* Pricing Section */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-3xl font-bold text-center mb-8">Simple Pricing</h2>
        <div className="bg-white/10 rounded-lg p-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-300 px-4 py-2 rounded-full text-lg font-medium mb-4">
            10-Day Free Trial
          </div>
          <p className="text-4xl font-bold mb-2">Then $29/month or $290/year</p>
          <p className="text-gray-300 mb-2">Unlimited leads. Unlimited quotes. One link.</p>
          <p className="text-sm text-gray-400 mb-6">Save $58 with annual. No contracts. Cancel anytime.</p>
          <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100" onClick={() => navigate("/get-started")}>
            Start Free Trial
          </Button>
        </div>
      </div>

      <footer className="max-w-6xl mx-auto px-4 py-8 text-center text-gray-400 text-sm">© 2025 ZipLeadPro</footer>
    </div>
  );
};

// ============== LANGUAGE PICKER ==============
// Floating bottom-right button that lets visitors translate the entire page
// into any of the 130+ languages supported by Google Translate's element widget.
// Each entry carries both an English name (for predictable A–Z sorting and
// scanning by US users) and a native-script label (shown alongside so native
// speakers can also recognize their language at a glance).
const LanguagePicker = () => {
  const [open, setOpen] = React.useState(false);
  const [translating, setTranslating] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const LANGS = [
    { code: "af", en: "Afrikaans", native: "Afrikaans" },
    { code: "sq", en: "Albanian", native: "Shqip" },
    { code: "am", en: "Amharic", native: "አማርኛ" },
    { code: "ar", en: "Arabic", native: "العربية" },
    { code: "hy", en: "Armenian", native: "Հայերեն" },
    { code: "as", en: "Assamese", native: "অসমীয়া" },
    { code: "ay", en: "Aymara", native: "Aymar aru" },
    { code: "az", en: "Azerbaijani", native: "Azərbaycan" },
    { code: "bm", en: "Bambara", native: "Bamanankan" },
    { code: "eu", en: "Basque", native: "Euskara" },
    { code: "be", en: "Belarusian", native: "Беларуская" },
    { code: "bn", en: "Bengali", native: "বাংলা" },
    { code: "bho", en: "Bhojpuri", native: "भोजपुरी" },
    { code: "bs", en: "Bosnian", native: "Bosanski" },
    { code: "bg", en: "Bulgarian", native: "Български" },
    { code: "ca", en: "Catalan", native: "Català" },
    { code: "ceb", en: "Cebuano", native: "Cebuano" },
    { code: "ny", en: "Chichewa", native: "Chichewa" },
    { code: "zh-CN", en: "Chinese (Simplified)", native: "中文 (简体)" },
    { code: "zh-TW", en: "Chinese (Traditional)", native: "中文 (繁體)" },
    { code: "co", en: "Corsican", native: "Corsu" },
    { code: "hr", en: "Croatian", native: "Hrvatski" },
    { code: "cs", en: "Czech", native: "Čeština" },
    { code: "da", en: "Danish", native: "Dansk" },
    { code: "dv", en: "Dhivehi", native: "ދިވެހި" },
    { code: "doi", en: "Dogri", native: "डोगरी" },
    { code: "nl", en: "Dutch", native: "Nederlands" },
    { code: "en", en: "English", native: "English" },
    { code: "eo", en: "Esperanto", native: "Esperanto" },
    { code: "et", en: "Estonian", native: "Eesti" },
    { code: "ee", en: "Ewe", native: "Eʋegbe" },
    { code: "tl", en: "Filipino", native: "Filipino" },
    { code: "fi", en: "Finnish", native: "Suomi" },
    { code: "fr", en: "French", native: "Français" },
    { code: "fy", en: "Frisian", native: "Frysk" },
    { code: "gl", en: "Galician", native: "Galego" },
    { code: "ka", en: "Georgian", native: "ქართული" },
    { code: "de", en: "German", native: "Deutsch" },
    { code: "el", en: "Greek", native: "Ελληνικά" },
    { code: "gn", en: "Guarani", native: "Avañe'ẽ" },
    { code: "gu", en: "Gujarati", native: "ગુજરાતી" },
    { code: "ht", en: "Haitian Creole", native: "Kreyòl Ayisyen" },
    { code: "ha", en: "Hausa", native: "Hausa" },
    { code: "haw", en: "Hawaiian", native: "ʻŌlelo Hawaiʻi" },
    { code: "he", en: "Hebrew", native: "עברית" },
    { code: "hi", en: "Hindi", native: "हिन्दी" },
    { code: "hmn", en: "Hmong", native: "Hmoob" },
    { code: "hu", en: "Hungarian", native: "Magyar" },
    { code: "is", en: "Icelandic", native: "Íslenska" },
    { code: "ig", en: "Igbo", native: "Igbo" },
    { code: "ilo", en: "Ilocano", native: "Ilokano" },
    { code: "id", en: "Indonesian", native: "Bahasa Indonesia" },
    { code: "ga", en: "Irish", native: "Gaeilge" },
    { code: "it", en: "Italian", native: "Italiano" },
    { code: "ja", en: "Japanese", native: "日本語" },
    { code: "jw", en: "Javanese", native: "Basa Jawa" },
    { code: "kn", en: "Kannada", native: "ಕನ್ನಡ" },
    { code: "kk", en: "Kazakh", native: "Қазақ" },
    { code: "km", en: "Khmer", native: "ខ្មែរ" },
    { code: "rw", en: "Kinyarwanda", native: "Kinyarwanda" },
    { code: "gom", en: "Konkani", native: "कोंकणी" },
    { code: "ko", en: "Korean", native: "한국어" },
    { code: "kri", en: "Krio", native: "Krio" },
    { code: "ku", en: "Kurdish (Kurmanji)", native: "Kurdî" },
    { code: "ckb", en: "Kurdish (Sorani)", native: "کوردی" },
    { code: "ky", en: "Kyrgyz", native: "Кыргызча" },
    { code: "lo", en: "Lao", native: "ລາວ" },
    { code: "la", en: "Latin", native: "Latina" },
    { code: "lv", en: "Latvian", native: "Latviešu" },
    { code: "ln", en: "Lingala", native: "Lingála" },
    { code: "lt", en: "Lithuanian", native: "Lietuvių" },
    { code: "lg", en: "Luganda", native: "Luganda" },
    { code: "lb", en: "Luxembourgish", native: "Lëtzebuergesch" },
    { code: "mk", en: "Macedonian", native: "Македонски" },
    { code: "mai", en: "Maithili", native: "मैथिली" },
    { code: "mg", en: "Malagasy", native: "Malagasy" },
    { code: "ms", en: "Malay", native: "Bahasa Melayu" },
    { code: "ml", en: "Malayalam", native: "മലയാളം" },
    { code: "mt", en: "Maltese", native: "Malti" },
    { code: "mi", en: "Maori", native: "Māori" },
    { code: "mr", en: "Marathi", native: "मराठी" },
    { code: "mni-Mtei", en: "Meiteilon (Manipuri)", native: "ꯃꯤꯇꯩ ꯂꯣꯟ" },
    { code: "lus", en: "Mizo", native: "Mizo ṭawng" },
    { code: "mn", en: "Mongolian", native: "Монгол" },
    { code: "my", en: "Myanmar (Burmese)", native: "မြန်မာ" },
    { code: "ne", en: "Nepali", native: "नेपाली" },
    { code: "no", en: "Norwegian", native: "Norsk" },
    { code: "or", en: "Odia (Oriya)", native: "ଓଡ଼ିଆ" },
    { code: "om", en: "Oromo", native: "Afaan Oromoo" },
    { code: "ps", en: "Pashto", native: "پښتو" },
    { code: "fa", en: "Persian", native: "فارسی" },
    { code: "pl", en: "Polish", native: "Polski" },
    { code: "pt", en: "Portuguese", native: "Português" },
    { code: "pa", en: "Punjabi", native: "ਪੰਜਾਬੀ" },
    { code: "qu", en: "Quechua", native: "Runa Simi" },
    { code: "ro", en: "Romanian", native: "Română" },
    { code: "ru", en: "Russian", native: "Русский" },
    { code: "sm", en: "Samoan", native: "Gagana Sāmoa" },
    { code: "sa", en: "Sanskrit", native: "संस्कृतम्" },
    { code: "gd", en: "Scots Gaelic", native: "Gàidhlig" },
    { code: "nso", en: "Sepedi", native: "Sepedi" },
    { code: "sr", en: "Serbian", native: "Српски" },
    { code: "st", en: "Sesotho", native: "Sesotho" },
    { code: "sn", en: "Shona", native: "Shona" },
    { code: "sd", en: "Sindhi", native: "سنڌي" },
    { code: "si", en: "Sinhala", native: "සිංහල" },
    { code: "sk", en: "Slovak", native: "Slovenčina" },
    { code: "sl", en: "Slovenian", native: "Slovenščina" },
    { code: "so", en: "Somali", native: "Soomaali" },
    { code: "es", en: "Spanish", native: "Español" },
    { code: "su", en: "Sundanese", native: "Basa Sunda" },
    { code: "sw", en: "Swahili", native: "Kiswahili" },
    { code: "sv", en: "Swedish", native: "Svenska" },
    { code: "tg", en: "Tajik", native: "Тоҷикӣ" },
    { code: "ta", en: "Tamil", native: "தமிழ்" },
    { code: "tt", en: "Tatar", native: "Татарча" },
    { code: "te", en: "Telugu", native: "తెలుగు" },
    { code: "th", en: "Thai", native: "ไทย" },
    { code: "ti", en: "Tigrinya", native: "ትግርኛ" },
    { code: "ts", en: "Tsonga", native: "Xitsonga" },
    { code: "tr", en: "Turkish", native: "Türkçe" },
    { code: "tk", en: "Turkmen", native: "Türkmen" },
    { code: "ak", en: "Twi", native: "Twi" },
    { code: "uk", en: "Ukrainian", native: "Українська" },
    { code: "ur", en: "Urdu", native: "اردو" },
    { code: "ug", en: "Uyghur", native: "ئۇيغۇرچە" },
    { code: "uz", en: "Uzbek", native: "Oʻzbek" },
    { code: "vi", en: "Vietnamese", native: "Tiếng Việt" },
    { code: "cy", en: "Welsh", native: "Cymraeg" },
    { code: "xh", en: "Xhosa", native: "isiXhosa" },
    { code: "yi", en: "Yiddish", native: "ייִדיש" },
    { code: "yo", en: "Yoruba", native: "Yorùbá" },
    { code: "zu", en: "Zulu", native: "isiZulu" },
  ];

  // Strict A–Z sort by English name. English itself is pinned first so users
  // always have an obvious one-tap reset back to the original page.
  const sortedLangs = React.useMemo(() => {
    const english = LANGS.find((l) => l.code === "en");
    const rest = LANGS
      .filter((l) => l.code !== "en")
      .sort((a, b) => a.en.localeCompare(b.en, "en"));
    return english ? [english, ...rest] : rest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter against both English and native labels so users can search either way.
  const visibleLangs = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedLangs;
    return sortedLangs.filter(
      (l) => l.en.toLowerCase().includes(q) || l.native.toLowerCase().includes(q)
    );
  }, [query, sortedLangs]);

  const pick = (code) => {
    setOpen(false);
    setTranslating(true);

    // Persist the choice so it survives navigation/refresh. Set the cookie on every
    // domain variant the browser might honor (current host, exact host, bare domain).
    const hostname = window.location.hostname;
    const variants = [hostname, "." + hostname];
    const bare = hostname.split(".").slice(-2).join(".");
    if (bare && bare !== hostname) variants.push("." + bare);
    const writeCookie = (value, extra = "") => {
      document.cookie = `googtrans=${value}; path=/${extra}`;
      variants.forEach((d) => { document.cookie = `googtrans=${value}; path=/; domain=${d}${extra}`; });
    };

    if (code === "en") {
      // Clear cookies + reload to undo any prior translation
      writeCookie("", "; expires=Thu, 01 Jan 1970 00:00:00 GMT");
      window.location.reload();
      return;
    }

    writeCookie(`/en/${code}`);

    // Drive Google Translate's hidden <select> directly so the page translates
    // immediately, no reload needed. The widget script loads async, so we wait
    // until it's mounted (poll up to ~5s) then dispatch the change event.
    let tries = 0;
    const maxTries = 50; // 50 * 100ms = 5s
    const tryApply = () => {
      const select = document.querySelector("#google_translate_element select.goog-te-combo");
      if (select) {
        select.value = code;
        select.dispatchEvent(new Event("change"));
        // Translation takes a moment to render — keep the indicator on briefly so the
        // user gets visible feedback even if their selected language re-uses Latin script.
        setTimeout(() => setTranslating(false), 1200);
        return;
      }
      if (++tries < maxTries) {
        setTimeout(tryApply, 100);
      } else {
        // Widget never showed up — fall back to a reload so the cookie kicks in.
        window.location.reload();
      }
    };
    tryApply();
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60]" data-testid="language-picker">
      {translating && (
        <div className="mb-2 bg-blue-800 text-white text-xs font-medium px-3 py-2 rounded-full shadow-lg flex items-center gap-2" data-testid="translating-indicator">
          <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
          Translating…
        </div>
      )}
      {open && (
        <div
          className="mb-2 bg-white shadow-2xl rounded-xl border border-gray-200 overflow-hidden w-72"
          data-testid="language-list"
        >
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search language…"
              autoFocus
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-700"
              data-testid="language-search"
            />
          </div>
          <div className="max-h-[55vh] overflow-y-auto">
            {visibleLangs.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500" data-testid="lang-empty">
                No language matches "{query}"
              </div>
            ) : (
              visibleLangs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => pick(l.code)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-slate-900 transition-colors border-b border-gray-100 last:border-b-0"
                  data-testid={`lang-${l.code}`}
                >
                  <div className="font-medium text-gray-900">{l.en}</div>
                  {l.native && l.native !== l.en && (
                    <div className="text-xs text-gray-500">{l.native}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
      <button
        onClick={() => { setOpen(o => { if (o) setQuery(""); return !o; }); }}
        className="bg-blue-800 hover:bg-slate-900 text-white rounded-full shadow-lg w-11 h-11 sm:w-auto sm:h-auto sm:px-3 sm:py-2 flex items-center justify-center sm:gap-1.5 transition-all"
        aria-label="Change language"
        data-testid="language-button"
      >
        <span className="text-base" aria-hidden>🌐</span>
        <span className="hidden sm:inline text-xs font-medium">Language</span>
      </button>
    </div>
  );
};


// ============== MAIN APP ==============
function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <div className="App">
          <Toaster position="top-right" richColors duration={2000} closeButton toastOptions={{ style: { maxWidth: '320px' } }} />
          <LanguagePicker />
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/faq" element={<PublicFAQPage />} />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />
              <Route path="/reset-password" element={<BusinessResetPassword />} />
              <Route path="/auth/verify" element={<MagicLinkVerify />} />
              <Route path="/get-started" element={<GetStarted />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/payment/success" element={<QuotePaymentSuccess />} />
              <Route path="/payment/cancel" element={<QuotePaymentCancel />} />
              <Route path="/invoice/:quoteId" element={<InvoicePage />} />
              <Route path="/i/:code" element={<ShortLinkRedirect />} />
              <Route path="/route/:routeId" element={<SharedRoutePage />} />
              <Route path="/onboarding/success" element={<OnboardingSuccess />} />
              <Route path="/onboarding/:token" element={<OnboardingPage />} />
              <Route path="/t/:businessSlug" element={<CustomerIntakeForm />} />
              
              {/* Protected routes */}
              <Route path="/admin" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/:slug/quote/:leadId" element={
                <ProtectedRoute requiredRole="business">
                  <DirectQuoteBuilder />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/:slug" element={
                <ProtectedRoute requiredRole="business">
                  <BusinessDashboard />
                </ProtectedRoute>
              } />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </div>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;